class ProductConfigurator {
  constructor(containerEl) {
    this.container = containerEl;
    
    try {
      const dataEl = document.getElementById('configurator-data');
      if (!dataEl) {
        return;
      }
      
      this.data = JSON.parse(dataEl.textContent);
    } catch (error) {
      return;
    }
    
    this.state = {
      selections: {},
      basePrice: 0,
      totalPrice: 0,
      modifiersTotal: 0,
      originalPrice: 0,
      selectedModel: null, // Currently selected Model
      currentConfiguration: null, // Current Model Size Configuration
      selectedVariantId: null, // Track selected variant for simple products
      isVariantMode: false, // Track if we're in simple variant mode
      rivetColorOverride: null, // Custom rivet color (null = use body color)
      craneHingeColorOverride: null // Custom crane hinge color (null = use body color)
    };
    
    this.currentStep = 1;
    this.steps = [];
    this.visitedSteps = new Set([1]); // Track which steps user has visited (start with step 1)
    this.isEditingFromSummary = false;
    this.savedStep = null;
    
    // Store models and configurations from data
    this.models = this.data.models || [];
    this.modelSizeConfigurations = this.data.modelSizeConfigurations || [];
    
    // Model image manifests (new system)
    this.modelImageManifests = {};
    
    // Visual manifest for door color rendering (legacy)
    this.visualManifest = null;
    this.loadVisualManifest();
    
    // Clear localStorage on page load/refresh
    this.clearStorageOnPageLoad();
    
    // Listen for pageshow event (fires when navigating back via browser back button)
    window.addEventListener('pageshow', (event) => {
      // Restore scroll in case it was blocked by a modal
      document.body.style.overflow = '';
      document.body.classList.remove('modal-open');
      
      // Close any open modals
      document.querySelectorAll('.modal').forEach(modal => {
        modal.classList.remove('active');
      });
    });
    
    // Listen for reset event from app block
    window.addEventListener('configurator:reset', () => {
      this.resetConfigurator();
    });
    
    // Initialize asynchronously to wait for manifest loading
    this.initAsync();
  }

  async initAsync() {
    // Load manifests and wait for door dimension detection to complete
    await this.loadModelImageManifests();
    
    // Now initialize the configurator
    this.init();
  }

  loadVisualManifest() {
    // Try to load manifest from product metafield or use default
    const manifestEl = document.getElementById('visual-manifest');
    if (manifestEl) {
      try {
        this.visualManifest = JSON.parse(manifestEl.textContent);
      } catch (e) {
        console.warn('Failed to parse visual manifest:', e);
      }
    }
  }

  async loadModelImageManifests() {
    const manifestEl = document.getElementById('model-image-manifests');
    if (manifestEl) {
      try {
        const data = JSON.parse(manifestEl.textContent);
        // Map model IDs to their manifests
        for (const item of data.manifests) {
          if (item.imageManifest) {
            try {
              // Parse the JSON string if it's a string, otherwise use directly
              const manifest = typeof item.imageManifest === 'string' 
                ? JSON.parse(item.imageManifest) 
                : item.imageManifest;
              
              // Auto-detect door dimensions if not provided
              if (manifest.masks?.doorMasks && !manifest.doorDimensions) {
                console.log('🔍 Auto-detecting door dimensions for model:', item.modelName);
                manifest.doorDimensions = {};
                
                // Detect dimensions for each size
                const detectionPromises = [];
                for (const [sizeCode, doorMaskUrl] of Object.entries(manifest.masks.doorMasks)) {
                  detectionPromises.push(
                    this.detectDoorDimensionsSync(doorMaskUrl, sizeCode).then(dimensions => {
                      manifest.doorDimensions[sizeCode] = dimensions;
                      console.log(`📐 Auto-detected door dimensions for ${sizeCode}:`, dimensions);
                    })
                  );
                }
                
                // Wait for all detections to complete
                await Promise.all(detectionPromises);
              }
              
              this.modelImageManifests[item.modelId] = manifest;
            } catch (e) {
              console.warn(`Failed to parse manifest for model ${item.modelName}:`, e);
            }
          }
        }
        console.log('✅ Loaded model image manifests:', this.modelImageManifests);
      } catch (e) {
        console.warn('Failed to parse model image manifests:', e);
      }
    }
  }

  clearStorageOnPageLoad() {
    // Clear configurator selections from localStorage when page loads
    localStorage.removeItem('configuratorSelections');
    
    // Ensure body scroll is enabled (in case modal was open during navigation)
    document.body.style.overflow = '';
    document.body.classList.remove('modal-open');
  }

  init() {
    // Organize option values into groups
    this.data.optionGroups.forEach(group => {
      const valuesForGroup = this.data.optionValues.filter(v => v.groupId === group.systemId);
      group.values = valuesForGroup.sort((a, b) => a.sortOrder - b.sortOrder);
    });
    
    // Sort option groups by sort_order
    this.data.optionGroups.sort((a, b) => a.sortOrder - b.sortOrder);

    // Create steps from available groups
    this.createSteps();
    
    // Render size selection
    this.renderSizeSelection();
    
    // Render quiz steps
    this.renderQuizSteps();
    
    // Create step indicator
    this.createStepIndicator();
    
    // Create modal for image zoom
    this.createImageModal();
    
    // Initialize visual rendering
    this.initializeVisualRendering();
    
    // Attach event listeners
    this.attachListeners();
    
    // Initialize Lucide icons
    if (typeof lucide !== 'undefined') {
      lucide.createIcons();
    }
    
    // Initialize pricing
    this.initializePricing();
    // Initialize Key Features behavior scoped to this configurator instance
    this.initKeyFeatures();

    // Track last cabinet auto-open to avoid repeated opens
    this._lastAutoOpenedCabinet = null;

    // Listen for configuration updates (from blocks or other sources).
    // If a cabinet_size appears in the flat selections, attempt to auto-open the watch_winders inline group.
    window.addEventListener('configurator:updated', (event) => {
      try {
        const selections = event.detail?.selections || window.configuratorState?.selections || {};
        const cabSel = selections['cabinet_size'] || this.state.selections['cabinet_size'];
        let cabCode = undefined;
        if (Array.isArray(cabSel)) cabCode = cabSel[0];
        else if (cabSel && typeof cabSel === 'object') cabCode = Object.keys(cabSel)[0];
        else if (typeof cabSel === 'string') cabCode = cabSel;

        // NOTE: Auto-opening watch_winders is now handled via inline_unlock_groups dependency
        // on _WW cabinet variants (e.g., CAB_SML_HALF_WW), not for regular cabinet options
        // This section has been removed to prevent opening watch_winders for regular cabinet selections
      } catch (e) {
        console.warn('Error in configurator:updated auto-open handler:', e);
      }
    });
  }

  initializeVisualRendering() {
    // Don't render on initial page load - safeImageContainer only shows configured images
    // The normal product images from Shopify will display by default
    // renderSafeImage() will be called when showing summary with completed configuration
  }

  renderSafeImage(targetPrefix = '') {
    // Get current model first to determine brand
    const currentModel = this.getCurrentModel();
    if (!currentModel) {
      console.warn('⚠️ No current model found');
      return;
    }
    
    // Get manifest for this model
    const manifest = this.modelImageManifests[currentModel.id];
    if (!manifest) {
      console.warn('⚠️ No image manifest found for model:', currentModel.name);
      return;
    }
    
    const brand = manifest?.brand;
    
    // Determine option group prefix based on brand
    let optionPrefix = '';
    if (brand === 'American Security' || brand === 'AMSEC') {
      optionPrefix = 'amsec_';
    } else if (brand === 'Liberty Safe') {
      optionPrefix = 'ls_';
    }
    
    console.log('🎨 Detected brand:', brand, 'prefix:', optionPrefix || 'none');
    
    // Manifest-level explicit option group handle overrides.
    // When a brand uses non-standard or inconsistent handle naming, add an
    // "optionGroups" map to the manifest to declare exactly which handles to use.
    // Example:  "optionGroups": { "size": "ls_size", "color": "ls_color" }
    const manifestGroupMap = manifest.optionGroups || {};
    const _findGroup = (manifestKey, ...fallbacks) => {
      if (manifestGroupMap[manifestKey]) {
        return this.data.optionGroups.find(g => g.handle === manifestGroupMap[manifestKey]) || null;
      }
      for (const fn of fallbacks) {
        const found = this.data.optionGroups.find(fn);
        if (found) return found;
      }
      return null;
    };
    
    // Find the size and finish option group handles dynamically
    // For AMSEC, prioritize amsec_ prefixed handles
    let sizeGroup = _findGroup('size',
                      g => g.handle === `${optionPrefix}size`,
                      g => g.handle === 'size',
                      g => g.type === 'size',
                      g => g.handle.includes('size'));
    const finishGroup = _findGroup('finish',
                        g => g.handle === `${optionPrefix}exterior_finish`,
                        g => g.handle === 'exterior_finish',
                        g => g.type === 'finish' || g.type === 'exterior_finish');
    const lockTypeGroup = _findGroup('lockType',
                          g => g.handle === `${optionPrefix}lock_type`,
                          g => g.handle === 'lock_type',
                          g => g.type === 'lock_type',
                          g => g.handle.includes('lock_type'));
    const lockColorGroup = _findGroup('lockColor',
                           g => g.handle === `${optionPrefix}lockgraphic_color`,
                           g => g.handle === 'lockgraphic_color',
                           g => g.type === 'lockgraphic_color');
    const graphicGroup = _findGroup('graphic',
                         g => g.handle === `${optionPrefix}graphic`,
                         g => g.handle === 'graphic',
                         g => g.type === 'graphic');
    const hingeTypeGroup = _findGroup('hingeType',
                           g => g.handle === `${optionPrefix}hinge_type`,
                           g => g.handle === 'hinge_type',
                           g => g.type === 'hinge_type',
                           g => g.handle.includes('hinge'));
    const hingeColorGroup = _findGroup('hingeColor',
                            g => g.handle === `${optionPrefix}hinge_color`,
                            g => g.handle === 'hinge_color',
                            g => g.type === 'hinge_color');
    const doorColorGroup = _findGroup('doorColor',
                           g => g.handle === `${optionPrefix}door_color`,
                           g => g.handle === 'door_color',
                           g => g.type === 'door_color');
    const bodyColorGroup = _findGroup('bodyColor',
                           g => g.handle === `${optionPrefix}body_color`,
                           g => g.handle === 'body_color',
                           g => g.type === 'body_color');
    const singleColorGroup = _findGroup('color',
                             g => g.handle === `${optionPrefix}color`,
                             g => g.handle === 'color_single',
                             g => g.type === 'color' || g.type === 'color_single',
                             g => g.handle.includes('color'));
    const accessoryGroup = _findGroup('accessory',
                           g => g.handle === `${optionPrefix}accessory`,
                           g => g.handle === 'accessory',
                           g => g.type === 'accessory');
    const hardwareColorGroup = _findGroup('hardwareColor',
                               g => g.handle === `${optionPrefix}hardware_color`,
                               g => g.handle === 'ls_hardware_color');
    
    // Prefer the active size group picked during size-card rendering.
    // This prevents selecting unrelated groups like cabinet_size.
    if (this.currentSizeGroupHandle) {
      const preferredSizeGroup = this.data.optionGroups.find(g => g.handle === this.currentSizeGroupHandle);
      if (preferredSizeGroup) {
        sizeGroup = preferredSizeGroup;
      }
    }

    // Get current selections using dynamic handles
    let selectedFinish = finishGroup ? this.state.selections[finishGroup.handle]?.[0] : null;
    const selectedSize = sizeGroup ? this.state.selections[sizeGroup.handle]?.[0] : null;
    const selectedLockType = lockTypeGroup ? this.state.selections[lockTypeGroup.handle]?.[0] : null;
    const selectedLockColor = lockColorGroup ? this.state.selections[lockColorGroup.handle]?.[0] : null;
    const selectedGraphic = graphicGroup ? this.state.selections[graphicGroup.handle]?.[0] : null;
    const selectedHingeType = hingeTypeGroup ? this.state.selections[hingeTypeGroup.handle]?.[0] : null;
    const selectedHingeColor = hingeColorGroup ? this.state.selections[hingeColorGroup.handle]?.[0] : null;
    const selectedDoorColor = doorColorGroup ? this.state.selections[doorColorGroup.handle]?.[0] : null;
    const selectedBodyColor = bodyColorGroup ? this.state.selections[bodyColorGroup.handle]?.[0] : null;
    const selectedSingleColor = singleColorGroup ? this.state.selections[singleColorGroup.handle]?.[0] : null;
    const accessories = accessoryGroup ? this.state.selections[accessoryGroup.handle] : null;
    const selectedHardwareColor = hardwareColorGroup ? this.state.selections[hardwareColorGroup.handle]?.[0] : null;
    const graphicTintCode = selectedHardwareColor || selectedLockColor || manifest.defaultHardwareColor || null;

    // Determine finish type from the active color label — used by bodyImagesByFinish and graphicsByFinish.
    // Also retain the raw color code for products that use color-keyed precolored body art.
    const _activeColorCode = selectedSingleColor || selectedBodyColor || selectedDoorColor;
    const _activeColorGroup = singleColorGroup || bodyColorGroup;
    const _activeColorValue = (_activeColorGroup && _activeColorCode)
      ? this.getValueByCode(_activeColorGroup.handle, _activeColorCode)
      : null;
    const _activeColorLabel = (_activeColorValue?.label || '').toLowerCase();
    const colorFinishType = _activeColorLabel.includes('marble') ? 'marble'
                          : _activeColorLabel.includes('textured') ? 'textured'
                          : _activeColorLabel.includes('feathered') ? 'feathered'
                          : 'gloss';
    
    // For products without a separate finish group (like AMSEC), extract finish from color value
    // e.g., "THERMAL_BLUE_GLOSS" -> finish is "GLOSS"
    if (!selectedFinish && selectedSingleColor) {
      const colorUpper = selectedSingleColor.toUpperCase();
      if (colorUpper.includes('_GLOSS')) {
        selectedFinish = 'GLOSS';
      } else if (colorUpper.includes('_TEXTURED') || colorUpper.includes('_TEXTURE')) {
        selectedFinish = 'TEXTURED';
      } else if (colorUpper.includes('_MATTE')) {
        selectedFinish = 'MATTE';
      } else {
        // Default to GLOSS if no finish suffix found
        selectedFinish = 'GLOSS';
      }
      console.log('🎨 Extracted finish from color value:', selectedSingleColor, '->', selectedFinish);
    }
    
    console.log('🎨 Rendering safe image:', { 
      selectedFinish, 
      selectedSize, 
      selectedLockType,
      selectedLockColor,
      selectedGraphic,
      selectedHingeType,
      selectedHingeColor,
      selectedDoorColor,
      selectedBodyColor,
      selectedSingleColor
    });
    console.log('🎨 Using option groups:', {
      size: sizeGroup?.handle,
      finish: finishGroup?.handle,
      color: singleColorGroup?.handle
    });
    
    if (!selectedSize) {
      console.warn('⚠️ Missing size selection', { selectedFinish, selectedSize });
      return;
    }
    
    console.log('📦 Current model:', currentModel.name, currentModel.id);
    console.log('📋 Manifest:', manifest);
    
    // Normalize finish type when one exists: convert TWO_TONE to two-tone, GLOSS to gloss.
    const normalizedFinish = selectedFinish ? selectedFinish.toLowerCase().replace(/_/g, '-') : null;
    
    // Get size code from the selected size option value
    const sizeValue = this.getValueByCode(sizeGroup.handle, selectedSize);
    const sizeCode = sizeValue?.code;
    
    if (!sizeCode) {
      console.warn('⚠️ No size code found for selected size:', selectedSize);
      return;
    }
    
    console.log('🎨 RenderSafeImage: Using size code:', sizeCode);
    console.log('🎨 RenderSafeImage: Available body masks:', manifest.masks?.bodyMasks ? Object.keys(manifest.masks.bodyMasks) : 'none');
    console.log('🎨 RenderSafeImage: Available door masks:', manifest.masks?.doorMasks ? Object.keys(manifest.masks.doorMasks) : 'none');
    
    // Calculate scale factor for accessories based on reference size
    const referenceSize = manifest.referenceSize || '4026x20'; // Default reference size
    const scaleFactor = this.calculateAccessoryScale(sizeCode, referenceSize);
    console.log('📏 Accessory scale factor:', scaleFactor, 'for size:', sizeCode, 'vs reference:', referenceSize);
    
    // Determine target container
    const containerId = targetPrefix === 'summary' ? 'summaryConfiguredSafe' : 'safeImageContainer';
    const container = document.getElementById(containerId);
    
    if (!container) {
      console.warn(`⚠️ Container not found: ${containerId}`);
      return;
    }
    
    console.log('🎨 Container found:', containerId, 'current children:', container.children.length);
    
    // Clear existing layers
    container.innerHTML = '';
    
    console.log('🎨 Container cleared, children now:', container.children.length);
    
    // Add data attribute for size-specific CSS targeting
    container.setAttribute('data-safe-size', sizeCode);
    
    // Add hinge swing direction for CSS targeting (will be set when hinges are rendered)
    // This gets updated below when we know the hinge swing selection
    
    // Add subtle border to container for definition
    container.style.border = '1px solid rgba(0, 0, 0, 0.15)';
    container.style.borderRadius = '4px';
    
    // Layer 1: Base image. Supports either a direct URL per size or the existing size + finish map.
    const baseImageEntry = manifest.baseImages?.[sizeCode];
    
    // Try hinge-side keyed images first (for products where hinge side determines the image)
    let baseImageUrl = null;
    const hingeSwing = this.state.selections['hinge_swing']?.[0] || this.state.selections['ls_hinge_side']?.[0];
    if (manifest.baseImagesByHingeSide && hingeSwing) {
      const hingeKey = hingeSwing.toUpperCase();
      const hingeKeyLower = hingeSwing.toLowerCase();
      const normalizedHingeSuffix = hingeKeyLower
        .replace(/^ls_hinge_side_/, '')
        .replace(/^hinge_swing_/, '');
      const hingeCandidates = [
        hingeSwing,
        hingeKey,
        hingeKeyLower,
        `ls_hinge_side_${normalizedHingeSuffix}`,
        `hinge_swing_${normalizedHingeSuffix}`
      ];

      // Preferred structure: baseImagesByHingeSide[sizeCode][hingeCode]
      const sizeScopedHingeImages = manifest.baseImagesByHingeSide[sizeCode];
      if (sizeScopedHingeImages && typeof sizeScopedHingeImages === 'object') {
        baseImageUrl = hingeCandidates.find(k => sizeScopedHingeImages[k])
          ? sizeScopedHingeImages[hingeCandidates.find(k => sizeScopedHingeImages[k])]
          : null;
      }

      // Backward compatibility: flat map baseImagesByHingeSide[hingeCode]
      if (!baseImageUrl) {
        const flatKey = hingeCandidates.find(k => manifest.baseImagesByHingeSide[k]);
        baseImageUrl = flatKey ? manifest.baseImagesByHingeSide[flatKey] : null;
      }
      if (baseImageUrl) {
        console.log('✅ Using hinge-side keyed image:', { hingeKey, baseImageUrl });
      }
    }
    
    // Fall back to traditional size/finish keyed images
    if (!baseImageUrl) {
      const imageEntry = typeof baseImageEntry === 'string'
        ? baseImageEntry
        : baseImageEntry?.[normalizedFinish] || baseImageEntry?.default || baseImageEntry?.base;
      baseImageUrl = imageEntry;
    }
    if (baseImageUrl) {
      this.addImageLayer(container, baseImageUrl, 1, 'base');
      console.log('✅ Base image rendered:', { sizeCode, normalizedFinish: normalizedFinish || 'none', hingeSwing: hingeSwing || 'none' });
    } else {
      console.log('ℹ️ No base image defined - will render using masks only');
    }

    // Color-keyed pre-colored body art. When this exists, the body should render
    // from a direct image instead of the finish/mask pipeline below.
    const bodyColorSelection = selectedSingleColor || selectedBodyColor || selectedDoorColor;
    if (manifest.bodyImagesByColor && bodyColorSelection) {
      const bodyColorCode = this.normalizeCode(bodyColorSelection);
      const bodyColorEntry = manifest.bodyImagesByColor[bodyColorCode] || manifest.bodyImagesByColor[bodyColorSelection];
      const bodyColorUrl = typeof bodyColorEntry === 'string' ? bodyColorEntry : bodyColorEntry?.[sizeCode] || null;

      if (bodyColorUrl) {
        this.addImageLayer(container, bodyColorUrl, 2, 'body-color-image', 1);
        console.log('✅ Body color image rendered:', { sizeCode, bodyColorCode, bodyColorUrl });
      }
    }
    
    // Layer 2: Apply color based on finish type
    if (normalizedFinish === 'two-tone-fade' && !manifest.bodyImagesByColor) {
      // Two-tone-fade: Body gets body color, door gets body color, then door fade mask gets door color
      if (selectedBodyColor) {
        const bodyColorValue = this.getValueByCode(bodyColorGroup.handle, selectedBodyColor);
        const bodyColor = bodyColorValue?.hexColor;
        const bodyColorCode = selectedBodyColor.toUpperCase();
        
        // Check if there's a pre-rendered texture image for this color and size
        const bodyTextureUrl = manifest.textures?.bodyTextures?.[bodyColorCode]?.[sizeCode];
        
        if (bodyTextureUrl) {
          // Use pre-rendered texture image (for granite, rimrock, etc.)
          this.addImageLayer(container, bodyTextureUrl, 2, 'body-texture', 1);
          console.log('✅ Body texture image applied:', bodyColorCode, bodyTextureUrl);
        } else {
          // Fall back to color masking for solid colors
          const bodyMaskUrl = manifest.masks?.bodyMasks?.[sizeCode];
          console.log('🎨 Two-Tone-Fade: Body mask URL for', sizeCode, ':', bodyMaskUrl);
          if (bodyMaskUrl && bodyColor) {
            // First render the mask as a visible texture layer
            this.addImageLayer(container, bodyMaskUrl, 2, 'body-texture', 1);
            // Then apply the body color overlay (solid, no fade)
            this.addColorMaskLayer(container, bodyMaskUrl, bodyColor, 3, 'body', 'multiply', '0.7');
            console.log('✅ Body color applied:', bodyColor);
          } else {
            console.warn('⚠️ Missing body mask or color:', { bodyMaskUrl, bodyColor });
          }
        }
        
        // Apply body color to door mask (so entire safe is body color)
        const doorMaskUrl = manifest.masks?.doorMasks?.[sizeCode];
        console.log('🎨 Two-Tone-Fade: Door mask URL for', sizeCode, ':', doorMaskUrl);
        if (doorMaskUrl && bodyColor) {
          this.addColorMaskLayer(container, doorMaskUrl, bodyColor, 4, 'door-body', 'multiply', '0.7');
          console.log('✅ Body color applied to door area:', bodyColor);
        }
      }
      
      // Apply door color to door fade mask (creates the fade effect on top)
      if (selectedDoorColor && selectedBodyColor) {
        const doorColorValue = this.getValueByCode(doorColorGroup.handle, selectedDoorColor);
        const doorColor = doorColorValue?.hexColor;
        const doorColorCode = selectedDoorColor.toUpperCase();
        
        // Check if there's a pre-rendered texture image for this door color and size
        const doorTextureUrl = manifest.textures?.doorTextures?.[doorColorCode]?.[sizeCode];
        
        if (doorTextureUrl) {
          // Use pre-rendered door texture image (for granite, rimrock, etc.)
          this.addImageLayer(container, doorTextureUrl, 5, 'door-fade-texture', 1);
          console.log('✅ Door fade texture image applied:', doorColorCode, doorTextureUrl);
        } else {
          // Use door fade mask with solid door color
          const doorFadeMaskUrl = manifest.masks?.doorFadeMasks?.[sizeCode];
          
          if (doorFadeMaskUrl && doorColor) {
            // Apply door color to the fade mask - solid color, no transparency
            this.addColorMaskLayer(container, doorFadeMaskUrl, doorColor, 5, 'door-fade', 'normal', '0.9');
            
            console.log('✅ Door fade color applied (solid):', doorColor);
          } else {
            console.warn('⚠️ Missing door fade mask or color:', { doorFadeMaskUrl, doorColor });
          }
        }
      }
      
      // Add door border (applies to two-tone-fade)
      const doorBorderUrl = manifest.doorBorders?.[sizeCode];
      if (doorBorderUrl) {
        this.addDoorBorder(container, doorBorderUrl, 7);
      }
    } else if (normalizedFinish === 'two-tone' && !manifest.bodyImagesByColor) {
      // Two-tone: Apply body color and door color (no fade)
      if (selectedBodyColor) {
        const bodyColorValue = this.getValueByCode(bodyColorGroup.handle, selectedBodyColor);
        const bodyColor = bodyColorValue?.hexColor;
        const bodyColorCode = selectedBodyColor.toUpperCase();
        
        // Check if there's a pre-rendered texture image for this color and size
        const bodyTextureUrl = manifest.textures?.bodyTextures?.[bodyColorCode]?.[sizeCode];
        
        if (bodyTextureUrl) {
          // Use pre-rendered texture image (for granite, rimrock, etc.)
          this.addImageLayer(container, bodyTextureUrl, 2, 'body-texture', 1);
          console.log('✅ Body texture image applied:', bodyColorCode, bodyTextureUrl);
        } else {
          // Fall back to color masking for solid colors
          const bodyMaskUrl = manifest.masks?.bodyMasks?.[sizeCode];
          console.log('🎨 Two-Tone: Body mask URL for', sizeCode, ':', bodyMaskUrl);
          if (bodyMaskUrl && bodyColor) {
            // First render the mask as a visible texture layer
            this.addImageLayer(container, bodyMaskUrl, 2, 'body-texture', 1);
            // Then apply the color overlay on top
            this.addColorMaskLayer(container, bodyMaskUrl, bodyColor, 3, 'body', 'multiply', '0.7');
            console.log('✅ Body color applied:', bodyColor);
          } else {
            console.warn('⚠️ Missing body mask or color:', { bodyMaskUrl, bodyColor });
          }
        }
      }
      
      // Door rendering - optional (e.g., AMSEC only uses body masks)
      if (selectedDoorColor && manifest.masks?.doorMasks?.[sizeCode]) {
        const doorColorValue = this.getValueByCode(doorColorGroup.handle, selectedDoorColor);
        const doorColor = doorColorValue?.hexColor;
        const doorColorCode = selectedDoorColor.toUpperCase();
        
        // Check if there's a pre-rendered texture image for this color and size
        const doorTextureUrl = manifest.textures?.doorTextures?.[doorColorCode]?.[sizeCode];
        
        if (doorTextureUrl) {
          // Use pre-rendered texture image (for granite, rimrock, etc.)
          this.addImageLayer(container, doorTextureUrl, 4, 'door-texture', 1);
          console.log('✅ Door texture image applied:', doorColorCode, doorTextureUrl);
        } else {
          // Fall back to color masking for solid colors
          const doorMaskUrl = manifest.masks.doorMasks[sizeCode];
          console.log('🎨 Two-Tone: Door mask URL for', sizeCode, ':', doorMaskUrl);
          if (doorMaskUrl && doorColor) {
            // First render the mask as a visible texture layer
            this.addImageLayer(container, doorMaskUrl, 4, 'door-texture', 1);
            // Then apply the color overlay on top
            this.addColorMaskLayer(container, doorMaskUrl, doorColor, 5, 'door', 'multiply', '0.7');
            console.log('✅ Door color applied:', doorColor);
          } else {
            console.warn('⚠️ Missing door mask or color:', { doorMaskUrl, doorColor });
          }
        }
      } else if (selectedDoorColor) {
        console.log('ℹ️ Door color selected but no door masks defined for this product (body-only rendering)');
      }
      
      // Add door border (applies to both two-tone finishes)
      const doorBorderUrl = manifest.doorBorders?.[sizeCode];
      if (doorBorderUrl) {
        this.addDoorBorder(container, doorBorderUrl, 6);
      }
    } else if (normalizedFinish === 'gloss' && selectedSingleColor && !manifest.bodyImagesByColor) {
      // Gloss: Apply same color to both body and door masks to preserve black lines
      console.log('🎨 Gloss rendering started:', { singleColorGroup: singleColorGroup?.handle, selectedSingleColor });
      const singleColorValue = this.getValueByCode(singleColorGroup.handle, selectedSingleColor);
      console.log('🎨 Gloss color value:', singleColorValue);
      console.log('🎨 Full color value object:', JSON.stringify(singleColorValue, null, 2));
      const glossColor = singleColorValue?.hexColor;
      const singleColorCode = selectedSingleColor.toUpperCase();
      
      // Check if this is a textured color (for AMSEC products)
      const isTexturedColor = singleColorCode.includes('TEXTURED');
      let textureImageUrl = isTexturedColor ? singleColorValue?.productImage : null;
      
      // Add protocol if missing (Shopify CDN URLs sometimes lack protocol)
      if (textureImageUrl && textureImageUrl.startsWith('//')) {
        textureImageUrl = 'https:' + textureImageUrl;
      }
      
      console.log('🎨 Texture detection:', { 
        isTexturedColor,
        hasProductImage: !!singleColorValue?.productImage,
        productImageValue: singleColorValue?.productImage,
        textureImageUrl
      });
      
      console.log('🎨 Gloss details:', { 
        glossColor, 
        singleColorCode, 
        isTexturedColor,
        textureImageUrl,
        hasTextures: !!manifest.textures, 
        hasMasks: !!manifest.masks 
      });
      
      // Check if there's a pre-rendered texture image for this color and size
      const bodyTextureUrl = manifest.textures?.bodyTextures?.[singleColorCode]?.[sizeCode];
      const doorTextureUrl = manifest.textures?.doorTextures?.[singleColorCode]?.[sizeCode];
      
      console.log('🎨 Gloss texture check:', { bodyTextureUrl, doorTextureUrl, textureImageUrl });
      
      if (bodyTextureUrl) {
        // Use pre-rendered body texture image (for granite, rimrock, etc.)
        this.addImageLayer(container, bodyTextureUrl, 2, 'body-texture', 1);
        console.log('✅ Gloss body texture image applied:', singleColorCode, bodyTextureUrl);
      } else if (isTexturedColor && textureImageUrl) {
        // For AMSEC textured colors: use product_image as texture overlay
        const bodyMaskUrl = manifest.masks?.bodyMasks?.[sizeCode];
        console.log('🎨 Textured AMSEC color: Body mask URL for', sizeCode, ':', bodyMaskUrl);
        if (bodyMaskUrl) {
          // First render the body mask as a base layer for detail
          this.addImageLayer(container, bodyMaskUrl, 2, 'body-mask-base', 1);
          
          // Then create texture layer with transparency to show mask detail
          const wrapper = document.createElement('div');
          wrapper.style.position = 'absolute';
          wrapper.style.inset = '0';
          wrapper.style.width = '100%';
          wrapper.style.height = '100%';
          wrapper.style.zIndex = 3;
          wrapper.style.pointerEvents = 'none';
          wrapper.style.isolation = 'isolate';
          
          const layer = document.createElement('div');
          layer.className = 'safe-layer-amsec-textured-body';
          layer.style.position = 'absolute';
          layer.style.inset = '0';
          layer.style.width = '100%';
          layer.style.height = '100%';
          layer.style.backgroundImage = `url('${textureImageUrl}')`;
          layer.style.backgroundSize = 'cover';
          layer.style.backgroundPosition = 'center';
          layer.style.backgroundRepeat = 'no-repeat';
          layer.style.webkitMaskImage = `url('${bodyMaskUrl}')`;
          layer.style.maskImage = `url('${bodyMaskUrl}')`;
          layer.style.webkitMaskSize = 'contain';
          layer.style.maskSize = 'contain';
          layer.style.webkitMaskPosition = 'center';
          layer.style.maskPosition = 'center';
          layer.style.webkitMaskRepeat = 'no-repeat';
          layer.style.maskRepeat = 'no-repeat';
          layer.style.opacity = '0.75'; // Allow body mask detail to show through
          
          wrapper.appendChild(layer);
          container.appendChild(wrapper);
          console.log('✅ AMSEC textured body applied with transparency:', textureImageUrl);
        } else {
          console.warn('⚠️ Missing body mask for textured AMSEC color');
        }
      } else if (glossColor) {
        // Fall back to color masking for solid colors
        const bodyMaskUrl = manifest.masks?.bodyMasks?.[sizeCode];
        console.log('🎨 Gloss: Body mask URL for', sizeCode, ':', bodyMaskUrl);
        if (bodyMaskUrl) {
          // First render the mask as a visible texture layer
          this.addImageLayer(container, bodyMaskUrl, 2, 'body-texture', 1);
          // Then apply the color overlay on top
          this.addColorMaskLayer(container, bodyMaskUrl, glossColor, 3, 'body', 'multiply', '0.7');
          console.log('✅ Gloss body color applied:', glossColor);
        } else {
          console.warn('⚠️ Missing body mask for gloss');
        }
      } else {
        console.warn('⚠️ No glossColor or texture found for:', singleColorCode);
      }
      
      // Door mask is optional - only render if defined (e.g., AMSEC only uses body masks)
      if (doorTextureUrl) {
        // Use pre-rendered door texture image (for granite, rimrock, etc.)
        this.addImageLayer(container, doorTextureUrl, 4, 'door-texture', 1);
        console.log('✅ Gloss door texture image applied:', singleColorCode, doorTextureUrl);
      } else if (isTexturedColor && textureImageUrl && manifest.masks?.doorMasks?.[sizeCode]) {
        // For AMSEC textured colors with door masks
        const doorMaskUrl = manifest.masks.doorMasks[sizeCode];
        console.log('🎨 Textured AMSEC color: Door mask URL for', sizeCode, ':', doorMaskUrl);
        
        // First render the door mask as a base layer for detail
        this.addImageLayer(container, doorMaskUrl, 4, 'door-mask-base', 1);
        
        // Then create texture layer with transparency to show mask detail
        const wrapper = document.createElement('div');
        wrapper.style.position = 'absolute';
        wrapper.style.inset = '0';
        wrapper.style.width = '100%';
        wrapper.style.height = '100%';
        wrapper.style.zIndex = 5;
        wrapper.style.pointerEvents = 'none';
        wrapper.style.isolation = 'isolate';
        
        const layer = document.createElement('div');
        layer.className = 'safe-layer-amsec-textured-door';
        layer.style.position = 'absolute';
        layer.style.inset = '0';
        layer.style.width = '100%';
        layer.style.height = '100%';
        layer.style.backgroundImage = `url('${textureImageUrl}')`;
        layer.style.backgroundSize = 'cover';
        layer.style.backgroundPosition = 'center';
        layer.style.backgroundRepeat = 'no-repeat';
        layer.style.webkitMaskImage = `url('${doorMaskUrl}')`;
        layer.style.maskImage = `url('${doorMaskUrl}')`;
        layer.style.webkitMaskSize = 'contain';
        layer.style.maskSize = 'contain';
        layer.style.webkitMaskPosition = 'center';
        layer.style.maskPosition = 'center';
        layer.style.webkitMaskRepeat = 'no-repeat';
        layer.style.maskRepeat = 'no-repeat';
        layer.style.opacity = '0.85'; // Allow door mask detail to show through
        
        wrapper.appendChild(layer);
        container.appendChild(wrapper);
        console.log('✅ AMSEC textured door applied with transparency:', textureImageUrl);
      } else if (glossColor && manifest.masks?.doorMasks?.[sizeCode]) {
        // Only render door mask if it exists in manifest
        const doorMaskUrl = manifest.masks.doorMasks[sizeCode];
        console.log('🎨 Gloss: Door mask URL for', sizeCode, ':', doorMaskUrl);
        // First render the mask as a visible texture layer
        this.addImageLayer(container, doorMaskUrl, 4, 'door-texture', 1);
        // Then apply the color overlay on top
        this.addColorMaskLayer(container, doorMaskUrl, glossColor, 5, 'door', 'multiply', '0.7');
        console.log('✅ Gloss door color applied:', glossColor);
      } else {
        console.log('ℹ️ No door mask defined for this product (body-only rendering)');
      }
      
      // Add door border for gloss finish
      const doorBorderUrl = manifest.doorBorders?.[sizeCode];
      if (doorBorderUrl) {
        this.addDoorBorder(container, doorBorderUrl, 6);
      }
    } else if (normalizedFinish === 'textured' && selectedSingleColor && !manifest.bodyImagesByColor) {
      // Textured: Use pre-rendered texture images for both body and door (like masks)
      const singleColorCode = selectedSingleColor.toUpperCase();
      
      console.log('🎨 Rendering textured finish:', { 
        finish: normalizedFinish, 
        color: singleColorCode, 
        size: sizeCode 
      });
      
      // Get texture images for this color and size
      const bodyTextureUrl = manifest.textures?.bodyTextures?.[singleColorCode]?.[sizeCode];
      const doorTextureUrl = manifest.textures?.doorTextures?.[singleColorCode]?.[sizeCode];
      
      console.log('🔍 Looking for textures:', {
        bodyPath: `manifest.textures.bodyTextures.${singleColorCode}.${sizeCode}`,
        bodyUrl: bodyTextureUrl,
        doorPath: `manifest.textures.doorTextures.${singleColorCode}.${sizeCode}`,
        doorUrl: doorTextureUrl,
        hasTextures: !!manifest.textures,
        availableColors: manifest.textures?.bodyTextures ? Object.keys(manifest.textures.bodyTextures) : 'none'
      });
      
      if (bodyTextureUrl) {
        // Render body texture image
        this.addImageLayer(container, bodyTextureUrl, 2, 'body-texture', 1);
        console.log('✅ Texture body image applied:', singleColorCode, bodyTextureUrl);
      } else {
        console.warn('⚠️ Missing body texture for:', singleColorCode, 'size:', sizeCode);
      }
      
      if (doorTextureUrl) {
        // Render door texture image
        this.addImageLayer(container, doorTextureUrl, 4, 'door-texture', 1);
        console.log('✅ Texture door image applied:', singleColorCode, doorTextureUrl);
      } else {
        console.warn('⚠️ Missing door texture for:', singleColorCode, 'size:', sizeCode);
      }
      
      // Add door border for texture finish
      const doorBorderUrl = manifest.doorBorders?.[sizeCode];
      if (doorBorderUrl) {
        this.addDoorBorder(container, doorBorderUrl, 6);
      }
    }

    // Fallback for minimal manifests: if no finish layers rendered, at least show body mask for selected size.
    // Supports: bodyImagesByFinish.{gloss|textured}.{sizeCode} or masks.bodyMasks.{sizeCode}
    if (container.children.length === 0) {
      const fallbackColorCode = selectedSingleColor || selectedBodyColor || selectedDoorColor;
      const fallbackColorGroup = singleColorGroup || bodyColorGroup;
      const fallbackColorValue = (fallbackColorGroup && fallbackColorCode)
        ? this.getValueByCode(fallbackColorGroup.handle, fallbackColorCode)
        : null;
      let finishImageRendered = false;

      // ── PRE-COLORED IMAGE BY COLOR + LOCK ────────────────────────────────────
      // For models like Collector where the full safe (body, hardware, graphic)
      // is baked into a single pre-colored image per color+lock combination.
      // Manifest key: imagesByColorAndLock.{colorCode}.{lockTypeCode}.{sizeCode}
      if (manifest.imagesByColorAndLock && fallbackColorCode && selectedLockType) {
        const imageUrl = manifest.imagesByColorAndLock[fallbackColorCode]?.[selectedLockType]?.[sizeCode];
        if (imageUrl) {
          this.addImageLayer(container, imageUrl, 1, 'precolored-composite', 1);
          finishImageRendered = true;
          console.log('✅ Pre-colored composite render:', { sizeCode, color: fallbackColorCode, lock: selectedLockType });
        }
      }
      // ─────────────────────────────────────────────────────────────────────────

      // ── LIBERTY TWO-TONE RENDERING ───────────────────────────────────────────
      // Body is pre-colored (rendered as-is). Door is colored using the color
      // option value's hexColor field.
      // Manifest keys: twoToneImages.body.{sizeCode}, twoToneImages.door.{sizeCode}
      if (!finishImageRendered && manifest.twoToneImages) {
        const bodyUrl = manifest.twoToneImages.body?.[sizeCode];
        const doorUrl = manifest.twoToneImages.door?.[sizeCode];
        const doorHex = fallbackColorValue?.hexColor;

        if (bodyUrl) {
          // Body is pre-colored — render as-is, no color mask
          this.addImageLayer(container, bodyUrl, 1, 'two-tone-body', 1);
        }

        if (doorUrl && doorHex) {
          // Door gets colored with the selected color's hex value
          this.addImageLayer(container, doorUrl, 5, 'two-tone-door', 1);
          this.addColorMaskLayer(container, doorUrl, doorHex, 6, 'two-tone-door-color', 'multiply', '0.75');
          // Shading restore for door
          this.addImageLayer(container, doorUrl, 7, 'two-tone-door-shading');
          container.lastElementChild.style.opacity = '0.45';
          container.lastElementChild.style.mixBlendMode = 'multiply';
        }

        if (bodyUrl || doorUrl) {
          finishImageRendered = true;
          console.log('✅ Two-tone render:', { sizeCode, doorHex: doorHex || 'none' });
        }
      }
      // ─────────────────────────────────────────────────────────────────────────

      // Check for finish-specific pre-colored body images first
      if (!finishImageRendered && manifest.bodyImagesByFinish) {
        const finishType = colorFinishType;
        const finishBodyUrl = manifest.bodyImagesByFinish[finishType]?.[sizeCode];
        if (finishBodyUrl) {
          this.addImageLayer(container, finishBodyUrl, 1, 'body-finish-image', 1);
          const fallbackColor = fallbackColorValue?.hexColor;
          const bodyPreColored = manifest.preColored === true || manifest.preColored?.body === true;
          if (fallbackColor && !bodyPreColored) {
            this.addColorMaskLayer(container, finishBodyUrl, fallbackColor, 2, 'body-finish-color', 'multiply', '0.9');
          }
          // ── LIBERTY SHADING RESTORE ──────────────────────────────────────────
          // Re-renders the original body image on top of the color mask to burn
          // the door/body border lines and shadows back in. Liberty Safe only.
          // To revert: delete the four lines between the dashes.
          if (brand === 'Liberty Safe' && fallbackColor && !bodyPreColored) {
            this.addImageLayer(container, finishBodyUrl, 3, 'body-shading-restore');
            container.lastElementChild.style.opacity = '0.45';
            container.lastElementChild.style.mixBlendMode = 'multiply';
          }
          // ────────────────────────────────────────────────────────────────────
          // ── LIBERTY CSS GRADIENT SHADING ─────────────────────────────────────
          // Adds a programmatic light/shadow gradient to fake 3D depth.
          // Top-left gets a subtle highlight, bottom-right gets shadow.
          // To revert: delete the ten lines between the dashes.
          if (brand === 'Liberty Safe' && fallbackColor && !bodyPreColored) {
            const gradientWrapper = document.createElement('div');
            gradientWrapper.style.position = 'absolute';
            gradientWrapper.style.inset = '0';
            gradientWrapper.style.zIndex = '4';
            gradientWrapper.style.pointerEvents = 'none';
            gradientWrapper.style.background = 'linear-gradient(145deg, transparent 40%, rgba(0,0,0,0.22) 100%)';
            gradientWrapper.style.mixBlendMode = 'multiply';
            // Clip gradient to safe body shape so it doesn't bleed onto white container background
            gradientWrapper.style.webkitMaskImage = `url('${finishBodyUrl}')`;
            gradientWrapper.style.maskImage = `url('${finishBodyUrl}')`;
            gradientWrapper.style.webkitMaskSize = 'contain';
            gradientWrapper.style.maskSize = 'contain';
            gradientWrapper.style.webkitMaskPosition = 'center';
            gradientWrapper.style.maskPosition = 'center';
            gradientWrapper.style.webkitMaskRepeat = 'no-repeat';
            gradientWrapper.style.maskRepeat = 'no-repeat';
            container.appendChild(gradientWrapper);
          }
          // ─────────────────────────────────────────────────────────────────────
          finishImageRendered = true;
          console.log('✅ Applied body-finish-image render:', { sizeCode, finishType, finishBodyUrl, color: bodyPreColored ? 'skipped (preColored)' : (fallbackColor || 'none') });
        } else {
          console.warn('⚠️ bodyImagesByFinish defined but no image for:', { finishType, sizeCode });
        }
      }

      if (!finishImageRendered) {
        const fallbackBodyMaskUrl = manifest.masks?.bodyMasks?.[sizeCode];
        if (fallbackBodyMaskUrl) {
          this.addImageLayer(container, fallbackBodyMaskUrl, 1, 'body-mask-fallback', 1);

          const fallbackColor = fallbackColorValue?.hexColor;
          const bodyPreColored = manifest.preColored === true || manifest.preColored?.body === true;
          if (fallbackColor && !bodyPreColored) {
            this.addColorMaskLayer(container, fallbackBodyMaskUrl, fallbackColor, 2, 'body-fallback-color', 'multiply', '0.7');
          }

          console.log('✅ Applied body-mask fallback render:', { sizeCode, fallbackBodyMaskUrl, fallbackColor: bodyPreColored ? 'skipped (preColored)' : (fallbackColor || 'none') });
        } else {
          console.warn('⚠️ No renderable layers found and no fallback body mask for size:', sizeCode);
        }
      }
    }
    
    // Layer 7 (test hook): Liberty decorative door graphic — always shown when a lock is selected.
    // graphicsBySize and graphics are decorative overlays (door design/logo).
    // graphicsByLock is the lock hardware image and is handled in Layer 8 below.
    // graphicsBySelection: keyed by option code — overrides default graphic when that option is selected.
    let resolvedGraphicsUrl = null;
    if (manifest.graphicsBySelection) {
      for (const [optionCode, graphicUrl] of Object.entries(manifest.graphicsBySelection)) {
        const isSelected = Object.values(this.state.selections).some(sel => {
          const arr = Array.isArray(sel) ? sel : Object.keys(sel);
          return arr.includes(optionCode);
        });
        if (isSelected) {
          // graphicUrl can be a string (same for all sizes) or an object keyed by sizeCode
          resolvedGraphicsUrl = (typeof graphicUrl === 'object' ? graphicUrl[sizeCode] : graphicUrl) || null;
          break;
        }
      }
    }
    if (!resolvedGraphicsUrl && manifest.graphicsByFinish) {
      // graphicsByFinish: { "marble": { sizeCode: url }, "gloss": { sizeCode: url } }
      // Falls back through finish → any defined finish → graphicsBySize
      resolvedGraphicsUrl = manifest.graphicsByFinish[colorFinishType]?.[sizeCode]
        || Object.values(manifest.graphicsByFinish).find(bySize => bySize[sizeCode])?.[sizeCode]
        || null;
    }
    if (!resolvedGraphicsUrl) {
      resolvedGraphicsUrl = manifest.graphicsBySize?.[sizeCode] ||
                            (typeof manifest.graphics === 'string' ? manifest.graphics : null);
    }

    if (brand === 'Liberty Safe' && resolvedGraphicsUrl && graphicTintCode) {
      this.addImageLayer(container, resolvedGraphicsUrl, 100, 'graphic-test', 1);

      const graphicPreColored = manifest.preColored === true || manifest.preColored?.graphic === true;
      if (!graphicPreColored) {
        const hardwareColorValue = (hardwareColorGroup && graphicTintCode)
          ? this.getValueByCode(hardwareColorGroup.handle, graphicTintCode)
          : null;
        const hardwareHex = hardwareColorValue?.hexColor || '#cfd6e4';
        const effectiveHardwareColorCode = graphicTintCode;
        const isBlackChrome = effectiveHardwareColorCode === 'ls-hardware_black_chrome';
        if (isBlackChrome) {
          // Certain body colors need a custom black chrome tint on the graphic
          const blackChromeGraphicHexOverrides = {
            'ls_feathered_burgundy': '#4a3c3a',
            'ls_feathered_emerald_green': '#414a3e'
          };
          const blackChromeGraphicHex = blackChromeGraphicHexOverrides[_activeColorCode] ?? '#26251e';
          // Stack multiple color mask passes to compensate for faint/light graphic PNG pixels
          // Each multiply pass pushes the graphic closer to pure black
          const graphicBaseWrapper = container.lastElementChild;
          graphicBaseWrapper.style.mixBlendMode = 'multiply';
          this.addColorMaskLayer(container, resolvedGraphicsUrl, blackChromeGraphicHex, 101, 'graphic-hardware-color', 'multiply', '1');
          this.addColorMaskLayer(container, resolvedGraphicsUrl, blackChromeGraphicHex, 102, 'graphic-hardware-color-2', 'multiply', '1');
        } else {
          this.addColorMaskLayer(container, resolvedGraphicsUrl, hardwareHex, 101, 'graphic-hardware-color', 'multiply', '0.85');
        }
      }

      console.log('✅ Liberty decorative graphic rendered:', {
        sizeCode,
        graphicsUrl: resolvedGraphicsUrl,
        hardwareColor: graphicTintCode || 'default-silver'
      });
    }

    // Layer 7: Graphic with color masking (except LOGO_ONLY which stays pre-colored) - z-index 100
    if (selectedGraphic && selectedGraphic.toLowerCase() !== 'none') {
      // Get door dimensions for positioning
      const doorDimensions = manifest.doorDimensions?.[sizeCode];
      
      // Check if rivet trim is selected or included
      const hasRivetsSelected = accessories && Array.isArray(accessories) 
        ? accessories.includes('RIVET_TRIM')
        : accessories && typeof accessories === 'object' 
          ? accessories['RIVET_TRIM'] 
          : false;
      const hasRivetsIncluded = this.checkIfIncluded('RIVET_TRIM');
      const hasRivets = hasRivetsSelected || hasRivetsIncluded;
      
      // If rivets are selected AND traditional graphic is selected, override to show LOGO_ONLY
      let effectiveGraphic = selectedGraphic;
      if (hasRivets && selectedGraphic.toUpperCase() === 'TRADITIONAL') {
        effectiveGraphic = 'LOGO_ONLY';
        console.log('🔄 Rivets + Traditional detected - switching to LOGO_ONLY graphic');
      }
      
      // All graphics use size-specific grayscale with color mask overlay
      const normalizedGraphicType = effectiveGraphic.toUpperCase();
      console.log('🎨 Rendering graphic with color mask system', hasRivets ? '(RIVET VERSION)' : '');
        
        // Look up grayscale graphic by size
        // If rivets are selected, check for rivet-specific version first
        let grayscaleGraphicUrl = null;
        
        if (hasRivets) {
          // Try rivet-specific graphic first: graphics[graphic_type].RIVETS[sizeCode]
          grayscaleGraphicUrl = manifest.graphics?.[effectiveGraphic]?.RIVETS?.[sizeCode] ||
                               manifest.graphics?.[effectiveGraphic]?.rivets?.[sizeCode];
          
          if (grayscaleGraphicUrl) {
            console.log('✓ Using rivet-specific graphic for', effectiveGraphic, 'at size', sizeCode);
          } else {
            console.log('ℹ️ No rivet-specific graphic found for', effectiveGraphic, 'at size', sizeCode, '- falling back to normal graphic');
          }
        }
        
        // Fall back to normal grayscale graphic if no rivet version or rivets not selected
        if (!grayscaleGraphicUrl) {
          grayscaleGraphicUrl = manifest.graphics?.[effectiveGraphic]?.GRAYSCALE?.[sizeCode] || 
                                 manifest.graphics?.[effectiveGraphic]?.grayscale?.[sizeCode];
        }
        
        // For LOGO_ONLY: Check if GRAYSCALE is a direct string (not size-specific)
        const isLogoOnly = normalizedGraphicType === 'LOGO_ONLY' || normalizedGraphicType === 'LOGO-ONLY';
        if (!grayscaleGraphicUrl && isLogoOnly) {
          // Try LOGO_ONLY.GRAYSCALE as a single string (not size-specific)
          const logoGrayscale = manifest.graphics?.[effectiveGraphic]?.GRAYSCALE || 
                               manifest.graphics?.[effectiveGraphic]?.grayscale;
          
          if (typeof logoGrayscale === 'string') {
            grayscaleGraphicUrl = logoGrayscale;
            console.log('ℹ️ Using single LOGO_ONLY grayscale image:', grayscaleGraphicUrl);
          } else {
            // Final fallback: old color-based structure (all point to same file)
            grayscaleGraphicUrl = manifest.graphics?.[effectiveGraphic]?.GOLD ||
                                 manifest.graphics?.[effectiveGraphic]?.gold ||
                                 manifest.graphics?.[effectiveGraphic]?.SILVER ||
                                 manifest.graphics?.[effectiveGraphic]?.silver ||
                                 manifest.graphics?.[effectiveGraphic]?.BLACK_CHROME;
            console.log('ℹ️ Using legacy LOGO_ONLY structure:', grayscaleGraphicUrl);
          }
        }
        
        if (grayscaleGraphicUrl && selectedLockColor) {
          // Only PERFORMANCE graphic uses gold gradient, all others use solid colors
          const supportsGradients = ['PERFORMANCE'].includes(normalizedGraphicType);
          
          const isGoldGradient = supportsGradients && (selectedLockColor.toUpperCase() === 'GOLD');
          const isSilverGradient = supportsGradients && (selectedLockColor.toUpperCase() === 'SILVER');
          const isBlackChromeGradient = supportsGradients && 
                                         (selectedLockColor.toUpperCase() === 'BLACK_CHROME' || selectedLockColor.toUpperCase() === 'BLACK-CHROME');
          
          // Map color selection to hex color or gradient
          const colorMap = {
            'GOLD': '#ffe285',
            'gold': '#ffe285',
            'SILVER': '#d5d8e6',
            'silver': '#d5d8e6',
            'BLACK_CHROME': '#010300',
            'black_chrome': '#010300',
            'BLACK-CHROME': '#010300',
            'black-chrome': '#010300'
          };
          
          const hexColor = colorMap[selectedLockColor];
          
          if (hexColor || isGoldGradient || isSilverGradient || isBlackChromeGradient) {
            let colorDescription = hexColor;
            if (isGoldGradient) colorDescription = 'gradient (#f7ce18 → #8c5b00)';
            if (isSilverGradient) colorDescription = 'gradient (#f7fcfc → #a5b2cc)';
            if (isBlackChromeGradient) colorDescription = 'gradient (#47453d → #302f29)';
            console.log(`✓ Applying color mask: ${selectedLockColor} (${colorDescription}) to ${effectiveGraphic} for size ${sizeCode}`);
            
            // Create wrapper - all graphics fill entire container (size-specific images are pre-positioned)
            const wrapper = document.createElement('div');
            wrapper.style.position = 'absolute';
            wrapper.style.zIndex = 100;
            wrapper.style.pointerEvents = 'none';
            wrapper.style.isolation = 'isolate';
            wrapper.style.inset = '0';
            wrapper.style.width = '100%';
            wrapper.style.height = '100%';
            
            // Layer 1: Grayscale base image (100% opaque to show details)
            const baseLayer = document.createElement('div');
            baseLayer.className = `safe-layer-graphic-base-${effectiveGraphic}`;
            baseLayer.style.position = 'absolute';
            baseLayer.style.inset = '0';
            baseLayer.style.width = '100%';
            baseLayer.style.height = '100%';
            baseLayer.style.backgroundImage = `url('${grayscaleGraphicUrl}')`;
            baseLayer.style.backgroundSize = 'contain'; // Always preserve aspect ratio
            baseLayer.style.backgroundPosition = 'center';
            baseLayer.style.backgroundRepeat = 'no-repeat';
            baseLayer.style.opacity = '1'; // Base image is fully opaque
            
            // Layer 2: Color overlay with transparency (to show details through)
            const colorLayer = document.createElement('div');
            colorLayer.className = `safe-layer-graphic-color-${effectiveGraphic}`;
            colorLayer.style.position = 'absolute';
            colorLayer.style.inset = '0';
            colorLayer.style.width = '100%';
            colorLayer.style.height = '100%';
            colorLayer.style.webkitMaskImage = `url('${grayscaleGraphicUrl}')`;
            colorLayer.style.maskImage = `url('${grayscaleGraphicUrl}')`;
            colorLayer.style.webkitMaskSize = 'contain'; // Always preserve aspect ratio
            colorLayer.style.maskSize = 'contain';
            colorLayer.style.webkitMaskPosition = 'center';
            colorLayer.style.maskPosition = 'center';
            colorLayer.style.webkitMaskRepeat = 'no-repeat';
            colorLayer.style.maskRepeat = 'no-repeat';
            
            // Apply gradient for graphics with gradient support (Gold/Silver/Black Chrome), solid color for others
            if (isGoldGradient) {
              colorLayer.style.backgroundImage = 'linear-gradient(to bottom, #f7ce18 0%, #8c5b00 100%)';
            } else if (isSilverGradient) {
              colorLayer.style.backgroundImage = 'linear-gradient(to bottom, #f7fcfc 0%, #a5b2cc 100%)';
            } else {
              colorLayer.style.backgroundColor = hexColor;
            }
            
            colorLayer.style.mixBlendMode = 'multiply'; // Blend with base to preserve details
            colorLayer.style.opacity = '0.9'; // Semi-transparent so details show through
            
            wrapper.appendChild(baseLayer);
            wrapper.appendChild(colorLayer);
            container.appendChild(wrapper);
            
            console.log('✅ Graphic rendered with color mask:', effectiveGraphic, 'Color:', selectedLockColor, 'Size:', sizeCode);
          } else {
            console.warn('⚠️ No color mapping found for:', selectedLockColor);
          }
        } else {
          if (!grayscaleGraphicUrl) {
            console.warn(`⚠️ Grayscale graphic not found for ${effectiveGraphic} at size ${sizeCode}. Expected: graphics.${effectiveGraphic}.GRAYSCALE.${sizeCode}`);
          }
          if (!selectedLockColor) {
            console.warn('⚠️ No lock/graphic color selected');
          }
        }
    }
    
    // Layer 7.5: Static layers - z-index 120 (always-on)
    // Manifest key: staticLayers — object mapping a label to a URL string or config object.
    // String:  { "pull-handle": "https://..." }
    //          — single URL, renders as-is
    // Object with byHardwareColor:
    //   { "hinge-caps": { "byHardwareColor": { "ls-hardware_chrome": "https://...", ... } } }
    //          — picks the pre-colored image matching the selected (or default) hardware color
    if (manifest.staticLayers && typeof manifest.staticLayers === 'object') {
      const _staticEffectiveHwCode = selectedHardwareColor || manifest.defaultHardwareColor || null;

      Object.entries(manifest.staticLayers).forEach(([label, entry], i) => {
        const zIndex = 120 + i;
        let url = null;

        if (typeof entry === 'string') {
          url = entry;
        } else if (entry?.byHardwareColor) {
          const hwEntry = (_staticEffectiveHwCode && entry.byHardwareColor[_staticEffectiveHwCode]) || null;
          // hwEntry can be a plain URL string or a size-keyed object
          url = (typeof hwEntry === 'string' ? hwEntry : hwEntry?.[sizeCode]) || null;
        } else if (entry?.url) {
          url = entry.url;
        }

        if (url) {
          this.addImageLayer(container, url, zIndex, `static-${label}`, 1);
        }
      });
    }

    // Layer 8: Lock - z-index 130 (above graphic and hinges)
    // Supports structures:
    // 1) graphicsByLock[type][hardwareColor] = url — hardware-color-specific (Liberty)
    // 2) graphicsByLock[type][sizeCode] = url      — size-specific lock image (Liberty)
    // 3) locksBySize[type][sizeCode] = url         — size-specific
    // 4) Flat:          locks[type] = url
    // 5) Color-keyed:   locks[type][color] = url
    // 6) defaultLockImage[hardwareColor][sizeCode] = url — default lock image when no lock is selected
    // 7) defaultLockImage[sizeCode] = url — size-only fallback for simple manifests
    {
      let lockImageUrl = null;
      let lockIsSizeSpecific = false;
      const effectiveHardwareColor = selectedHardwareColor || manifest.defaultHardwareColor || null;

      // Check graphicsByLock first (Liberty hardware-color or size-keyed lock images)
      // Supports two structures:
      //   2-level: graphicsByLock[lockType][hwColor] = url
      //   3-level: graphicsByLock[lockType][hwColor][sizeCode] = url  (e.g. Magnum — size AND hardware color)
      if (selectedLockType && manifest.graphicsByLock?.[selectedLockType]) {
        const lockEntry = manifest.graphicsByLock[selectedLockType];
        if (typeof lockEntry === 'object') {
          // Use selected hardware color, or fall back to manifest defaultHardwareColor
          const hwEntry = (effectiveHardwareColor && lockEntry[effectiveHardwareColor]) || null;
          if (hwEntry) {
            // hwEntry can be a direct URL string or a size-keyed object
            lockImageUrl = (typeof hwEntry === 'string' ? hwEntry : hwEntry?.[sizeCode]) || null;
          }
          if (!lockImageUrl) {
            // Fall back to size-keyed entry at the top level (no hardware color dimension)
            lockImageUrl = (typeof lockEntry[sizeCode] === 'string' ? lockEntry[sizeCode] : null);
          }
          if (lockImageUrl) lockIsSizeSpecific = true;
        }
      }

      // Check size-specific locksBySize structure
      if (!lockImageUrl) {
        const sizeSpecificLockUrl = selectedLockType ? manifest.locksBySize?.[selectedLockType]?.[sizeCode] : null;
        if (sizeSpecificLockUrl) {
          lockImageUrl = sizeSpecificLockUrl;
          lockIsSizeSpecific = true;
        }
      }

      if (!lockImageUrl) {
        if (selectedLockType) {
          const lockEntry = manifest.locks?.[selectedLockType];
          if (typeof lockEntry === 'string') {
            lockImageUrl = lockEntry;
          } else if (selectedLockColor && lockEntry && typeof lockEntry === 'object') {
            lockImageUrl = lockEntry[selectedLockColor] || null;
          }
        } else if (manifest.defaultLockImage) {
          const defaultLockEntry = manifest.defaultLockImage;
          if (typeof defaultLockEntry === 'string') {
            lockImageUrl = defaultLockEntry;
          } else if (effectiveHardwareColor && defaultLockEntry[effectiveHardwareColor]) {
            const hardwareLockEntry = defaultLockEntry[effectiveHardwareColor];
            lockImageUrl = (typeof hardwareLockEntry === 'string' ? hardwareLockEntry : hardwareLockEntry?.[sizeCode]) || null;
          }

          if (!lockImageUrl && defaultLockEntry[sizeCode]) {
            lockImageUrl = (typeof defaultLockEntry[sizeCode] === 'string' ? defaultLockEntry[sizeCode] : null);
          }

          if (lockImageUrl) {
            lockIsSizeSpecific = true;
          }
        }
      }

      // Fallback to case-insensitive lookup for manifest keys
      if (!lockImageUrl && selectedLockType && manifest.locks && typeof manifest.locks === 'object') {
        const selectedLockTypeUpper = selectedLockType.toUpperCase();
        const matchedTypeKey = Object.keys(manifest.locks).find(k => String(k).toUpperCase() === selectedLockTypeUpper);
        if (matchedTypeKey) {
          const matchedEntry = manifest.locks[matchedTypeKey];
          if (typeof matchedEntry === 'string') {
            lockImageUrl = matchedEntry;
          } else if (selectedLockColor && matchedEntry && typeof matchedEntry === 'object') {
            const selectedLockColorUpper = selectedLockColor.toUpperCase();
            const matchedColorKey = Object.keys(matchedEntry).find(k => String(k).toUpperCase() === selectedLockColorUpper);
            if (matchedColorKey) {
              lockImageUrl = matchedEntry[matchedColorKey];
            }
          }
        }
      }

      if (lockImageUrl) {
        // Liberty USA flat lock manifests should render exactly as provided,
        // without dynamic scaling or position adjustments.
        const shouldRenderLockAsIs = brand === 'Liberty Safe' && (
          lockIsSizeSpecific ||
          (selectedLockType && typeof manifest.locks?.[selectedLockType] === 'string') ||
          (!selectedLockType && manifest.defaultLockImage)
        );

        if (shouldRenderLockAsIs) {
          this.addImageLayer(container, lockImageUrl, 130, 'lock', 1);

          // Default Liberty lock tint: apply silver overlay when no lock color is selected.
          const lockPreColored = manifest.preColored === true || manifest.preColored?.lock === true;
          if (!graphicTintCode && !lockPreColored) {
            this.addColorMaskLayer(container, lockImageUrl, '#cfd6e4', 131, 'lock-default-silver', 'multiply', '0.55');
          }

          console.log('✅ Lock rendered as-is for Liberty Safe:', {
            selectedLockType: selectedLockType || 'default-lock',
            selectedLockColor: graphicTintCode || 'none',
            scaleFactor: 1,
            positionAdjusted: false
          });
        } else {
        // Check if this is a redundant lock with crane hinges selected
        const isRedundantLock = selectedLockType.toUpperCase().includes('REDUNDANT');
        const hingeTypeUpper = selectedHingeType?.toUpperCase();
        const isCraneHinge = hingeTypeUpper === 'CRANE' || hingeTypeUpper === 'CRANE_BLANK';
        let lockScaleFactor = scaleFactor * 1.2; // Increase all locks by 20%
        let additionalTopOffset = '0px';
        
        // Increase redundant lock size when crane hinges are selected
        if (isRedundantLock && isCraneHinge) {
          // Make redundant locks larger with crane hinges, but scale down for larger safes
          // Base: 1.2 * 1.08 = 1.3x total (reduced from 1.5x)
          lockScaleFactor = scaleFactor * 1.2 * 1.08;
          
          // Parse size dimensions for positioning and scaling
          let height, width, depth;
          
          // Try to parse the size code
          const sizeMatch = sizeCode.match(/^(\d{2})(\d{2})x(\d+)/);
          if (sizeMatch) {
            height = parseInt(sizeMatch[1]); // First 2 digits = height
            width = parseInt(sizeMatch[2]);  // Next 2 digits = width
            depth = parseInt(sizeMatch[3]);  // After 'x' = depth
          } else {
            // Fallback for other formats
            const fallbackMatch = sizeCode.match(/(\d+)x(\d+)(?:x(\d+))?/);
            if (fallbackMatch) {
              height = parseInt(fallbackMatch[1]);
              width = parseInt(fallbackMatch[2]);
              depth = fallbackMatch[3] ? parseInt(fallbackMatch[3]) : 20;
            }
          }
          
          if (height && width && depth) {
            // Calculate size ratios
            const heightRatio = height / 60;
            const widthRatio = width / 26;
            const depthRatio = depth / 20;
            let maxRatio = Math.max(heightRatio, widthRatio, depthRatio);
            
            // Special case: 6031x27 should use same scaling as 6637x27
            if (sizeCode === '6031x27') {
              // Use 6637x27's maxRatio (1.42) for consistent sizing
              maxRatio = 1.1;
            }
            
            // Check if larger than base size (60x26x20)
            const isLargerThanBase = height > 60 || width > 26 || depth > 20 || sizeCode === '6031x27';
            
            if (isLargerThanBase) {
              // Apply more aggressive scaling reduction for larger safes (20% reduction per unit ratio, minimum 60%)
              const sizeReduction = Math.max(0.60, 1 - (maxRatio - 1) * 0.20);
              lockScaleFactor = lockScaleFactor * sizeReduction;
              
              // Move down more as safe gets bigger (proportional to height increase)
              const baseOffset = -2; // Base offset in percentage (moved from -5 to -2 = 3% down)
              const additionalOffset = (heightRatio - 1) * 1; // 1% more per unit increase
              additionalTopOffset = `${baseOffset + additionalOffset}%`;
              
              console.log('📏 Redundant lock with crane hinges sizing:', { 
                size: sizeCode, 
                dimensions: { height, width, depth },
                ratios: { heightRatio, widthRatio, depthRatio, maxRatio },
                sizeReduction,
                scale: lockScaleFactor,
                baseScale: '1.5x',
                offset: additionalTopOffset
              });
            }
          }
        }
        
        // Get lock position from manifest (vertical offset control)
        const lockPosition = manifest.lockPosition?.[sizeCode];
        const lockTopOffset = lockPosition?.top || null;
        
        // Add the lock image layer with adjusted scale
        this.addImageLayer(container, lockImageUrl, 130, 'lock', lockScaleFactor);

        // Default tint for non-flat lock structures when no lock color is selected.
        if (!graphicTintCode) {
          this.addColorMaskLayer(container, lockImageUrl, '#cfd6e4', 131, 'lock-default-silver', 'multiply', '0.55');
        }
        
        // Apply vertical positioning
        const lockLayer = container.querySelector('.safe-layer-lock');
        if (lockLayer && lockLayer.parentElement) {
          // For redundant locks with crane hinges on large safes, use calculated offset (ignore manifest)
          if (isRedundantLock && isCraneHinge && additionalTopOffset !== '0px') {
            // Parse size to check if it's above 60x26x20
            let height, width, depth;
            
            const sizeMatch = sizeCode.match(/^(\d{2})(\d{2})x(\d+)/);
            if (sizeMatch) {
              height = parseInt(sizeMatch[1]);
              width = parseInt(sizeMatch[2]);
              depth = parseInt(sizeMatch[3]);
            } else {
              const fallbackMatch = sizeCode.match(/(\d+)x(\d+)(?:x(\d+))?/);
              if (fallbackMatch) {
                height = parseInt(fallbackMatch[1]);
                width = parseInt(fallbackMatch[2]);
                depth = fallbackMatch[3] ? parseInt(fallbackMatch[3]) : 20;
              }
            }
            
            if (height && width && depth) {
              const isLargerThanBase = height > 60 || width > 26 || depth > 20;
              
              if (isLargerThanBase) {
                // Use calculated offset for large redundant locks with crane hinges
                lockLayer.parentElement.style.top = additionalTopOffset;
                lockLayer.parentElement.style.bottom = 'auto';
                console.log('🔒 Redundant lock with crane hinges positioned at calculated offset (ignoring manifest):', additionalTopOffset);
              } else if (lockTopOffset) {
                // Use manifest position for small redundant locks
                lockLayer.parentElement.style.top = lockTopOffset;
                lockLayer.parentElement.style.bottom = 'auto';
                console.log('🔒 Redundant lock positioned at manifest offset:', lockTopOffset);
              }
            }
          } else if (lockTopOffset) {
            // Use manifest position for all other locks (non-redundant or redundant without crane hinges)
            // Move all locks down by 3%
            let adjustedTopOffset = lockTopOffset;
            const currentOffset = parseFloat(lockTopOffset);
            if (!isNaN(currentOffset)) {
              adjustedTopOffset = `${currentOffset + 3}%`;
            }
            
            // Special adjustment for 6026x20 size - no additional adjustment needed (already moved down 3%)
            if (sizeCode === '6026x20') {
              console.log('🔒 6026x20 lock position adjusted from', lockTopOffset, 'to', adjustedTopOffset);
            }
            
            lockLayer.parentElement.style.top = adjustedTopOffset;
            lockLayer.parentElement.style.bottom = 'auto';
            console.log('🔒 Lock positioned at offset:', adjustedTopOffset);
          }
        }
        
        console.log('✅ Lock rendered:', { selectedLockType: selectedLockType || 'default-lock', selectedLockColor: graphicTintCode || 'none', scaleFactor: lockScaleFactor, isRedundant: isRedundantLock, isCraneHinge });
        }
      } else {
        console.warn('⚠️ No lock image found:', {
          selectedLockType: selectedLockType || 'default-lock',
          selectedLockColor: graphicTintCode || 'none',
          triedFlatPath: selectedLockType ? `manifest.locks.${selectedLockType}` : 'manifest.defaultLockImage',
          triedNestedPath: selectedLockType && selectedLockColor ? `manifest.locks.${selectedLockType}.${selectedLockColor}` : 'n/a'
        });
      }
    }
    
    // Layer 9: Hinges (multiple positioned based on size, type, swing, and color)
    const selectedHingeSwing = this.state.selections['hinge_swing']?.[0];
    
    // Add hinge swing direction to container for CSS targeting
    if (selectedHingeSwing) {
      container.setAttribute('data-hinge-swing', selectedHingeSwing.toLowerCase());
    }
    
    if (selectedHingeType && selectedHingeSwing) {
      // Only show hinges if NOT internal type
      const hingeTypeUpper = selectedHingeType.toUpperCase();
      
      if (hingeTypeUpper !== 'INTERNAL') {
        // EXTERNAL HINGES - New size-specific system with body color masking
        if (hingeTypeUpper === 'EXTERNAL') {
          console.log('🔧 Rendering EXTERNAL hinges with size-specific images');
          
          // Get size-specific hinge images from manifest
          const hingeConfig = manifest.hinges?.images?.EXTERNAL;
          if (!hingeConfig || !hingeConfig[sizeCode]) {
            console.warn(`⚠️ No external hinge configuration found for size: ${sizeCode}`);
          } else {
            const sizeHingeImages = hingeConfig[sizeCode];
            const hingeImageUrl = sizeHingeImages?.grayscale;
            
            if (!hingeImageUrl) {
              console.warn(`⚠️ No grayscale hinge image found for size: ${sizeCode}`);
            } else {
              const hingePosition = manifest.hingePositions?.[sizeCode]?.EXTERNAL;
              const hingeCount = hingePosition?.count || 2;
              const shouldMirror = selectedHingeSwing === 'RIGHT';
              
              // Determine body color for masking
              let hingeBodyColor = null;
              if (normalizedFinish === 'two-tone-fade' || normalizedFinish === 'two-tone') {
                hingeBodyColor = selectedBodyColor;
              } else {
                hingeBodyColor = selectedSingleColor;
              }
              
              const bodyColorValue = this.getValueByCode(
                normalizedFinish === 'two-tone-fade' || normalizedFinish === 'two-tone' ? 'body_color' : 'color_single',
                hingeBodyColor
              );
              
              const bodyHexColor = bodyColorValue?.hexColor;
              const bodyGradient = bodyColorValue?.gradient;
              const textureCode = bodyColorValue?.code;
              
              // Get body color or gradient
              let bodyColorOrGradient = bodyHexColor;
              if (bodyGradient) {
                bodyColorOrGradient = bodyGradient;
              }
              
              // Check if texture exists in manifest for this color code OR if finish is distressed
              const isDistressedFinish = normalizedFinish === 'distressed';
              const hasTexture = manifest.hinges?.textures?.[textureCode] || 
                                (isDistressedFinish && manifest.hinges?.textures?.DISTRESSED);
              
              console.log('🎨 Hinge color info:', {
                hingeBodyColor,
                bodyHexColor,
                bodyGradient,
                textureCode,
                normalizedFinish,
                isDistressedFinish,
                hasTexture: !!hasTexture,
                shouldMirror
              });
              
              // Render hinges based on finish type - check if texture exists in manifest
              if (hasTexture) {
                // Textured finish: grayscale hinge image masked with texture
                const textureUrl = isDistressedFinish && !manifest.hinges?.textures?.[textureCode]
                  ? manifest.hinges.textures.DISTRESSED
                  : manifest.hinges.textures[textureCode];
                console.log(`🎨 Applying texture to hinges: ${isDistressedFinish ? 'DISTRESSED' : textureCode} -> ${textureUrl}`);
                this.addExternalHingeLayerWithTexture(
                  container,
                  hingeImageUrl,
                  textureUrl,
                  110,
                  'external-hinges',
                  shouldMirror
                );
              } else {
                // Solid color: grayscale base + body color mask
                console.log('🎨 Applying solid color to hinges:', bodyColorOrGradient);
                this.addExternalHingeLayerWithColor(
                  container,
                  hingeImageUrl,
                  bodyColorOrGradient,
                  110,
                  'external-hinges',
                  shouldMirror
                );
              }
            }
          }
        }
        
        // Check if this is a crane hinge type (uses lock/graphic color variants)
        const isCraneHinge = hingeTypeUpper === 'CRANE' || hingeTypeUpper === 'CRANE_BLANK';
        
        let hingeImageUrl = null;
        let hingePosition = manifest.hinges?.positions?.[sizeCode]; // Get position for this size
        let isSplitStructureHinge = false; // Track if this is a split structure (will be rendered separately)
        
        if (isCraneHinge) {
          // For crane hinges, check for new grayscale + color mask structure first
          // New structure: hinges.images.CRANE_BLANK.GRAYSCALE[LEFT/RIGHT][sizeCode]
          // Legacy structure: hinges.images.CRANE[LEFT/RIGHT][GOLD/BLACK_CHROME/SILVER]
          const swingDirectionUpper = selectedHingeSwing.toUpperCase(); // 'LEFT' or 'RIGHT'
          const colorVariantUpper = selectedLockColor?.toUpperCase();
          const colorVariantLower = selectedLockColor?.toLowerCase();
          
          console.log('🔍 Looking for crane hinge image:', { 
            hingeType: hingeTypeUpper,
            swing: swingDirectionUpper,
            size: sizeCode,
            colorUpper: colorVariantUpper || 'not selected',
            colorLower: colorVariantLower || 'not selected',
            manifestPath: `manifest.hinges.images.${hingeTypeUpper}.GRAYSCALE.${swingDirectionUpper}.${sizeCode} OR .${swingDirectionUpper}.${colorVariantUpper}`,
            availableSwings: manifest.hinges?.images?.[hingeTypeUpper] ? Object.keys(manifest.hinges.images[hingeTypeUpper]) : 'N/A'
          });
          
          // Check if GRAYSCALE structure exists (new system with color masking)
          const hasGrayscaleStructure = manifest.hinges?.images?.[hingeTypeUpper]?.GRAYSCALE;
          
          if (hasGrayscaleStructure) {
            // Check if this is a split structure (object with BASE/BODY_COLOR/CRANE_COLOR) first
            // CRANE_BLANK always uses LEFT images (mirrored for RIGHT swing)
            // CRANE has separate LEFT/RIGHT images
            const checkSwing = hingeTypeUpper === 'CRANE_BLANK' ? 'LEFT' : swingDirectionUpper;
            const hingeData = manifest.hinges.images[hingeTypeUpper].GRAYSCALE[checkSwing]?.[sizeCode];
            
            // Check if it's a split structure (object) or single image (string)
            if (hingeData && typeof hingeData === 'object' && (hingeData.BASE || hingeData.BODY_COLOR || hingeData.CRANE_COLOR)) {
              console.log(`✅ Found split grayscale crane hinge structure for ${hingeTypeUpper} ${sizeCode} (using LEFT images, will mirror for RIGHT)`);
              // Set flag - split structure will be rendered later
              isSplitStructureHinge = true;
              hingeImageUrl = null;
            } else if (typeof hingeData === 'string') {
              // Single grayscale image
              hingeImageUrl = hingeData;
              console.log(`✅ Found grayscale crane hinge for ${hingeTypeUpper} ${checkSwing} ${sizeCode}:`, hingeImageUrl);
              console.log(`🎨 Will apply ${colorVariantUpper} color mask`);
            } else {
              console.warn(`⚠️ No grayscale image found for ${hingeTypeUpper} ${checkSwing} ${sizeCode}`);
            }
          } else {
            // Use legacy pre-colored structure
            // Try to get swing-specific image first (new structure)
            hingeImageUrl = manifest.hinges?.images?.[hingeTypeUpper]?.[swingDirectionUpper]?.[colorVariantUpper] ||
                           manifest.hinges?.images?.[hingeTypeUpper]?.[swingDirectionUpper]?.[colorVariantLower];
            
            // Fallback to old structure (no swing direction) for backwards compatibility
            if (!hingeImageUrl) {
              console.log('ℹ️ No swing-specific image found, trying legacy structure...');
              hingeImageUrl = manifest.hinges?.images?.[hingeTypeUpper]?.[colorVariantUpper] ||
                             manifest.hinges?.images?.[hingeTypeUpper]?.[colorVariantLower];
            }
            
            if (!hingeImageUrl) {
              console.warn(`⚠️ No ${colorVariantUpper} variant found for ${hingeTypeUpper} ${swingDirectionUpper}, trying fallback`);
              // Fallback to default crane hinge if color variant not found
              hingeImageUrl = typeof manifest.hinges?.images?.[hingeTypeUpper] === 'string' 
                ? manifest.hinges.images[hingeTypeUpper] 
                : manifest.hinges?.images?.['EXTERNAL'];
            } else {
              console.log(`✅ Found pre-colored crane hinge for ${hingeTypeUpper} ${swingDirectionUpper} in ${colorVariantUpper}:`, hingeImageUrl);
            }
          }
          
          // For crane hinges, use door dimensions for positioning
          const doorDimensions = manifest.doorDimensions?.[sizeCode];
          if (doorDimensions) {
            hingePosition = {
              left: doorDimensions.left,
              right: `calc(100% - ${doorDimensions.left} - ${doorDimensions.width})`
            };
            console.log('📐 Using door dimensions for crane hinge positioning:', hingePosition);
          }
        } else {
          // Standard hinge logic (non-crane)
          // Check if hinge images are organized by finish type
          const hingeImages = manifest.hinges?.images?.[hingeTypeUpper];
          
          if (hingeImages && typeof hingeImages === 'object' && !Array.isArray(hingeImages)) {
            // Hinges are organized by finish - try to find finish-specific image
            const finishData = hingeImages[normalizedFinish];
            
            // Check if finish data is an object with color variants (e.g., textured with RIMROCK, LIGHT_GRANITE, etc.)
            if (finishData && typeof finishData === 'object' && !Array.isArray(finishData)) {
              // Textured finish with color-specific hinges
              const selectedColorCode = selectedSingleColor?.toUpperCase();
              
              if (selectedColorCode && finishData[selectedColorCode]) {
                hingeImageUrl = finishData[selectedColorCode];
                console.log(`✅ Found ${normalizedFinish} hinge for color ${selectedColorCode}:`, hingeImageUrl);
              } else {
                // Fallback to first available color if selected color not found
                const availableColors = Object.keys(finishData);
                if (availableColors.length > 0) {
                  hingeImageUrl = finishData[availableColors[0]];
                  console.warn(`⚠️ Color ${selectedColorCode} not found for ${normalizedFinish} hinges, using fallback:`, availableColors[0]);
                }
              }
            } else if (typeof finishData === 'string') {
              // Simple finish-specific image (e.g., distressed)
              hingeImageUrl = finishData;
            }
            
            // Fallback to 'default' if finish-specific not found
            if (!hingeImageUrl) {
              hingeImageUrl = hingeImages['default'] || hingeImages['DEFAULT'];
            }
            
            console.log(`🔍 Looking for ${normalizedFinish} hinge image:`, { 
              found: !!hingeImageUrl, 
              url: hingeImageUrl,
              selectedColor: selectedSingleColor,
              availableFinishes: Object.keys(hingeImages)
            });
          } else {
            // Simple string URL - use as-is (backwards compatibility)
            hingeImageUrl = hingeImages || manifest.hinges?.images?.['EXTERNAL'];
          }
        }
        
        const hingeCount = manifest.hinges?.counts?.[sizeCode] || 2; // Default to 2 hinges
        const swingDirection = selectedHingeSwing.toUpperCase(); // 'LEFT' or 'RIGHT'
        
        // Calculate hinge scale factor (larger on smaller safes, smaller on larger safes)
        const referenceSize = manifest.referenceSize || '4026x20';
        const hingeScaleFactor = this.calculateAccessoryScale(sizeCode, referenceSize);
        
        // Get body color for hinge masking (use single color for gloss, body color for two-tone)
        // For crane hinges, textured, and distressed finishes, we don't apply color masking since they use pre-colored images
        let hingeColor = null;
        const isPreColoredHinge = isCraneHinge || normalizedFinish === 'textured' || normalizedFinish === 'distressed';
        
        if (!isPreColoredHinge) {
          if (normalizedFinish === 'gloss' && selectedSingleColor) {
            const colorValue = singleColorGroup ? this.getValueByCode(singleColorGroup.handle, selectedSingleColor) : null;
            hingeColor = colorValue?.hexColor;
          } else if ((normalizedFinish === 'two-tone' || normalizedFinish === 'two-tone-fade') && selectedBodyColor) {
            const colorValue = bodyColorGroup ? this.getValueByCode(bodyColorGroup.handle, selectedBodyColor) : null;
            hingeColor = colorValue?.hexColor;
          }
        }
        
        if (hingeImageUrl || isSplitStructureHinge) {
          if (isCraneHinge) {
            // Get crane-specific dimensions from manifest for this size and swing direction
            // Try swing-specific dimensions first (new structure), then fall back to old structure
            const swingDirectionUpper = selectedHingeSwing.toUpperCase();
            let craneDimensions = manifest.hinges?.craneDimensions?.[sizeCode]?.[swingDirectionUpper];
            
            // Fallback to old structure (no swing direction) for backwards compatibility
            if (!craneDimensions) {
              console.log('ℹ️ No swing-specific dimensions found, using legacy dimensions...');
              craneDimensions = manifest.hinges?.craneDimensions?.[sizeCode];
            }
            
            // Check if we're using grayscale + color mask system
            const hasGrayscaleStructure = manifest.hinges?.images?.[hingeTypeUpper]?.GRAYSCALE;
            
            if (hasGrayscaleStructure) {
              // Check if this size has split hinge structure (object with BASE/BODY_COLOR/CRANE_COLOR)
              // Look for LEFT swing first (default), which will be mirrored for RIGHT swing
              const hingeDataLeft = manifest.hinges?.images?.[hingeTypeUpper]?.GRAYSCALE?.LEFT?.[sizeCode];
              const isSplitHingeStructure = hingeDataLeft && typeof hingeDataLeft === 'object' && (hingeDataLeft.BODY_COLOR || hingeDataLeft.CRANE_COLOR);
              
              if (isSplitHingeStructure) {
                console.log('🧪 SPLIT HINGE MODE: Rendering split crane hinges for', sizeCode, 'swing:', swingDirectionUpper);
                
                // Use LEFT images for both swings (will mirror for RIGHT)
                const hingeData = hingeDataLeft;
                const shouldMirror = swingDirectionUpper === 'RIGHT';
                console.log('🔄 Mirroring decision:', { swingDirectionUpper, shouldMirror, explanation: shouldMirror ? 'Will mirror (RIGHT swing)' : 'No mirror (LEFT swing)' });
                
                // Get base, body color, crane color, and caps hinge images
                const baseHingeUrl = hingeData.BASE;
                const bodyColorHingeUrl = hingeData.BODY_COLOR;
                const craneColorHingeUrl = hingeData.CRANE_COLOR;
                const capsHingeUrl = hingeData.CAPS;
                
                // Get lock/graphic color for caps
                const colorVariantUpper = selectedLockColor?.toUpperCase();
                let capsColorValue = null;
                
                if (colorVariantUpper === 'GOLD') {
                  capsColorValue = '#fcd962';
                } else if (colorVariantUpper === 'SILVER') {
                  capsColorValue = 'linear-gradient(to bottom, #f7fcfc 0%, #a5b2cc 100%)';
                } else if (colorVariantUpper === 'BLACK_CHROME') {
                  capsColorValue = 'linear-gradient(to bottom, #47453d 0%, #302f29 100%)';
                }
                
                // Determine crane hinge color for BASE and CRANE_COLOR layers: use override if set, otherwise use body color
                let craneHingeColor = this.state.craneHingeColorOverride;
                if (!craneHingeColor) {
                  // Fall back to body color logic
                  if (normalizedFinish === 'two-tone' || normalizedFinish === 'two-tone-fade') {
                    craneHingeColor = selectedBodyColor;
                  } else {
                    craneHingeColor = selectedSingleColor;
                  }
                }
                
                console.log('🎨 Crane hinge color for BASE/CRANE_COLOR:', craneHingeColor, 'Override:', this.state.craneHingeColorOverride);
                
                // Get crane hinge color/texture for BASE and CRANE_COLOR layers
                let craneHingeColorValue = null;
                let craneHingeTextureUrl = null;
                
                // Check if the crane hinge color is a texture
                const textureColors = ['RIMROCK', 'LIGHT_GRANITE', 'DARK_GRANITE', 'DISTRESSED'];
                const isTextureColor = craneHingeColor && textureColors.includes(craneHingeColor.toUpperCase());
                
                if (isTextureColor) {
                  // Apply texture to BASE and CRANE_COLOR
                  let textureColorCode = craneHingeColor.toUpperCase();
                  const textureData = manifest.hinges?.textures?.[textureColorCode];
                  
                  // Support both single texture (string) or size-specific textures (object)
                  if (typeof textureData === 'string') {
                    craneHingeTextureUrl = textureData; // Single texture for all sizes
                  } else if (textureData && typeof textureData === 'object') {
                    craneHingeTextureUrl = textureData[sizeCode]; // Size-specific texture
                  }
                  
                  console.log('🎨 Applying texture to BASE/CRANE_COLOR:', { color: textureColorCode, size: sizeCode, textureUrl: craneHingeTextureUrl });
                } else if (craneHingeColor) {
                  // Apply gloss color to BASE and CRANE_COLOR
                  const colorValue = singleColorGroup ? this.getValueByCode(singleColorGroup.handle, craneHingeColor) : null;
                  craneHingeColorValue = colorValue?.hexColor;
                  console.log('🎨 Applying gloss color to BASE/CRANE_COLOR:', craneHingeColorValue);
                }
                
                // Render BASE layer with crane hinge color - z-index 115 (above graphics/rivets at 100)
                if (baseHingeUrl) {
                  if (craneHingeTextureUrl) {
                    this.addCraneHingeLayerWithTexture(container, baseHingeUrl, craneHingeTextureUrl, 115, 'crane-hinges-base', craneDimensions, shouldMirror);
                    console.log('✅ BASE crane structure rendered with texture:', craneHingeTextureUrl, shouldMirror ? '(mirrored)' : '');
                  } else if (craneHingeColorValue) {
                    this.addCraneHingeLayerWithMask(container, baseHingeUrl, craneHingeColorValue, 115, 'crane-hinges-base', craneDimensions, shouldMirror);
                    console.log('✅ BASE crane structure rendered with color:', craneHingeColorValue, shouldMirror ? '(mirrored)' : '');
                  } else {
                    this.addCraneHingeLayer(container, baseHingeUrl, 115, 'crane-hinges-base', craneDimensions, shouldMirror);
                    console.log('✅ BASE crane structure rendered without color', shouldMirror ? '(mirrored)' : '');
                  }
                }
                
                if (bodyColorHingeUrl && craneColorHingeUrl) {
                  // BODY_COLOR layer: ALWAYS use the default body color (not affected by crane selector)
                  let defaultBodyColor = null;
                  let defaultBodyTextureUrl = null;
                  
                  // Determine default body color based on finish
                  if (normalizedFinish === 'two-tone' || normalizedFinish === 'two-tone-fade') {
                    const colorValue = bodyColorGroup ? this.getValueByCode(bodyColorGroup.handle, selectedBodyColor) : null;
                    defaultBodyColor = colorValue?.hexColor;
                  } else if (normalizedFinish === 'textured' || normalizedFinish === 'distressed') {
                    // For textured finishes, get the texture
                    let singleColorCode = selectedSingleColor?.toUpperCase();
                    if (singleColorCode && singleColorCode.includes('DISTRESSED')) {
                      singleColorCode = 'DISTRESSED';
                    }
                    const textureData = manifest.hinges?.textures?.[singleColorCode];
                    if (typeof textureData === 'string') {
                      defaultBodyTextureUrl = textureData;
                    } else if (textureData && typeof textureData === 'object') {
                      defaultBodyTextureUrl = textureData[sizeCode];
                    }
                  } else {
                    const colorValue = singleColorGroup ? this.getValueByCode(singleColorGroup.handle, selectedSingleColor) : null;
                    defaultBodyColor = colorValue?.hexColor;
                  }
                  
                  console.log('🎨 BODY_COLOR layer using default body color:', defaultBodyColor || defaultBodyTextureUrl);
                  
                  // Render BODY_COLOR layer with default body color - z-index 115
                  if (defaultBodyTextureUrl) {
                    this.addCraneHingeLayerWithTexture(container, bodyColorHingeUrl, defaultBodyTextureUrl, 115, 'crane-hinges-body-color', craneDimensions, shouldMirror);
                    console.log('✅ BODY_COLOR crane hinges rendered with texture:', defaultBodyTextureUrl, shouldMirror ? '(mirrored)' : '');
                  } else if (defaultBodyColor) {
                    this.addCraneHingeLayerWithMask(container, bodyColorHingeUrl, defaultBodyColor, 115, 'crane-hinges-body-color', craneDimensions, shouldMirror);
                    console.log('✅ BODY_COLOR crane hinges rendered:', defaultBodyColor, shouldMirror ? '(mirrored)' : '');
                  } else {
                    this.addCraneHingeLayer(container, bodyColorHingeUrl, 115, 'crane-hinges-body-color', craneDimensions, shouldMirror);
                    console.log('✅ BODY_COLOR crane hinges rendered without color', shouldMirror ? '(mirrored)' : '');
                  }
                  
                  // Render CRANE_COLOR layer with crane hinge color - z-index 115
                  if (craneHingeTextureUrl) {
                    this.addCraneHingeLayerWithTexture(container, craneColorHingeUrl, craneHingeTextureUrl, 115, 'crane-hinges-crane-color', craneDimensions, shouldMirror);
                    console.log('✅ CRANE_COLOR layer rendered with texture:', craneHingeTextureUrl, shouldMirror ? '(mirrored)' : '');
                  } else if (craneHingeColorValue) {
                    this.addCraneHingeLayerWithMask(container, craneColorHingeUrl, craneHingeColorValue, 115, 'crane-hinges-crane-color', craneDimensions, shouldMirror);
                    console.log('✅ CRANE_COLOR layer rendered with color:', craneHingeColorValue, shouldMirror ? '(mirrored)' : '');
                  } else {
                    this.addCraneHingeLayer(container, craneColorHingeUrl, 115, 'crane-hinges-crane-color', craneDimensions, shouldMirror);
                    console.log('✅ CRANE_COLOR layer rendered without color', shouldMirror ? '(mirrored)' : '');
                  }
                  
                  // Render crane-specific hinge caps (if provided) with lock/graphic color - z-index 115 (above graphics/rivets at 100)
                  if (capsHingeUrl && capsColorValue) {
                    this.addCraneHingeLayerWithMask(container, capsHingeUrl, capsColorValue, 115, 'crane-hinges-caps', craneDimensions, shouldMirror);
                    console.log('✅ Crane hinge caps rendered with lock/graphic color:', capsColorValue, shouldMirror ? '(mirrored)' : '');
                  } else if (capsHingeUrl) {
                    this.addCraneHingeLayer(container, capsHingeUrl, 115, 'crane-hinges-caps', craneDimensions, shouldMirror);
                    console.log('✅ Crane hinge caps rendered without color', shouldMirror ? '(mirrored)' : '');
                  }
                  
                  console.log('ℹ️ Split crane hinge rendering complete (BASE, BODY_COLOR, CRANE_COLOR, CAPS)');
                } else {
                  console.warn('⚠️ Split hinge structure found but missing BODY_COLOR or CRANE_COLOR images');
                  // Don't render anything if split structure is incomplete
                }
              } else {
                // Standard crane hinge rendering (single image for the size)
                // Check if regular CRANE should use CRANE_BLANK's split structure
                const blankHingeDataLeft = manifest.hinges?.images?.CRANE_BLANK?.GRAYSCALE?.LEFT?.[sizeCode];
                const shouldUseSplitFromBlank = hingeTypeUpper === 'CRANE' && blankHingeDataLeft && typeof blankHingeDataLeft === 'object' && (blankHingeDataLeft.BODY_COLOR || blankHingeDataLeft.CRANE_COLOR);
                
                if (shouldUseSplitFromBlank) {
                  console.log('🔗 CRANE using CRANE_BLANK split structure for', sizeCode, 'swing:', swingDirectionUpper);
                  
                  // Determine if we should mirror CRANE_BLANK overlays (default is LEFT, so mirror for RIGHT)
                  const shouldMirrorOverlays = swingDirectionUpper === 'RIGHT';
                  
                  // Get lock/graphic color for caps
                  const colorVariantUpper = selectedLockColor?.toUpperCase();
                  let capsColorValue = null;
                  
                  if (colorVariantUpper === 'GOLD') {
                    capsColorValue = '#fcd962';
                  } else if (colorVariantUpper === 'SILVER') {
                    capsColorValue = 'linear-gradient(to bottom, #f7fcfc 0%, #a5b2cc 100%)';
                  } else if (colorVariantUpper === 'BLACK_CHROME') {
                    capsColorValue = 'linear-gradient(to bottom, #47453d 0%, #302f29 100%)';
                  }
                  
                  // Determine crane hinge color for base CRANE image: use override if set, otherwise use body color
                  let craneHingeColor = this.state.craneHingeColorOverride;
                  if (!craneHingeColor) {
                    // Fall back to body color logic
                    if (normalizedFinish === 'two-tone' || normalizedFinish === 'two-tone-fade') {
                      craneHingeColor = selectedBodyColor;
                    } else {
                      craneHingeColor = selectedSingleColor;
                    }
                  }
                  
                  console.log('🎨 CRANE base image color:', craneHingeColor, 'Override:', this.state.craneHingeColorOverride);
                  
                  // Get crane hinge color/texture for base CRANE image
                  let craneBaseColorValue = null;
                  let craneBaseTextureUrl = null;
                  
                  // Check if the crane hinge color is a texture
                  const textureColors = ['RIMROCK', 'LIGHT_GRANITE', 'DARK_GRANITE', 'DISTRESSED'];
                  const isTextureColor = craneHingeColor && textureColors.includes(craneHingeColor.toUpperCase());
                  
                  if (isTextureColor) {
                    // Apply texture to base CRANE image
                    let textureColorCode = craneHingeColor.toUpperCase();
                    const textureData = manifest.hinges?.textures?.[textureColorCode];
                    
                    if (typeof textureData === 'string') {
                      craneBaseTextureUrl = textureData;
                    } else if (textureData && typeof textureData === 'object') {
                      craneBaseTextureUrl = textureData[sizeCode];
                    }
                    
                    console.log('🎨 Applying texture to CRANE base image:', { color: textureColorCode, textureUrl: craneBaseTextureUrl });
                  } else if (craneHingeColor) {
                    // Apply gloss color to base CRANE image
                    const colorValue = singleColorGroup ? this.getValueByCode(singleColorGroup.handle, craneHingeColor) : null;
                    craneBaseColorValue = colorValue?.hexColor;
                    console.log('🎨 Applying gloss color to CRANE base image:', craneBaseColorValue);
                  }
                  
                  // Render base CRANE image with crane hinge color - z-index 115 (above graphics/rivets at 100)
                  if (craneBaseTextureUrl) {
                    this.addCraneHingeLayerWithTexture(container, hingeImageUrl, craneBaseTextureUrl, 115, 'crane-hinges-base', craneDimensions, false);
                    console.log('✅ CRANE base image rendered with texture:', craneBaseTextureUrl);
                  } else if (craneBaseColorValue) {
                    this.addCraneHingeLayerWithMask(container, hingeImageUrl, craneBaseColorValue, 115, 'crane-hinges-base', craneDimensions, false);
                    console.log('✅ CRANE base image rendered with color:', craneBaseColorValue);
                  } else {
                    this.addCraneHingeLayer(container, hingeImageUrl, 115, 'crane-hinges-base', craneDimensions, false);
                    console.log('✅ CRANE base image rendered without color');
                  }
                  
                  // Now overlay CRANE_BLANK's split structure (mirror for RIGHT swing)
                  const blankHingeData = blankHingeDataLeft;
                  const bodyColorHingeUrl = blankHingeData.BODY_COLOR;
                  const craneColorHingeUrl = blankHingeData.CRANE_COLOR;
                  const capsHingeUrl = blankHingeData.CAPS;
                  
                  // BODY_COLOR layer: ALWAYS use the default body color (not affected by crane selector)
                  let defaultBodyColor = null;
                  let defaultBodyTextureUrl = null;
                  
                  // Determine default body color based on finish
                  if (normalizedFinish === 'two-tone' || normalizedFinish === 'two-tone-fade') {
                    const colorValue = bodyColorGroup ? this.getValueByCode(bodyColorGroup.handle, selectedBodyColor) : null;
                    defaultBodyColor = colorValue?.hexColor;
                  } else if (normalizedFinish === 'textured' || normalizedFinish === 'distressed') {
                    // For textured finishes, get the texture
                    let singleColorCode = selectedSingleColor?.toUpperCase();
                    if (singleColorCode && singleColorCode.includes('DISTRESSED')) {
                      singleColorCode = 'DISTRESSED';
                    }
                    const textureData = manifest.hinges?.textures?.[singleColorCode];
                    if (typeof textureData === 'string') {
                      defaultBodyTextureUrl = textureData;
                    } else if (textureData && typeof textureData === 'object') {
                      defaultBodyTextureUrl = textureData[sizeCode];
                    }
                  } else {
                    const colorValue = singleColorGroup ? this.getValueByCode(singleColorGroup.handle, selectedSingleColor) : null;
                    defaultBodyColor = colorValue?.hexColor;
                  }
                  
                  console.log('🎨 CRANE overlay BODY_COLOR using default body color:', defaultBodyColor || defaultBodyTextureUrl);
                  
                  // Get crane hinge color/texture for CRANE_COLOR layer (same as base)
                  let craneOverlayColorValue = craneBaseColorValue;
                  let craneOverlayTextureUrl = craneBaseTextureUrl;
                  
                  // Render BODY_COLOR layer with default body color (mirror if RIGHT swing) - z-index 115
                  if (bodyColorHingeUrl && defaultBodyTextureUrl) {
                    this.addCraneHingeLayerWithTexture(container, bodyColorHingeUrl, defaultBodyTextureUrl, 115, 'crane-hinges-body-color', craneDimensions, shouldMirrorOverlays);
                    console.log('✅ CRANE overlay BODY_COLOR rendered with texture:', defaultBodyTextureUrl, shouldMirrorOverlays ? '(mirrored)' : '');
                  } else if (bodyColorHingeUrl && defaultBodyColor) {
                    this.addCraneHingeLayerWithMask(container, bodyColorHingeUrl, defaultBodyColor, 115, 'crane-hinges-body-color', craneDimensions, shouldMirrorOverlays);
                    console.log('✅ CRANE overlay BODY_COLOR rendered:', defaultBodyColor, shouldMirrorOverlays ? '(mirrored)' : '');
                  } else if (bodyColorHingeUrl) {
                    this.addCraneHingeLayer(container, bodyColorHingeUrl, 115, 'crane-hinges-body-color', craneDimensions, shouldMirrorOverlays);
                    console.log('✅ CRANE overlay BODY_COLOR rendered without color', shouldMirrorOverlays ? '(mirrored)' : '');
                  }
                  
                  // Render CRANE_COLOR layer with crane hinge color (mirror if RIGHT swing) - z-index 115
                  if (craneColorHingeUrl && craneOverlayTextureUrl) {
                    this.addCraneHingeLayerWithTexture(container, craneColorHingeUrl, craneOverlayTextureUrl, 115, 'crane-hinges-crane-color', craneDimensions, shouldMirrorOverlays);
                    console.log('✅ CRANE overlay CRANE_COLOR rendered with texture:', craneOverlayTextureUrl, shouldMirrorOverlays ? '(mirrored)' : '');
                  } else if (craneColorHingeUrl && craneOverlayColorValue) {
                    this.addCraneHingeLayerWithMask(container, craneColorHingeUrl, craneOverlayColorValue, 115, 'crane-hinges-crane-color', craneDimensions, shouldMirrorOverlays);
                    console.log('✅ CRANE overlay CRANE_COLOR rendered with color:', craneOverlayColorValue, shouldMirrorOverlays ? '(mirrored)' : '');
                  } else if (craneColorHingeUrl) {
                    this.addCraneHingeLayer(container, craneColorHingeUrl, 115, 'crane-hinges-crane-color', craneDimensions, shouldMirrorOverlays);
                    console.log('✅ CRANE overlay CRANE_COLOR rendered without color', shouldMirrorOverlays ? '(mirrored)' : '');
                  }
                  
                  // Render caps (mirror if RIGHT swing) - z-index 115 (above graphics/rivets at 100)
                  if (capsHingeUrl && capsColorValue) {
                    this.addCraneHingeLayerWithMask(container, capsHingeUrl, capsColorValue, 115, 'crane-hinges-caps', craneDimensions, shouldMirrorOverlays);
                    console.log('✅ CRANE caps rendered:', capsColorValue, shouldMirrorOverlays ? '(mirrored)' : '');
                  } else if (capsHingeUrl) {
                    this.addCraneHingeLayer(container, capsHingeUrl, 115, 'crane-hinges-caps', craneDimensions, shouldMirrorOverlays);
                    console.log('✅ CRANE caps rendered without color', shouldMirrorOverlays ? '(mirrored)' : '');
                  }
                } else {
                  // Standard single crane hinge rendering with color masking
                  const colorVariantUpper = selectedLockColor?.toUpperCase();
                  let craneColor = null;
                  
                  // Use solid color for gold, gradients for silver and black chrome
                  if (colorVariantUpper === 'GOLD') {
                    craneColor = '#fcd962';
                  } else if (colorVariantUpper === 'SILVER') {
                    craneColor = 'linear-gradient(to bottom, #f7fcfc 0%, #a5b2cc 100%)';
                  } else if (colorVariantUpper === 'BLACK_CHROME') {
                    craneColor = 'linear-gradient(to bottom, #47453d 0%, #302f29 100%)';
                  }
                  
                  if (craneColor) {
                    // Render with color masking (more transparent to show detail) - z-index 115 (above graphics/rivets at 100, below locks)
                    this.addCraneHingeLayerWithMask(container, hingeImageUrl, craneColor, 115, 'crane-hinges', craneDimensions);
                    console.log('✅ Crane hinges rendered with color mask:', { 
                      type: hingeTypeUpper, 
                      swing: swingDirectionUpper, 
                      color: colorVariantUpper,
                      colorValue: craneColor,
                      imageUrl: hingeImageUrl, 
                      dimensions: craneDimensions
                    });
                  } else {
                    // No lock color selected or not a valid color - render without color mask (plain grayscale)
                    console.log(`ℹ️ Rendering grayscale crane hinge without color mask (lock color: ${colorVariantUpper || 'not selected'})`);
                    this.addCraneHingeLayer(container, hingeImageUrl, 115, 'crane-hinges', craneDimensions);
                  }
                }
              }
            } else {
              // Use legacy pre-colored rendering
              // Render crane hinges as single pre-composed image with custom dimensions
              // The image already contains all hinges in their correct positions - z-index 115 (above graphics/rivets at 100, below locks)
              this.addCraneHingeLayer(container, hingeImageUrl, 115, 'crane-hinges', craneDimensions);
              console.log('✅ Pre-colored crane hinges rendered:', { 
                type: hingeTypeUpper, 
                swing: swingDirectionUpper, 
                color: selectedLockColor, 
                imageUrl: hingeImageUrl, 
                dimensions: craneDimensions,
                backgroundSize: craneDimensions?.backgroundSize || 'contain (default)'
              });
            }
          } else if (isPreColoredHinge) {
            // Render pre-colored hinges WITHOUT color masking (textured, distressed) - z-index 100 (below locks)
            // Pass null for color to skip color overlay
            this.addMultipleHinges(container, hingeImageUrl, hingeCount, swingDirection, null, hingePosition, sizeCode, 100, hingeScaleFactor);
            console.log('✅ Pre-colored hinges rendered:', { type: hingeTypeUpper, finish: normalizedFinish, count: hingeCount, side: swingDirection, position: hingePosition, scale: hingeScaleFactor });
          } else if (hingeColor) {
            // Render standard hinges with color masking (multiple individual hinges) - z-index 100 (below locks)
            this.addMultipleHinges(container, hingeImageUrl, hingeCount, swingDirection, hingeColor, hingePosition, sizeCode, 100, hingeScaleFactor);
            console.log('✅ Hinges rendered:', { type: hingeTypeUpper, count: hingeCount, side: swingDirection, color: hingeColor, position: hingePosition, scale: hingeScaleFactor });
          } else {
            console.warn('⚠️ No body/single color selected for hinge masking');
          }
        } else {
          console.warn('⚠️ No hinge image found for type:', hingeTypeUpper);
        }
      } else {
        console.log('ℹ️ Internal hinges selected - not rendering hinges');
      }
    }
    
    // AMSEC-specific rendering: Logo and Lock with hardware finish color masking
    if (optionPrefix === 'amsec_') {
      console.log('🎨 Rendering AMSEC logo, lock, and handle hardware');
      
      // Get AMSEC-specific option groups
      const amsecHandleGroup = this.data.optionGroups.find(g => g.handle === 'amsec_handle');
      const amsecLockTypeGroup = this.data.optionGroups.find(g => g.handle === 'amsec_lock_type');
      const amsecHardwareFinishGroup = this.data.optionGroups.find(g => g.handle === 'amsec_hardware_finish');
      
      // Get selections
      const selectedHandle = amsecHandleGroup ? this.state.selections[amsecHandleGroup.handle]?.[0] : null;
      const selectedAmsecLockType = amsecLockTypeGroup ? this.state.selections[amsecLockTypeGroup.handle]?.[0] : null;
      const selectedHardwareFinish = amsecHardwareFinishGroup ? this.state.selections[amsecHardwareFinishGroup.handle]?.[0] : null;
      
      // Get hardware finish color for masking logo and handle
      let hardwareColor = null;
      let hardwareColorLogo = null;
      let hardwareColorHandle = null;
      
      if (selectedHardwareFinish && amsecHardwareFinishGroup) {
        const hardwareValue = this.getValueByCode(amsecHardwareFinishGroup.handle, selectedHardwareFinish);
        const finishUpper = selectedHardwareFinish.toUpperCase();
        
        // Apply gradient for brass to create realistic metallic look
        if (finishUpper.includes('BRASS')) {
          // Alternating gradient with brass tones for depth and realism
          hardwareColor = 'linear-gradient(135deg, #dec57a 0%, #fcf39f 25%, #dec57a 50%, #fcf39f 75%, #dec57a 100%)';
          // Use a solid, non-transparent brass tone for the logo per request
          hardwareColorLogo = '#baa05d';
          // Keep the gradient for handles to retain metallic look
          hardwareColorHandle = hardwareColor;
          console.log('🎨 AMSEC Hardware finish: BRASS (logo: solid #baa05d, handle: gradient)');
        } else if (finishUpper.includes('BLACK_NICKEL')) {
          // Black nickel: solid black for logo, gradient for handle
          hardwareColorLogo = '#000000';
          hardwareColorHandle = 'linear-gradient(135deg, #000000 0%, #948e8d 50%, #000000 100%)';
          hardwareColor = hardwareColorLogo; // For fallback
          console.log('🎨 AMSEC Hardware finish: BLACK_NICKEL (logo: black, handle: gradient)');
        } else {
          hardwareColor = hardwareValue?.hexColor;
          hardwareColorLogo = hardwareColor;
          hardwareColorHandle = hardwareColor;
          console.log('🎨 AMSEC Hardware finish:', { selectedHardwareFinish, hardwareColor });
        }
      }
      
      // 1. Render logo with hardware color mask (z-index 125)
      const logoUrl = manifest.logo?.[sizeCode];
      if (logoUrl && hardwareColorLogo) {
        console.log('🎨 Rendering AMSEC logo with hardware color:', hardwareColorLogo);
        
        // Create wrapper for logo with color masking
        const wrapper = document.createElement('div');
        wrapper.style.position = 'absolute';
        wrapper.style.zIndex = 125;
        wrapper.style.pointerEvents = 'none';
        wrapper.style.isolation = 'isolate';
        wrapper.style.inset = '0';
        wrapper.style.width = '100%';
        wrapper.style.height = '100%';
        
        // Layer 1: Grayscale base image
        const baseLayer = document.createElement('div');
        baseLayer.className = 'safe-layer-amsec-logo-base';
        baseLayer.style.position = 'absolute';
        baseLayer.style.inset = '0';
        baseLayer.style.width = '100%';
        baseLayer.style.height = '100%';
        baseLayer.style.backgroundImage = `url('${logoUrl}')`;
        baseLayer.style.backgroundSize = 'contain';
        baseLayer.style.backgroundPosition = 'center';
        baseLayer.style.backgroundRepeat = 'no-repeat';
        baseLayer.style.opacity = '1';
        
        // Layer 2: Color mask overlay
        const colorLayer = document.createElement('div');
        colorLayer.className = 'safe-layer-amsec-logo-color';
        colorLayer.style.position = 'absolute';
        colorLayer.style.inset = '0';
        colorLayer.style.width = '100%';
        colorLayer.style.height = '100%';
        
        // Apply gradient or solid color
        if (hardwareColorLogo.startsWith('linear-gradient') || hardwareColorLogo.startsWith('radial-gradient')) {
          colorLayer.style.backgroundImage = hardwareColorLogo;
        } else {
          colorLayer.style.backgroundColor = hardwareColorLogo;
        }
        colorLayer.style.mixBlendMode = 'multiply';
        colorLayer.style.webkitMaskImage = `url('${logoUrl}')`;
        colorLayer.style.maskImage = `url('${logoUrl}')`;
        colorLayer.style.webkitMaskSize = 'contain';
        colorLayer.style.maskSize = 'contain';
        colorLayer.style.webkitMaskPosition = 'center';
        colorLayer.style.maskPosition = 'center';
        colorLayer.style.webkitMaskRepeat = 'no-repeat';
        colorLayer.style.maskRepeat = 'no-repeat';
        colorLayer.style.opacity = '1';
        
        wrapper.appendChild(baseLayer);
        wrapper.appendChild(colorLayer);
        container.appendChild(wrapper);
        
        console.log('✅ AMSEC logo rendered with color mask:', hardwareColorLogo);
      } else if (logoUrl) {
        console.log('ℹ️ AMSEC logo found but no hardware finish selected');
      }
      
      // 2. Render lock - pre-colored, no color mask needed (z-index 130)
      if (selectedHardwareFinish && selectedAmsecLockType) {
        // Normalize hardware finish to uppercase for manifest lookup
        const hardwareFinishType = selectedHardwareFinish.toUpperCase();
        
        // Determine if lock is mechanical or electronic based on the lock type value
        const lockTypeUpper = selectedAmsecLockType.toUpperCase();
        const isElectronic = lockTypeUpper.includes('ESL') || 
                           lockTypeUpper.includes('TITAN') || 
                           lockTypeUpper.includes('ELECTRONIC') ||
                           lockTypeUpper.includes('DIGITAL');
        const lockMechanism = isElectronic ? 'electronic' : 'mechanical';
        
        console.log('🔒 AMSEC lock type analysis:', { 
          selectedAmsecLockType, 
          lockTypeUpper,
          isElectronic, 
          lockMechanism 
        });
        
        // Lookup lock image: manifest.locks[hardwareFinish][lockMechanism][sizeCode]
        // Handle typo in manifest: "mechancial" instead of "mechanical"
        let lockUrl = manifest.locks?.[hardwareFinishType]?.[lockMechanism]?.[sizeCode];
        if (!lockUrl && lockMechanism === 'mechanical') {
          // Fallback to check for common typo
          lockUrl = manifest.locks?.[hardwareFinishType]?.['mechancial']?.[sizeCode];
          if (lockUrl) {
            console.log('⚠️ Found lock using typo "mechancial" - consider fixing manifest');
          }
        }
        
        console.log('🔒 Looking up AMSEC lock:', { 
          hardwareFinishType, 
          lockMechanism, 
          sizeCode, 
          found: !!lockUrl,
          path: `manifest.locks.${hardwareFinishType}.${lockMechanism}.${sizeCode}`
        });
        
        if (lockUrl) {
          console.log('🎨 Rendering AMSEC lock (pre-colored, no mask)');
          
          // Render lock as simple image layer (no color masking)
          this.addImageLayer(container, lockUrl, 130, 'amsec-lock', 1);
          
          console.log('✅ AMSEC lock rendered:', { hardwareFinishType, lockMechanism });
        } else {
          console.warn('⚠️ No AMSEC lock image found for:', { hardwareFinishType, lockMechanism, sizeCode });
        }
      }
      
      // 3. Render handle with hardware color mask (z-index 135, above lock)
      if (selectedHandle && hardwareColorHandle) {
        const handleType = selectedHandle.toUpperCase();
        
        // Lookup handle image: manifest.handles[handleType][sizeCode]
        const handleUrl = manifest.handles?.[handleType]?.[sizeCode];
        
        console.log('🔧 Looking up AMSEC handle:', { 
          handleType, 
          sizeCode, 
          found: !!handleUrl,
          path: `manifest.handles.${handleType}.${sizeCode}`
        });
        
        if (handleUrl) {
          console.log('🎨 Rendering AMSEC handle with hardware color:', hardwareColorHandle);
          
          // Create wrapper for handle with color masking
          const wrapper = document.createElement('div');
          wrapper.style.position = 'absolute';
          wrapper.style.zIndex = 135;
          wrapper.style.pointerEvents = 'none';
          wrapper.style.isolation = 'isolate';
          wrapper.style.inset = '0';
          wrapper.style.width = '100%';
          wrapper.style.height = '100%';
          
          // Layer 1: Grayscale base image
          const baseLayer = document.createElement('div');
          baseLayer.className = 'safe-layer-amsec-handle-base';
          baseLayer.style.position = 'absolute';
          baseLayer.style.inset = '0';
          baseLayer.style.width = '100%';
          baseLayer.style.height = '100%';
          baseLayer.style.backgroundImage = `url('${handleUrl}')`;
          baseLayer.style.backgroundSize = 'contain';
          baseLayer.style.backgroundPosition = 'center';
          baseLayer.style.backgroundRepeat = 'no-repeat';
          baseLayer.style.opacity = '1';
          
          // Layer 2: Color mask overlay
          const colorLayer = document.createElement('div');
          colorLayer.className = 'safe-layer-amsec-handle-color';
          colorLayer.style.position = 'absolute';
          colorLayer.style.inset = '0';
          colorLayer.style.width = '100%';
          colorLayer.style.height = '100%';
          
          // Apply gradient or solid color
          if (hardwareColorHandle.startsWith('linear-gradient') || hardwareColorHandle.startsWith('radial-gradient')) {
            colorLayer.style.backgroundImage = hardwareColorHandle;
          } else {
            colorLayer.style.backgroundColor = hardwareColorHandle;
          }
          colorLayer.style.mixBlendMode = 'multiply';
          colorLayer.style.webkitMaskImage = `url('${handleUrl}')`;
          colorLayer.style.maskImage = `url('${handleUrl}')`;
          colorLayer.style.webkitMaskSize = 'contain';
          colorLayer.style.maskSize = 'contain';
          colorLayer.style.webkitMaskPosition = 'center';
          colorLayer.style.maskPosition = 'center';
          colorLayer.style.webkitMaskRepeat = 'no-repeat';
          colorLayer.style.maskRepeat = 'no-repeat';
          colorLayer.style.opacity = '0.7';
          
          wrapper.appendChild(baseLayer);
          wrapper.appendChild(colorLayer);
          container.appendChild(wrapper);
          
          console.log('✅ AMSEC handle rendered with color mask:', { handleType, hardwareColor: hardwareColorHandle });
        } else {
          console.warn('⚠️ No AMSEC handle image found for:', { handleType, sizeCode });
        }
      }
    }
    
    // Layer 10: Hinge Caps (if selected as accessory OR included in configuration, positioned same as hinges)
    // Check if HINGE_CAPS is selected in the accessory group
    const hasHingeCapsSelected = accessories && Array.isArray(accessories) 
      ? accessories.includes('HINGE_CAPS')
      : accessories && typeof accessories === 'object' 
        ? accessories['HINGE_CAPS'] 
        : false;
    
    // Also check if hinge caps are included in the configuration
    const hasHingeCapsIncluded = this.checkIfIncluded('HINGE_CAPS');
    
    // Render hinge caps ONLY if either selected OR included AND hinge type is EXTERNAL
    if ((hasHingeCapsSelected || hasHingeCapsIncluded) && selectedHingeType === 'EXTERNAL' && selectedHingeSwing && selectedLockColor) {
      console.log('🔧 Rendering hinge caps with graphic color');
      
      // Get size-specific hinge cap images from manifest
      const hingeCapConfig = manifest.hingeCaps?.images?.EXTERNAL;
      if (!hingeCapConfig || !hingeCapConfig[sizeCode]) {
        console.warn(`⚠️ No hinge cap configuration found for size: ${sizeCode}`);
      } else {
        const sizeHingeCapImages = hingeCapConfig[sizeCode];
        const hingeCapImageUrl = sizeHingeCapImages?.grayscale;
        
        if (!hingeCapImageUrl) {
          console.warn(`⚠️ No grayscale hinge cap image found for size: ${sizeCode}`);
        } else {
          const hingePosition = manifest.hingePositions?.[sizeCode]?.EXTERNAL;
          const hingeCount = hingePosition?.count || 2;
          const shouldMirror = selectedHingeSwing === 'RIGHT';
          
          // Use graphic/lock color for hinge caps
          const graphicColorValue = lockColorGroup ? this.getValueByCode(lockColorGroup.handle, selectedLockColor) : null;
          const graphicHexColor = graphicColorValue?.hexColor;
          const graphicGradient = graphicColorValue?.gradient;
          
          // Get color or gradient
          let graphicColorOrGradient = graphicHexColor;
          if (graphicGradient) {
            graphicColorOrGradient = graphicGradient;
          }
          
          if (!graphicColorOrGradient) {
            console.warn('⚠️ No graphic color found for hinge caps');
          } else {
            console.log('🎨 Hinge cap color:', graphicColorOrGradient);
            
            // Render hinge caps with graphic color mask (z-index 120 - above hinges at 110)
            this.addExternalHingeCapLayerWithColor(
              container,
              hingeCapImageUrl,
              graphicColorOrGradient,
              120,
              'external-hinge-caps',
              shouldMirror
            );
          }
        }
      }
    }
    
    // Layer 11: Pull Handle (if selected as accessory)
    // Check if PULL_HANDLE is selected in the accessory group
    const hasPullHandleSelected = accessories && Array.isArray(accessories) 
      ? accessories.includes('PULL_HANDLE')
      : accessories && typeof accessories === 'object' 
        ? accessories['PULL_HANDLE'] 
        : false;
    
    // Also check if pull handle is included in the configuration
    const hasPullHandleIncluded = this.checkIfIncluded('PULL_HANDLE');
    
    // Render pull handle ONLY if either selected OR included
    if ((hasPullHandleSelected || hasPullHandleIncluded) && selectedLockColor) {
      const colorVariantUpper = selectedLockColor.toUpperCase();
      
      // Get grayscale pull handle image for this size
      // Manifest structure: pullHandle.images.GRAYSCALE[sizeCode]
      const grayscalePullHandleUrl = manifest.pullHandle?.images?.GRAYSCALE?.[sizeCode];
      
      if (grayscalePullHandleUrl) {
        // Map color selection to hex color - pull handle specific colors
        const colorMap = {
          'GOLD': '#ffe285',
          'gold': '#ffe285',
          'SILVER': '#d5d8e6',
          'silver': '#d5d8e6',
          'BLACK_CHROME': '#4a4a49',
          'black_chrome': '#4a4a49',
          'BLACK-CHROME': '#4a4a49',
          'black-chrome': '#4a4a49'
        };
        
        const hexColor = colorMap[selectedLockColor];
        
        if (hexColor) {
          // Check if crane hinges are selected and flip pull handle to opposite side
          const hingeTypeUpper = selectedHingeType?.toUpperCase();
          const isCraneHinge = hingeTypeUpper === 'CRANE' || hingeTypeUpper === 'CRANE_BLANK';
          const hingeSwingUpper = selectedHingeSwing?.toUpperCase();
          const shouldFlipPullHandle = isCraneHinge && hingeSwingUpper === 'LEFT';
          
          console.log(`✓ Applying color mask to pull handle: ${selectedLockColor} (${hexColor}) for size ${sizeCode}`, {
            craneHinge: isCraneHinge,
            swing: hingeSwingUpper,
            flipped: shouldFlipPullHandle
          });
          
          // Create wrapper - fill entire container (size-specific images are pre-positioned)
          const wrapper = document.createElement('div');
          wrapper.style.position = 'absolute';
          wrapper.style.zIndex = 130;
          wrapper.style.pointerEvents = 'none';
          wrapper.style.isolation = 'isolate';
          wrapper.style.inset = '0';
          wrapper.style.width = '100%';
          wrapper.style.height = '100%';
          
          // Apply horizontal flip if crane hinge is on the left
          if (shouldFlipPullHandle) {
            wrapper.style.transform = 'scaleX(-1)';
          }
          
          // Layer 1: Grayscale base image (100% opaque to show details)
          const baseLayer = document.createElement('div');
          baseLayer.className = 'safe-layer-pull-handle-base';
          baseLayer.style.position = 'absolute';
          baseLayer.style.inset = '0';
          baseLayer.style.width = '100%';
          baseLayer.style.height = '100%';
          baseLayer.style.backgroundImage = `url('${grayscalePullHandleUrl}')`;
          baseLayer.style.backgroundSize = 'contain';
          baseLayer.style.backgroundPosition = 'center';
          baseLayer.style.backgroundRepeat = 'no-repeat';
          baseLayer.style.opacity = '1';
          
          // Layer 2: Color overlay with transparency (more transparent for black to show detail)
          const colorLayer = document.createElement('div');
          colorLayer.className = 'safe-layer-pull-handle-color';
          colorLayer.style.position = 'absolute';
          colorLayer.style.inset = '0';
          colorLayer.style.width = '100%';
          colorLayer.style.height = '100%';
          colorLayer.style.webkitMaskImage = `url('${grayscalePullHandleUrl}')`;
          colorLayer.style.maskImage = `url('${grayscalePullHandleUrl}')`;
          colorLayer.style.webkitMaskSize = 'contain';
          colorLayer.style.maskSize = 'contain';
          colorLayer.style.webkitMaskPosition = 'center';
          colorLayer.style.maskPosition = 'center';
          colorLayer.style.webkitMaskRepeat = 'no-repeat';
          colorLayer.style.maskRepeat = 'no-repeat';
          colorLayer.style.backgroundColor = hexColor;
          colorLayer.style.mixBlendMode = 'multiply';
          // Use lower opacity for black chrome to show more detail
          colorLayer.style.opacity = colorVariantUpper === 'BLACK_CHROME' || colorVariantUpper === 'BLACK-CHROME' ? '0.6' : '0.9';
          
          wrapper.appendChild(baseLayer);
          wrapper.appendChild(colorLayer);
          container.appendChild(wrapper);
          
          console.log('✅ Pull handle rendered with color mask:', { 
            color: colorVariantUpper,
            hexColor: hexColor,
            size: sizeCode,
            imageUrl: grayscalePullHandleUrl,
            source: hasPullHandleSelected ? 'selected' : 'included'
          });
        } else {
          console.warn('⚠️ No color mapping found for pull handle:', selectedLockColor);
        }
      } else {
        console.warn(`⚠️ No grayscale pull handle image found for size ${sizeCode}. Expected: pullHandle.images.GRAYSCALE.${sizeCode}`);
      }
    }
    
    // Layer 12: Rivet Trim (if selected as accessory)
    // Check if RIVET_TRIM is selected in the accessory group
    const hasRivetTrimSelected = accessories && Array.isArray(accessories) 
      ? accessories.includes('RIVET_TRIM')
      : accessories && typeof accessories === 'object' 
        ? accessories['RIVET_TRIM'] 
        : false;
    
    // Also check if rivet trim is included in the configuration
    const hasRivetTrimIncluded = this.checkIfIncluded('RIVET_TRIM');
    
    // Determine rivet color: use override if set, otherwise use body color
    let rivetColor = this.state.rivetColorOverride;
    if (!rivetColor) {
      // Default to body color
      if (normalizedFinish === 'two-tone-fade' || normalizedFinish === 'two-tone') {
        rivetColor = selectedBodyColor;
      } else {
        rivetColor = selectedSingleColor;
      }
    }
    
    // Render rivet trim ONLY if either selected OR included AND we have a color
    if ((hasRivetTrimSelected || hasRivetTrimIncluded) && rivetColor) {
      const colorVariantUpper = rivetColor.toUpperCase();
      
      // Get grayscale rivet trim image for this size
      // Manifest structure: rivetTrim.images.GRAYSCALE[sizeCode]
      const grayscaleRivetTrimUrl = manifest.rivetTrim?.images?.GRAYSCALE?.[sizeCode];
      
      if (grayscaleRivetTrimUrl) {
        // Lookup hex color from the rivet color option
        const colorOption = this.getValueByCode('color_single', rivetColor) || this.getValueByCode('body_color', rivetColor);
        let hexColor = colorOption?.hexColor;
        
        // Fallback color map if hexColor not found in metaobjects
        if (!hexColor) {
          const colorMap = {
            'GOLD': '#ffe285',
            'gold': '#ffe285',
            'SILVER': '#d5d8e6',
            'silver': '#d5d8e6',
            'BLACK_CHROME': '#4a4a49',
            'black_chrome': '#4a4a49',
            'BLACK-CHROME': '#4a4a49',
            'black-chrome': '#4a4a49'
          };
          hexColor = colorMap[rivetColor];
        }
        
        if (hexColor) {
          console.log(`✓ Applying color mask to rivet trim: ${selectedLockColor} (${hexColor}) for size ${sizeCode}`);
          
          // Create wrapper - fill entire container (size-specific images are pre-positioned)
          const wrapper = document.createElement('div');
          wrapper.style.position = 'absolute';
          wrapper.style.zIndex = 130;
          wrapper.style.pointerEvents = 'none';
          wrapper.style.isolation = 'isolate';
          wrapper.style.inset = '0';
          wrapper.style.width = '100%';
          wrapper.style.height = '100%';
          
          // Layer 1: Grayscale base image (100% opaque to show details)
          const baseLayer = document.createElement('div');
          baseLayer.className = 'safe-layer-rivet-trim-base';
          baseLayer.style.position = 'absolute';
          baseLayer.style.inset = '0';
          baseLayer.style.width = '100%';
          baseLayer.style.height = '100%';
          baseLayer.style.backgroundImage = `url('${grayscaleRivetTrimUrl}')`;
          baseLayer.style.backgroundSize = 'contain';
          baseLayer.style.backgroundPosition = 'center';
          baseLayer.style.backgroundRepeat = 'no-repeat';
          baseLayer.style.opacity = '1';
          
          // Layer 2: Color overlay with transparency (more transparent for black to show detail)
          const colorLayer = document.createElement('div');
          colorLayer.className = 'safe-layer-rivet-trim-color';
          colorLayer.style.position = 'absolute';
          colorLayer.style.inset = '0';
          colorLayer.style.width = '100%';
          colorLayer.style.height = '100%';
          colorLayer.style.webkitMaskImage = `url('${grayscaleRivetTrimUrl}')`;
          colorLayer.style.maskImage = `url('${grayscaleRivetTrimUrl}')`;
          colorLayer.style.webkitMaskSize = 'contain';
          colorLayer.style.maskSize = 'contain';
          colorLayer.style.webkitMaskPosition = 'center';
          colorLayer.style.maskPosition = 'center';
          colorLayer.style.webkitMaskRepeat = 'no-repeat';
          colorLayer.style.maskRepeat = 'no-repeat';
          colorLayer.style.backgroundColor = hexColor;
          colorLayer.style.mixBlendMode = 'multiply';
          // Use lower opacity for black chrome to show more detail
          colorLayer.style.opacity = colorVariantUpper === 'BLACK_CHROME' || colorVariantUpper === 'BLACK-CHROME' ? '0.6' : '0.9';
          
          wrapper.appendChild(baseLayer);
          wrapper.appendChild(colorLayer);
          container.appendChild(wrapper);
          
          console.log('✅ Rivet trim rendered with color mask:', { 
            color: colorVariantUpper,
            hexColor: hexColor,
            size: sizeCode,
            imageUrl: grayscaleRivetTrimUrl,
            source: hasRivetTrimSelected ? 'selected' : 'included'
          });
        } else {
          console.warn('⚠️ No color mapping found for rivet trim:', selectedLockColor);
        }
      } else {
        console.warn(`⚠️ No grayscale rivet trim image found for size ${sizeCode}. Expected: rivetTrim.images.GRAYSCALE.${sizeCode}`);
      }
    }
  }
  
  addCraneHingeLayerWithMask(container, imageUrl, color, zIndex, className, craneDimensions = null, shouldMirror = false) {
    if (!imageUrl || !color) return;
    
    // Create a wrapper for crane hinge - full container size (images pre-positioned in Photoshop)
    const wrapper = document.createElement('div');
    wrapper.style.position = 'absolute';
    wrapper.style.zIndex = zIndex;
    wrapper.style.pointerEvents = 'none';
    wrapper.style.isolation = 'isolate';
    wrapper.style.inset = '0';
    wrapper.style.width = '100%';
    wrapper.style.height = '100%';
    
    // Create base layer (grayscale image)
    const baseLayer = document.createElement('div');
    baseLayer.className = `safe-layer-${className}-base`;
    baseLayer.style.position = 'absolute';
    baseLayer.style.inset = '0';
    baseLayer.style.width = '100%';
    baseLayer.style.height = '100%';
    baseLayer.style.backgroundImage = `url('${imageUrl}')`;
    baseLayer.style.backgroundPosition = 'center';
    baseLayer.style.backgroundRepeat = 'no-repeat';
    baseLayer.style.backgroundSize = 'contain';
    
    // Mirror horizontally if RIGHT swing
    if (shouldMirror) {
      baseLayer.style.transform = 'scaleX(-1)';
    }
    
    // Create color mask layer (overlays the grayscale with gradient/color)
    const colorLayer = document.createElement('div');
    colorLayer.className = `safe-layer-${className}-color`;
    colorLayer.style.position = 'absolute';
    colorLayer.style.inset = '0';
    colorLayer.style.width = '100%';
    colorLayer.style.height = '100%';
    
    // Apply mask using the same grayscale image
    colorLayer.style.webkitMaskImage = `url('${imageUrl}')`;
    colorLayer.style.maskImage = `url('${imageUrl}')`;
    colorLayer.style.webkitMaskSize = 'contain';
    colorLayer.style.maskSize = 'contain';
    
    colorLayer.style.webkitMaskPosition = 'center';
    colorLayer.style.maskPosition = 'center';
    colorLayer.style.webkitMaskRepeat = 'no-repeat';
    colorLayer.style.maskRepeat = 'no-repeat';
    
    // Mirror horizontally if RIGHT swing
    if (shouldMirror) {
      colorLayer.style.transform = 'scaleX(-1)';
    }
    
    // Apply color (gradient or solid) - check if it's a gradient or solid color
    if (color.startsWith('linear-gradient') || color.startsWith('radial-gradient')) {
      colorLayer.style.backgroundImage = color;
    } else {
      colorLayer.style.backgroundColor = color;
    }
    colorLayer.style.mixBlendMode = 'multiply';
    colorLayer.style.opacity = '0.75'; // Less transparent for more solid color appearance
    
    wrapper.appendChild(baseLayer);
    wrapper.appendChild(colorLayer);
    container.appendChild(wrapper);
  }
  
  addCraneHingeLayerWithTexture(container, imageUrl, textureUrl, zIndex, className, craneDimensions = null, shouldMirror = false) {
    if (!imageUrl || !textureUrl) return;
    
    console.log('🎨 Applying texture to crane hinge:', { imageUrl, textureUrl, className });
    
    // Check if this is Rimrock texture (slightly less transparent than others)
    const isRimrock = textureUrl.toLowerCase().includes('rimrock');
    const textureOpacity = isRimrock ? '0.7' : '0.65'; // Reduced for better detail visibility
    
    // Create a wrapper for crane hinge - full container size (images pre-positioned in Photoshop)
    const wrapper = document.createElement('div');
    wrapper.style.position = 'absolute';
    wrapper.style.zIndex = zIndex;
    wrapper.style.pointerEvents = 'none';
    wrapper.style.isolation = 'isolate';
    wrapper.style.inset = '0';
    wrapper.style.width = '100%';
    wrapper.style.height = '100%';
    
    // Create base layer (grayscale hinge image)
    const baseLayer = document.createElement('div');
    baseLayer.className = `safe-layer-${className}-base`;
    baseLayer.style.position = 'absolute';
    baseLayer.style.inset = '0';
    baseLayer.style.width = '100%';
    baseLayer.style.height = '100%';
    baseLayer.style.backgroundImage = `url('${imageUrl}')`;
    baseLayer.style.backgroundPosition = 'center';
    baseLayer.style.backgroundRepeat = 'no-repeat';
    baseLayer.style.backgroundSize = 'contain';
    
    // Mirror horizontally if RIGHT swing
    if (shouldMirror) {
      baseLayer.style.transform = 'scaleX(-1)';
    }
    
    // Create texture layer (uses hinge as mask, shows texture through)
    const textureLayer = document.createElement('div');
    textureLayer.className = `safe-layer-${className}-texture`;
    textureLayer.style.position = 'absolute';
    textureLayer.style.inset = '0';
    textureLayer.style.width = '100%';
    textureLayer.style.height = '100%';
    
    // Apply hinge as mask for the texture
    textureLayer.style.webkitMaskImage = `url('${imageUrl}')`;
    textureLayer.style.maskImage = `url('${imageUrl}')`;
    textureLayer.style.webkitMaskSize = 'contain';
    textureLayer.style.maskSize = 'contain';
    textureLayer.style.webkitMaskPosition = 'center';
    textureLayer.style.maskPosition = 'center';
    textureLayer.style.webkitMaskRepeat = 'no-repeat';
    textureLayer.style.maskRepeat = 'no-repeat';
    
    // Apply texture as background
    textureLayer.style.backgroundImage = `url('${textureUrl}')`;
    textureLayer.style.backgroundSize = 'contain';
    textureLayer.style.backgroundPosition = 'center';
    textureLayer.style.backgroundRepeat = 'no-repeat';
    
    // Mirror horizontally if RIGHT swing
    if (shouldMirror) {
      textureLayer.style.transform = 'scaleX(-1)';
    }
    
    textureLayer.style.mixBlendMode = 'multiply';
    textureLayer.style.opacity = textureOpacity; // Rimrock 90%, others 85%
    
    wrapper.appendChild(baseLayer);
    wrapper.appendChild(textureLayer);
    container.appendChild(wrapper);
  }
  
  addCraneHingeLayer(container, imageUrl, zIndex, className, craneDimensions = null, shouldMirror = false) {
    if (!imageUrl) return;
    
    // Create a wrapper for crane hinge - full container size (images pre-positioned in Photoshop)
    const wrapper = document.createElement('div');
    wrapper.style.position = 'absolute';
    wrapper.style.zIndex = zIndex;
    wrapper.style.pointerEvents = 'none';
    wrapper.style.isolation = 'isolate';
    wrapper.style.inset = '0';
    wrapper.style.width = '100%';
    wrapper.style.height = '100%';
    
    const layer = document.createElement('div');
    layer.className = `safe-layer-${className}`;
    layer.style.position = 'absolute';
    layer.style.inset = '0';
    layer.style.width = '100%';
    layer.style.height = '100%';
    layer.style.backgroundImage = `url('${imageUrl}')`;
    layer.style.backgroundPosition = 'center';
    layer.style.backgroundRepeat = 'no-repeat';
    layer.style.backgroundSize = 'contain';
    
    // Mirror horizontally if RIGHT swing
    if (shouldMirror) {
      layer.style.transform = 'scaleX(-1)';
    }
    
    wrapper.appendChild(layer);
    container.appendChild(wrapper);
  }
  
  addImageLayer(container, imageUrl, zIndex, className, scaleFactor = 1) {
    if (!imageUrl) return;
    
    // Create a wrapper to ensure complete isolation from mask layers
    const wrapper = document.createElement('div');
    wrapper.style.position = 'absolute';
    wrapper.style.inset = '0';
    wrapper.style.width = '100%';
    wrapper.style.height = '100%';
    wrapper.style.zIndex = zIndex;
    wrapper.style.pointerEvents = 'none'; // Don't interfere with interactions
    wrapper.style.isolation = 'isolate'; // Create new stacking context
    
    const layer = document.createElement('div');
    layer.className = `safe-layer-${className}`;
    layer.style.position = 'absolute';
    layer.style.inset = '0';
    layer.style.width = '100%';
    layer.style.height = '100%';
    layer.style.backgroundImage = `url('${imageUrl}')`;
    layer.style.backgroundSize = 'contain';
    layer.style.backgroundPosition = 'center';
    layer.style.backgroundRepeat = 'no-repeat';
    
    // Apply scale transformation if scale factor is not 1
    if (scaleFactor !== 1) {
      layer.style.transform = `scale(${scaleFactor})`;
      layer.style.transformOrigin = 'center center';
    }
    
    wrapper.appendChild(layer);
    container.appendChild(wrapper);
  }
  
  addColorMaskLayer(container, maskUrl, color, zIndex, className, blendMode = 'multiply', opacity = '0.7', gradientMask = null) {
    if (!maskUrl || !color) return;
    
    // Create a wrapper to isolate the masked layer from siblings
    const wrapper = document.createElement('div');
    wrapper.style.position = 'absolute';
    wrapper.style.inset = '0';
    wrapper.style.width = '100%';
    wrapper.style.height = '100%';
    wrapper.style.zIndex = zIndex;
    wrapper.style.pointerEvents = 'none'; // Don't interfere with interactions
    wrapper.style.isolation = 'isolate'; // Create new stacking context
    
    const layer = document.createElement('div');
    layer.className = `safe-layer-color-${className}`;
    layer.style.position = 'absolute';
    layer.style.inset = '0';
    layer.style.width = '100%';
    layer.style.height = '100%';
    
    // Apply mask image and optional gradient mask
    if (gradientMask) {
      // Combine the image mask with gradient mask for fade effect
      layer.style.webkitMaskImage = `${gradientMask}, url('${maskUrl}')`;
      layer.style.maskImage = `${gradientMask}, url('${maskUrl}')`;
      layer.style.webkitMaskComposite = 'source-in';
      layer.style.maskComposite = 'intersect';
    } else {
      // Just use the image mask
      layer.style.webkitMaskImage = `url('${maskUrl}')`;
      layer.style.maskImage = `url('${maskUrl}')`;
    }
    layer.style.webkitMaskSize = 'contain';
    layer.style.maskSize = 'contain';
    layer.style.webkitMaskPosition = 'center';
    layer.style.maskPosition = 'center';
    layer.style.webkitMaskRepeat = 'no-repeat';
    layer.style.maskRepeat = 'no-repeat';
    layer.style.backgroundColor = color;
    layer.style.mixBlendMode = blendMode; // Use provided blend mode
    layer.style.opacity = opacity; // Use provided opacity
    layer.style.filter = 'contrast(1.1)'; // Enhance texture visibility
    
    wrapper.appendChild(layer);
    container.appendChild(wrapper);
  }
  
  addDoorBorder(container, borderImageUrl, zIndex) {
    if (!borderImageUrl) return;
    
    // Render door border as a simple image layer (no scaling needed, same size as safe)
    this.addImageLayer(container, borderImageUrl, zIndex, 'door-border', 1);
    console.log('✅ Door border rendered');
  }
  
  addGraphicLayer(container, imageUrl, zIndex, className, doorDimensions = null) {
    if (!imageUrl) return;
    
    // Use fallback dimensions if not provided
    if (!doorDimensions) {
      console.warn('⚠️ No door dimensions detected for this size. Using fallback dimensions.');
      doorDimensions = {
        top: '10%',
        left: '30%',
        width: '40%',
        height: '80%'
      };
    }
    
    console.log('🎯 Positioning graphic with dimensions:', doorDimensions);
    
    // Create a wrapper positioned exactly at the door boundaries
    const wrapper = document.createElement('div');
    wrapper.style.position = 'absolute';
    wrapper.style.top = doorDimensions.top;
    wrapper.style.left = doorDimensions.left;
    wrapper.style.width = doorDimensions.width;
    wrapper.style.height = doorDimensions.height;
    wrapper.style.zIndex = zIndex;
    wrapper.style.pointerEvents = 'none';
    wrapper.style.isolation = 'isolate';
    wrapper.style.display = 'flex';
    wrapper.style.alignItems = 'center';
    wrapper.style.justifyContent = 'center';
    
    // Create graphic layer - fills almost entire door area (98% to stay just inside the edges)
    const layer = document.createElement('div');
    layer.className = `safe-layer-${className}`;
    layer.style.position = 'relative';
    layer.style.width = '100%'; // Fill the entire door width
    layer.style.height = '100%'; // Fill the entire door height
    layer.style.backgroundImage = `url('${imageUrl}')`;
    layer.style.backgroundSize = '100% 100%'; // Stretch to exact dimensions without cropping
    layer.style.backgroundPosition = 'center';
    layer.style.backgroundRepeat = 'no-repeat';
    
    wrapper.appendChild(layer);
    container.appendChild(wrapper);
  }
  
  addPullHandleLayer(container, imageUrl, position, zIndex, scaleFactor = 1) {
    if (!imageUrl) return;
    
    console.log('🔧 Rendering pull handle:', { imageUrl, scaleFactor });
    
    // Create a wrapper that fills the container
    const wrapper = document.createElement('div');
    wrapper.style.position = 'absolute';
    wrapper.style.inset = '0';
    wrapper.style.width = '100%';
    wrapper.style.height = '100%';
    wrapper.style.zIndex = zIndex;
    wrapper.style.pointerEvents = 'none';
    wrapper.style.isolation = 'isolate';
    wrapper.style.display = 'flex';
    wrapper.style.alignItems = 'center';
    wrapper.style.justifyContent = 'center';
    
    // Create pull handle layer
    const layer = document.createElement('div');
    layer.className = 'safe-layer-pull-handle';
    layer.style.position = 'relative';
    layer.style.width = '100%'; // Fixed width for now to make it visible
    layer.style.height = '100%'; // Fixed height for now to make it visible
    layer.style.backgroundImage = `url('${imageUrl}')`;
    layer.style.backgroundSize = 'contain';
    layer.style.backgroundPosition = 'center';
    layer.style.backgroundRepeat = 'no-repeat';
    
    // Apply scale factor if different from 1
    if (scaleFactor !== 1) {
      layer.style.transform = `scale(${scaleFactor})`;
      layer.style.transformOrigin = 'center';
    }
    
    wrapper.appendChild(layer);
    container.appendChild(wrapper);
  }
  
  detectDoorDimensionsSync(doorMaskUrl, sizeCode) {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          canvas.width = img.width;
          canvas.height = img.height;
          
          ctx.drawImage(img, 0, 0);
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const pixels = imageData.data;
          
          let minX = canvas.width;
          let minY = canvas.height;
          let maxX = 0;
          let maxY = 0;
          let foundPixel = false;
          
          // Find bounding box of non-transparent pixels
          for (let y = 0; y < canvas.height; y++) {
            for (let x = 0; x < canvas.width; x++) {
              const index = (y * canvas.width + x) * 4;
              const alpha = pixels[index + 3];
              
              if (alpha > 10) {
                foundPixel = true;
                if (x < minX) minX = x;
                if (x > maxX) maxX = x;
                if (y < minY) minY = y;
                if (y > maxY) maxY = y;
              }
            }
          }
          
          if (!foundPixel) {
            console.warn(`⚠️ No visible pixels found in door mask for ${sizeCode}`);
            // Return sensible defaults for tall narrow doors
            resolve({
              top: '10%',
              left: '30%',
              width: '40%',
              height: '80%'
            });
            return;
          }
          
          // Convert to percentages
          const dimensions = {
            top: `${(minY / canvas.height * 100).toFixed(1)}%`,
            left: `${(minX / canvas.width * 100).toFixed(1)}%`,
            width: `${((maxX - minX) / canvas.width * 100).toFixed(1)}%`,
            height: `${((maxY - minY) / canvas.height * 100).toFixed(1)}%`
          };
          
          resolve(dimensions);
        } catch (error) {
          console.error(`❌ Error detecting door dimensions for ${sizeCode}:`, error);
          // Return sensible defaults
          resolve({
            top: '10%',
            left: '30%',
            width: '40%',
            height: '80%'
          });
        }
      };
      
      img.onerror = () => {
        console.warn(`⚠️ Failed to load door mask for ${sizeCode}:`, doorMaskUrl);
        // Return sensible defaults for tall narrow doors
        resolve({
          top: '10%',
          left: '30%',
          width: '40%',
          height: '80%'
        });
      };
      
      img.src = doorMaskUrl;
    });
  }
  
  addExternalHingeLayerWithColor(container, hingeImageUrl, color, zIndex, className, shouldMirror = false) {
    if (!hingeImageUrl || !color) return;
    
    console.log('🔧 Rendering external hinges with color:', color);
    
    // Create a wrapper for external hinges - full container size (images pre-positioned in Photoshop)
    const wrapper = document.createElement('div');
    wrapper.style.position = 'absolute';
    wrapper.style.zIndex = zIndex;
    wrapper.style.pointerEvents = 'none';
    wrapper.style.isolation = 'isolate';
    wrapper.style.inset = '0';
    wrapper.style.width = '100%';
    wrapper.style.height = '100%';
    
    // Create base layer (grayscale hinge image)
    const baseLayer = document.createElement('div');
    baseLayer.className = `safe-layer-${className}-base`;
    baseLayer.style.position = 'absolute';
    baseLayer.style.inset = '0';
    baseLayer.style.width = '100%';
    baseLayer.style.height = '100%';
    baseLayer.style.backgroundImage = `url('${hingeImageUrl}')`;
    baseLayer.style.backgroundPosition = 'center';
    baseLayer.style.backgroundRepeat = 'no-repeat';
    baseLayer.style.backgroundSize = 'contain';
    
    // Mirror horizontally if RIGHT swing
    if (shouldMirror) {
      baseLayer.style.transform = 'scaleX(-1)';
    }
    
    // Create color mask layer
    const colorLayer = document.createElement('div');
    colorLayer.className = `safe-layer-${className}-color`;
    colorLayer.style.position = 'absolute';
    colorLayer.style.inset = '0';
    colorLayer.style.width = '100%';
    colorLayer.style.height = '100%';
    
    // Apply mask using the grayscale hinge image
    colorLayer.style.webkitMaskImage = `url('${hingeImageUrl}')`;
    colorLayer.style.maskImage = `url('${hingeImageUrl}')`;
    colorLayer.style.webkitMaskSize = 'contain';
    colorLayer.style.maskSize = 'contain';
    colorLayer.style.webkitMaskPosition = 'center';
    colorLayer.style.maskPosition = 'center';
    colorLayer.style.webkitMaskRepeat = 'no-repeat';
    colorLayer.style.maskRepeat = 'no-repeat';
    
    // Mirror horizontally if RIGHT swing
    if (shouldMirror) {
      colorLayer.style.transform = 'scaleX(-1)';
    }
    
    // Apply color (gradient or solid)
    if (color.startsWith('linear-gradient') || color.startsWith('radial-gradient')) {
      colorLayer.style.backgroundImage = color;
    } else {
      colorLayer.style.backgroundColor = color;
    }
    colorLayer.style.mixBlendMode = 'multiply';
    colorLayer.style.opacity = '0.7';
    
    wrapper.appendChild(baseLayer);
    wrapper.appendChild(colorLayer);
    container.appendChild(wrapper);
    
    console.log('✅ External hinges rendered with color');
  }
  
  addExternalHingeLayerWithTexture(container, hingeImageUrl, textureUrl, zIndex, className, shouldMirror = false) {
    if (!hingeImageUrl || !textureUrl) return;
    
    console.log('🔧 Rendering external hinges with texture:', textureUrl);
    
    // Create a wrapper for external hinges - full container size (images pre-positioned in Photoshop)
    const wrapper = document.createElement('div');
    wrapper.style.position = 'absolute';
    wrapper.style.zIndex = zIndex;
    wrapper.style.pointerEvents = 'none';
    wrapper.style.isolation = 'isolate';
    wrapper.style.inset = '0';
    wrapper.style.width = '100%';
    wrapper.style.height = '100%';
    
    // Create base grayscale layer (opaque) - shows the hinge detail
    const baseLayer = document.createElement('div');
    baseLayer.className = `safe-layer-${className}-base`;
    baseLayer.style.position = 'absolute';
    baseLayer.style.inset = '0';
    baseLayer.style.width = '100%';
    baseLayer.style.height = '100%';
    baseLayer.style.backgroundImage = `url('${hingeImageUrl}')`;
    baseLayer.style.backgroundSize = 'contain';
    baseLayer.style.backgroundPosition = 'center';
    baseLayer.style.backgroundRepeat = 'no-repeat';
    baseLayer.style.opacity = '1'; // Fully opaque grayscale base
    
    // Mirror horizontally if RIGHT swing
    if (shouldMirror) {
      baseLayer.style.transform = 'scaleX(-1)';
    }
    
    // Create texture layer - shows texture image masked to hinge shape (semi-transparent)
    const textureLayer = document.createElement('div');
    textureLayer.className = `safe-layer-${className}-texture`;
    textureLayer.style.position = 'absolute';
    textureLayer.style.inset = '0';
    textureLayer.style.width = '100%';
    textureLayer.style.height = '100%';
    
    // Apply grayscale hinge as mask for the texture
    textureLayer.style.webkitMaskImage = `url('${hingeImageUrl}')`;
    textureLayer.style.maskImage = `url('${hingeImageUrl}')`;
    textureLayer.style.webkitMaskSize = 'contain';
    textureLayer.style.maskSize = 'contain';
    textureLayer.style.webkitMaskPosition = 'center';
    textureLayer.style.maskPosition = 'center';
    textureLayer.style.webkitMaskRepeat = 'no-repeat';
    textureLayer.style.maskRepeat = 'no-repeat';
    
    // Apply texture image as background
    textureLayer.style.backgroundImage = `url('${textureUrl}')`;
    textureLayer.style.backgroundSize = 'cover';
    textureLayer.style.backgroundPosition = 'center';
    textureLayer.style.backgroundRepeat = 'repeat';
    
    // Add transparency to texture overlay to show grayscale hinge detail underneath
    textureLayer.style.opacity = '0.85';
    
    // Mirror horizontally if RIGHT swing
    if (shouldMirror) {
      textureLayer.style.transform = 'scaleX(-1)';
    }
    
    wrapper.appendChild(baseLayer);
    wrapper.appendChild(textureLayer);
    container.appendChild(wrapper);
    
    console.log('✅ External hinges rendered with opaque base and transparent texture overlay');
  }
  
  addExternalHingeCapLayerWithColor(container, hingeCapImageUrl, color, zIndex, className, shouldMirror = false) {
    if (!hingeCapImageUrl || !color) return;
    
    console.log('🔧 Rendering external hinge caps with color:', color);
    
    // Create a wrapper for external hinge caps - full container size (images pre-positioned in Photoshop)
    const wrapper = document.createElement('div');
    wrapper.style.position = 'absolute';
    wrapper.style.zIndex = zIndex;
    wrapper.style.pointerEvents = 'none';
    wrapper.style.isolation = 'isolate';
    wrapper.style.inset = '0';
    wrapper.style.width = '100%';
    wrapper.style.height = '100%';
    
    // Create base layer (grayscale hinge cap image)
    const baseLayer = document.createElement('div');
    baseLayer.className = `safe-layer-${className}-base`;
    baseLayer.style.position = 'absolute';
    baseLayer.style.inset = '0';
    baseLayer.style.width = '100%';
    baseLayer.style.height = '100%';
    baseLayer.style.backgroundImage = `url('${hingeCapImageUrl}')`;
    baseLayer.style.backgroundPosition = 'center';
    baseLayer.style.backgroundRepeat = 'no-repeat';
    baseLayer.style.backgroundSize = 'contain';
    
    // Mirror horizontally if RIGHT swing
    if (shouldMirror) {
      baseLayer.style.transform = 'scaleX(-1)';
    }
    
    // Create color mask layer
    const colorLayer = document.createElement('div');
    colorLayer.className = `safe-layer-${className}-color`;
    colorLayer.style.position = 'absolute';
    colorLayer.style.inset = '0';
    colorLayer.style.width = '100%';
    colorLayer.style.height = '100%';
    
    // Apply mask using the grayscale hinge cap image
    colorLayer.style.webkitMaskImage = `url('${hingeCapImageUrl}')`;
    colorLayer.style.maskImage = `url('${hingeCapImageUrl}')`;
    colorLayer.style.webkitMaskSize = 'contain';
    colorLayer.style.maskSize = 'contain';
    colorLayer.style.webkitMaskPosition = 'center';
    colorLayer.style.maskPosition = 'center';
    colorLayer.style.webkitMaskRepeat = 'no-repeat';
    colorLayer.style.maskRepeat = 'no-repeat';
    
    // Mirror horizontally if RIGHT swing
    if (shouldMirror) {
      colorLayer.style.transform = 'scaleX(-1)';
    }
    
    // Apply color (gradient or solid)
    if (color.startsWith('linear-gradient') || color.startsWith('radial-gradient')) {
      colorLayer.style.backgroundImage = color;
    } else {
      colorLayer.style.backgroundColor = color;
    }
    colorLayer.style.mixBlendMode = 'multiply';
    colorLayer.style.opacity = '0.8';
    
    wrapper.appendChild(baseLayer);
    wrapper.appendChild(colorLayer);
    container.appendChild(wrapper);
    
    console.log('✅ External hinge caps rendered with graphic color');
  }

  addMultipleHinges(container, hingeImageUrl, count, side, color, position, sizeCode, zIndex, scaleFactor = 1) {
    if (!hingeImageUrl || count < 1) return;
    
    // Calculate vertical positions for even distribution
    // Hinges are distributed evenly along the door height
    // Using percentages: for 2 hinges at 25% and 75%, for 3 at 20%, 50%, 80%, etc.
    
    const positions = [];
    if (count === 1) {
      positions.push(50); // Single hinge at center
    } else {
      // Get spacing from manifest or use default
      // Get current model's manifest for spacing lookup
      const currentModel = this.getCurrentModel();
      const manifest = currentModel ? this.modelImageManifests[currentModel.id] : null;
      const margin = manifest?.hinges?.spacing?.[sizeCode] ?? 15; // Percentage margin from top/bottom (default 15%)
      
      const availableSpace = 100 - (2 * margin);
      const spacing = availableSpace / (count - 1);
      
      for (let i = 0; i < count; i++) {
        positions.push(margin + (spacing * i));
      }
    }
    
    // Get horizontal position from manifest or use default
    const leftOffset = position?.left || '7%';
    const rightOffset = position?.right || '7%';
    
    // Determine if we should apply color masking (skip for crane hinges which use pre-colored images)
    const applyColorMasking = color !== null && color !== undefined;
    
    // Render each hinge at its position
    positions.forEach((topPercent, index) => {
      const wrapper = document.createElement('div');
      wrapper.style.position = 'absolute';
      wrapper.style.inset = '0';
      wrapper.style.width = '100%';
      wrapper.style.height = '100%';
      wrapper.style.zIndex = zIndex;
      wrapper.style.pointerEvents = 'none';
      wrapper.style.isolation = 'isolate';
      
      // Create hinge container to hold both base image and color overlay
      const hingeContainer = document.createElement('div');
      hingeContainer.className = `safe-layer-hinge-${index + 1}`;
      hingeContainer.style.position = 'absolute';
      hingeContainer.style.top = `${topPercent}%`;
      hingeContainer.style.transform = 'translateY(-50%)'; // Center on position
      
      // Apply scaling to hinge size (base size scaled by factor)
      // Increased base size to make hinges more prominent, especially on smaller safes
      const baseWidth = 35; // Base width percentage (increased from 25)
      const baseHeight = 16; // Base height percentage (increased from 12)
      hingeContainer.style.width = `${baseWidth * scaleFactor}%`;
      hingeContainer.style.height = `${baseHeight * scaleFactor}%`;
      
      // Position at door border edge using manifest-defined positions
      if (side === 'LEFT') {
        hingeContainer.style.left = leftOffset;
      } else if (side === 'RIGHT') {
        hingeContainer.style.right = rightOffset;
      }
      
      // Layer 1: Base hinge image (visible)
      const baseImage = document.createElement('div');
      baseImage.style.position = 'absolute';
      baseImage.style.inset = '0';
      baseImage.style.width = '100%';
      baseImage.style.height = '100%';
      baseImage.style.backgroundImage = `url('${hingeImageUrl}')`;
      baseImage.style.backgroundSize = 'contain';
      baseImage.style.backgroundPosition = 'center';
      baseImage.style.backgroundRepeat = 'no-repeat';
      
      hingeContainer.appendChild(baseImage);
      
      // Layer 2: Color overlay with mask (only for non-crane hinges)
      if (applyColorMasking) {
        const colorOverlay = document.createElement('div');
        colorOverlay.style.position = 'absolute';
        colorOverlay.style.inset = '0';
        colorOverlay.style.width = '100%';
        colorOverlay.style.height = '100%';
        colorOverlay.style.webkitMaskImage = `url('${hingeImageUrl}')`;
        colorOverlay.style.maskImage = `url('${hingeImageUrl}')`;
        colorOverlay.style.webkitMaskSize = 'contain';
        colorOverlay.style.maskSize = 'contain';
        colorOverlay.style.webkitMaskPosition = 'center';
        colorOverlay.style.maskPosition = 'center';
        colorOverlay.style.webkitMaskRepeat = 'no-repeat';
        colorOverlay.style.maskRepeat = 'no-repeat';
        colorOverlay.style.backgroundColor = color;
        colorOverlay.style.mixBlendMode = 'multiply'; // Blend with base image
        colorOverlay.style.opacity = '0.7'; // Semi-transparent to show hinge details through color
        
        hingeContainer.appendChild(colorOverlay);
      }
      
      wrapper.appendChild(hingeContainer);
      container.appendChild(wrapper);
    });
    
    const colorInfo = applyColorMasking ? `with color ${color}` : 'pre-colored';
    console.log(`✅ Rendered ${count} hinges on ${side} side ${colorInfo} at positions:`, positions, `offset: ${side === 'LEFT' ? leftOffset : rightOffset}`, `scale: ${scaleFactor}`);
  }

  createSteps() {
    // Icon mapping for different group types
    const iconMap = {
      'exterior_finish': 'palette',
      'color_single': 'droplet',
      'body_color': 'droplet',
      'door_color': 'droplet',
      'lock_type': 'lock',
      'lockgraphic_color': 'palette',
      'graphic': 'image',
      'hinge_type': 'settings',
      'hinge_swing': 'arrow-left-right',
      'ls_hinge_side': 'arrow-left-right',
      'interior_layout': 'layout-grid',
      'interior_fabric': 'layers',
      'cabinet': 'box',
      'securityfire_upgrade': 'shield',
      'bundle': 'package',
      'accessory': 'plus'
    };

    // Brand to size group mapping
    const brandSizeGroupMap = {
      'Fort Knox': 'size',
      'American Security': 'amsec_size',
      'Liberty Safe': 'ls_size'
      // Add more brands here as needed: 'Brand Name': 'size_group_handle'
    };

    // Get available option group IDs from the current model
    let availableGroupIds = [];
    let currentModel = null;
    if (this.state.currentConfiguration) {
      const currentModelIds = (this.state.currentConfiguration.modelIds || []).map(id => this.normalizeId(id));
      currentModel = this.models.find(model => currentModelIds.includes(this.normalizeId(model.id)));
      if (currentModel) {
        availableGroupIds = currentModel.availableOptionGroupIds || [];
      }
    }

    // Some models rely on hinge-side image switching but may omit the hinge-side
    // group from availableOptionGroupIds. If the manifest declares
    // baseImagesByHingeSide, keep only the model's hinge-side group in the flow.
    const currentManifest = currentModel ? this.modelImageManifests[currentModel.id] : null;
    const forcedGroupHandles = new Set();
    if (currentManifest?.baseImagesByHingeSide) {
      const hingeSideHandle = currentManifest?.optionGroups?.hingeSide || 'ls_hinge_side';
      if (this.data.optionGroups.some(g => g.handle === hingeSideHandle)) {
        forcedGroupHandles.add(hingeSideHandle);
      }
    }

    // Filter out size group and color groups that are dependency-based
    // Color groups will be handled dynamically based on exterior finish selection
    // Also filter out bundle and accessory as we'll combine them
    // Also filter out cabinet_inserts as it's shown inline only
    // Also filter by available groups for the current model

    // Get all size group handles to exclude from modal steps
    // Include handles declared in loaded manifests (manifest override support)
    const sizeGroupHandles = new Set(Object.values(brandSizeGroupMap));
    Object.values(this.modelImageManifests).forEach(m => {
      if (m?.optionGroups?.size) sizeGroupHandles.add(m.optionGroups.size);
    });

    const nonSizeGroups = this.data.optionGroups
      .filter(g => !sizeGroupHandles.has(g.handle) &&  // Exclude all size groups
                   g.handle !== 'color_single' &&
                   g.handle !== 'body_color' &&
                   g.handle !== 'door_color' &&
                   g.handle !== 'bundle' &&
                   g.handle !== 'accessory' &&
                   g.handle !== 'cabinet_inserts' &&
                   // Exclude watch_winders from modal steps - it will be shown inline when cabinet_size is selected
                   g.handle !== 'watch_winders' &&
                   (availableGroupIds.length === 0 || availableGroupIds.includes(g.systemId) || forcedGroupHandles.has(g.handle)))
      .sort((a, b) => a.sortOrder - b.sortOrder);

    this.steps = nonSizeGroups.map((group, index) => ({
      id: index + 1,
      key: group.handle,
      title: group.displayName,
      description: group.description || `Choose your ${group.displayName.toLowerCase()}`,
      groups: [group.handle],
      icon: iconMap[group.handle] || 'check',
      isOptional: group.isOptional || false
    }));
    
    // Add combined bundle and accessories step if they exist and are available
    const bundleGroup = this.data.optionGroups.find(g => g.handle === 'bundle' && (availableGroupIds.length === 0 || availableGroupIds.includes(g.systemId)));
    const accessoryGroup = this.data.optionGroups.find(g => g.handle === 'accessory' && (availableGroupIds.length === 0 || availableGroupIds.includes(g.systemId)));
    
    if (bundleGroup || accessoryGroup) {
      const combinedGroups = [];
      if (bundleGroup) combinedGroups.push('bundle');
      if (accessoryGroup) combinedGroups.push('accessory');
      
      // Step is optional if both groups are optional (or if only one exists and it's optional)
      const isOptional = (bundleGroup ? bundleGroup.isOptional : true) && (accessoryGroup ? accessoryGroup.isOptional : true);
      
      this.steps.push({
        id: this.steps.length + 1,
        key: 'bundle_accessories',
        title: 'Bundles & Accessories',
        description: 'Choose bundles and add-on accessories',
        groups: combinedGroups,
        icon: 'package',
        isMultiGroup: true,
        isOptional: isOptional
      });
    }
  }

  normalizeId(value) {
    return value === null || value === undefined ? '' : String(value).trim();
  }

  normalizeCode(value) {
    return value === null || value === undefined ? '' : String(value).trim().toUpperCase();
  }

  renderSizeSelection() {
    const sizeGrid = document.getElementById('size-grid');
    if (!sizeGrid) return;

    // Find models that match the current product
    const currentProductId = this.normalizeId(this.data.productId);
    const matchingModels = this.models.filter(model => this.normalizeId(model.productId) === currentProductId);

    console.log('🔍 renderSizeSelection — productId:', currentProductId);
    console.log('🔍 All models:', this.models.map(m => ({ id: m.id, name: m.name, brand: m.brand, productId: m.productId })));
    console.log('🔍 Matching models:', matchingModels.map(m => ({ id: m.id, name: m.name, brand: m.brand })));
    console.log('🔍 All modelSizeConfigurations:', this.modelSizeConfigurations.map(c => ({ name: c.name, sizeCode: c.sizeCode, modelIds: c.modelIds })));
    
    // Check if we have any model size configurations for this product
    const hasConfigurations = this.modelSizeConfigurations.some(config => 
      config.modelIds.some(modelId => matchingModels.some(model => this.normalizeId(model.id) === this.normalizeId(modelId)))
    );

    console.log('🔍 hasConfigurations:', hasConfigurations, '| variants count:', this.data.productVariants?.length);
    
    // If no configurations found, check if product has variants (simple variant mode)
    if (!hasConfigurations && this.data.productVariants && this.data.productVariants.length > 0) {
      console.warn('⚠️ No configurations matched — falling back to variant mode. Check that the model metaobject has this product linked and the model size configuration references the correct model.');
      this.state.isVariantMode = true;
      this.renderVariantSizes();
      return;
    }
    
    // Normal configurator mode with model size configurations
    this.state.isVariantMode = false;

    // Find size group - handle different brands having different size group handles
    const brandSizeGroupMap = {
      'Fort Knox': 'size',
      'American Security': 'amsec_size',
      'Liberty Safe': 'ls_size'
      // Add more brands here as needed: 'Brand Name': 'size_group_handle'
    };
    
    let sizeGroupHandle = 'size'; // default fallback
    if (matchingModels.length > 0) {
      const firstModel = matchingModels[0];
      const brand = firstModel.brand;
      // Manifest override takes priority over the brand map
      const manifestEntry = this.modelImageManifests[firstModel.id];
      if (manifestEntry?.optionGroups?.size) {
        sizeGroupHandle = manifestEntry.optionGroups.size;
      } else {
        sizeGroupHandle = brandSizeGroupMap[brand] || 'size';
      }
    }
    
    // Store the size group handle for use in selectSize()
    this.currentSizeGroupHandle = sizeGroupHandle;
    
    let sizeGroup = this.data.optionGroups.find(g => g.handle === sizeGroupHandle);

    // Last-resort fallback: find whichever option group contains a value whose
    // code appears as a sizeCode in one of this product's modelSizeConfigurations
    if (!sizeGroup) {
      const matchingModelIds = matchingModels.map(m => this.normalizeId(m.id));
      const expectedSizeCodes = new Set(
        this.modelSizeConfigurations
          .filter(c => c.modelIds.some(id => matchingModelIds.includes(this.normalizeId(id))))
          .map(c => this.normalizeCode(c.sizeCode))
          .filter(Boolean)
      );
      sizeGroup = this.data.optionGroups.find(g =>
        g.values?.some(v => expectedSizeCodes.has(this.normalizeCode(v.code)))
      );
      if (sizeGroup) {
        sizeGroupHandle = sizeGroup.handle;
        this.currentSizeGroupHandle = sizeGroupHandle;
        console.log('ℹ️ Size group resolved via fallback scan:', sizeGroupHandle);
      }
    }

    if (!sizeGroup) {
      console.warn('⚠️ No size group found for handle:', sizeGroupHandle);
      return;
    }

    sizeGrid.innerHTML = '';

    const matchingModelIds = matchingModels.map(model => this.normalizeId(model.id));

    // Filter sizes to only show ones that have a Model Size Configuration for this product's models
    const availableSizes = sizeGroup.values.filter(value => {
      // Check if there's a Model Size Configuration for this size that matches one of our models
      const hasConfiguration = this.modelSizeConfigurations.some(config => {
        const sizeMatches = this.normalizeCode(config.sizeCode) === this.normalizeCode(value.code);
        // Check if this configuration's models include any of our matching models
        const modelMatches = config.modelIds.some(modelId => matchingModelIds.includes(this.normalizeId(modelId)));
        return sizeMatches && modelMatches;
      });
      
      return hasConfiguration;
    });

    console.log('🔍 sizeGroupHandle:', sizeGroupHandle, '| sizeGroup values:', sizeGroup.values.map(v => v.code));
    console.log('🔍 matchingModelIds:', matchingModelIds);
    console.log('🔍 modelSizeConfigs for this product:', this.modelSizeConfigurations.filter(c => c.modelIds.some(id => matchingModelIds.includes(this.normalizeId(id)))).map(c => ({ name: c.name, sizeCode: c.sizeCode, normalizedSizeCode: this.normalizeCode(c.sizeCode) })));
    console.log('🔍 availableSizes count:', availableSizes.length, '| codes:', availableSizes.map(v => v.code));

    // Deduplicate sizes by code - keep only the first occurrence of each unique size code
    const uniqueSizes = [];
    const seenCodes = new Set();
    
    availableSizes.forEach(value => {
      if (!seenCodes.has(value.code)) {
        seenCodes.add(value.code);
        uniqueSizes.push(value);
      }
    });

    uniqueSizes.forEach(value => {
      // Find the Model Size Configuration for this size that matches our product's models
      const configuration = this.modelSizeConfigurations.find(config => 
        this.normalizeCode(config.sizeCode) === this.normalizeCode(value.code) && 
        config.modelIds.some(modelId => matchingModelIds.includes(this.normalizeId(modelId)))
      );
      
      // basePrice is in cents, convert to dollars for display
      const basePrice = configuration ? (configuration.basePrice / 100) : 0;
      
      const hasSizeImage = value.sizeImage && value.sizeImage.trim() !== '';
      const hasTooltip = value.tooltip && value.tooltip.trim() !== '';
      
      const card = document.createElement('button');
      card.className = `size-card ${hasSizeImage ? 'has-image' : ''}`;
      card.dataset.size = value.code;
      
      if (hasSizeImage) {
        card.innerHTML = `
          ${hasTooltip ? `
            <div class="tooltip-icon" data-tooltip='${value.tooltip}' tabindex="0" onclick="event.stopPropagation();">
              <i data-lucide="info"></i>
            </div>
          ` : ''}
          <div class="size-image-container">
            <img src="${value.sizeImage}" alt="${value.label}" class="size-image" />
            <div class="expand-icon" data-image="${value.sizeImage}" data-label="${value.label}">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
                <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
            <div class="size-tooltip">
              <div class="size-tooltip-text">${value.label}</div>
              ${basePrice > 0 ? 
                `<div class="size-tooltip-price">$${this.formatPrice(basePrice)}</div>` : ''}
            </div>
          </div>
        `;
      } else {
        card.innerHTML = `
          ${hasTooltip ? `
            <div class="tooltip-icon" data-tooltip="${value.tooltip}" tabindex="0" onclick="event.stopPropagation();">
              <i data-lucide="info"></i>
            </div>
          ` : ''}
          <div class="size-icon">
            <i data-lucide="box" class="icon-lg"></i>
          </div>
          <h3>${value.label}</h3>
          ${basePrice > 0 ? 
            `<p>$${this.formatPrice(basePrice)}</p>` : ''}
        `;
      }
      
      sizeGrid.appendChild(card);
    });

    // Re-initialize icons
    if (typeof lucide !== 'undefined') {
      lucide.createIcons();
    }
    
    // Highlight saved size if any
    this.highlightSavedSize();
  }
  
  renderVariantSizes() {
    const sizeGrid = document.getElementById('size-grid');
    const simpleAddToCart = document.getElementById('simpleAddToCart');
    
    if (!sizeGrid) return;
    
    sizeGrid.innerHTML = '';
    
    // Show simple add to cart button
    if (simpleAddToCart) {
      simpleAddToCart.style.display = 'block';
    }
    
    // Hide the quiz modal trigger since we don't need it
    // The configurator flow will be skipped
    
    this.data.productVariants.forEach(variant => {
      const card = document.createElement('div');
      card.className = 'size-card';
      if (variant.image) {
        card.classList.add('has-image');
      }
      card.dataset.variantId = variant.id;
      
      let cardContent = '';
      
      // Add image if available
      if (variant.image) {
        cardContent += `
          <div class="size-image-container">
            <img src="${variant.image}" alt="${variant.title}" class="size-image" loading="lazy" />
          </div>
        `;
      } else {
        // Icon fallback
        cardContent += `
          <div class="size-icon">
            <i data-lucide="box"></i>
          </div>
        `;
      }
      
      cardContent += `
        <h3>${variant.title}</h3>
        <p class="size-label">$${this.formatPrice(variant.price)}</p>
      `;
      
      if (!variant.available) {
        cardContent += '<p style="color: #dc2626; font-size: 14px; margin-top: 8px;">Out of Stock</p>';
        card.style.opacity = '0.6';
        card.style.pointerEvents = 'none';
      }
      
      card.innerHTML = cardContent;
      sizeGrid.appendChild(card);
    });
    
    // Re-initialize Lucide icons
    if (typeof lucide !== 'undefined') {
      lucide.createIcons();
    }
  }
  
  highlightSavedSize() {
    try {
      const stored = localStorage.getItem('configuratorSelections');
      if (stored) {
        const data = JSON.parse(stored);
        const savedSize = data.selections?.size?.[0];
        if (savedSize) {
          const sizeCard = document.querySelector(`[data-size="${savedSize}"]`);
          if (sizeCard) {
            sizeCard.classList.add('selected');
          }
        }
      }
    } catch (error) {
      // Silently fail if no saved data
    }
  }

  createStepIndicator() {
    const container = document.getElementById('stepIndicatorContainer');
    if (!container) return;
    
    container.innerHTML = '';
    
    // Always show all steps, even if some are dynamically added later
    const totalSteps = this.steps.length;
    
    for (let i = 1; i <= totalSteps; i++) {
      const circle = document.createElement('div');
      circle.className = 'step-circle';
      circle.dataset.step = i;
      circle.textContent = i;
      
      // Add click handler
      circle.addEventListener('click', (e) => {
        const step = this.steps[i - 1];
        
        // Allow clicking on:
        // 1. Current step (no-op but shouldn't error)
        // 2. Completed steps (steps with selections)
        // 3. Steps that have been visited (stepNumber <= currentStep)
        // Prevent clicking on future disabled steps
        if (i > this.currentStep && (!step || !this.isStepComplete(step))) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        
        // Allow navigation to current or previous steps
        this.goToStep(i);
      });
      
      // Prevent double-click issues
      circle.addEventListener('dblclick', (e) => {
        e.preventDefault();
        e.stopPropagation();
      });
      
      container.appendChild(circle);
    }
  }

  createImageModal() {
    // Create modal overlay
    const overlay = document.createElement('div');
    overlay.className = 'size-image-modal-overlay';
    overlay.innerHTML = `
      <div class="size-image-zoom">
        <img src="" alt="" />
        <div class="zoom-label"></div>
          </div>
    `;
    document.body.appendChild(overlay);
    
    this.modal = overlay;
    
    // Close modal when clicking overlay
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        this.closeImageModal();
      }
    });
    
    // Close modal on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('visible')) {
        this.closeImageModal();
      }
    });
  }

  openImageModal(imageSrc, label) {
    const modal = this.modal;
    const img = modal.querySelector('.size-image-zoom img');
    const labelEl = modal.querySelector('.zoom-label');
    
    img.src = imageSrc;
    img.alt = label;
    labelEl.textContent = label;
    
    modal.classList.add('visible');
    document.body.style.overflow = 'hidden';
  }

  closeImageModal() {
    this.modal.classList.remove('visible');
    document.body.style.overflow = '';
  }

  attachListeners() {
    // Size card click handlers
    this.container.addEventListener('click', (e) => {
      const sizeCard = e.target.closest('.size-card');
      if (sizeCard) {
        // Check if variant mode
        const variantId = sizeCard.dataset.variantId;
        if (variantId) {
          // Variant mode - just select the variant, don't open modal
          this.selectSize(variantId);
          return;
        }
        
        const size = sizeCard.dataset.size;
        
        // Check if we have saved progress and if it's a different size
        const hasProgress = localStorage.getItem('configuratorSelections');
        if (hasProgress) {
          try {
            const saved = JSON.parse(hasProgress);
            const savedSize = saved.selections?.size?.[0];
            
            // If clicking on the same size, just reopen the modal
            if (savedSize === size) {
              this.openQuizModal();
              return;
            }
          } catch (error) {
            // If error parsing, just proceed
          }
        }
        
        // Select size (may internally open modal if user had progress)
        this.selectSize(size);
        // Only open quiz modal if selectSize didn't already handle it
        if (!document.getElementById('quizModal').classList.contains('active') &&
            !document.getElementById('summaryModal').classList.contains('active')) {
          this.openQuizModal();
        }
        return;
      }

      const expandIcon = e.target.closest('.expand-icon');
      if (expandIcon) {
        e.preventDefault();
        e.stopPropagation();
        const imageSrc = expandIcon.dataset.image;
        const label = expandIcon.dataset.label;
        this.openImageModal(imageSrc, label);
        return;
      }
    });

    // Quiz modal event delegation
    document.addEventListener('click', (e) => {
      // Handle summary edit buttons
      const editBtn = e.target.closest('.summary-edit');
      if (editBtn) {
        const stepNumber = parseInt(editBtn.dataset.step);
        const openRivetSelector = editBtn.dataset.openRivetSelector === 'true';
        const openCraneHingeSelector = editBtn.dataset.openCraneHingeSelector === 'true';
        if (stepNumber) {
          this.goToStep(stepNumber, openRivetSelector, openCraneHingeSelector);
        }
        return;
      }
      
      const optionCard = e.target.closest('.option-card');
      if (optionCard && document.getElementById('quizModal').classList.contains('active')) {
        // Don't allow clicking included options
        if (optionCard.classList.contains('option-included')) {
          return;
        }
        
        const step = optionCard.closest('.quiz-step');
        const stepNumber = parseInt(step.dataset.step);
        const value = optionCard.dataset.value;
        this.selectOption(stepNumber, value, optionCard);
        return;
      }
    });

    // Navigation buttons
    const nextBtn = document.getElementById('nextBtn');
    const backBtn = document.getElementById('backBtn');
    const editBtn = document.getElementById('editBtn');
    const confirmBtn = document.getElementById('confirmBtn');
    const saveConfigBtn = document.getElementById('saveConfigBtn');
    const returnToSummaryBtn = document.getElementById('returnToSummaryBtn');
    
    // Close buttons for modals
    const quizModalClose = document.getElementById('quizModalClose');
    const summaryModalClose = document.getElementById('summaryModalClose');

    if (nextBtn) {
      nextBtn.addEventListener('click', () => this.nextStep());
    }
    if (backBtn) {
      backBtn.addEventListener('click', () => this.previousStep());
    }
    if (editBtn) {
      editBtn.addEventListener('click', () => this.reopenQuiz());
    }
    if (saveConfigBtn) {
      saveConfigBtn.addEventListener('click', () => this.saveConfigurationPDF());
    }
    if (confirmBtn) {
      confirmBtn.addEventListener('click', () => this.confirmConfiguration());
    }
    if (returnToSummaryBtn) {
      returnToSummaryBtn.addEventListener('click', () => {
        this.isEditingFromSummary = false;
        this.saveSelectionsToStorage();
        document.getElementById('quizModal').classList.remove('active');
        setTimeout(() => {
          this.showSummary();
        }, 300);
      });
    }
    
    // Simple add to cart button for variant-only products
    const simpleAddToCartBtn = document.getElementById('simpleAddToCartBtn');
    if (simpleAddToCartBtn) {
      simpleAddToCartBtn.addEventListener('click', () => {
        this.addVariantToCart();
      });
    }
    
    // Close button handlers
    if (quizModalClose) {
      quizModalClose.addEventListener('click', () => {
        this.closeAllModals();
      });
    }
    if (summaryModalClose) {
      summaryModalClose.addEventListener('click', () => {
        this.closeAllModals();
      });
    }

    // Close modal on outside click
    document.querySelectorAll('.modal').forEach(modal => {
      modal.addEventListener('click', (e) => {
        if (e.target === modal) {
          this.closeAllModals();
        }
      });
    });

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      const quizModal = document.getElementById('quizModal');
      const summaryModal = document.getElementById('summaryModal');
      
      if (quizModal.classList.contains('active')) {
        if (e.key === 'Escape') {
          this.closeAllModals();
        } else if (e.key === 'ArrowRight' || e.key === 'Enter') {
          const nextBtn = document.getElementById('nextBtn');
          if (!nextBtn.disabled) {
            this.nextStep();
          }
        } else if (e.key === 'ArrowLeft') {
          if (this.currentStep > 1) {
            this.previousStep();
          }
        }
      } else if (summaryModal.classList.contains('active') && e.key === 'Escape') {
        this.closeAllModals();
      }
    });
  }



  selectSize(size) {
    // Handle variant mode differently
    if (this.state.isVariantMode) {
      this.selectVariant(size);
      return;
    }
    
    // Check if user had completed all selections before changing size
    const wasComplete = this.steps.every(step => this.isStepComplete(step));
    
    // Remember current step
    const previousStep = this.currentStep;
    
    // Use the current size group handle (set in renderSizeSelection)
    const sizeGroupHandle = this.currentSizeGroupHandle || 'size';
    this.state.selections[sizeGroupHandle] = [size];
    
    // Find the Model Size Configuration for this size
    // Filter by models that match the current product
    const currentProductId = this.normalizeId(this.data.productId);
    const matchingModels = this.models.filter(model => this.normalizeId(model.productId) === currentProductId);
    const matchingModelIds = matchingModels.map(model => this.normalizeId(model.id));
    
    let configuration = null;
    
    if (this.state.selectedModel) {
      // Find configuration matching both model and size
      configuration = this.modelSizeConfigurations.find(config => 
        config.modelIds.some(modelId => this.normalizeId(modelId) === this.normalizeId(this.state.selectedModel.id)) && 
        this.normalizeCode(config.sizeCode) === this.normalizeCode(size) &&
        config.modelIds.some(modelId => matchingModelIds.includes(this.normalizeId(modelId)))
      );
    } else {
      // Find configuration with this size that matches one of our product's models
      configuration = this.modelSizeConfigurations.find(config => 
        this.normalizeCode(config.sizeCode) === this.normalizeCode(size) &&
        config.modelIds.some(modelId => matchingModelIds.includes(this.normalizeId(modelId)))
      );
      
      // If found, auto-select the first matching model
      if (configuration && configuration.modelIds.length > 0) {
        const firstMatchingModelId = configuration.modelIds.find(modelId => matchingModelIds.includes(this.normalizeId(modelId)));
        if (firstMatchingModelId) {
          this.state.selectedModel = this.models.find(m => this.normalizeId(m.id) === this.normalizeId(firstMatchingModelId));
        }
      }
    }
    
    // Store the configuration
    this.state.currentConfiguration = configuration;

    // Clear manifest-defined size-restricted options that aren't valid for the new size
    const sizeRestrictedCleared = this.clearSizeRestrictedOptions(size);
    for (const clearedHandle of sizeRestrictedCleared) {
      this.autoSelectDefaultForGroup(clearedHandle);
    }

    // Clear any selections whose requires_any conditions are no longer satisfied
    this.clearRequiresAnyViolations();

    // Don't reset selections - keep user's previous choices when changing sizes
    // this.resetSelections();
    
    // Don't reset visited steps - keep progress
    // this.visitedSteps = new Set([1]);
    
    // Update visual feedback
    document.querySelectorAll('.size-card').forEach(card => {
      card.classList.remove('selected');
    });
    document.querySelector(`[data-size="${size}"]`).classList.add('selected');
    
    // Calculate price for the selected size
    this.calculatePrice();
    
    // Don't render safe image in main preview - it shows product featured image
    // Visual rendering only happens in summary
    
    // Update config badge
    this.updateConfigBadge();
    
    // Notify the app block about configuration changes
    this.notifyConfigurationUpdate();
    
    // Update steps based on model's available option groups
    this.createSteps();
    this.createStepIndicator();

    // Size-only products should jump straight to summary as soon as the size is chosen.
    if (this.steps.length === 0) {
      this.showSummary();
      return;
    }
    
    // Preset application commented out - uncomment to re-enable
    // this.applyPresetForSize(size);
    
    // Check if summary modal is currently open
    const summaryModal = document.getElementById('summaryModal');
    const isSummaryOpen = summaryModal && summaryModal.classList.contains('active');
    
    // Check if quiz modal is currently open
    const quizModal = document.getElementById('quizModal');
    const isQuizOpen = quizModal && quizModal.classList.contains('active');
    
    if (isSummaryOpen) {
      // User was on summary page - take them back to summary
      this.showSummary();
    } else if (isQuizOpen || wasComplete) {
      // User was in quiz modal OR had completed all steps - restore to their previous step
      const validStep = Math.min(previousStep, this.steps.length);
      this.currentStep = validStep;

      // If user wasn't in quiz modal but had completed all steps, go to summary
      if (!isQuizOpen && wasComplete) {
        this.savedStep = this.steps.length + 1; // sentinel for summary
        this.openQuizModal(); // will detect sentinel and show summary
        return;
      }
      
      // Re-render quiz steps with updated configuration
      this.renderQuizSteps();
      
      // Show the correct step (updateStepUI calls renderQuizSteps again, so we'll manually update)
      document.querySelectorAll('.quiz-step').forEach(step => {
        step.classList.remove('active');
      });
      const currentStepEl = document.querySelector(`.quiz-step[data-step="${this.currentStep}"]`);
      if (currentStepEl) {
        currentStepEl.classList.add('active');
      }
      
      // Restore selections for the current step
      this.restoreCurrentStepSelections();
      
      // Update step indicator and buttons
      this.updateStepIndicator();
      
      // Update back button visibility
      document.getElementById('backBtn').style.display = this.currentStep > 1 ? 'flex' : 'none';
      
      // Update return to summary button visibility
      const returnToSummaryBtn = document.getElementById('returnToSummaryBtn');
      if (returnToSummaryBtn) {
        returnToSummaryBtn.style.display = this.isEditingFromSummary ? 'flex' : 'none';
      }
      
      // Update next button text and state
      const nextBtn = document.getElementById('nextBtn');
      if (this.currentStep === this.steps.length) {
        nextBtn.innerHTML = `
          Complete
          <i data-lucide="check" class="icon-sm"></i>
        `;
      } else {
        nextBtn.innerHTML = `
          Next
          <i data-lucide="arrow-right" class="icon-sm"></i>
        `;
      }
      
      // Check if current step is complete
      const currentStep = this.steps[this.currentStep - 1];
      nextBtn.disabled = !(currentStep && this.isStepComplete(currentStep));
      
      // Update step title
      const totalStepsEl = document.getElementById('totalSteps');
      if (totalStepsEl) {
        totalStepsEl.textContent = this.steps.length;
      }
      document.getElementById('stepTitle').innerHTML = `Step ${this.currentStep} of <span id="totalSteps">${this.steps.length}</span>`;
      
      // Update progress bar
      this.updateProgress();
      
      // Re-initialize icons
      if (typeof lucide !== 'undefined') {
        lucide.createIcons();
      }
    }
  }
  
  // PRESET FUNCTIONALITY - COMMENTED OUT FOR NOW
  // Uncomment this entire method to re-enable preset selections when changing sizes
  /*
  applyPresetForSize(sizeCode) {
    console.log('🎯 Starting preset application for size:', sizeCode);
    
    // Define preset selections in order
    const presetSteps = [
      { group: 'exterior_finish', value: 'GLOSS', label: 'Gloss Finish' },
      { group: 'color_single', value: 'FOREST_GREEN', label: 'Forest Green' },
      { group: 'lock_type', value: 'DIAL', label: 'Dial Lock' },
      { group: 'lockgraphic_color', value: 'GOLD', label: 'Gold' },
      { group: 'graphic', value: 'TRADITIONAL', label: 'Traditional' },
      { group: 'hinge_type', value: 'EXTERNAL', label: 'Exterior Hinges' },
      { group: 'hinge_swing', value: 'LEFT', label: 'Left Swing' },
      { group: 'hinge_color', value: 'GOLD', label: 'Gold' }
    ];
    
    // Apply each preset selection
    for (const { group: groupHandle, value: desiredValue, label } of presetSteps) {
      const group = this.data.optionGroups.find(g => g.handle === groupHandle);
      
      if (!group) {
        console.warn(`⚠️ Group not found: ${groupHandle}`);
        continue;
      }
      
      if (!group.values || group.values.length === 0) {
        console.warn(`⚠️ No values in group: ${groupHandle}`);
        continue;
      }
      
      // Find the desired value
      const optionValue = group.values.find(v => v.code === desiredValue);
      
      if (!optionValue) {
        console.warn(`⚠️ Value '${desiredValue}' not found in group '${groupHandle}'. Available:`, group.values.map(v => `${v.code} (${v.label})`));
        continue;
      }
      
      // Apply the selection
      this.state.selections[groupHandle] = [desiredValue];
      console.log(`✓ Applied: ${groupHandle} = ${desiredValue} (${label})`);
      
      // Handle exterior finish dependencies immediately
      if (groupHandle === 'exterior_finish') {
        this.handleExteriorFinishDependencies(desiredValue);
        this.createSteps();
        this.createStepIndicator();
      }
    }
    
    console.log('📊 Final selections:', this.state.selections);
    
    // Save preset selections
    this.saveSelectionsToStorage();
    
    // Recalculate price
    this.calculatePrice();
    
    // Render safe image with preset
    this.renderSafeImage();
    
    console.log('✅ Preset application complete');
  }
  */
  
  selectVariant(variantId) {
    // Store selected variant
    this.state.selectedVariantId = variantId;
    
    // Update visual feedback
    document.querySelectorAll('.size-card').forEach(card => {
      card.classList.remove('selected');
    });
    document.querySelector(`[data-variant-id="${variantId}"]`).classList.add('selected');
    
    // Enable add to cart button
    const addToCartBtn = document.getElementById('simpleAddToCartBtn');
    if (addToCartBtn) {
      addToCartBtn.disabled = false;
    }
    
    // Update price display
    const variant = this.data.productVariants.find(v => v.id === variantId);
    if (variant) {
      this.state.totalPrice = variant.price;
      this.state.basePrice = variant.price;
    }
  }
  
  async addVariantToCart() {
    if (!this.state.selectedVariantId) {
      alert('Please select a size first');
      return;
    }
    
    const addToCartBtn = document.getElementById('simpleAddToCartBtn');
    if (addToCartBtn) {
      addToCartBtn.disabled = true;
      addToCartBtn.innerHTML = '<i data-lucide="loader" class="icon-sm"></i> Adding...';
      if (typeof lucide !== 'undefined') {
        lucide.createIcons();
      }
    }
    
    try {
      const item = {
        id: this.state.selectedVariantId,
        quantity: 1
      };

      // If backend returned properties for this configured variant (from saved quote), include them
      if (this.state.selectedVariantProperties && Object.keys(this.state.selectedVariantProperties).length > 0) {
        item.properties = this.state.selectedVariantProperties;
      }

      const formData = { items: [ item ] };
      
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }
      
      // Success - redirect to cart or show success message
      window.location.href = '/cart';
      
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('There was an error adding the item to your cart. Please try again.');
      
      // Re-enable button
      if (addToCartBtn) {
        addToCartBtn.disabled = false;
        addToCartBtn.innerHTML = '<i data-lucide="shopping-cart" class="icon-sm"></i> Add to Cart';
        if (typeof lucide !== 'undefined') {
          lucide.createIcons();
        }
      }
    }
  }

  resetSelections() {
    // Clear all selections except size (preserve all possible size group handles)
    const sizeHandles = ['size', 'amsec_size', 'ls_size']; // Add more as needed
    Object.keys(this.state.selections).forEach(key => {
      if (!sizeHandles.includes(key)) {
        delete this.state.selections[key];
      }
    });
    
    // Clear local storage
    localStorage.removeItem('configuratorSelections');
    
    // Reset current step
    this.currentStep = 1;
  }



  saveSelectionsToStorage() {
    try {
      const storageData = {
        selections: this.state.selections,
        currentStep: this.currentStep,
        visitedSteps: Array.from(this.visitedSteps), // Convert Set to Array for storage
        isEditing: this.isEditingFromSummary || false,
        rivetColorOverride: this.state.rivetColorOverride,
        craneHingeColorOverride: this.state.craneHingeColorOverride
      };
      localStorage.setItem('configuratorSelections', JSON.stringify(storageData));
    } catch (error) {
      // Silently fail if localStorage is not available
    }
  }

  loadSelectionsFromStorage() {
    try {
      const stored = localStorage.getItem('configuratorSelections');
      if (stored) {
        const storageData = JSON.parse(stored);
        // Support both old format (just selections) and new format (object with selections and step)
        if (storageData.selections) {
          this.state.selections = { ...this.state.selections, ...storageData.selections };
          if (storageData.currentStep) {
            this.savedStep = storageData.currentStep;
          }
          // Restore visited steps or initialize with current step
          if (storageData.visitedSteps) {
            this.visitedSteps = new Set(storageData.visitedSteps);
          }
          if (storageData.isEditing) {
            this.isEditingFromSummary = storageData.isEditing;
          }
          // Restore rivet color override
          if (storageData.rivetColorOverride) {
            this.state.rivetColorOverride = storageData.rivetColorOverride;
          }
          // Restore crane hinge color override
          if (storageData.craneHingeColorOverride) {
            this.state.craneHingeColorOverride = storageData.craneHingeColorOverride;
          }
        } else {
          // Old format - just selections object
          this.state.selections = { ...this.state.selections, ...storageData };
        }
      }
    } catch (error) {
      // Silently fail if localStorage is not available
    }
  }

  selectOption(stepNumber, value, cardElement) {
    const step = this.steps[stepNumber - 1];
    if (!step) return;

    // Don't allow selection of disabled options
    if (cardElement.classList.contains('option-disabled') || cardElement.disabled) {
      return;
    }

    const groupHandle = cardElement.dataset.group;
    const optionValue = this.getValueByCode(groupHandle, value);
    
    // Handle accessories - simple toggle without quantity selector
    if (groupHandle === 'accessory') {
      this.toggleAccessorySelection(cardElement, value);
      return;
    }
    
    // Handle bundles - simple toggle with dependency handling
    if (groupHandle === 'bundle') {
      this.toggleBundleSelection(cardElement, value, optionValue);
      return;
    }
    
    // Check if this option supports multiple quantities (like carbon steel liner)
    // This must come BEFORE the securityfire_upgrade check
    // Also enable quantity selector for STAINLESS_10GA_PKG and ARMAKNOX_AR500_3_16
    const isQuantityEnabledLiner = ['STAINLESS_10GA_PKG', 'ARMAKNOX_AR500_3_16'].includes(value);
    
    if ((optionValue && optionValue.maxCount && optionValue.maxCount > 1) || isQuantityEnabledLiner) {
      // For multi-select options or quantity-enabled liners, toggle the quantity selector
      // Create a modified optionValue with maxCount if it's a liner
      const effectiveOptionValue = isQuantityEnabledLiner && (!optionValue.maxCount || optionValue.maxCount === 1)
        ? { ...optionValue, maxCount: 4 }
        : optionValue;
      this.toggleQuantitySelector(cardElement, effectiveOptionValue);
      return;
    }
    
    // Handle security/fire upgrades - allow multiple selections
    if (groupHandle === 'securityfire_upgrade') {
      this.toggleMultiSelectOption(cardElement, value, groupHandle);
      return;
    }
    
    // Handle cabinet - allow multiple selections
    if (groupHandle === 'cabinet') {
      this.toggleMultiSelectOption(cardElement, value, groupHandle);
      return;
    }
    
    // For single-select options, use the original behavior
    this.selectSingleOption(stepNumber, value, cardElement, groupHandle);
  }
  
  toggleAccessorySelection(cardElement, value) {
    const groupHandle = 'accessory';
    
    // Initialize selections object if it doesn't exist
    if (!this.state.selections[groupHandle]) {
      this.state.selections[groupHandle] = {};
    }
    
    // Toggle selection
    if (cardElement.classList.contains('selected')) {
      // Deselect
      cardElement.classList.remove('selected');
      delete this.state.selections[groupHandle][value];
      
      // Clean up empty object
      if (Object.keys(this.state.selections[groupHandle]).length === 0) {
        delete this.state.selections[groupHandle];
      }
      
      // If deselecting rivets, clear color override and hide selector
      if (value === 'RIVET_TRIM') {
        this.state.rivetColorOverride = null;
        this.hideRivetColorSelector();
      }
    } else {
      // Select
      cardElement.classList.add('selected');
      this.state.selections[groupHandle][value] = 1;
      
      // If selecting rivets, show color selector
      if (value === 'RIVET_TRIM') {
        this.showRivetColorSelector(cardElement);
      }
    }
    
    // Save to local storage
    this.saveSelectionsToStorage();
    
    // Update config badge
    this.updateConfigBadge();
    
    // Recalculate and update pricing
    this.calculatePrice();
    
    // Notify the app block about configuration changes
    this.notifyConfigurationUpdate();

    // If cabinet size changed, re-render quiz steps so dependent groups
    // (like `watch_winders`) update availability immediately
    if (groupHandle === 'cabinet_size') {
      console.log('🎯 Cabinet size selected:', value);
      console.log('🔍 Current selections:', this.state.selections);
      
      try {
        this.renderQuizSteps();
        this.updateStepUI();
      } catch (err) {
        console.warn('Failed to re-render quiz steps after cabinet_size change', err);
      }
      
      // NOTE: Watch winders no longer auto-open for all cabinet selections
      // Only cabinet options with inline_unlock_groups dependency will open watch winders
      // Regular cabinet options will not trigger the inline group
    }
  }

  toggleBundleSelection(cardElement, value, optionValue) {
    const groupHandle = 'bundle';
    
    // Initialize selections object if it doesn't exist
    if (!this.state.selections[groupHandle]) {
      this.state.selections[groupHandle] = {};
    }
    
    // Toggle selection
    if (cardElement.classList.contains('selected')) {
      // Deselect bundle
      cardElement.classList.remove('selected');
      delete this.state.selections[groupHandle][value];
      
      // Clean up empty object
      if (Object.keys(this.state.selections[groupHandle]).length === 0) {
        delete this.state.selections[groupHandle];
      }
      
      // Show accessories that were hidden by this bundle
      if (optionValue && optionValue.dependencies && optionValue.dependencies.hides_included) {
        this.showHiddenAccessories(optionValue.dependencies.includes);
      }
    } else {
      // Select bundle
      cardElement.classList.add('selected');
      this.state.selections[groupHandle][value] = 1;
      
      // Hide accessories included in the bundle
      if (optionValue && optionValue.dependencies && optionValue.dependencies.hides_included) {
        this.hideIncludedAccessories(optionValue.dependencies.includes);
      }
    }
    
    // Save to local storage
    this.saveSelectionsToStorage();
    
    // Update config badge
    this.updateConfigBadge();
    
    // Recalculate and update pricing
    this.calculatePrice();
    
    // Notify the app block about configuration changes
    this.notifyConfigurationUpdate();
  }

  hideIncludedAccessories(includedItems) {
    if (!includedItems || !Array.isArray(includedItems)) return;
    
    // Find all accessory cards across different accessory groups
    const accessoryCards = document.querySelectorAll('[data-group="accessory"], [data-group="exterior_accessories"], [data-group="additional_accessories"], [data-group*="accessories"]');
    
    accessoryCards.forEach(card => {
      const itemCode = card.dataset.value;
      if (includedItems.includes(itemCode)) {
        // Hide the card
        card.style.display = 'none';
        
        // If it was selected, deselect it
        if (card.classList.contains('selected')) {
          card.classList.remove('selected');
          if (this.state.selections['accessory'] && this.state.selections['accessory'][itemCode]) {
            delete this.state.selections['accessory'][itemCode];
          }
        }
      }
    });
  }

  showHiddenAccessories(includedItems) {
    if (!includedItems || !Array.isArray(includedItems)) return;
    
    // Find all accessory cards across different accessory groups
    const accessoryCards = document.querySelectorAll('[data-group="accessory"], [data-group="exterior_accessories"], [data-group="additional_accessories"], [data-group*="accessories"]');
    
    accessoryCards.forEach(card => {
      const itemCode = card.dataset.value;
      if (includedItems.includes(itemCode)) {
        // Show the card
        card.style.display = '';
      }
    });
  }

  toggleMultiSelectOption(cardElement, value, groupHandle) {
    // Initialize selections object if it doesn't exist (NOT array - use object for quantities)
    if (!this.state.selections[groupHandle]) {
      this.state.selections[groupHandle] = {};
    }
    
    // Toggle selection
    if (cardElement.classList.contains('selected')) {
      // Deselect
      cardElement.classList.remove('selected');
      delete this.state.selections[groupHandle][value];
      
      // Clean up empty object
      if (Object.keys(this.state.selections[groupHandle]).length === 0) {
        delete this.state.selections[groupHandle];
      }
      
      // Check if this option has inline_unlock_groups dependency and hide the inline group
      const optionValue = this.getValueByCode(groupHandle, value);
      if (optionValue && optionValue.dependencies) {
        try {
          const deps = typeof optionValue.dependencies === 'string' 
            ? JSON.parse(optionValue.dependencies) 
            : optionValue.dependencies;
          
          if (deps.inline_unlock_groups && Array.isArray(deps.inline_unlock_groups)) {
            deps.inline_unlock_groups.forEach(unlockedGroupHandle => {
              // Create namespace key for this trigger option
              const namespaceKey = `${unlockedGroupHandle}_for_${groupHandle}_${value}`;
              
              // Delete all selections in this namespaced inline group
              if (this.state.selections[namespaceKey]) {
                delete this.state.selections[namespaceKey];
              }
              
              // Reset the trigger card's label and price display
              const labelSpan = cardElement.querySelector('span:not(.option-price):not(.inline-group-badge)');
              if (labelSpan && labelSpan.dataset.originalText) {
                labelSpan.textContent = labelSpan.dataset.originalText;
                delete labelSpan.dataset.originalText;
              }
              
              const priceEl = cardElement.querySelector('.option-price');
              if (priceEl) {
                priceEl.style.display = 'none';
              }
              
              // Hide the inline group
              this.hideInlineGroup(unlockedGroupHandle, cardElement);
            });
            
            // Recalculate total price after removing inline selections
            this.calculatePrice();
            this.saveSelectionsToStorage();
            this.updateConfigBadge();
          }
        } catch (e) {
          console.warn('Failed to parse dependencies:', e);
        }
      }
    } else {
      // Select - store with quantity of 1
      cardElement.classList.add('selected');
      this.state.selections[groupHandle][value] = 1;
      
      // Check if this option has inline_unlock_groups dependency and show the inline group
      const optionValue = this.getValueByCode(groupHandle, value);
      if (optionValue && optionValue.dependencies) {
        try {
          const deps = typeof optionValue.dependencies === 'string' 
            ? JSON.parse(optionValue.dependencies) 
            : optionValue.dependencies;
          
          if (deps.inline_unlock_groups && Array.isArray(deps.inline_unlock_groups)) {
            deps.inline_unlock_groups.forEach(unlockedGroupHandle => {
              this.showInlineGroup(unlockedGroupHandle, cardElement);
            });
          }
        } catch (e) {
          console.warn('Failed to parse dependencies:', e);
        }
      }
    }
    
    // Update disabled states for all options in this group
    const currentStepEl = document.querySelector(`.quiz-step[data-step="${this.currentStep}"]`);
    if (currentStepEl) {
      const step = this.steps[this.currentStep - 1];
      const group = this.data.optionGroups.find(g => g.handle === groupHandle);
      
      if (group) {
        currentStepEl.querySelectorAll(`[data-group="${groupHandle}"]`).forEach(card => {
          const cardValue = card.dataset.value;
          const optionValue = this.getValueByCode(groupHandle, cardValue);
          
          if (optionValue) {
            const isAvailable = this.isValueAvailableForStep(step, optionValue);
            const isFilteredBySize = this.isFilteredByShowOptions(group, optionValue);
            
            // Don't modify cards that are filtered by size (they shouldn't be visible)
            if (!isFilteredBySize) {
              if (isAvailable) {
                // Enable the option
                card.classList.remove('option-disabled');
                card.disabled = false;
                card.style.cursor = '';
              } else {
                // Disable the option (but keep it visible for multi-select)
                card.classList.add('option-disabled');
                card.disabled = true;
                card.style.cursor = 'not-allowed';
              }
            }
          }
        });
      }
    }
    
    // Check if step is complete and enable/disable next button
    const step = this.steps[this.currentStep - 1];
    if (step && this.isStepComplete(step)) {
      document.getElementById('nextBtn').disabled = false;
    } else {
      document.getElementById('nextBtn').disabled = true;
    }
    
    // Save to local storage
    this.saveSelectionsToStorage();
    
    // Update config badge
    this.updateConfigBadge();
    
    // Recalculate and update pricing
    this.calculatePrice();
    
    // Notify the app block about configuration changes
    this.notifyConfigurationUpdate();
  }

  selectSingleOption(stepNumber, value, cardElement, groupHandle) {
    // Check if clicking on an already selected option - allow deselection
    const isCurrentlySelected = cardElement.classList.contains('selected');
    
    if (isCurrentlySelected) {
      // Deselect the option
      delete this.state.selections[groupHandle];
      cardElement.classList.remove('selected');
      
      // If deselecting a crane hinge, clear the auto-selected graphic restriction
      if (groupHandle === 'hinge_type') {
        const previousValue = value.toUpperCase();
        if (previousValue === 'CRANE' || previousValue === 'CRANE_BLANK') {
          // Clear the graphic selection to allow user to choose again
          delete this.state.selections['graphic'];
          console.log('✅ Cleared graphic selection after deselecting crane hinge');
          
          // Save to storage immediately
          this.saveSelectionsToStorage();
          
          // If the graphic step is currently visible, re-render it
          const graphicStep = this.steps.find(s => s.groups && s.groups.includes('graphic'));
          if (graphicStep && this.currentStep === graphicStep.id) {
            this.renderStep(graphicStep.id);
          }
        }
      }
      
      // Save to local storage
      this.saveSelectionsToStorage();
      
      // Disable next button since no selection
      document.getElementById('nextBtn').disabled = true;
      
      // Update config badge
      this.updateConfigBadge();
      
      // Recalculate and update pricing
      this.calculatePrice();
      
      // Notify the app block about configuration changes
      this.notifyConfigurationUpdate();
      
      return;
    }
    
    // Capture old value before overwriting so we can clear dependents after
    const previousValue = (Array.isArray(this.state.selections[groupHandle]) && this.state.selections[groupHandle][0] !== value)
      ? this.state.selections[groupHandle][0]
      : null;

    // Store selection by group handle
    this.state.selections[groupHandle] = [value];

    // Now clear any selections whose requires are no longer satisfied by the new state
    if (previousValue) {
      const clearedGroups = this.removeDependentSelections(groupHandle, previousValue);
      // Auto-select a compatible default for any group that was cleared
      for (const clearedHandle of clearedGroups) {
        this.autoSelectDefaultForGroup(clearedHandle);
      }
    }

    // If a size group changed, clear manifest-defined size-restricted options that aren't valid for the new size
    const sizeGroupHandles = new Set(['size', 'ls_size', 'amsec_size']);
    if (sizeGroupHandles.has(groupHandle)) {
      const sizeRestrictedCleared = this.clearSizeRestrictedOptions(value);
      for (const clearedHandle of sizeRestrictedCleared) {
        this.autoSelectDefaultForGroup(clearedHandle);
      }
    }

    // Get the option value object for dependency checking
    const optionValue = this.getValueByCode(groupHandle, value);
    
    // Save to local storage
    this.saveSelectionsToStorage();
    
    // Remove selected class from all cards in the SAME GROUP only
    cardElement.closest('.quiz-step').querySelectorAll(`[data-group="${groupHandle}"]`).forEach(card => {
      card.classList.remove('selected');
    });
    
    // Add selected class to clicked card
    cardElement.classList.add('selected');
    
    // Handle dependencies - if this is exterior finish, check for unlocks
    if (groupHandle === 'exterior_finish') {
      this.handleExteriorFinishDependencies(value);
      // Re-render safe image with new finish
      this.renderSafeImage('summary');
    }
    
    // Handle crane hinge selection - auto-select "no graphic" and show color selector
    if (groupHandle === 'hinge_type') {
      const hingeTypeUpper = value.toUpperCase();
      const previousHingeType = this.state.selections[groupHandle]?.[0]?.toUpperCase();
      const wasCraneHinge = previousHingeType === 'CRANE' || previousHingeType === 'CRANE_BLANK';
      const isCraneHinge = hingeTypeUpper === 'CRANE' || hingeTypeUpper === 'CRANE_BLANK';
      
      if (isCraneHinge) {
        // Auto-select "no graphic" option (code is 'NONE')
        const graphicGroup = this.data.optionGroups.find(g => g.handle === 'graphic');
        if (graphicGroup) {
          const noGraphicOption = graphicGroup.values.find(v => v.code === 'NONE');
          if (noGraphicOption) {
            this.state.selections['graphic'] = ['NONE'];
            console.log('✅ Auto-selected NONE graphic for crane hinge');
            
            // Save to storage immediately
            this.saveSelectionsToStorage();
            
            // If the graphic step is currently visible, re-render it to show only "no graphic"
            const graphicStep = this.steps.find(s => s.groups && s.groups.includes('graphic'));
            if (graphicStep && this.currentStep === graphicStep.id) {
              this.renderStep(graphicStep.id);
            }
          }
        }
        
        // Show crane hinge color selector after a short delay
        setTimeout(() => {
          const modalBody = document.querySelector('#quizModal .modal-body');
          if (modalBody) {
            modalBody.scrollTop = 0;
          }
          this.showCraneHingeColorSelector(cardElement);
        }, 400);
        
      } else if (wasCraneHinge && !isCraneHinge) {
        // Switching from crane hinge to non-crane hinge - clear graphic restriction
        delete this.state.selections['graphic'];
        console.log('✅ Cleared graphic selection when switching from crane hinge');
        
        // Save to storage immediately
        this.saveSelectionsToStorage();
        
        // If the graphic step is currently visible, re-render it
        const graphicStep = this.steps.find(s => s.groups && s.groups.includes('graphic'));
        if (graphicStep && this.currentStep === graphicStep.id) {
          this.renderStep(graphicStep.id);
        }
      }
    }
    
    // Re-render safe image when color, lock, graphic, hinge, or hinge swing changes
    // Check dynamically based on option group type rather than hardcoded handles
    const group = this.data.optionGroups.find(g => g.handle === groupHandle);
    const shouldRenderImage = group && (
      group.type === 'color' || group.type === 'color_single' || groupHandle.includes('color') ||
      group.type === 'lock_type' || groupHandle.includes('lock_type') ||
      group.type === 'graphic' || groupHandle === 'graphic' ||
      group.type === 'hinge_type' || groupHandle.includes('hinge') ||
      group.type === 'hinge_color' || group.type === 'hinge_swing'
    );
    
    if (shouldRenderImage) {
      console.log('🎨 Triggering safe image render for group:', groupHandle, 'type:', group?.type);
      this.renderSafeImage('summary');
    }
    
    // Auto-toggle to door color after selecting body color in two-tone interface
    if (groupHandle === 'body_color') {
      const twoToneContainer = cardElement.closest('.two-tone-container');
      if (twoToneContainer) {
        setTimeout(() => {
          const doorTab = twoToneContainer.querySelector('.color-tab[data-tab="door_color"]');
          if (doorTab) {
            doorTab.click();
            
            // Scroll the modal body to top with smooth animation
            const modalBody = document.querySelector('.modal-body');
            if (modalBody) {
              modalBody.scrollTo({
                top: 0,
                behavior: 'smooth'
              });
            }
          }
        }, 300); // Small delay for visual feedback
      }
    }
    
    // Check if step is complete (all required groups have selections)
    const step = this.steps[stepNumber - 1];
    const isStepComplete = this.isStepComplete(step);
    
    // Enable next button if step is complete
    document.getElementById('nextBtn').disabled = !isStepComplete;
    
    // Don't update visual rendering in main preview during configuration
    // Visual rendering only happens in summary
    
    // Update config badge
    this.updateConfigBadge();
    
    // Recalculate and update pricing
    this.calculatePrice();
    
    // Notify the app block about configuration changes
    this.notifyConfigurationUpdate();
    
    // Check if this option has inline_unlock_groups dependency (e.g., cabinet watch winder variants)
    // Only _WW variants should trigger inline groups, not regular cabinet options
    let opensInlineGroup = false;
    if (optionValue && optionValue.dependencies && value.endsWith('_WW')) {
      try {
        const deps = typeof optionValue.dependencies === 'string' 
          ? JSON.parse(optionValue.dependencies) 
          : optionValue.dependencies;
        
        if (deps.inline_unlock_groups && Array.isArray(deps.inline_unlock_groups)) {
          opensInlineGroup = true;
          // Open each inline group specified (e.g., watch_winders)
          deps.inline_unlock_groups.forEach(inlineGroupHandle => {
            setTimeout(() => {
              try {
                const inlineGroup = this.data.optionGroups.find(g => g.handle === inlineGroupHandle);
                if (inlineGroup) {
                  const availableOptions = inlineGroup.values.filter(v => this.isValueAvailable(inlineGroup, v));
                  if (availableOptions.length > 0) {
                    console.log(`✨ Opening inline group '${inlineGroupHandle}' for option ${value}`);
                    this.showInlineGroup(inlineGroupHandle, cardElement);
                  } else {
                    console.warn(`⚠️ No available options in inline group '${inlineGroupHandle}'`);
                  }
                } else {
                  console.warn(`⚠️ Inline group '${inlineGroupHandle}' not found in data`);
                }
              } catch (err) {
                console.warn(`Failed to open inline group '${inlineGroupHandle}'`, err);
              }
            }, 100);
          });
        }
      } catch (err) {
        console.warn('Failed to parse dependencies for inline_unlock_groups', err);
      }
    }
    
    // Auto-advance to next step if step is complete (with small delay for visual feedback)
    // BUT: Don't auto-advance for crane hinges OR options that open inline groups
    const isCraneHinge = groupHandle === 'hinge_type' && (value === 'CRANE' || value === 'CRANE_BLANK');
    if (isStepComplete && !this.isEditingFromSummary && !isCraneHinge && !opensInlineGroup) {
      setTimeout(() => {
        this.nextStep();
      }, 400);
    }
  }

  toggleQuantitySelector(cardElement, optionValue) {
    const groupHandle = cardElement.dataset.group;
    const value = cardElement.dataset.value;
    
    // Get current quantity
    const quantityDisplay = cardElement.querySelector('.quantity-display');
    const currentQuantity = quantityDisplay ? parseInt(quantityDisplay.textContent) : 0;
    
    // Check if this is a liner option with shared maximum
    const isLinerOption = ['STAINLESS_10GA_PKG', 'ARMAKNOX_AR500_3_16', 'CARBON_STEEL_LINER'].includes(value);
    
    if (currentQuantity === 0) {
      // Not selected yet - select it and set to 1
      
      // Check if we can add more (for liner options)
      if (isLinerOption) {
        const currentTotal = this.getTotalLinerCount(value);
        if (currentTotal >= 4) {
          // Can't add more, already at max
          return;
        }
      }
      
      // Select the card
      cardElement.classList.add('selected');
      
      // Set quantity to 1
      if (quantityDisplay) {
        quantityDisplay.textContent = '1';
      }
      
      // Update selections
      if (!this.state.selections[groupHandle]) {
        this.state.selections[groupHandle] = {};
      }
      this.state.selections[groupHandle][value] = 1;
      
    } else {
      // Already selected - deselect it and set to 0
      cardElement.classList.remove('selected');
      
      // Set quantity to 0
      if (quantityDisplay) {
        quantityDisplay.textContent = '0';
      }
      
      // Remove from selections
      if (this.state.selections[groupHandle] && this.state.selections[groupHandle][value]) {
        delete this.state.selections[groupHandle][value];
        if (Object.keys(this.state.selections[groupHandle]).length === 0) {
          delete this.state.selections[groupHandle];
        }
      }
    }
    
    // Check if step is complete and enable/disable next button
    const step = this.steps[this.currentStep - 1];
    if (step && this.isStepComplete(step)) {
      document.getElementById('nextBtn').disabled = false;
    } else {
      document.getElementById('nextBtn').disabled = true;
    }
    
    // Save to local storage
    this.saveSelectionsToStorage();
    
    // Update config badge
    this.updateConfigBadge();
    
    // Recalculate and update pricing
    this.calculatePrice();
    
    // Notify the app block about configuration changes
    this.notifyConfigurationUpdate();
  }

  createQuantitySelector(optionValue) {
    const selector = document.createElement('div');
    selector.className = 'quantity-selector';
    selector.innerHTML = `
      <button class="quantity-btn minus" type="button">
        <i data-lucide="minus" class="icon-xs"></i>
      </button>
      <span class="quantity-display">0</span>
      <button class="quantity-btn plus" type="button">
        <i data-lucide="plus" class="icon-xs"></i>
      </button>
    `;
    
    // Add event listeners
    const minusBtn = selector.querySelector('.minus');
    const plusBtn = selector.querySelector('.plus');
    const display = selector.querySelector('.quantity-display');
    
    minusBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      const currentValue = parseInt(display.textContent);
      if (currentValue > 0) {
        const newValue = currentValue - 1;
        display.textContent = newValue;
        this.updateQuantitySelection(selector, newValue);
        
        // If quantity goes to 0, deselect the option
        if (newValue === 0) {
          const cardElement = selector.closest('.option-card');
          cardElement.classList.remove('selected');
        }
      }
    });
    
    plusBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      const currentValue = parseInt(display.textContent);
      const maxCount = optionValue.maxCount || 10; // Default max if not specified
      
      // Check if this is one of the liner options with shared maximum
      const cardElement = selector.closest('.option-card');
      const value = cardElement?.dataset.value;
      const isLinerOption = ['STAINLESS_10GA_PKG', 'ARMAKNOX_AR500_3_16', 'CARBON_STEEL_LINER'].includes(value);
      
      if (isLinerOption) {
        // Check shared maximum of 4 across all liner options
        const currentTotal = this.getTotalLinerCount(value); // Total of OTHER liner types
        const sharedMax = 4;
        const newValue = currentValue + 1;
        
        // Allow incrementing only if total (including this increment) won't exceed the shared max
        if (currentValue < maxCount && (currentTotal + newValue) <= sharedMax) {
          display.textContent = newValue;
          this.updateQuantitySelection(selector, newValue);
          
          // Select the card when quantity goes above 0
          if (newValue > 0) {
            cardElement.classList.add('selected');
          }
        }
      } else {
        // Regular quantity limit
        if (currentValue < maxCount) {
          const newValue = currentValue + 1;
          display.textContent = newValue;
          this.updateQuantitySelection(selector, newValue);
          
          // Select the card when quantity goes above 0
          if (newValue > 0) {
            cardElement.classList.add('selected');
          }
        }
      }
    });
    
    // Initialize Lucide icons
    if (typeof lucide !== 'undefined') {
      lucide.createIcons();
    }
    
    return selector;
  }

  // Helper method to calculate total liner count for shared maximum
  getTotalLinerCount(excludeValue = null) {
    const linerCodes = ['STAINLESS_10GA_PKG', 'ARMAKNOX_AR500_3_16', 'CARBON_STEEL_LINER'];
    const selections = this.state.selections['securityfire_upgrade'] || {};
    
    let total = 0;
    for (const code of linerCodes) {
      if (code !== excludeValue && selections[code]) {
        total += selections[code];
      }
    }
    return total;
  }

  updateQuantitySelection(selector, quantity) {
    const cardElement = selector.closest('.option-card');
    const groupHandle = cardElement.dataset.group;
    const value = cardElement.dataset.value;
    
    // Update the selection
    if (quantity === 0) {
      // Remove from selections if quantity is 0
      if (this.state.selections[groupHandle] && this.state.selections[groupHandle][value]) {
        delete this.state.selections[groupHandle][value];
        // Clean up empty group
        if (Object.keys(this.state.selections[groupHandle]).length === 0) {
          delete this.state.selections[groupHandle];
        }
      }
    } else {
      // Add or update selection
      if (!this.state.selections[groupHandle]) {
        this.state.selections[groupHandle] = {};
      }
      this.state.selections[groupHandle][value] = quantity;
    }
    
    // Save to local storage
    this.saveSelectionsToStorage();
    
    // Update config badge
    this.updateConfigBadge();
    
    // Recalculate and update pricing
    this.calculatePrice();
    
    // Notify the app block about configuration changes
    this.notifyConfigurationUpdate();
  }

  showInlineGroup(groupHandle, afterElement) {
    // Check if inline group already exists
    const currentStep = document.querySelector(`.quiz-step[data-step="${this.currentStep}"]`);
    if (!currentStep) return;
    
    const existingInline = currentStep.querySelector(`.inline-group[data-group="${groupHandle}"]`);
    if (existingInline) {
      // Already showing, don't create duplicate
      return;
    }
    
    // Get trigger option info for namespacing
    const triggerCard = afterElement.closest('.option-card');
    const triggerGroup = triggerCard?.dataset.group;
    const triggerValue = triggerCard?.dataset.value;
    const namespaceKey = `${groupHandle}_for_${triggerGroup}_${triggerValue}`;
    
    // Find the group definition
    const group = this.data.optionGroups.find(g => g.handle === groupHandle);
    if (!group) return;
    
    // Create inline group container
    const inlineContainer = document.createElement('div');
    inlineContainer.className = 'inline-group';
    inlineContainer.dataset.group = groupHandle;
    inlineContainer.dataset.namespace = namespaceKey;
    inlineContainer.dataset.triggerGroup = triggerGroup;
    inlineContainer.dataset.triggerValue = triggerValue;
    
    // Create header with back arrow and title
    const header = document.createElement('div');
    header.className = 'inline-group-header';
    header.innerHTML = `
      <button class="inline-group-back" aria-label="Back">
        <i data-lucide="arrow-left" class="icon-sm"></i>
      </button>
      <h3>${group.displayName || group.titleName || groupHandle}</h3>
      <button class="inline-group-close" aria-label="Close">
        <i data-lucide="x" class="icon-sm"></i>
      </button>
    `;
    
    // Create options grid - force 3 columns for cabinet_inserts
    const optionsGrid = document.createElement('div');
    optionsGrid.className = 'option-grid inline-group-options';
    
    // Force 3 columns (CSS handles this but set data attribute for consistency)
    optionsGrid.setAttribute('data-custom-columns', '3');
    
    // Render options
    group.values.forEach(value => {
      // Check if available
      if (!this.isValueAvailable(group, value)) return;
      
      const card = document.createElement('div');
      card.className = 'option-card';
      card.dataset.value = value.code;
      card.dataset.group = groupHandle;
      
      // Check if already selected in this namespaced group
      if (this.state.selections[namespaceKey] && this.state.selections[namespaceKey][value.code]) {
        card.classList.add('selected');
      }
      
      // Add image if available
      if (value.optionImage) {
        card.classList.add('has-image');
        const imageContainer = document.createElement('div');
        imageContainer.className = 'size-image-container';
        imageContainer.innerHTML = `<img src="${value.optionImage}" alt="${value.label}" class="size-image" />`;
        card.appendChild(imageContainer);
      }
      
      // Add label
      const label = document.createElement('span');
      label.textContent = value.label;
      card.appendChild(label);
      
      // Add price if applicable
      const price = this.getOptionPrice(value, groupHandle);
      if (price > 0) {
        const priceEl = document.createElement('div');
        priceEl.className = 'option-price';
        priceEl.textContent = `+${this.formatPrice(price)}`;
        card.appendChild(priceEl);
      }
      
      optionsGrid.appendChild(card);
    });
    
    // Create footer with apply button
    const footer = document.createElement('div');
    footer.className = 'inline-group-footer';
    footer.innerHTML = `
      <button class="btn btn-primary inline-group-apply">
        <i data-lucide="check" class="icon-sm"></i>
        Apply Selection
      </button>
    `;
    
    // Assemble the inline group
    inlineContainer.appendChild(header);
    inlineContainer.appendChild(optionsGrid);
    inlineContainer.appendChild(footer);
    
    // Append to current step (will slide up over content)
    currentStep.appendChild(inlineContainer);
    
    // Initialize Lucide icons
    if (typeof lucide !== 'undefined') {
      lucide.createIcons();
    }
    
    // Disable navigation buttons and step indicators
    this.disableNavigation(true);
    
    // Attach event listeners
    const closeBtn = inlineContainer.querySelector('.inline-group-close');
    const backBtn = inlineContainer.querySelector('.inline-group-back');
    const applyBtn = inlineContainer.querySelector('.inline-group-apply');
    
    // Disable apply button initially
    applyBtn.disabled = true;
    
    // Create temporary storage for inline selections (only committed on apply)
    let tempSelections = {};
    
    // Copy existing committed selections to temp if any
    if (this.state.selections[namespaceKey]) {
      tempSelections = { ...this.state.selections[namespaceKey] };
      // Enforce single-selection for watch_winders when restoring into the inline UI
      if (groupHandle === 'watch_winders') {
        const keys = Object.keys(tempSelections);
        if (keys.length > 1) {
          // Keep only the first key
          const first = keys[0];
          tempSelections = { [first]: tempSelections[first] };
        }
      }
    }
    
    // Function to update apply button state
    const updateApplyButtonState = () => {
      const hasSelections = Object.keys(tempSelections).length > 0;
      if (hasSelections) {
        applyBtn.disabled = false;
      } else {
        applyBtn.disabled = true;
      }
    };
    
    const closeHandler = () => {
      // Discard temporary selections - don't commit them
      // Check if there are any committed selections
      const hasCommittedSelections = this.state.selections[namespaceKey] && 
                            Object.keys(this.state.selections[namespaceKey]).length > 0;
      
      // If no committed selections, deselect the trigger option that opened this group
      if (!hasCommittedSelections && afterElement) {
        const triggerCard = afterElement.closest('.option-card');
        if (triggerCard) {
          triggerCard.classList.remove('selected');
          
          // Remove from state
          const triggerGroup = triggerCard.dataset.group;
          const triggerValue = triggerCard.dataset.value;
          if (this.state.selections[triggerGroup]) {
            delete this.state.selections[triggerGroup][triggerValue];
            if (Object.keys(this.state.selections[triggerGroup]).length === 0) {
              delete this.state.selections[triggerGroup];
            }
          }
          
          // Remove the namespaced selections
          delete this.state.selections[namespaceKey];
          
          this.saveSelectionsToStorage();
          this.updateConfigBadge();
          this.calculatePrice();
          this.notifyConfigurationUpdate();
        }
      }
      
      this.hideInlineGroup(groupHandle, afterElement);
    };
    
    const applyHandler = () => {
      // Commit temporary selections to state
      if (Object.keys(tempSelections).length > 0) {
        this.state.selections[namespaceKey] = { ...tempSelections };
      } else {
        delete this.state.selections[namespaceKey];
      }
      
      // Update trigger card to show selection count and price
      const hasSelections = this.state.selections[namespaceKey] && 
                            Object.keys(this.state.selections[namespaceKey]).length > 0;
      
      if (hasSelections && afterElement) {
        const triggerCard = afterElement.closest('.option-card');
        if (triggerCard) {
          const count = Object.keys(this.state.selections[namespaceKey]).length;
          
          // Calculate total price from inline group selections
          let totalPrice = 0;
          const group = this.data.optionGroups.find(g => g.handle === groupHandle);
          if (group) {
            for (const valueCode in this.state.selections[namespaceKey]) {
              const value = group.values.find(v => v.code === valueCode);
              if (value) {
                const price = this.getOptionPrice(value, groupHandle);
                totalPrice += price;
              }
            }
          }
          
          // Update the label to show count (e.g., "Cabinet Drawer 1 (2x)")
          const labelSpan = triggerCard.querySelector('span:not(.option-price):not(.inline-group-badge)');
          if (labelSpan && !labelSpan.dataset.originalText) {
            labelSpan.dataset.originalText = labelSpan.textContent;
          }
          if (labelSpan) {
            labelSpan.textContent = `${labelSpan.dataset.originalText || labelSpan.textContent} (${count}x)`;
          }
          
          // Add or update price display
          let priceEl = triggerCard.querySelector('.option-price');
          if (!priceEl) {
            priceEl = document.createElement('div');
            priceEl.className = 'option-price';
            triggerCard.appendChild(priceEl);
          }
          
          if (totalPrice > 0) {
            priceEl.textContent = `+${this.formatPrice(totalPrice)}`;
            priceEl.style.display = 'block';
          } else {
            priceEl.style.display = 'none';
          }
          
          // Ensure trigger card stays highlighted
          triggerCard.classList.add('selected');
        }
      }
      
      // Update price
      this.calculatePrice();
      this.saveSelectionsToStorage();
      this.updateConfigBadge();
      
      this.hideInlineGroup(groupHandle, afterElement);

      // If this inline group was opened from a cabinet_size trigger, advance to next step
      if (triggerGroup === 'cabinet_size') {
        setTimeout(() => {
          try { this.nextStep(); } catch (e) { console.warn('Failed to advance to next step after inline apply', e); }
        }, 350);
      }
    };
    
    if (closeBtn) closeBtn.addEventListener('click', closeHandler);
    if (backBtn) backBtn.addEventListener('click', closeHandler);
    if (applyBtn) applyBtn.addEventListener('click', applyHandler);
    
    // Handle option selection within inline group
    optionsGrid.addEventListener('click', (e) => {
      e.stopPropagation();
      
      const card = e.target.closest('.option-card');
      if (!card) return;
      
      // Prevent event if card is disabled
      if (card.classList.contains('option-disabled') || card.classList.contains('disabled')) {
        e.preventDefault();
        return;
      }
      
      const value = card.dataset.value;

      // For watch_winders enforce single-selection
      if (groupHandle === 'watch_winders') {
        // If already selected, unselect it
        if (card.classList.contains('selected')) {
          delete tempSelections[value];
          card.classList.remove('selected');
        } else {
          // Clear any other temp selections
          Object.keys(tempSelections).forEach(k => delete tempSelections[k]);
          // Clear selected class from other cards
          optionsGrid.querySelectorAll('.option-card.selected').forEach(c => c.classList.remove('selected'));
          tempSelections = {};
          tempSelections[value] = 1;
          card.classList.add('selected');
        }
      } else {
        // Toggle selection in temporary storage (not committed until apply)
        if (card.classList.contains('selected')) {
          delete tempSelections[value];
          card.classList.remove('selected');
        } else {
          tempSelections[value] = 1;
          card.classList.add('selected');
        }
      }
      
      // Update apply button state based on temp selections
      updateApplyButtonState();
    });
    
    // Add class to modal-body to prevent background bleed
    const modalBody = currentStep.closest('.modal-body');
    if (modalBody) {
      modalBody.classList.add('inline-panel-open');
      modalBody.scrollTop = 0;
    }

    // Animate in - slide up from bottom
    setTimeout(() => {
      inlineContainer.classList.add('visible');
    }, 50);
  }

  hideInlineGroup(groupHandle, triggerElement) {
    const currentStep = document.querySelector(`.quiz-step[data-step="${this.currentStep}"]`);
    if (!currentStep) return;

    const inlineGroup = currentStep.querySelector(`.inline-group[data-group="${groupHandle}"]`);
    if (!inlineGroup) return;

    // Re-enable navigation buttons and step indicators
    this.disableNavigation(false);

    // Remove background bleed prevention class
    const modalBody = currentStep.closest('.modal-body');
    if (modalBody) {
      modalBody.classList.remove('inline-panel-open');
    }

    // Animate out
    inlineGroup.classList.remove('visible');

    // Remove from DOM after animation
    setTimeout(() => {
      inlineGroup.remove();
    }, 300);
  }
  
  showRivetColorSelector(rivetCard) {
    const currentStep = document.querySelector(`.quiz-step[data-step="${this.currentStep}"]`);
    if (!currentStep) return;
    
    // Check if selector already exists
    const existingSelector = currentStep.querySelector('.rivet-color-selector');
    if (existingSelector) {
      existingSelector.classList.add('visible');
      // Prevent background scrolling
      currentStep.style.overflow = 'hidden';
      return;
    }
    
    // Get gloss color options
    const glossColorGroup = this.data.optionGroups.find(g => g.handle === 'color_single');
    if (!glossColorGroup) return;
    
    // Get current body color to highlight it
    const selectedFinish = this.state.selections['exterior_finish']?.[0];
    const normalizedFinish = selectedFinish?.toLowerCase().replace(/_/g, '-');
    let currentBodyColor = null;
    if (normalizedFinish === 'two-tone-fade' || normalizedFinish === 'two-tone') {
      currentBodyColor = this.state.selections['body_color']?.[0];
    } else {
      currentBodyColor = this.state.selections['color_single']?.[0];
    }
    
    // Create color selector container
    const selectorContainer = document.createElement('div');
    selectorContainer.className = 'rivet-color-selector';
    selectorContainer.innerHTML = `
      <div class="rivet-color-header">
        <button class="rivet-color-back" aria-label="Back">
          <i data-lucide="arrow-left" class="icon-sm"></i>
        </button>
        <h3>Rivet Color</h3>
        <button class="rivet-color-close" aria-label="Close">
          <i data-lucide="x" class="icon-sm"></i>
        </button>
      </div>
      <div class="rivet-color-info">
        <i data-lucide="info" class="icon-sm"></i>
        <span>Default: Same as body color</span>
      </div>
      <div class="rivet-color-grid"></div>
      <div class="rivet-color-footer">
        <button class="btn btn-secondary rivet-color-reset">Use Body Color</button>
        <button class="btn btn-primary rivet-color-apply">Apply Color</button>
      </div>
    `;
    
    const colorGrid = selectorContainer.querySelector('.rivet-color-grid');
    
    // Filter out texture colors (RIMROCK, LIGHT_GRANITE, DARK_GRANITE)
    const textureColors = ['RIMROCK', 'LIGHT_GRANITE', 'DARK_GRANITE'];
    
    console.log('🎨 Current body color:', currentBodyColor);
    console.log('🎨 Rivet color override:', this.state.rivetColorOverride);
    
    // Helper function to format color names
    const formatColorName = (name) => {
      return name
        .split('_')
        .map(word => word.charAt(0) + word.slice(1).toLowerCase())
        .join(' ');
    };
    
    // Render color options
    glossColorGroup.values.forEach(colorValue => {
      // Skip texture colors
      if (textureColors.includes(colorValue.code)) {
        console.log('🚫 Filtering out texture color:', colorValue.code);
        return;
      }
      
      const colorCard = document.createElement('div');
      colorCard.className = 'rivet-color-option';
      colorCard.dataset.color = colorValue.code;
      
      // Check if this is the current override color
      if (this.state.rivetColorOverride === colorValue.code) {
        colorCard.classList.add('selected');
      }
      
      // Highlight if this is the current body color (default)
      if (colorValue.code === currentBodyColor && !this.state.rivetColorOverride) {
        colorCard.classList.add('body-color-default');
        console.log('✅ Highlighting body color:', colorValue.code);
      }
      
      const colorOption = this.getValueByCode('color_single', colorValue.code);
      const hexColor = colorOption?.hexColor;
      
      // Format the display name
      const displayName = colorValue.displayName || formatColorName(colorValue.code);
      console.log(`📝 Color: ${colorValue.code} → Display: ${displayName}`);
      
      colorCard.innerHTML = `
        <div class="rivet-color-swatch" style="background-color: ${hexColor || '#ccc'}"></div>
        <span class="rivet-color-name">${displayName}</span>
        ${colorValue.code === currentBodyColor && !this.state.rivetColorOverride ? '<span class="body-color-badge">Body Color</span>' : ''}
      `;
      
      colorGrid.appendChild(colorCard);
    });
    
    // Append to current step
    currentStep.appendChild(selectorContainer);
    
    // Prevent background scrolling - disable modal body scrolling
    const modalBody = document.querySelector('#quizModal .modal-body');
    if (modalBody) {
      modalBody.style.overflow = 'hidden';
    }
    currentStep.style.overflow = 'hidden';
    
    // Scroll modal body to top to show selector
    if (modalBody) {
      modalBody.scrollTop = 0;
    }
    
    // Initialize Lucide icons
    if (typeof lucide !== 'undefined') {
      lucide.createIcons();
    }
    
    // Disable navigation
    this.disableNavigation(true);
    
    // Store temporary color selection
    let tempColor = this.state.rivetColorOverride;
    
    // Event handlers
    const closeBtn = selectorContainer.querySelector('.rivet-color-close');
    const backBtn = selectorContainer.querySelector('.rivet-color-back');
    const resetBtn = selectorContainer.querySelector('.rivet-color-reset');
    const applyBtn = selectorContainer.querySelector('.rivet-color-apply');
    
    const closeHandler = () => {
      selectorContainer.classList.remove('visible');
      this.disableNavigation(false);
      // Restore scrolling for both current step and modal body
      currentStep.style.overflow = '';
      const modalBody = document.querySelector('#quizModal .modal-body');
      if (modalBody) {
        modalBody.style.overflow = '';
      }
      setTimeout(() => selectorContainer.remove(), 300);
    };
    
    const applyHandler = () => {
      this.state.rivetColorOverride = tempColor;
      this.saveSelectionsToStorage();
      this.renderSafeImage();
      this.renderSafeImage('summary');
      closeHandler();
    };
    
    const resetHandler = () => {
      // Get current body color to use as rivet color
      const bodyColor = this.state.selections['body_color']?.[0] || this.state.selections['color_single']?.[0];
      tempColor = bodyColor;
      // Apply immediately - set to body color
      this.state.rivetColorOverride = bodyColor;
      this.saveSelectionsToStorage();
      this.renderSafeImage();
      this.renderSafeImage('summary');
      closeHandler();
    };
    
    closeBtn?.addEventListener('click', closeHandler);
    backBtn?.addEventListener('click', closeHandler);
    applyBtn?.addEventListener('click', applyHandler);
    resetBtn?.addEventListener('click', resetHandler);
    
    // Color option selection
    colorGrid.addEventListener('click', (e) => {
      const colorOption = e.target.closest('.rivet-color-option');
      if (!colorOption) return;
      
      // Update temp selection
      tempColor = colorOption.dataset.color;
      
      // Update UI
      selectorContainer.querySelectorAll('.rivet-color-option').forEach(opt => {
        opt.classList.remove('selected');
      });
      colorOption.classList.add('selected');
    });
    
    // Animate in
    setTimeout(() => {
      selectorContainer.classList.add('visible');
    }, 50);
  }
  
  hideRivetColorSelector() {
    const currentStep = document.querySelector(`.quiz-step[data-step="${this.currentStep}"]`);
    if (!currentStep) return;
    
    const selector = currentStep.querySelector('.rivet-color-selector');
    if (!selector) return;
    
    selector.classList.remove('visible');
    this.disableNavigation(false);
    
    // Restore scrolling for both current step and modal body
    currentStep.style.overflow = '';
    const modalBody = document.querySelector('#quizModal .modal-body');
    if (modalBody) {
      modalBody.style.overflow = '';
    }
    
    setTimeout(() => selector.remove(), 300);
  }
  
  showCraneHingeColorSelector(hingeCard) {
    const currentStep = document.querySelector(`.quiz-step[data-step="${this.currentStep}"]`);
    if (!currentStep) return;
    
    // Check if selector already exists
    const existingSelector = currentStep.querySelector('.crane-hinge-color-selector');
    if (existingSelector) {
      existingSelector.classList.add('visible');
      return;
    }
    
    // Get gloss color options (same as rivets - includes textures)
    const glossColorGroup = this.data.optionGroups.find(g => g.handle === 'color_single');
    if (!glossColorGroup) return;
    
    // Get current body color to highlight it
    const selectedFinish = this.state.selections['exterior_finish']?.[0];
    const normalizedFinish = selectedFinish?.toLowerCase().replace(/_/g, '-');
    let currentBodyColor = null;
    if (normalizedFinish === 'two-tone-fade' || normalizedFinish === 'two-tone') {
      currentBodyColor = this.state.selections['body_color']?.[0];
    } else {
      currentBodyColor = this.state.selections['color_single']?.[0];
    }
    
    // Create color selector container (using same structure as rivet selector)
    const selectorContainer = document.createElement('div');
    selectorContainer.className = 'crane-hinge-color-selector';
    selectorContainer.innerHTML = `
      <div class="rivet-color-header">
        <button class="crane-hinge-color-back" aria-label="Back">
          <i data-lucide="arrow-left" class="icon-sm"></i>
        </button>
        <h3>Crane Hinge Color</h3>
        <button class="crane-hinge-color-close" aria-label="Close">
          <i data-lucide="x" class="icon-sm"></i>
        </button>
      </div>
      <div class="rivet-color-info">
        <i data-lucide="info" class="icon-sm"></i>
        <span>Default: Same as body color. Custom colors incur two-tone finish upcharge.</span>
      </div>
      <div class="rivet-color-grid crane-hinge-color-grid"></div>
      <div class="rivet-color-footer">
        <button class="btn btn-secondary crane-hinge-color-reset">Use Body Color</button>
        <button class="btn btn-primary crane-hinge-color-apply">Apply Color</button>
      </div>
    `;
    
    const colorGrid = selectorContainer.querySelector('.crane-hinge-color-grid');
    
    console.log('🎨 Current body color:', currentBodyColor);
    console.log('🎨 Crane hinge color override:', this.state.craneHingeColorOverride);
    
    // Get the two-tone finish price for upcharge display
    const finishGroup = this.data.optionGroups.find(g => g.handle === 'exterior_finish');
    const twoToneOption = finishGroup?.values.find(v => v.code === 'TWO_TONE');
    const twoTonePrice = twoToneOption ? this.getOptionPrice(twoToneOption, 'exterior_finish') : 0;
    
    // Get texture URLs from manifest
    const currentModel = this.getCurrentModel();
    const manifest = currentModel ? this.modelImageManifests[currentModel.id] : null;
    const textureUrls = manifest?.hinges?.textures || {};
    
    // Helper function to format color names
    const formatColorName = (name) => {
      return name.split('_').map(word => 
        word.charAt(0) + word.slice(1).toLowerCase()
      ).join(' ');
    };
    
    // Texture color codes that should use images instead of colors
    const textureColorCodes = ['RIMROCK', 'LIGHT_GRANITE', 'DARK_GRANITE', 'DISTRESSED'];
    
    // Track which texture colors we've already rendered
    const renderedTextures = new Set();
    
    // Render ALL color options from glossColorGroup
    glossColorGroup.values.forEach(colorValue => {
      const colorCard = document.createElement('div');
      colorCard.className = 'rivet-color-option';
      colorCard.dataset.color = colorValue.code;
      
      // Check if this is the current body color
      const isBodyColor = currentBodyColor && colorValue.code === currentBodyColor;
      if (isBodyColor) {
        colorCard.classList.add('body-color-default');
      }
      
      // Check if this is currently selected
      if (this.state.craneHingeColorOverride === colorValue.code) {
        colorCard.classList.add('selected');
      }
      
      const displayName = formatColorName(colorValue.code);
      const hexColor = colorValue.hexColor || '#cccccc';
      
      // Check if this is a texture color
      const isTexture = textureColorCodes.includes(colorValue.code);
      const textureUrl = isTexture ? textureUrls[colorValue.code] : null;
      
      // Track rendered textures
      if (isTexture) {
        renderedTextures.add(colorValue.code);
      }
      
      // Price display
      let priceDisplay = '';
      if (isBodyColor) {
        priceDisplay = '<span class="color-price">+$0</span>';
      } else if (twoTonePrice > 0) {
        priceDisplay = `<span class="color-price">+${this.formatPrice(twoTonePrice)}</span>`;
      }
      
      // Create swatch - either with texture image or solid color
      let swatchHtml = '';
      if (isTexture && textureUrl) {
        swatchHtml = `<div class="rivet-color-swatch" style="background-image: url('${textureUrl}'); background-size: cover; background-position: center;"></div>`;
      } else {
        swatchHtml = `<div class="rivet-color-swatch" style="background-color: ${hexColor}"></div>`;
      }
      
      colorCard.innerHTML = `
        ${swatchHtml}
        <span class="rivet-color-name">${displayName}</span>
        ${priceDisplay}
        ${isBodyColor ? '<span class="body-color-badge">Body Color</span>' : ''}
      `;
      
      colorGrid.appendChild(colorCard);
    });
    
    // Add any missing texture options that weren't in the color group
    textureColorCodes.forEach(textureCode => {
      if (!renderedTextures.has(textureCode) && textureUrls[textureCode]) {
        const colorCard = document.createElement('div');
        colorCard.className = 'rivet-color-option';
        colorCard.dataset.color = textureCode;
        
        // Check if this is currently selected
        if (this.state.craneHingeColorOverride === textureCode) {
          colorCard.classList.add('selected');
        }
        
        const displayName = formatColorName(textureCode);
        const textureUrl = textureUrls[textureCode];
        
        // Price display
        const priceDisplay = twoTonePrice > 0 ? `<span class="color-price">+${this.formatPrice(twoTonePrice)}</span>` : '';
        
        colorCard.innerHTML = `
          <div class="rivet-color-swatch" style="background-image: url('${textureUrl}'); background-size: cover; background-position: center;"></div>
          <span class="rivet-color-name">${displayName}</span>
          ${priceDisplay}
        `;
        
        colorGrid.appendChild(colorCard);
      }
    });
    
    // Append to current step
    currentStep.appendChild(selectorContainer);
    
    // Prevent background scrolling - disable modal body scrolling
    const modalBody = document.querySelector('#quizModal .modal-body');
    if (modalBody) {
      modalBody.style.overflow = 'hidden';
    }
    currentStep.style.overflow = 'hidden';
    
    // Scroll modal body to top to show selector
    if (modalBody) {
      modalBody.scrollTop = 0;
    }
    
    // Initialize Lucide icons
    if (typeof lucide !== 'undefined') {
      lucide.createIcons();
    }
    
    // Disable navigation
    this.disableNavigation(true);
    
    // Store temporary color selection
    let tempColor = this.state.craneHingeColorOverride;
    
    // Event handlers
    const closeBtn = selectorContainer.querySelector('.crane-hinge-color-close');
    const backBtn = selectorContainer.querySelector('.crane-hinge-color-back');
    const resetBtn = selectorContainer.querySelector('.crane-hinge-color-reset');
    const applyBtn = selectorContainer.querySelector('.crane-hinge-color-apply');
    
    const closeHandler = () => {
      selectorContainer.classList.remove('visible');
      this.disableNavigation(false);
      // Restore scrolling for both current step and modal body
      currentStep.style.overflow = '';
      const modalBody = document.querySelector('#quizModal .modal-body');
      if (modalBody) {
        modalBody.style.overflow = '';
      }
      setTimeout(() => selectorContainer.remove(), 300);
    };
    
    const applyHandler = () => {
      this.state.craneHingeColorOverride = tempColor;
      this.saveSelectionsToStorage();
      this.calculatePrice(); // Recalculate price with crane hinge upcharge
      this.renderSafeImage();
      this.renderSafeImage('summary');
      closeHandler();
    };
    
    const resetHandler = () => {
      // Get current body color to use as crane hinge color
      const bodyColor = this.state.selections['body_color']?.[0] || this.state.selections['color_single']?.[0];
      tempColor = bodyColor;
      this.state.craneHingeColorOverride = bodyColor;
      this.saveSelectionsToStorage();
      this.calculatePrice(); // Recalculate price - no upcharge when matching body color
      this.renderSafeImage();
      this.renderSafeImage('summary');
      closeHandler();
    };
    
    closeBtn?.addEventListener('click', closeHandler);
    backBtn?.addEventListener('click', closeHandler);
    applyBtn?.addEventListener('click', applyHandler);
    resetBtn?.addEventListener('click', resetHandler);
    
    // Color option selection
    colorGrid.addEventListener('click', (e) => {
      const colorOption = e.target.closest('.rivet-color-option');
      if (!colorOption) return;
      
      // Update temp selection
      tempColor = colorOption.dataset.color;
      
      // Update UI
      selectorContainer.querySelectorAll('.rivet-color-option').forEach(opt => {
        opt.classList.remove('selected');
      });
      colorOption.classList.add('selected');
    });
    
    // Animate in
    setTimeout(() => {
      selectorContainer.classList.add('visible');
    }, 50);
  }
  
  hideCraneHingeColorSelector() {
    const currentStep = document.querySelector(`.quiz-step[data-step="${this.currentStep}"]`);
    if (!currentStep) return;
    
    const selector = currentStep.querySelector('.crane-hinge-color-selector');
    if (!selector) return;
    
    selector.classList.remove('visible');
    this.disableNavigation(false);
    
    // Restore scrolling for both current step and modal body
    currentStep.style.overflow = '';
    const modalBody = document.querySelector('#quizModal .modal-body');
    if (modalBody) {
      modalBody.style.overflow = '';
    }
    
    setTimeout(() => selector.remove(), 300);
    
    // Auto-advance to next step after color selector closes (if not editing from summary)
    if (!this.isEditingFromSummary) {
      const step = this.steps[this.currentStep - 1];
      if (step && this.isStepComplete(step)) {
        setTimeout(() => this.nextStep(), 400);
      }
    }
  }
  
  disableNavigation(disable) {
    const backBtn = document.getElementById('backBtn');
    const nextBtn = document.getElementById('nextBtn');
    const stepCircles = document.querySelectorAll('.step-circle');
    
    if (disable) {
      if (backBtn) {
        backBtn.disabled = true;
        backBtn.style.opacity = '0.5';
        backBtn.style.cursor = 'not-allowed';
      }
      if (nextBtn) {
        nextBtn.disabled = true;
        nextBtn.style.opacity = '0.5';
        nextBtn.style.cursor = 'not-allowed';
      }
      stepCircles.forEach(circle => {
        circle.style.pointerEvents = 'none';
        circle.style.opacity = '0.5';
        circle.style.cursor = 'not-allowed';
      });
    } else {
      if (backBtn) {
        backBtn.disabled = false;
        backBtn.style.opacity = '';
        backBtn.style.cursor = '';
      }
      if (nextBtn) {
        nextBtn.disabled = false;
        nextBtn.style.opacity = '';
        nextBtn.style.cursor = '';
      }
      stepCircles.forEach(circle => {
        circle.style.pointerEvents = '';
        circle.style.opacity = '';
        circle.style.cursor = '';
      });
    }
  }

  notifyConfigurationUpdate() {
    // Format selections as flat object (optionKey: valueKey or optionKey: {value: quantity})
    const flatSelections = {};
    for (const [groupHandle, selectedCodes] of Object.entries(this.state.selections)) {
      // Check if selection exists (could be string, array, or object)
      if (!selectedCodes) continue;
      
      // Handle different selection formats
      if (typeof selectedCodes === 'object' && !Array.isArray(selectedCodes)) {
        // Multi-select stored as object with quantities {CODE: qty, CODE2: qty}
        const hasSelections = Object.keys(selectedCodes).length > 0;
        if (hasSelections) {
          flatSelections[groupHandle] = selectedCodes;
        }
      } else if (Array.isArray(selectedCodes) && selectedCodes.length > 0) {
        // Single-select stored as array (legacy format)
        flatSelections[groupHandle] = selectedCodes[0];
      } else if (typeof selectedCodes === 'string') {
        // Direct string value
        flatSelections[groupHandle] = selectedCodes;
      }
    }
    
    // Add color overrides for cart rendering and pricing
    // These are stored separately from selections but need to be sent to backend
    if (this.state.rivetColorOverride) {
      flatSelections['rivet_color'] = this.state.rivetColorOverride;
      console.log('📦 Including rivet_color in configurator state:', this.state.rivetColorOverride);
    }
    
    if (this.state.craneHingeColorOverride) {
      flatSelections['crane_hinge_color'] = this.state.craneHingeColorOverride;
      console.log('📦 Including crane_hinge_color in configurator state:', this.state.craneHingeColorOverride);
    }

    // Store state globally for blocks to access
    window.configuratorState = {
      selections: flatSelections,
      totalPrice: this.state.totalPrice,
      basePrice: this.state.basePrice,
      modifiersTotal: this.state.modifiersTotal,
      modelId: this.state.selectedModel ? this.state.selectedModel.id : null
    };

    // Dispatch custom event for app blocks. modelId is required by the backend pricing
    // engine to resolve model-scoped dependencies.price_by_size[modelId][size] entries (e.g.
    // the vault door models' shared RO_* size codes) — the app block previously had no way to
    // get this at all, which was the root cause of a real wrong-price order.
    window.dispatchEvent(new CustomEvent('configurator:updated', {
      detail: {
        selections: flatSelections,
        totalPrice: this.state.totalPrice,
        basePrice: this.state.basePrice,
        modifiersTotal: this.state.modifiersTotal,
        modelId: this.state.selectedModel ? this.state.selectedModel.id : null
      }
    }));

  }

  handleExteriorFinishDependencies(finishValue) {
    // Clear existing color steps first
    this.clearColorSteps();
    
    // Get the exterior finish option value
    const finishOption = this.getValueByCode('exterior_finish', finishValue);
    if (!finishOption || !finishOption.dependencies) return;
    
    const deps = finishOption.dependencies;
    
    // Handle rivet trim for distressed finish
    const isDistressed = finishValue === 'DISTRESSED' || finishValue === 'DISTRESSED_FINISH';
    
    if (isDistressed) {
      // Distressed finish includes rivet trim - remove it from accessory selections if present
      if (this.state.selections.accessory) {
        if (Array.isArray(this.state.selections.accessory)) {
          // Remove RIVET_TRIM from array
          this.state.selections.accessory = this.state.selections.accessory.filter(acc => acc !== 'RIVET_TRIM');
        } else if (typeof this.state.selections.accessory === 'object') {
          // Remove RIVET_TRIM from object
          delete this.state.selections.accessory['RIVET_TRIM'];
        }
      }
      
      // Remove visual selection from rivet trim cards
      document.querySelectorAll('[data-value="RIVET_TRIM"]').forEach(card => {
        card.classList.remove('selected');
      });
    } else {
      // Non-distressed finish - rivet trim is no longer included
      // If it was previously included (from distressed), it should be removed from selections
      // User can manually add it back if they want
    }
    
    // Check if this finish unlocks color groups
    if (deps.unlocks && Array.isArray(deps.unlocks)) {
      // Map the unlocked groups to our actual group handles
      const groupMapping = {
        'color_body': 'body_color',
        'color_door': 'door_color',
        'color_single': 'color_single'
      };
      
      // Clear any existing color selections that are not unlocked
      const allColorGroups = ['color_single', 'body_color', 'door_color'];
      allColorGroups.forEach(groupHandle => {
        const isUnlocked = deps.unlocks.some(unlockKey => {
          const mappedGroup = groupMapping[unlockKey];
          return mappedGroup === groupHandle;
        });
        
        if (!isUnlocked) {
          // Remove selection for locked groups
          delete this.state.selections[groupHandle];
          
          // Remove visual selection from UI
          document.querySelectorAll(`[data-group="${groupHandle}"]`).forEach(card => {
            card.classList.remove('selected');
          });
        }
      });
      
      // Save updated selections
      this.saveSelectionsToStorage();
    }
    
    // Recalculate price after changing finish
    this.calculatePrice();
  }

  clearColorSteps() {
    // Remove color steps from the steps array
    this.steps = this.steps.filter(step => 
      step.key !== 'body_color' && 
      step.key !== 'door_color' && 
      step.key !== 'color_single' &&
      step.key !== 'two_tone_colors'
    );
    
    // Update step IDs
    this.steps.forEach((step, index) => {
      step.id = index + 1;
    });
    
    // Don't recreate step indicator - just update the existing one
    this.updateStepIndicator();
  }

  addColorStepsAfterExteriorFinish() {
    // Check if color steps already exist to prevent duplicates
    const hasColorSteps = this.steps.some(step => 
      step.key === 'body_color' || step.key === 'door_color' || step.key === 'color_single' || step.key === 'two_tone_colors'
    );
    
    if (hasColorSteps) return;
    
    // Get the selected exterior finish
    const finishValue = this.state.selections['exterior_finish']?.[0];
    if (!finishValue) return;
    
    const finishOption = this.getValueByCode('exterior_finish', finishValue);
    if (!finishOption || !finishOption.dependencies) return;
    
    const deps = finishOption.dependencies;
    if (!deps.unlocks || !Array.isArray(deps.unlocks)) return;
    
    // Map the unlocked groups to our actual group handles
    const groupMapping = {
      'color_body': 'body_color',
      'color_door': 'door_color',
      'color_single': 'color_single'
    };
    
    // Get the unlocked color groups
    const unlockedGroups = deps.unlocks
      .map(unlockKey => groupMapping[unlockKey])
      .filter(Boolean)
      .map(groupHandle => this.data.optionGroups.find(g => g.handle === groupHandle))
      .filter(Boolean);
    
    // Filter out groups where the only option is the same as the exterior finish
    const selectedFinish = this.state.selections['exterior_finish']?.[0];
    const filteredUnlockedGroups = unlockedGroups.filter(group => {
      const availableOptions = group.values.filter(v => this.isValueAvailable(group, v));
      
      // If this group has only one option and it's the same as the selected finish, skip it entirely
      if (availableOptions.length === 1 && selectedFinish && availableOptions[0].code === selectedFinish) {
        return false;
      }
      
      return true;
    });
    
    if (filteredUnlockedGroups.length === 0) {
      // No groups to unlock, just continue to next step
      return;
    }
    
    // Check if it's a single color group with only one option - auto-select it
    if (filteredUnlockedGroups.length === 1 && !filteredUnlockedGroups[0].handle.includes('body_color') && !filteredUnlockedGroups[0].handle.includes('door_color')) {
      const singleGroup = filteredUnlockedGroups[0];
      const availableOptions = singleGroup.values.filter(v => this.isValueAvailable(singleGroup, v));
      
      if (availableOptions.length === 1) {
        const singleOption = availableOptions[0];
        
        // Auto-select the single option
        this.state.selections[singleGroup.handle] = [singleOption.code];
        this.saveSelectionsToStorage();
        this.calculatePrice();
        this.notifyConfigurationUpdate();
        
        // Don't create a step for this - it's auto-selected
        // Update step IDs just in case
        this.steps.forEach((step, index) => {
          step.id = index + 1;
        });
        return;
      }
    }
    
    // Check if we have BOTH body_color AND door_color (two-tone finish)
    const hasBodyColor = filteredUnlockedGroups.some(g => g.handle === 'body_color');
    const hasDoorColor = filteredUnlockedGroups.some(g => g.handle === 'door_color');
    const isTwoTone = hasBodyColor && hasDoorColor;
    
    if (isTwoTone) {
      // Create a single two-tone step that includes both body and door color
      const bodyColorGroup = filteredUnlockedGroups.find(g => g.handle === 'body_color');
      const doorColorGroup = filteredUnlockedGroups.find(g => g.handle === 'door_color');
      
      // Step is optional if both color groups are optional
      const isOptional = (bodyColorGroup?.isOptional || false) && (doorColorGroup?.isOptional || false);
      
      const twoToneStep = {
        id: this.steps.length + 1,
        key: 'two_tone_colors',
        title: 'Choose a Two Tone Finish',
        description: 'Select One',
        groups: ['body_color', 'door_color'],
        icon: 'palette',
        isTwoTone: true,
        isOptional: isOptional
      };
      
      this.steps.splice(1, 0, twoToneStep);
    } else {
      // Create a SINGLE step for all unlocked color groups (not separate steps)
      const colorGroupHandles = filteredUnlockedGroups.map(g => g.handle);
      const firstGroup = filteredUnlockedGroups[0];
      
      // Step is optional if the first group is optional (since all are combined)
      const isOptional = firstGroup.isOptional || false;
      
      const colorStep = {
        id: this.steps.length + 1,
        key: firstGroup.handle,
        title: firstGroup.titleName || firstGroup.displayName,
        description: 'Select one',
        groups: colorGroupHandles, // All color groups in one step
        icon: 'droplet',
        isOptional: isOptional
      };
      
      this.steps.splice(1, 0, colorStep);
    }
    
    // Update all step IDs after insertion
    this.steps.forEach((step, index) => {
      step.id = index + 1;
    });
    
    // Don't recreate step indicator - just update the existing one
    // This prevents the last step from being highlighted incorrectly
    this.updateStepIndicator();
  }

  createTwoToneInterface(stepGroups) {
    const container = document.createElement('div');
    container.className = 'two-tone-container';
    
    // Create tabs
    const tabsContainer = document.createElement('div');
    tabsContainer.className = 'color-tabs';
    
    const bodyTab = document.createElement('button');
    bodyTab.className = 'color-tab active';
    bodyTab.dataset.tab = 'body_color';
    bodyTab.textContent = 'BODY COLOR';
    
    const doorTab = document.createElement('button');
    doorTab.className = 'color-tab';
    doorTab.dataset.tab = 'door_color';
    doorTab.textContent = 'DOOR COLOR';
    
    tabsContainer.appendChild(bodyTab);
    tabsContainer.appendChild(doorTab);
    
    // Create color grids container
    const gridsContainer = document.createElement('div');
    gridsContainer.className = 'color-grids-container';
    
    // Create grids for each color group
    stepGroups.forEach(group => {
      const grid = document.createElement('div');
      grid.className = `color-grid ${group.handle === 'body_color' ? 'active' : ''}`;
      grid.dataset.group = group.handle;
      
      // Filter values based on availability (respects hides, requires, etc.)
      const availableValues = group.values.filter(value => this.isValueAvailable(group, value));
      
      grid.setAttribute('data-option-count', availableValues.length);
      
      // Add color options (only available ones)
      availableValues.forEach(value => {
        const hasColor = value.hexColor && value.hexColor.trim() !== '';
        const hasImage = value.optionImage && value.optionImage.trim() !== '';
        const hasTooltip = value.tooltip && value.tooltip.trim() !== '';
        
        let priceDisplay = '';
        if (value.priceModifierValue && value.priceModifierValue !== 0) {
          const sign = value.priceModifierValue > 0 ? '+' : '';
          priceDisplay = `<span class="option-price">${sign}$${this.formatPrice(value.priceModifierValue)}</span>`;
        }

        const optionCard = document.createElement('button');
        optionCard.className = `option-card color-option ${hasImage ? 'has-image' : ''}`;
        optionCard.dataset.value = value.code;
        optionCard.dataset.group = group.handle;
        
        if (hasImage) {
          optionCard.innerHTML = `
            ${hasTooltip ? `
              <div class="tooltip-icon" data-tooltip="${value.tooltip}" tabindex="0" onclick="event.stopPropagation();">
                <i data-lucide="info"></i>
              </div>
            ` : ''}
            <div class="size-image-container">
              <img src="${value.optionImage}" alt="${value.label}" class="size-image" />
              <div class="size-tooltip">
                <div class="size-tooltip-text">${value.label}</div>
                ${priceDisplay ? `<div class="size-tooltip-price">${priceDisplay}</div>` : ''}
              </div>
            </div>
            <span>${value.label}</span>
            ${priceDisplay}
          `;
        } else if (hasColor) {
          optionCard.innerHTML = `
            ${hasTooltip ? `
              <div class="tooltip-icon" data-tooltip="${value.tooltip}" tabindex="0" onclick="event.stopPropagation();">
                <i data-lucide="info"></i>
              </div>
            ` : ''}
            <div class="color-preview" style="background-color: ${value.hexColor}"></div>
            <span>${value.label}</span>
            ${priceDisplay}
          `;
        } else {
          optionCard.innerHTML = `
            ${hasTooltip ? `
              <div class="tooltip-icon" data-tooltip="${value.tooltip}" tabindex="0" onclick="event.stopPropagation();">
                <i data-lucide="info"></i>
              </div>
            ` : ''}
            <div class="option-icon">
              <i data-lucide="check" class="icon-lg"></i>
            </div>
            <div class="option-content">
              <h3>${value.label}</h3>
              ${priceDisplay ? `<p>${priceDisplay}</p>` : ''}
            </div>
          `;
        }
        
        grid.appendChild(optionCard);
      });
      
      gridsContainer.appendChild(grid);
    });
    
    container.appendChild(tabsContainer);
    container.appendChild(gridsContainer);
    
    // Add tab click handlers
    tabsContainer.addEventListener('click', (e) => {
      const tab = e.target.closest('.color-tab');
      if (!tab) return;
      
      // Update active tab
      tabsContainer.querySelectorAll('.color-tab').forEach(t => t.classList.remove('active'));
      tab.classList.add('active');
      
      // Update active grid
      const tabGroup = tab.dataset.tab;
      gridsContainer.querySelectorAll('.color-grid').forEach(grid => {
        grid.classList.toggle('active', grid.dataset.group === tabGroup);
      });
    });
    
    return container;
  }

  isStepComplete(step) {
    // If step is optional, it's always considered complete (can be skipped)
    if (step.isOptional) {
      return true;
    }
    
    // Handle two-tone step specially
    if (step.isTwoTone) {
      const hasBodyColor = this.state.selections['body_color'] && this.state.selections['body_color'].length > 0;
      const hasDoorColor = this.state.selections['door_color'] && this.state.selections['door_color'].length > 0;
      return hasBodyColor && hasDoorColor;
    }
    
    // Handle bundle_accessories step - check if it's optional (legacy support)
    if (step.key === 'bundle_accessories') {
      // If not explicitly marked as optional, default to true for backwards compatibility
      if (step.isOptional === undefined) {
        return true;
      }
      // Otherwise use the isOptional flag
      return step.isOptional ? true : this.hasSelectionForStep(step);
    }
    
    // For multi-group steps, check if at least one required group has a selection
    if (step.isMultiGroup) {
      // For bundle_accessories, at least bundle should have a selection if it exists
      // But accessories are always optional
      const bundleSelection = this.state.selections['bundle'];
      if (bundleSelection && (Array.isArray(bundleSelection) ? bundleSelection.length > 0 : Object.keys(bundleSelection).length > 0)) {
        return true;
      }
      // If no bundle selected, that's okay too - it's optional
      return true;
    }
    
    // Check if the single group in this step has a selection
    const groupHandle = step.groups[0];
    const selection = this.state.selections[groupHandle];
    
    if (!selection) return false;
    
    // Handle multi-select options (object with quantities)
    if (typeof selection === 'object' && !Array.isArray(selection)) {
      return Object.keys(selection).length > 0;
    }
    
    // Handle single-select options (array)
    return selection && selection.length > 0;
  }

  hasSelectionForStep(step) {
    // Helper function to check if a step has any selection
    for (const groupHandle of step.groups) {
      const selection = this.state.selections[groupHandle];
      if (selection) {
        if (Array.isArray(selection)) {
          if (selection.length > 0) return true;
        } else if (typeof selection === 'object') {
          if (Object.keys(selection).length > 0) return true;
        }
      }
    }
    return false;
  }

  openQuizModal() {
    // Don't load selections from storage - selections should not persist across page refreshes
    // this.loadSelectionsFromStorage();

    // If savedStep is beyond the last step, user was on summary — go straight there
    if (this.savedStep && this.savedStep > this.steps.length) {
      this.savedStep = null;
      this.showSummary();
      return;
    }

    // Restore saved step if available, otherwise start at step 1
    if (this.savedStep && this.savedStep <= this.steps.length) {
      this.currentStep = this.savedStep;
    } else {
      this.currentStep = 1;
    }
    
    const modal = document.getElementById('quizModal');
    modal.classList.add('active');
    
    // Prevent body scroll on mobile
    document.body.classList.add('modal-open');
    document.body.style.overflow = 'hidden';
    
    // Reset selections display
    document.querySelectorAll('.option-card').forEach(card => {
      card.classList.remove('selected');
    });
    
    // Restore previous selections if any (from current session only, not storage)
    this.restorePreviousSelections();
    
    // Recalculate price based on loaded selections
    this.calculatePrice();
    
    // Update UI
    this.updateStepUI();
    this.updateProgress();
  }


  restorePreviousSelections() {
    this.steps.forEach((step, index) => {
      // Check each group in the step
      step.groups.forEach(groupHandle => {
        const value = this.state.selections[groupHandle];
        if (value) {
          if (Array.isArray(value)) {
            // Could be single-select with one item or multi-select with multiple items
            if (groupHandle === 'securityfire_upgrade') {
              // Multi-select - restore all selected items
              value.forEach(itemValue => {
                const card = document.querySelector(`.quiz-step[data-step="${index + 1}"] [data-group="${groupHandle}"][data-value="${itemValue}"]`);
                if (card) {
                  card.classList.add('selected');
                }
              });
            } else if (value.length > 0) {
              // Single-select - restore the first item
              const card = document.querySelector(`.quiz-step[data-step="${index + 1}"] [data-group="${groupHandle}"][data-value="${value[0]}"]`);
              if (card) {
                card.classList.add('selected');
              }
            }
          } else if (typeof value === 'object' && Object.keys(value).length > 0) {
            // Multi-select option with quantities (accessories, bundles)
            for (const [optionValue, quantity] of Object.entries(value)) {
              const card = document.querySelector(`.quiz-step[data-step="${index + 1}"] [data-group="${groupHandle}"][data-value="${optionValue}"]`);
              if (card) {
                card.classList.add('selected');
                
                // Handle bundle dependencies - hide included accessories
                if (groupHandle === 'bundle') {
                  const optionValueObj = this.getValueByCode(groupHandle, optionValue);
                  if (optionValueObj && optionValueObj.dependencies && optionValueObj.dependencies.hides_included) {
                    this.hideIncludedAccessories(optionValueObj.dependencies.includes);
                  }
                }
                
                // Restore quantity selector if it doesn't exist
                let quantitySelector = card.querySelector('.quantity-selector');
                if (!quantitySelector) {
                  const optionValueObj = this.getValueByCode(groupHandle, optionValue);
                  if (optionValueObj && optionValueObj.maxCount && optionValueObj.maxCount > 1) {
                    quantitySelector = this.createQuantitySelector(optionValueObj);
                    card.appendChild(quantitySelector);
                    // Set the correct quantity
                    const display = quantitySelector.querySelector('.quantity-display');
                    if (display) {
                      display.textContent = quantity;
                    }
                  }
                }
              }
            }
          }
        }
      });
    });
    
    // Enable next button if current step is complete
    const currentStep = this.steps[this.currentStep - 1];
    if (currentStep && this.isStepComplete(currentStep)) {
      document.getElementById('nextBtn').disabled = false;
    }
  }

  nextStep() {
    // Prevent navigation if inline group is active
    const inlineGroupActive = document.querySelector('.inline-group.visible');
    if (inlineGroupActive) {
      return;
    }
    
    // Check if we need to add color steps after exterior finish
    if (this.currentStep === 1 && this.state.selections['exterior_finish']) {
      this.addColorStepsAfterExteriorFinish();
    }
    
    if (this.currentStep < this.steps.length) {
      this.currentStep++;
      this.saveSelectionsToStorage();
      this.updateStepUI();
      this.updateProgress();
      
    } else {
      this.showSummary();
    }
  }

  previousStep() {
    // Prevent navigation if inline group is active
    const inlineGroupActive = document.querySelector('.inline-group.visible');
    if (inlineGroupActive) {
      return;
    }
    
    if (this.currentStep > 1) {
      this.currentStep--;
      this.saveSelectionsToStorage();
      this.updateStepUI();
      this.updateProgress();
    }
  }

  updateStepUI() {
    // Mark current step as visited
    this.visitedSteps.add(this.currentStep);
    
    // Re-render quiz steps to handle dynamic steps
    this.renderQuizSteps();
    
    // Update step visibility
    document.querySelectorAll('.quiz-step').forEach(step => {
      step.classList.remove('active');
    });
    const currentStepEl = document.querySelector(`.quiz-step[data-step="${this.currentStep}"]`);
    if (currentStepEl) {
      currentStepEl.classList.add('active');
    }
    
    // Restore selections for the current step
    this.restoreCurrentStepSelections();
    
    // Update step indicator circles
    this.updateStepIndicator();
    
    // Update back button visibility
    document.getElementById('backBtn').style.display = this.currentStep > 1 ? 'flex' : 'none';
    
    // Update return to summary button visibility
    const returnToSummaryBtn = document.getElementById('returnToSummaryBtn');
    if (returnToSummaryBtn) {
      returnToSummaryBtn.style.display = this.isEditingFromSummary ? 'flex' : 'none';
    }
    
    // Update next button text and state
    const nextBtn = document.getElementById('nextBtn');
    if (this.currentStep === this.steps.length) {
      nextBtn.innerHTML = `
        Complete
        <i data-lucide="check" class="icon-sm"></i>
      `;
    } else {
      nextBtn.innerHTML = `
        Next
        <i data-lucide="arrow-right" class="icon-sm"></i>
      `;
    }
    
    // Check if current step is complete
    const currentStep = this.steps[this.currentStep - 1];
    nextBtn.disabled = !(currentStep && this.isStepComplete(currentStep));
    
    // Update step title
    const totalStepsEl = document.getElementById('totalSteps');
    if (totalStepsEl) {
      totalStepsEl.textContent = this.steps.length;
    }
    document.getElementById('stepTitle').innerHTML = `Step ${this.currentStep} of <span id="totalSteps">${this.steps.length}</span>`;
    
    // Re-initialize icons
    if (typeof lucide !== 'undefined') {
      lucide.createIcons();
    }
  }

  restoreCurrentStepSelections() {
    // Get the current step
    const step = this.steps[this.currentStep - 1];
    if (!step) return;
    
    // Check each group in the current step
    step.groups.forEach(groupHandle => {
      const value = this.state.selections[groupHandle];
      if (value) {
        if (Array.isArray(value)) {
          // Single-select with one item (size, exterior_finish, etc.)
          if (value.length > 0) {
            const card = document.querySelector(`.quiz-step[data-step="${this.currentStep}"] [data-group="${groupHandle}"][data-value="${value[0]}"]`);
            if (card) {
              card.classList.add('selected');
            }
          }
        } else if (typeof value === 'object' && Object.keys(value).length > 0) {
          // Multi-select option with quantities (accessories, bundles, security/fire upgrades)
          for (const [optionValue, quantity] of Object.entries(value)) {
            const card = document.querySelector(`.quiz-step[data-step="${this.currentStep}"] [data-group="${groupHandle}"][data-value="${optionValue}"]`);
            if (card) {
              card.classList.add('selected');
              
              // Handle bundle dependencies - hide included accessories
              if (groupHandle === 'bundle') {
                const optionValueObj = this.getValueByCode(groupHandle, optionValue);

        // If a cabinet size was restored from external data, open watch_winders inline group
        try {
          const cabSel = this.state.selections['cabinet_size'];
          let selectedCabinetCode = undefined;
          if (Array.isArray(cabSel)) selectedCabinetCode = cabSel[0];
          else if (cabSel && typeof cabSel === 'object') selectedCabinetCode = Object.keys(cabSel)[0];
          else if (typeof cabSel === 'string') selectedCabinetCode = cabSel;

          // NOTE: Auto-opening watch_winders is now handled via inline_unlock_groups dependency
          // on _WW cabinet variants (e.g., CAB_SML_HALF_WW), not for regular cabinet options
          // This section has been removed to prevent opening watch_winders for regular cabinet selections
        } catch (e) {
          console.warn('Error while attempting to auto-open watch_winders inline group:', e);
        }
                if (optionValueObj && optionValueObj.dependencies && optionValueObj.dependencies.hides_included) {
                  this.hideIncludedAccessories(optionValueObj.dependencies.includes);
                }
              }
              
              // Restore quantity selector if it doesn't exist
              let quantitySelector = card.querySelector('.quantity-selector');
              if (!quantitySelector && quantity > 1) {
                const optionValueObj = this.getValueByCode(groupHandle, optionValue);
                if (optionValueObj && optionValueObj.maxCount && optionValueObj.maxCount > 1) {
                  quantitySelector = this.createQuantitySelector(optionValueObj);
                  card.appendChild(quantitySelector);
                  // Set the correct quantity
                  const display = quantitySelector.querySelector('.quantity-display');
                  if (display) {
                    display.textContent = quantity;
                  }
                }
              } else if (quantitySelector) {
                // Update existing quantity display
                const display = quantitySelector.querySelector('.quantity-display');
                if (display) {
                  display.textContent = quantity;
                }
              }
            }
          }
        }
      }
    });
  }

  updateStepIndicator() {
    const container = document.getElementById('stepIndicatorContainer');
    if (!container) return;
    
    const circles = container.querySelectorAll('.step-circle');
    
    // If the number of circles doesn't match the number of steps, recreate them
    if (circles.length !== this.steps.length) {
      this.createStepIndicator();
      // Now update the newly created circles
      this.updateStepIndicator();
      return;
    }
    
    circles.forEach((circle, index) => {
      const stepNumber = index + 1;
      const step = this.steps[stepNumber - 1];
      circle.classList.remove('current', 'completed', 'disabled', 'editing');
      
      if (stepNumber === this.currentStep) {
        circle.classList.add('current');
        // Add 'editing' class only when editing from summary
        if (this.isEditingFromSummary) {
          circle.classList.add('editing');
        }
        // Re-enable pointer events for current step
        circle.style.cursor = 'pointer';
        circle.style.pointerEvents = 'auto';
      } else if (this.visitedSteps.has(stepNumber)) {
        // Steps that have been visited should be marked as completed
        // This ensures visited steps stay highlighted when going backwards
        circle.classList.add('completed');
        // Re-enable pointer events for completed steps
        circle.style.cursor = 'pointer';
        circle.style.pointerEvents = 'auto';
      } else {
        // Future steps that haven't been visited are disabled
        circle.classList.add('disabled');
        // Disable pointer events for future steps
        circle.style.cursor = 'not-allowed';
        circle.style.pointerEvents = 'none';
      }
    });
  }

  updateProgress() {
    // This method is no longer needed but keeping for compatibility
    // Update total steps in the indicator
    const totalStepsEl = document.getElementById('totalSteps');
    if (totalStepsEl) {
      totalStepsEl.textContent = this.steps.length;
    }
  }

  createSafeZoomModal() {
    // Create zoom modal overlay if it doesn't exist
    if (document.getElementById('safeZoomOverlay')) return;
    
    const overlay = document.createElement('div');
    overlay.id = 'safeZoomOverlay';
    overlay.className = 'safe-zoom-overlay';
    overlay.innerHTML = `
      <div class="safe-zoom-container">
        <div class="safe-zoom-content">
          <div id="safeZoomImage"></div>
        </div>
      </div>
      <div class="safe-zoom-controls">
        <button class="safe-zoom-btn" id="safeZoomIn" title="Zoom In">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
          </svg>
        </button>
        <button class="safe-zoom-btn" id="safeZoomOut" title="Zoom Out">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M20 12H4" />
          </svg>
        </button>
        <button class="safe-zoom-btn" id="safeZoomReset" title="Reset Zoom">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>
      <button class="safe-zoom-close" id="safeZoomClose" title="Close">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    `;
    
    document.body.appendChild(overlay);
    
    // Close button handler
    document.getElementById('safeZoomClose').addEventListener('click', () => {
      this.closeSafeZoom();
    });
    
    // Close on overlay click (not on content)
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) {
        this.closeSafeZoom();
      }
    });
    
    // Close on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && overlay.classList.contains('active')) {
        this.closeSafeZoom();
      }
    });
    
    // Implement zoom and drag functionality
    const container = overlay.querySelector('.safe-zoom-container');
    const content = overlay.querySelector('.safe-zoom-content');
    const imageContainer = overlay.querySelector('#safeZoomImage');
    
    let isDragging = false;
    let startX = 0;
    let startY = 0;
    let currentX = 0;
    let currentY = 0;
    let scale = 1;
    const minScale = 1;
    const maxScale = 3;
    const zoomStep = 0.2;
    
    const updateTransform = () => {
      content.style.transform = `translate(${currentX}px, ${currentY}px)`;
      imageContainer.style.transform = `scale(${scale})`;
    };
    
    const setZoom = (newScale) => {
      scale = Math.max(minScale, Math.min(maxScale, newScale));
      
      // Reset position when zooming out to minimum
      if (scale === minScale) {
        currentX = 0;
        currentY = 0;
      }
      
      updateTransform();
      
      // Update button states
      const zoomInBtn = document.getElementById('safeZoomIn');
      const zoomOutBtn = document.getElementById('safeZoomOut');
      if (zoomInBtn) zoomInBtn.disabled = scale >= maxScale;
      if (zoomOutBtn) zoomOutBtn.disabled = scale <= minScale;
    };
    
    // Mouse wheel zoom
    container.addEventListener('wheel', (e) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -zoomStep : zoomStep;
      setZoom(scale + delta);
    }, { passive: false });
    
    // Zoom controls
    document.getElementById('safeZoomIn').addEventListener('click', () => {
      setZoom(scale + zoomStep);
    });
    
    document.getElementById('safeZoomOut').addEventListener('click', () => {
      setZoom(scale - zoomStep);
    });
    
    document.getElementById('safeZoomReset').addEventListener('click', () => {
      scale = minScale;
      currentX = 0;
      currentY = 0;
      content.style.transition = 'transform 0.3s ease';
      updateTransform();
      setTimeout(() => {
        content.style.transition = 'transform 0.1s ease-out';
      }, 300);
    });
    
    // Drag functionality
    container.addEventListener('mousedown', (e) => {
      isDragging = true;
      startX = e.clientX - currentX;
      startY = e.clientY - currentY;
      content.style.transition = 'none';
      container.style.cursor = 'grabbing';
    });
    
    container.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      
      e.preventDefault();
      currentX = e.clientX - startX;
      currentY = e.clientY - startY;
      
      updateTransform();
    });
    
    container.addEventListener('mouseup', () => {
      isDragging = false;
      content.style.transition = 'transform 0.1s ease-out';
      container.style.cursor = 'grab';
    });
    
    container.addEventListener('mouseleave', () => {
      if (isDragging) {
        isDragging = false;
        content.style.transition = 'transform 0.1s ease-out';
        container.style.cursor = 'grab';
      }
    });
  }
  
  openSafeZoom() {
    const summaryConfiguredSafe = document.getElementById('summaryConfiguredSafe');
    if (!summaryConfiguredSafe) return;
    
    // Create modal if it doesn't exist
    this.createSafeZoomModal();
    
    const overlay = document.getElementById('safeZoomOverlay');
    const zoomImage = document.getElementById('safeZoomImage');
    
    // Clone the configured safe content
    zoomImage.innerHTML = summaryConfiguredSafe.innerHTML;
    
    // Reset transform and zoom
    const content = overlay.querySelector('.safe-zoom-content');
    content.style.transform = 'translate(0, 0)';
    zoomImage.style.transform = 'scale(1)';
    
    // Reset zoom button states
    const zoomInBtn = document.getElementById('safeZoomIn');
    const zoomOutBtn = document.getElementById('safeZoomOut');
    if (zoomInBtn) zoomInBtn.disabled = false;
    if (zoomOutBtn) zoomOutBtn.disabled = true;
    
    // Show modal
    overlay.classList.add('active');
    document.body.style.overflow = 'hidden';
  }
  
  closeSafeZoom() {
    const overlay = document.getElementById('safeZoomOverlay');
    if (overlay) {
      overlay.classList.remove('active');
      document.body.style.overflow = '';
    }
  }

  loadSavedConfiguration(rawConfigData) {
    console.log('📂 Loading saved configuration:', rawConfigData);
    
    try {
      // Validate that we have the necessary data
      if (!rawConfigData || !rawConfigData.selections) {
        console.error('Invalid configuration data');
        alert('Invalid configuration data. Please try again.');
        return;
      }
      
      // Check if the product matches
      if (rawConfigData.productId && rawConfigData.productId !== this.productId) {
        console.warn('Configuration is for a different product');
        const proceed = confirm('This configuration is for a different product. Do you want to load it anyway?');
        if (!proceed) {
          return;
        }
      }
      
      // Restore the selections
      this.state.selections = JSON.parse(JSON.stringify(rawConfigData.selections)); // Deep copy
      
      // Restore other state properties
      if (rawConfigData.selectedModel) {
        this.state.selectedModel = rawConfigData.selectedModel;
      }
      
      if (rawConfigData.currentConfiguration) {
        this.state.currentConfiguration = rawConfigData.currentConfiguration;
      }
      
      if (rawConfigData.rivetColorOverride !== undefined) {
        this.state.rivetColorOverride = rawConfigData.rivetColorOverride;
      }
      
      if (rawConfigData.craneHingeColorOverride !== undefined) {
        this.state.craneHingeColorOverride = rawConfigData.craneHingeColorOverride;
      }
      
      // Recalculate prices with current pricing
      this.calculatePrice();
      
      // Check if pricing has changed from the saved configuration
      const priceDifference = rawConfigData.totalPrice ? 
        Math.abs(this.state.totalPrice - rawConfigData.totalPrice) : 0;
      
      if (priceDifference > 0.01) {
        console.log('⚠️ Price has changed from saved configuration');
        console.log(`Original: $${rawConfigData.totalPrice}, Current: $${this.state.totalPrice}`);
      }
      
      // Validate that all saved options are still available
      const unavailableOptions = [];
      for (const groupHandle in this.state.selections) {
        const selections = this.state.selections[groupHandle];
        const codes = Array.isArray(selections) ? selections : Object.keys(selections);
        
        codes.forEach(code => {
          const value = this.getValueByCode(groupHandle, code);
          if (!value) {
            unavailableOptions.push(`${groupHandle}: ${code}`);
          }
        });
      }
      
      if (unavailableOptions.length > 0) {
        console.warn('⚠️ Some options are no longer available:', unavailableOptions);
        alert(`Warning: Some options from your saved configuration are no longer available and have been removed:\n\n${unavailableOptions.join('\n')}\n\nPricing has been updated to reflect current options.`);
        
        // Remove unavailable options
        for (const groupHandle in this.state.selections) {
          const selections = this.state.selections[groupHandle];
          
          if (Array.isArray(selections)) {
            this.state.selections[groupHandle] = selections.filter(code => {
              return this.getValueByCode(groupHandle, code) !== null;
            });
          } else if (typeof selections === 'object') {
            for (const code in selections) {
              if (!this.getValueByCode(groupHandle, code)) {
                delete this.state.selections[groupHandle][code];
              }
            }
          }
        }
        
        // Recalculate price after removing unavailable options
        this.calculatePrice();
      }
      
      // Update UI to reflect loaded configuration
      this.updatePriceDisplay();
      
      // Show success message if price changed
      if (priceDifference > 0.01) {
        setTimeout(() => {
          const summaryHeader = document.querySelector('.summary-header p');
          if (summaryHeader) {
            const originalText = summaryHeader.textContent;
            summaryHeader.innerHTML = `<span style="color: #f59e0b;">⚠️ Pricing updated to current rates (Original: $${rawConfigData.totalPrice.toFixed(2)})</span>`;
            setTimeout(() => {
              summaryHeader.textContent = originalText;
            }, 5000);
          }
        }, 500);
      }
      
      // Notify blocks about the restored selections so blocks can enable their UI
      try {
        this.notifyConfigurationUpdate();
      } catch (err) {
        console.warn('notifyConfigurationUpdate failed after loading saved configuration', err);
      }

      // Open summary modal to show the loaded configuration
      this.showSummary(false);
      
      console.log('✅ Configuration loaded successfully');
      // Create the configured temporary variant on the backend so the saved quote
      // can be added to cart immediately (enables add button and attaches properties)
      // Create backend configured variant asynchronously (don't block the UI)
      this.createConfiguredVariantOnBackend().catch(err => {
        console.warn('Could not create backend variant for saved configuration:', err);
      });
      
    } catch (error) {
      console.error('❌ Error loading configuration:', error);
      alert('There was an error loading your configuration. Please try again.');
    }
  }

  /**
   * Create the configured temporary variant on the backend for the current selections
   * and store the returned variant id + properties so the simple add-to-cart button
   * can add the configured product immediately without rebuilding.
   */
  async createConfiguredVariantOnBackend() {
    try {
      // Prepare selections copy and include any color overrides
      const selectedOptions = JSON.parse(JSON.stringify(this.state.selections || {}));
      if (this.state.rivetColorOverride) {
        selectedOptions.rivet_color = this.state.rivetColorOverride;
      }
      if (this.state.craneHingeColorOverride) {
        selectedOptions.crane_hinge_color = this.state.craneHingeColorOverride;
      }

      const payload = {
        query: `
          mutation CreateConfiguredVariant(
            $productId: String!
            $selectedOptions: String!
            $quantity: Float
          ) {
            createConfiguredVariant(
              productId: $productId
              selectedOptions: $selectedOptions
              quantity: $quantity
            ) {
              success
              errors { message code }
              result
            }
          }
        `,
        variables: {
          productId: String(this.data.productId),
          selectedOptions: JSON.stringify(selectedOptions),
          quantity: 1
        }
      };

      const response = await fetch('/apps/meta-variant-builder-api', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const text = await response.text();
        console.error('Backend error creating configured variant:', text);
        return null;
      }

      const result = await response.json();
      if (result.errors && result.errors.length) {
        console.error('GraphQL errors creating configured variant:', result.errors);
        return null;
      }

      const data = result.data?.createConfiguredVariant;
      if (!data || !data.success) {
        console.error('Configured variant creation failed:', data?.errors || data);
        return null;
      }

      const variantData = data.result;
      if (!variantData || !variantData.variantId) {
        console.error('No variant returned from backend');
        return null;
      }

      // Convert GID variant id to numeric id if needed
      let variantId = variantData.variantId;
      if (typeof variantId === 'string' && variantId.startsWith('gid://')) {
        variantId = variantId.replace('gid://shopify/ProductVariant/', '');
      }

      // Save to state so the simple add-to-cart flow can use it
      this.state.selectedVariantId = variantId;
      this.state.selectedVariantProperties = variantData.properties || {};

      // Enable simple add-to-cart button (if present)
      const addToCartBtn = document.getElementById('simpleAddToCartBtn');
      if (addToCartBtn) {
        addToCartBtn.disabled = false;
      }

      console.log('✅ Backend configured variant ready', { variantId, properties: this.state.selectedVariantProperties });

      return variantData;
    } catch (err) {
      console.error('Error creating configured variant on backend:', err);
      return null;
    }
  }

  showSummary(keepDropdownOpen = false) {
    // Clear editing flag when showing summary
    this.isEditingFromSummary = false;
    this.saveSelectionsToStorage();
    
    // Close quiz modal
    document.getElementById('quizModal').classList.remove('active');
    
    // Build summary content
    const summaryBody = document.getElementById('summaryBody');
    summaryBody.innerHTML = '';
    
    // Wrap content in collapsible container for mobile
    const isMobile = window.innerWidth <= 768;
    if (isMobile) {
      summaryBody.innerHTML = `
        <button class="summary-collapse-toggle ${keepDropdownOpen ? 'open' : ''}" id="summaryCollapseToggle">
          <span>${keepDropdownOpen ? 'Hide Configuration Details' : 'View Configuration Details'}</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </button>
        <div class="summary-collapsible-content ${keepDropdownOpen ? 'open' : ''}" id="summaryCollapsibleContent">
          <div class="summary-body-inner" id="summaryBodyInner"></div>
        </div>
      `;
    }
    
    const contentContainer = isMobile ? document.getElementById('summaryBodyInner') : summaryBody;
    
    // Render the configured safe image in summary using JSON manifest.
    // Support legacy and brand-specific group handles (Fort Knox, AMSEC, Liberty, etc.).
    const hasRenderableSelection = !!(
      this.state.selections['size']?.[0] ||
      this.state.selections[this.currentSizeGroupHandle || 'size']?.[0] ||
      this.state.selections['exterior_finish']?.[0] ||
      this.state.selections['color_single']?.[0] ||
      this.state.selections['amsec_color']?.[0] ||
      this.state.selections['ls_color']?.[0] ||
      this.state.selections['body_color']?.[0] ||
      this.state.selections['door_color']?.[0] ||
      this.state.selections['ls_body_color']?.[0] ||
      this.state.selections['ls_door_color']?.[0]
    );
    
    if (hasRenderableSelection) {
      console.log('🎨 Summary: Has renderable selection, rendering safe image');
      this.renderSafeImage('summary');
    } else {
      console.log('🎨 Summary: No size/finish/color selection, using fallback image');
      // Use featured product image as fallback
      const summaryConfiguredSafe = document.getElementById('summaryConfiguredSafe');
      if (summaryConfiguredSafe) {
        const featuredImage = document.getElementById('safeImage');
        if (featuredImage) {
          summaryConfiguredSafe.innerHTML = `<img src="${featuredImage.src}" alt="Product" style="width: 100%; height: auto; object-fit: contain;" />`;
        }
      }
    }
    
    // Add disclaimers under the summary-size-image div
    const summarySizeImage = document.getElementById('summarySizeImage');
    if (summarySizeImage && hasRenderableSelection) {
      // Remove any existing disclaimers first
      const existingDisclaimer = summarySizeImage.parentElement?.querySelector('.image-disclaimers');
      if (existingDisclaimer) {
        existingDisclaimer.remove();
      } 
      
      // Get the selected finish to check if it's distressed.
      // For products without exterior_finish, infer finish suffix from active color code.
      const selectedFinish = this.state.selections['exterior_finish']?.[0];
      const inferredColorValue =
        this.state.selections['amsec_color']?.[0] ||
        this.state.selections['ls_color']?.[0] ||
        this.state.selections['color_single']?.[0] ||
        this.state.selections['body_color']?.[0] ||
        this.state.selections['ls_body_color']?.[0] ||
        this.state.selections['door_color']?.[0] ||
        this.state.selections['ls_door_color']?.[0];
      const extractedFinish = inferredColorValue ? inferredColorValue.split('_').pop() : null; // e.g., GLOSS/TEXTURED/MATTE
      const normalizedFinish = selectedFinish 
        ? selectedFinish.toLowerCase().replace(/_/g, '-')
        : extractedFinish 
          ? extractedFinish.toLowerCase().replace(/_/g, '-')
          : '';
      const isDistressed = normalizedFinish === 'distressed';
      
      // Create disclaimer container
      const disclaimerContainer = document.createElement('div');
      disclaimerContainer.className = 'image-disclaimers';
      disclaimerContainer.style.padding = '12px 16px';
      disclaimerContainer.style.backgroundColor = '#f8f9fa';
      disclaimerContainer.style.borderRadius = '6px';
      disclaimerContainer.style.fontSize = '13px';
      disclaimerContainer.style.lineHeight = '1.6';
      disclaimerContainer.style.color = '#6c757d';
      disclaimerContainer.style.border = '1px solid #e9ecef';
      
      // Color disclaimer (always shown)
      const colorDisclaimer = document.createElement('p');
      colorDisclaimer.style.margin = '0';
      colorDisclaimer.style.fontStyle = 'italic';
      colorDisclaimer.innerHTML = '<strong>Note:</strong> This image is computer-generated. Actual colors may vary slightly from what is displayed.';
      disclaimerContainer.appendChild(colorDisclaimer);
      
      // Distressed disclaimer (only for distressed finish)
      if (isDistressed) {
        const distressedDisclaimer = document.createElement('p');
        distressedDisclaimer.style.margin = '8px 0 0 0';
        distressedDisclaimer.style.fontStyle = 'italic';
        distressedDisclaimer.innerHTML = '<strong>Distressed Finish:</strong> Each distressed safe is unique and hand-finished. Variations in appearance are normal and add to the character of your safe.';
        disclaimerContainer.appendChild(distressedDisclaimer);
      }
      
      // Insert after the summary-size-image div
      summarySizeImage.parentElement.insertBefore(disclaimerContainer, summarySizeImage.nextSibling);
    }
    
    // Add Model + Brand card at the very top of the summary
    const currentModel = this.getCurrentModel();
    if (currentModel) {
      const modelName = currentModel.name || '';
      const brandName = currentModel.brand || '';
      const featuredImg = document.getElementById('safeImage');
      const productImageSrc = featuredImg ? featuredImg.src : '';

      const modelCard = document.createElement('div');
      modelCard.className = 'summary-card summary-model-card';
      modelCard.innerHTML = `
        ${productImageSrc ? `<img src="${productImageSrc}" alt="${modelName}" class="summary-model-card__image" />` : ''}
        <div class="summary-model-card__info">
          <div class="summary-model-card__brand">${brandName}</div>
          <div class="summary-model-card__name">${modelName}</div>
        </div>
      `;
      contentContainer.appendChild(modelCard);
    }

    // Add size to summary cards - handle all brand size groups
    const brandSizeGroupMap = {
      'Fort Knox': 'size',
      'American Security': 'amsec_size',
      'Liberty Safe': 'ls_size'
      // Add more brands here as needed
    };
    
    // Check all possible size group handles
    const allSizeHandles = Object.values(brandSizeGroupMap);
    let sizeValue = null;
    let sizeGroupHandle = null;
    
    for (const handle of allSizeHandles) {
      if (this.state.selections[handle]?.[0]) {
        sizeValue = this.state.selections[handle][0];
        sizeGroupHandle = handle;
        break;
      }
    }
    
    if (sizeValue && sizeGroupHandle) {
      const sizeGroup = this.data.optionGroups.find(g => g.handle === sizeGroupHandle);
      const sizeOption = sizeGroup?.values.find(v => v.code === sizeValue);
      if (sizeOption) {
        const image = sizeOption.sizeImage || sizeOption.optionImage || sizeOption.productImage;
        const price = sizeOption.priceModifierValue || 0;
        // Strip model name prefix from size label if it duplicates (e.g. "Colonial 23" when model is "Colonial")
        const modelNamePrefix = currentModel?.name ? currentModel.name.trim() + ' ' : '';
        const sizeLabel = (modelNamePrefix && sizeOption.label.startsWith(modelNamePrefix))
          ? sizeOption.label.slice(modelNamePrefix.length)
          : sizeOption.label;
        contentContainer.innerHTML += this.createSummaryCard(sizeGroup.displayName || 'Size', sizeLabel, 'box', null, image, null, false, price, false, sizeGroupHandle, sizeValue);

        // If this size option unlocks inline groups (e.g., watch_winders), render those inline selections directly under the size card
        try {
          const optionDeps = sizeOption.dependencies ? (typeof sizeOption.dependencies === 'string' ? JSON.parse(sizeOption.dependencies) : sizeOption.dependencies) : null;
          if (optionDeps && optionDeps.inline_unlock_groups && Array.isArray(optionDeps.inline_unlock_groups)) {
            optionDeps.inline_unlock_groups.forEach(inlineGroupHandle => {
              const namespaceKey = `${inlineGroupHandle}_for_${sizeGroupHandle}_${sizeValue}`;
              const inlineSelections = this.state.selections[namespaceKey];
              if (!inlineSelections) return;

              // Mark this namespace as rendered so the fallback path doesn't duplicate it
              renderedNamespaceKeys.add(namespaceKey);

              const inlineGroup = this.data.optionGroups.find(g => g.handle === inlineGroupHandle);
              if (!inlineGroup) return;

              for (const inlineCode of Object.keys(inlineSelections)) {
                const inlineValue = inlineGroup.values.find(v => v.code === inlineCode);
                if (!inlineValue) continue;
                const inlinePrice = this.getOptionPrice(inlineValue, inlineGroupHandle);
                contentContainer.innerHTML += this.createSummaryCard(
                  inlineGroup.displayName || inlineGroup.handle,
                  inlineValue.label,
                  inlineGroup.icon || 'watch',
                  null,
                  inlineValue.optionImage || inlineValue.productImage || inlineValue.sizeImage,
                  inlineValue.hexColor || null,
                  this.checkIfIncluded(inlineValue.code),
                  inlinePrice,
                  inlineGroup.isOptional || false,
                  inlineGroupHandle,
                  inlineCode
                );
              }
            });
          }
        } catch (e) {
          console.warn('Failed to render inline unlock groups for size option', e);
        }
        // (Previously we rendered namespaced `watch_winders_for_cabinet_size_*` here,
        // but that caused watch_winders to appear under the product Size card.
        // Namespaced watch_winders should be attached to the `cabinet_size` parent
        // inside the step rendering below. See fallback handling in the step loop.)
      }
    }
    
    // Track namespace keys already rendered (to avoid duplicate watch winder cards)
    const renderedNamespaceKeys = new Set();

    // Add other selections by step
    this.steps.forEach((step, index) => {
      // Collect all selections for this step
      const stepSelections = [];
      
      step.groups.forEach(groupHandle => {
        const value = this.state.selections[groupHandle];
        
        if (value) {
          const group = this.data.optionGroups.find(g => g.handle === groupHandle);
          
          if (Array.isArray(value) && value.length > 0) {
            // Single-select option
            const option = group?.values.find(v => v.code === value[0]);
            if (option) {
              const isIncluded = this.checkIfIncluded(option.code);
              const price = this.getOptionPrice(option, groupHandle);
              stepSelections.push({
                groupName: group.displayName,
                optionName: option.label,
                image: option.optionImage || option.productImage || option.sizeImage,
                hexColor: option.hexColor,
                isIncluded: isIncluded,
                price: price,
                isOptional: group.isOptional || false,
                groupHandle: groupHandle,
                optionCode: option.code
              });

              // Check for inline unlock groups on single-select options (e.g., watch_winders)
              try {
                const optionValue = option;
                const deps = optionValue && optionValue.dependencies ? (typeof optionValue.dependencies === 'string' ? JSON.parse(optionValue.dependencies) : optionValue.dependencies) : null;
                if (deps && deps.inline_unlock_groups && Array.isArray(deps.inline_unlock_groups)) {
                  deps.inline_unlock_groups.forEach(inlineGroupHandle => {
                    const namespaceKey = `${inlineGroupHandle}_for_${groupHandle}_${option.code}`;
                    // Skip if already rendered (e.g., under the size card)
                    if (renderedNamespaceKeys.has(namespaceKey)) return;
                    const inlineSelections = this.state.selections[namespaceKey];
                    if (inlineSelections && Object.keys(inlineSelections).length > 0) {
                      renderedNamespaceKeys.add(namespaceKey);
                      // Calculate count and total price
                      const count = Object.keys(inlineSelections).length;
                      let totalPrice = 0;
                      const inlineGroup = this.data.optionGroups.find(g => g.handle === inlineGroupHandle);
                      if (inlineGroup) {
                        for (const inlineCode in inlineSelections) {
                          const inlineValue = inlineGroup.values.find(v => v.code === inlineCode);
                          if (inlineValue) {
                            const inlinePrice = this.getOptionPrice(inlineValue, inlineGroupHandle);
                            totalPrice += inlinePrice;
                          }
                        }
                      }

                      // Insert inline selections immediately after the parent selection
                      if (inlineGroup) {
                        for (const inlineCode in inlineSelections) {
                          const inlineValue = inlineGroup.values.find(v => v.code === inlineCode);
                          if (!inlineValue) continue;
                          const inlinePrice = this.getOptionPrice(inlineValue, inlineGroupHandle);
                          stepSelections.push({
                            groupName: inlineGroup.displayName || inlineGroup.handle,
                            optionName: inlineValue.label,
                            image: inlineValue.optionImage || inlineValue.productImage || inlineValue.sizeImage,
                            hexColor: inlineValue.hexColor || null,
                            isIncluded: this.checkIfIncluded(inlineValue.code),
                            price: inlinePrice,
                            isOptional: inlineGroup.isOptional || false,
                            groupHandle: inlineGroupHandle,
                            optionCode: inlineCode,
                            isInlineGroup: true
                          });
                        }
                      }
                    }
                  });
                }
              } catch (e) {
                console.warn('Failed to parse inline_unlock_groups for single-select option', e);
              }

              // Fallback: Attach any namespaced `watch_winders_for_{groupHandle}_{option.code}`
              // under the parent selection even if the parent option doesn't declare deps.inline_unlock_groups.
              try {
                const inlineGroupHandle = 'watch_winders';
                const namespaceKey = `${inlineGroupHandle}_for_${groupHandle}_${option.code}`;
                // Skip if already rendered by the inline_unlock_groups path or size card
                if (!renderedNamespaceKeys.has(namespaceKey)) {
                  const inlineSelectionsFallback = this.state.selections[namespaceKey];
                  if (inlineSelectionsFallback && Object.keys(inlineSelectionsFallback).length > 0) {
                    renderedNamespaceKeys.add(namespaceKey);
                    const inlineGroup = this.data.optionGroups.find(g => g.handle === inlineGroupHandle);
                    if (inlineGroup) {
                      for (const inlineCode in inlineSelectionsFallback) {
                        const inlineValue = inlineGroup.values.find(v => v.code === inlineCode);
                        if (!inlineValue) continue;
                        const inlinePrice = this.getOptionPrice(inlineValue, inlineGroupHandle);
                        stepSelections.push({
                          groupName: inlineGroup.displayName || inlineGroup.handle,
                          optionName: inlineValue.label,
                          image: inlineValue.optionImage || inlineValue.productImage || inlineValue.sizeImage,
                          hexColor: inlineValue.hexColor || null,
                          isIncluded: this.checkIfIncluded(inlineValue.code),
                          price: inlinePrice,
                          isOptional: inlineGroup.isOptional || false,
                          groupHandle: inlineGroupHandle,
                          optionCode: inlineCode,
                          isInlineGroup: true
                        });
                      }
                    }
                  }
                }
              } catch (e) {
                console.warn('Failed to attach namespaced watch_winders under parent option', e);
              }
            }
          } else if (typeof value === 'object' && Object.keys(value).length > 0) {
            // Multi-select options with quantities
            for (const [optionCode, quantity] of Object.entries(value)) {
              const option = group?.values.find(v => v.code === optionCode);
              if (option) {
                const isIncluded = this.checkIfIncluded(option.code);
                const price = this.getOptionPrice(option, groupHandle) * quantity;
                stepSelections.push({
                  groupName: group.displayName,
                  optionName: `${option.label} (${quantity}x)`,
                  image: option.optionImage || option.productImage || option.sizeImage,
                  hexColor: option.hexColor,
                  isIncluded: isIncluded,
                  price: price,
                  isOptional: group.isOptional || false,
                  groupHandle: groupHandle,
                  optionCode: optionCode,
                  isInlineGroup: false,
                  inlineGroupCount: null,
                  inlineGroupPrice: null
                });
                
                // Check if this option has inline_unlock_groups dependency
                const optionValue = this.getValueByCode(groupHandle, optionCode);
                if (optionValue && optionValue.dependencies) {
                  try {
                    const deps = typeof optionValue.dependencies === 'string' 
                      ? JSON.parse(optionValue.dependencies) 
                      : optionValue.dependencies;
                    
                    if (deps.inline_unlock_groups && Array.isArray(deps.inline_unlock_groups)) {
                      // Check if there are selections in the namespaced inline groups
                      deps.inline_unlock_groups.forEach(inlineGroupHandle => {
                        // Create namespace key for this trigger option
                        const namespaceKey = `${inlineGroupHandle}_for_${groupHandle}_${optionCode}`;
                        const inlineSelections = this.state.selections[namespaceKey];
                        
                        if (inlineSelections && Object.keys(inlineSelections).length > 0) {
                          // Calculate count and total price
                          const count = Object.keys(inlineSelections).length;
                          let totalPrice = 0;
                          
                          const inlineGroup = this.data.optionGroups.find(g => g.handle === inlineGroupHandle);
                          if (inlineGroup) {
                            for (const inlineCode in inlineSelections) {
                              const inlineValue = inlineGroup.values.find(v => v.code === inlineCode);
                              if (inlineValue) {
                                const inlinePrice = this.getOptionPrice(inlineValue, inlineGroupHandle);
                                totalPrice += inlinePrice;
                              }
                            }
                          }
                          
                          // Update the last added selection with inline group data
                          const lastSelection = stepSelections[stepSelections.length - 1];
                          lastSelection.isInlineGroup = true;
                          lastSelection.inlineGroupCount = count;
                          lastSelection.inlineGroupPrice = totalPrice;
                        }
                      });
                    }
                  } catch (e) {
                    console.warn('Failed to parse dependencies:', e);
                  }
                }
              } else {
                console.warn(`      ⚠️ Option not found for code: ${optionCode}`);
              }
            }
          }
        }
      });
      
      // Create summary card for this step
      if (stepSelections.length > 0) {
        if (step.isTwoTone && stepSelections.length === 2) {
          // For two-tone steps, create separate cards for each color
          stepSelections.forEach((selection, selectionIndex) => {
            contentContainer.innerHTML += this.createSummaryCard(
              `${step.title} - ${selection.groupName}`,
              selection.optionName,
              'droplet',
              index + 1,
              selection.image,
              selection.hexColor,
              selection.isIncluded,
              selection.price,
              selection.isOptional,
              selection.groupHandle,
              selection.optionCode,
              selection.inlineGroupCount,
              selection.inlineGroupPrice
            );
          });
        } else if (stepSelections.length > 1) {
          // Multiple selections - create a card for each
          stepSelections.forEach((selection) => {
            contentContainer.innerHTML += this.createSummaryCard(
              selection.groupName,
              selection.optionName,
              step.icon,
              index + 1,
              selection.image,
              selection.hexColor,
              selection.isIncluded,
              selection.price,
              selection.isOptional,
              selection.groupHandle,
              selection.optionCode,
              selection.inlineGroupCount,
              selection.inlineGroupPrice
            );
          });
        } else {
          // Single selection
          const firstSelection = stepSelections[0];
          contentContainer.innerHTML += this.createSummaryCard(
            step.title,
            firstSelection.optionName,
            step.icon,
            index + 1,
            firstSelection.image,
            firstSelection.hexColor,
            firstSelection.isIncluded,
            firstSelection.price,
            firstSelection.isOptional,
            firstSelection.groupHandle,
            firstSelection.optionCode,
            firstSelection.inlineGroupCount,
            firstSelection.inlineGroupPrice
          );
        }
      }
    });
    
    // Calculate final pricing
    this.calculatePrice();

    // (Inline items are inserted next to their parent above.)
    
    // Show summary modal
    setTimeout(() => {
      const summaryModal = document.getElementById('summaryModal');
      summaryModal.classList.add('active');
      document.body.classList.add('modal-open');
      document.body.style.overflow = 'hidden';
      
      if (typeof lucide !== 'undefined') {
        lucide.createIcons();
      }
      
      // Add click handlers to summary cards and edit buttons
      document.querySelectorAll('.summary-card').forEach(card => {
        const stepNumber = card.dataset.step;
        if (stepNumber) {
          card.addEventListener('click', (e) => {
            // Don't navigate if clicking delete icon
            if (e.target.closest('.summary-delete')) {
              return;
            }
            
            // Check if clicking edit button or the card itself
            const isEditButton = e.target.closest('.summary-edit');
            
            // Set editing flag to show Return to Summary button and pulsating ring
            this.isEditingFromSummary = true;
            
            // Go to the step
            this.goToStep(parseInt(stepNumber));
          });
        }
      });
      
      // Add delete handlers for optional selections
      document.querySelectorAll('.summary-delete').forEach(deleteBtn => {
        deleteBtn.addEventListener('click', (e) => {
          e.stopPropagation(); // Prevent card click
          const groupHandle = deleteBtn.dataset.group;
          const optionCode = deleteBtn.dataset.option;
          
          if (groupHandle && optionCode) {
            this.deleteOptionalSelection(groupHandle, optionCode);
          }
        });
      });
      
      // Add click handler to configured safe for zoom
      const summaryConfiguredSafe = document.getElementById('summaryConfiguredSafe');
      if (summaryConfiguredSafe) {
        summaryConfiguredSafe.style.cursor = 'zoom-in';
        summaryConfiguredSafe.addEventListener('click', () => {
          this.openSafeZoom();
        });
      }
      
      // Add collapse toggle handler for mobile
      if (isMobile) {
        const toggleBtn = document.getElementById('summaryCollapseToggle');
        const collapsibleContent = document.getElementById('summaryCollapsibleContent');
        
        if (toggleBtn && collapsibleContent) {
          toggleBtn.addEventListener('click', () => {
            const isOpen = collapsibleContent.classList.contains('open');
            
            if (isOpen) {
              collapsibleContent.classList.remove('open');
              toggleBtn.classList.remove('open');
              toggleBtn.querySelector('span').textContent = 'View Configuration Details';
            } else {
              collapsibleContent.classList.add('open');
              toggleBtn.classList.add('open');
              toggleBtn.querySelector('span').textContent = 'Hide Configuration Details';
            }
          });
        }
      }
      
      // Initialize Lucide icons for summary cards
      if (typeof lucide !== 'undefined') {
        lucide.createIcons();
      }
    }, 300);
  }

  createSummaryCard(title, value, icon, stepNumber, image = null, hexColor = null, isIncluded = false, price = null, isOptional = false, groupHandle = null, optionCode = null, inlineGroupCount = null, inlineGroupPrice = null) {
    // Remove 'Fort Knox ' prefix from title if present
    const cleanTitle = title.replace(/^Fort Knox\s+/i, '');
    
    let imageHtml = '';
    if (image) {
      imageHtml = `
        <div class="summary-image">
          <img src="${image}" alt="${value}" />
        </div>
      `;
    } else if (hexColor) {
      imageHtml = `
        <div class="summary-color" style="background-color: ${hexColor}"></div>
      `;
    } else {
      imageHtml = `
        <div class="summary-icon">
          <i data-lucide="${icon}" class="icon-md"></i>
        </div>
      `;
    }
    
    const includedBadge = isIncluded ? '<span class="summary-included-badge">INCLUDED</span>' : '';
    
    // Add inline group count badge if present
    const inlineCountBadge = inlineGroupCount ? ` <span class="inline-group-badge">(${inlineGroupCount}x)</span>` : '';
    
    // Add rivet color info if this is a rivet trim
    let rivetColorInfo = '';
    if (optionCode === 'RIVET_TRIM') {
      const rivetColor = this.state.rivetColorOverride;
      if (rivetColor) {
        const colorOption = this.getValueByCode('color_single', rivetColor);
        const formatColorName = (name) => {
          return name.split('_').map(word => word.charAt(0) + word.slice(1).toLowerCase()).join(' ');
        };
        const displayName = colorOption?.displayName || formatColorName(rivetColor);
        rivetColorInfo = ` <span class="rivet-color-summary">(${displayName})</span>`;
      } else {
        rivetColorInfo = ' <span class="rivet-color-summary">(Body Color)</span>';
      }
    }
    
    // Add crane hinge color info if this is a crane hinge
    let craneHingeColorInfo = '';
    if (optionCode === 'CRANE' || optionCode === 'CRANE_BLANK') {
      const craneHingeColor = this.state.craneHingeColorOverride;
      if (craneHingeColor) {
        const colorOption = this.getValueByCode('color_single', craneHingeColor);
        const formatColorName = (name) => {
          return name.split('_').map(word => word.charAt(0) + word.slice(1).toLowerCase()).join(' ');
        };
        const displayName = colorOption?.displayName || formatColorName(craneHingeColor);
        craneHingeColorInfo = ` <span class="crane-hinge-color-summary">(${displayName})</span>`;
      } else {
        craneHingeColorInfo = ' <span class="crane-hinge-color-summary">(Body Color)</span>';
      }
    }
    
    // Calculate total price including inline group price
    const totalPrice = inlineGroupPrice !== null ? (price || 0) + inlineGroupPrice : price;
    
    // Add price display if provided (subtle but noticeable)
    const priceDisplay = totalPrice && totalPrice !== 0 ? ` <span class="summary-price">(${totalPrice > 0 ? '+' : ''}${this.formatPrice(Math.abs(totalPrice))})</span>` : '';
    
    // Add custom crane hinge color price if applicable
    let craneHingePriceDisplay = '';
    if ((optionCode === 'CRANE' || optionCode === 'CRANE_BLANK') && this.state.craneHingeColorOverride) {
      const selectedFinish = this.state.selections['exterior_finish']?.[0];
      const normalizedFinish = selectedFinish?.toLowerCase().replace(/_/g, '-');
      let bodyColor = null;
      
      if (normalizedFinish === 'two-tone' || normalizedFinish === 'two-tone-fade') {
        bodyColor = this.state.selections['body_color']?.[0];
      } else {
        bodyColor = this.state.selections['color_single']?.[0];
      }
      
      // If crane hinge color differs from body color, show the two-tone price
      if (bodyColor && this.state.craneHingeColorOverride !== bodyColor) {
        const finishGroup = this.data.optionGroups.find(g => g.handle === 'exterior_finish');
        const twoToneOption = finishGroup?.values.find(v => v.code === 'TWO_TONE');
        
        if (twoToneOption) {
          const twoTonePrice = this.getOptionPrice(twoToneOption, 'exterior_finish');
          craneHingePriceDisplay = ` <span class="summary-price">(+${this.formatPrice(twoTonePrice)})</span>`;
        }
      }
    }
    
    const cardClass = isIncluded ? 'summary-card summary-card-included' : 'summary-card';
    
    const editButton = (stepNumber && !isIncluded) ? `
      <button class="summary-edit" data-step="${stepNumber}" ${optionCode === 'RIVET_TRIM' ? 'data-open-rivet-selector="true"' : ''} ${optionCode === 'CRANE' || optionCode === 'CRANE_BLANK' ? 'data-open-crane-hinge-selector="true"' : ''} title="Edit this selection">
        <i data-lucide="pencil"></i>
      </button>
    ` : '';
    
    const deleteButton = (isOptional && !isIncluded && groupHandle && optionCode) ? `
      <button class="summary-delete" data-group="${groupHandle}" data-option="${optionCode}" title="Remove this selection">
        <i data-lucide="trash-2"></i>
      </button>
    ` : '';
    
    return `
      <div class="${cardClass}" ${stepNumber && !isIncluded ? 'data-step="' + stepNumber + '"' : ''}>
        ${imageHtml}
        <div class="summary-info">
          <h3>${cleanTitle}</h3>
          <p>${value}${rivetColorInfo}${craneHingeColorInfo}${inlineCountBadge}${includedBadge}${priceDisplay}${craneHingePriceDisplay}</p>
        </div>
        <div class="summary-card-actions">
          ${editButton}
          ${deleteButton}
        </div>
      </div>
    `;
  }


  goToStep(stepNumber, openRivetSelector = false, openCraneHingeSelector = false) {
    // Close summary modal if it's open
    document.getElementById('summaryModal').classList.remove('active');
    
    // If quiz modal is already open, just change the step
    const quizModal = document.getElementById('quizModal');
    if (quizModal.classList.contains('active')) {
      this.currentStep = stepNumber;
      this.saveSelectionsToStorage();
      this.updateStepUI();
      this.updateProgress();
      
      // Open rivet selector if requested
      if (openRivetSelector) {
        setTimeout(() => {
          const rivetCard = document.querySelector('[data-value="RIVET_TRIM"][data-group="accessory"]');
          if (rivetCard) {
            this.showRivetColorSelector(rivetCard);
          }
        }, 300);
      }
      
      // Open crane hinge selector if requested
      if (openCraneHingeSelector) {
        setTimeout(() => {
          const craneHingeCard = document.querySelector('[data-value="CRANE"][data-group="hinge_type"], [data-value="CRANE_BLANK"][data-group="hinge_type"]');
          if (craneHingeCard) {
            this.showCraneHingeColorSelector(craneHingeCard);
          }
        }, 300);
      }
    } else {
      // Open quiz modal at specific step
      setTimeout(() => {
        this.currentStep = stepNumber;
        this.saveSelectionsToStorage();
        quizModal.classList.add('active');
        this.updateStepUI();
        this.updateProgress();
        document.body.classList.add('modal-open');
        
        // Open rivet selector if requested
        if (openRivetSelector) {
          setTimeout(() => {
            const rivetCard = document.querySelector('[data-value="RIVET_TRIM"][data-group="accessory"]');
            if (rivetCard) {
              this.showRivetColorSelector(rivetCard);
            }
          }, 300);
        }
        
        // Open crane hinge selector if requested
        if (openCraneHingeSelector) {
          setTimeout(() => {
            const craneHingeCard = document.querySelector('[data-value="CRANE"][data-group="hinge_type"], [data-value="CRANE_BLANK"][data-group="hinge_type"]');
            if (craneHingeCard) {
              this.showCraneHingeColorSelector(craneHingeCard);
            }
          }, 300);
        }
      }, 300);
    }
  }

  deleteOptionalSelection(groupHandle, optionCode) {
    
    // Save mobile dropdown state before refresh
    const collapsibleContent = document.getElementById('summaryCollapsibleContent');
    const wasOpen = collapsibleContent?.classList.contains('open');
    
    // Check if this is a single-select or multi-select group
    const selection = this.state.selections[groupHandle];
    
    if (!selection) {
      console.warn('No selection found for group:', groupHandle);
      return;
    }
    
    if (Array.isArray(selection)) {
      // Single-select - remove the entire group selection
      delete this.state.selections[groupHandle];
    } else if (typeof selection === 'object') {
      // Multi-select - remove just this option
      delete this.state.selections[groupHandle][optionCode];
      
      // If no more selections in this group, remove the group
      if (Object.keys(this.state.selections[groupHandle]).length === 0) {
        delete this.state.selections[groupHandle];
      }
    }

    // Grouping behavior: cabinet_size and watch_winders are a package deal.
    // If cabinet_size is removed, also remove any namespaced inline groups
    // that were opened for it (e.g., watch_winders_for_cabinet_size_...).
    try {
      if (groupHandle === 'cabinet_size') {
        Object.keys(this.state.selections).forEach(key => {
          if (key.startsWith('watch_winders_for_')) {
            delete this.state.selections[key];
          }
        });
      }

      // If an inline group (like watch_winders) is being deleted via summary
      // but stored under a namespaced key, locate and remove the namespace
      // entry and also remove the associated trigger (e.g., cabinet_size).
      if (!this.state.selections[groupHandle] && groupHandle === 'watch_winders') {
        const knownHandles = (this.data.optionGroups || []).map(g => g.handle);
        Object.keys(this.state.selections).forEach(nsKey => {
          if (!nsKey.startsWith(`${groupHandle}_for_`)) return;
          const remainder = nsKey.split('_for_')[1];
          // Find the longest matching triggerGroup from known handles
          let matchedTrigger = null;
          for (const h of knownHandles) {
            if (remainder.startsWith(h + '_')) {
              if (!matchedTrigger || h.length > matchedTrigger.length) matchedTrigger = h;
            }
          }
          if (!matchedTrigger) return;
          const triggerValue = remainder.slice(matchedTrigger.length + 1);
          const inlineSelections = this.state.selections[nsKey];
          if (!inlineSelections) return;
          // If this namespace contains the optionCode, remove it
          if (inlineSelections[optionCode]) {
            delete this.state.selections[nsKey];

            // Also remove the trigger selection (cabinet_size) if it matches
            const triggerSel = this.state.selections[matchedTrigger];
            if (triggerSel) {
              if (Array.isArray(triggerSel)) {
                if (triggerSel.includes(triggerValue)) {
                  // remove the value
                  const idx = triggerSel.indexOf(triggerValue);
                  if (idx > -1) triggerSel.splice(idx, 1);
                  if (triggerSel.length === 0) delete this.state.selections[matchedTrigger];
                }
              } else if (typeof triggerSel === 'object') {
                if (triggerValue in triggerSel) {
                  delete triggerSel[triggerValue];
                  if (Object.keys(triggerSel).length === 0) delete this.state.selections[matchedTrigger];
                }
              } else if (typeof triggerSel === 'string') {
                if (triggerSel === triggerValue) delete this.state.selections[matchedTrigger];
              }
            }
          }
        });
      }
    } catch (e) {
      console.warn('Error applying cabinet_size <-> watch_winders grouping during deletion', e);
    }
    
    // Now check for dependent options that need to be removed
    // Loop through all selections and check if any options depend on what we just deleted
    this.removeDependentSelections(groupHandle, optionCode);
    
    // Save updated selections
    this.saveSelectionsToStorage();
    
    // Recalculate price
    this.calculatePrice();
    
    // Refresh the summary display
    this.showSummary(wasOpen);
    
    // Reinitialize Lucide icons after DOM changes
    if (typeof lucide !== 'undefined') {
      setTimeout(() => {
        lucide.createIcons();
      }, 100);
    }
    
    // Notify app block
    this.notifyConfigurationUpdate();
  }

  removeDependentSelections(deletedGroupHandle, deletedOptionCode) {
    const clearedGroups = new Set();
    // Get all current selections
    const selectionsToCheck = Object.entries(this.state.selections);
    
    for (const [groupHandle, selectedCodes] of selectionsToCheck) {
      // Get the group
      const group = this.data.optionGroups.find(g => g.handle === groupHandle);
      if (!group) continue;
      
      // Convert selections to array for easier checking
      const selectedOptions = Array.isArray(selectedCodes) ? selectedCodes : Object.keys(selectedCodes);
      
      for (const selectedCode of selectedOptions) {
        // Get the option value
        const optionValue = group.values.find(v => v.code === selectedCode);
        if (!optionValue || !optionValue.dependencies) continue;

        // Parse dependencies if stored as a JSON string
        let deps;
        try {
          deps = typeof optionValue.dependencies === 'string'
            ? JSON.parse(optionValue.dependencies)
            : optionValue.dependencies;
        } catch (e) { continue; }

        if (!deps.requires) continue;

        // Check if this option has dependencies on the deleted group
        const requires = deps.requires;
        
        if (requires[deletedGroupHandle]) {
          // This option depends on the group we just deleted from
          const requiredCodes = requires[deletedGroupHandle];
          
          // Check if the deleted code was one of the required options
          if (requiredCodes.includes(deletedOptionCode)) {
            // Now check if ANY of the required options are still selected
            const stillHasRequiredOption = requiredCodes.some(reqCode => {
              const currentSelection = this.state.selections[deletedGroupHandle];
              if (!currentSelection) return false;
              
              if (Array.isArray(currentSelection)) {
                return currentSelection.includes(reqCode);
              } else if (typeof currentSelection === 'object') {
                return reqCode in currentSelection;
              }
              return false;
            });
            
            // If NONE of the required options are selected anymore, delete this dependent option
            if (!stillHasRequiredOption) {

              if (Array.isArray(this.state.selections[groupHandle])) {
                // Single-select - remove the entire group
                delete this.state.selections[groupHandle];
                clearedGroups.add(groupHandle);
              } else if (typeof this.state.selections[groupHandle] === 'object') {
                // Multi-select - remove just this option
                delete this.state.selections[groupHandle][selectedCode];

                // Clean up empty group
                if (Object.keys(this.state.selections[groupHandle]).length === 0) {
                  delete this.state.selections[groupHandle];
                  clearedGroups.add(groupHandle);
                }
              }
            }
          }
        }
      }
    }
    return clearedGroups;
  }

  autoSelectDefaultForGroup(groupHandle) {
    const group = this.data.optionGroups.find(g => g.handle === groupHandle);
    if (!group || !group.values) return;

    // Find all values whose requires dependencies are currently satisfied
    const compatibleValues = group.values.filter(v => {
      if (!v.dependencies) return true;
      let deps;
      try {
        deps = typeof v.dependencies === 'string' ? JSON.parse(v.dependencies) : v.dependencies;
      } catch (e) { return true; }
      if (!deps.requires) return true;
      // Check every required group
      for (const [reqGroup, reqCodes] of Object.entries(deps.requires)) {
        const current = this.state.selections[reqGroup];
        if (!current) return false;
        const currentArr = Array.isArray(current) ? current : Object.keys(current);
        const satisfied = reqCodes.some(code => currentArr.includes(code));
        if (!satisfied) return false;
      }
      return true;
    });

    if (compatibleValues.length === 0) return;

    // Prefer isDefault: true, otherwise first compatible
    const defaultValue = compatibleValues.find(v => v.isDefault) || compatibleValues[0];
    this.state.selections[groupHandle] = [defaultValue.code];
  }

  clearRequiresAnyViolations() {
    // Iterate all currently selected option values and clear those whose requires_any
    // conditions are no longer fully satisfied (used after size changes).
    for (const [groupHandle, selectedCodes] of Object.entries(this.state.selections)) {
      const group = this.data.optionGroups.find(g => g.handle === groupHandle);
      if (!group) continue;

      const codes = Array.isArray(selectedCodes) ? selectedCodes : Object.keys(selectedCodes);
      for (const code of codes) {
        const optionValue = group.values?.find(v => v.code === code);
        if (!optionValue?.dependencies) continue;

        try {
          const deps = typeof optionValue.dependencies === 'string'
            ? JSON.parse(optionValue.dependencies)
            : optionValue.dependencies;
          if (!deps.requires_any) continue;

          const anySatisfied = deps.requires_any.some(conditionSet =>
            Object.entries(conditionSet).every(([reqGroup, reqCodes]) => {
              const reqGroupHandle = reqGroup.toLowerCase().replace(/[^\w]+/g, '_');
              const selected = this.state.selections[reqGroupHandle] || [];
              const selectedArr = Array.isArray(selected) ? selected : Object.keys(selected);
              return reqCodes.some(c => selectedArr.includes(c));
            })
          );

          if (!anySatisfied) {
            if (Array.isArray(this.state.selections[groupHandle])) {
              delete this.state.selections[groupHandle];
            } else {
              delete this.state.selections[groupHandle][code];
              if (Object.keys(this.state.selections[groupHandle]).length === 0) {
                delete this.state.selections[groupHandle];
              }
            }
          }
        } catch (e) { /* skip malformed deps */ }
      }
    }
  }

  clearSizeRestrictedOptions(newSizeCode) {
    const currentModel = this.getCurrentModel();
    if (!currentModel) return new Set();
    const manifest = this.modelImageManifests[currentModel.id];
    if (!manifest?.sizeRestrictedOptions) return new Set();

    const clearedGroups = new Set();

    // sizeRestrictedOptions: { "option_code": ["sizeCode1", "sizeCode2"] }
    // If the new size is NOT in an option's allowed list, clear that selection.
    for (const [optionCode, allowedSizes] of Object.entries(manifest.sizeRestrictedOptions)) {
      if (allowedSizes.includes(newSizeCode)) continue;
      // Find which group this option belongs to and clear it
      for (const [groupHandle, selectedCodes] of Object.entries(this.state.selections)) {
        const arr = Array.isArray(selectedCodes) ? selectedCodes : Object.keys(selectedCodes);
        if (!arr.includes(optionCode)) continue;
        if (Array.isArray(this.state.selections[groupHandle])) {
          delete this.state.selections[groupHandle];
          clearedGroups.add(groupHandle);
        } else {
          delete this.state.selections[groupHandle][optionCode];
          if (Object.keys(this.state.selections[groupHandle]).length === 0) {
            delete this.state.selections[groupHandle];
            clearedGroups.add(groupHandle);
          }
        }
      }
    }

    return clearedGroups;
  }

  async saveConfigurationPDF(returnBlob = false, headerTitle = null) {
    // Check if jsPDF is available, load if needed
    const button = document.getElementById('saveConfigBtn');
    const originalText = button ? button.innerHTML : null;

    if (typeof window.jspdf === 'undefined') {
      if (!returnBlob && button) {
        button.innerHTML = '<i data-lucide="loader" class="icon-sm"></i> Loading...';
        button.disabled = true;
        if (typeof lucide !== 'undefined') {
          lucide.createIcons();
        }
      }
      // Load jsPDF dynamically and wait
      await this.loadPDFLibraries();
    }

    if (!returnBlob && button) {
      button.innerHTML = '<i data-lucide="loader" class="icon-sm"></i> Generating PDF...';
      button.disabled = true;
      if (typeof lucide !== 'undefined') {
        lucide.createIcons();
      }
    }
    
    try {
      const { jsPDF } = window.jspdf;
      const doc = new jsPDF('p', 'mm', 'a4');

      // Page dimensions
      const pageWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const margin = 18;
      const contentWidth = pageWidth - (margin * 2);

      let yPosition = margin;

      // ── HEADER: Logo + Title + Date ──────────────────────────────
      // Try to load company logo first so we can place it in the header
      let logoDataUrl = null;
      try {
        const url = 'https://cdn.shopify.com/s/files/1/0530/6023/9548/files/norcal-logo.png?v=1769648945';
        const resp = await fetch(url);
        if (resp.ok) {
          const blob = await resp.blob();
          logoDataUrl = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
          });
        }
      } catch (e) {
        console.warn('Logo fetch skipped:', e);
      }

      if (logoDataUrl) {
        try {
          const img = new Image();
          img.src = logoDataUrl;
          await new Promise((resolve) => { img.onload = resolve; img.onerror = resolve; });
          const logoWidthMm = 36;
          const aspect = (img.naturalWidth && img.naturalHeight) ? (img.naturalHeight / img.naturalWidth) : 0.3;
          const logoHeightMm = logoWidthMm * aspect;
          doc.addImage(logoDataUrl, 'PNG', margin, yPosition, logoWidthMm, logoHeightMm);
        } catch (e) {
          console.warn('Could not add logo to PDF:', e);
        }
      }

      // Title (right-aligned or after logo)
      const titleText = headerTitle || 'Safe Configuration';
      doc.setFontSize(20);
      doc.setTextColor(80, 99, 116);
      doc.setFont(undefined, 'bold');
      doc.text(titleText, pageWidth - margin, yPosition + 6, { align: 'right' });

      // Date
      doc.setFontSize(9);
      doc.setTextColor(130, 140, 155);
      doc.setFont(undefined, 'normal');
      const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      doc.text(date, pageWidth - margin, yPosition + 12, { align: 'right' });

      yPosition += 20;

      // Thin divider under header
      doc.setDrawColor(210, 218, 226);
      doc.setLineWidth(0.4);
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 6;

      // ── MODEL INFO + SAFE IMAGE ──────────────────────────────────
      const currentModel = this.getCurrentModel();
      const modelName = currentModel?.name || '';
      const brandName = currentModel?.brand || '';

      // Get selected size label
      let selectedSizeLabel = '';
      const sizeHandles = ['size', 'amsec_size', 'ls_size'];
      let activeSizeHandle = null;
      for (const handle of sizeHandles) {
        if (this.state.selections[handle]?.[0]) {
          activeSizeHandle = handle;
          const sizeGroup = this.data.optionGroups.find(g => g.handle === handle);
          const sizeOption = sizeGroup?.values.find(v => v.code === this.state.selections[handle][0]);
          if (sizeOption) selectedSizeLabel = sizeOption.label;
          break;
        }
      }

      // Model and size text
      if (brandName || modelName) {
        doc.setFontSize(13);
        doc.setTextColor(51, 65, 85);
        doc.setFont(undefined, 'bold');
        const modelLine = [brandName, modelName].filter(Boolean).join(' – ');
        doc.text(modelLine, margin, yPosition + 4);
        yPosition += 7;
      }
      if (selectedSizeLabel) {
        doc.setFontSize(10);
        doc.setTextColor(100, 116, 139);
        doc.setFont(undefined, 'normal');
        doc.text(selectedSizeLabel, margin, yPosition + 3);
        yPosition += 6;
      }

      // Configured safe image (compact: 55mm wide, centered)
      const summaryConfiguredSafe = document.getElementById('summaryConfiguredSafe');
      if (summaryConfiguredSafe) {
        try {
          const canvas = await this.captureElementAsCanvas(summaryConfiguredSafe);
          const imgData = canvas.toDataURL('image/png');
          const imgWidth = contentWidth * 0.35;
          const imgHeight = imgWidth;
          const imgX = (pageWidth - imgWidth) / 2;
          doc.addImage(imgData, 'PNG', imgX, yPosition + 2, imgWidth, imgHeight);
          yPosition += imgHeight + 6;
        } catch (error) {
          console.error('Error capturing safe image:', error);
        }
      }

      // ── CONFIGURATION DETAILS TABLE ──────────────────────────────
      doc.setFontSize(12);
      doc.setTextColor(80, 99, 116);
      doc.setFont(undefined, 'bold');
      doc.text('Configuration Details', margin, yPosition + 4);
      yPosition += 8;

      // Table header line
      doc.setDrawColor(80, 99, 116);
      doc.setLineWidth(0.5);
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 5;

      // Column layout: Category (55mm) | Selection (flexible) | Price (30mm right-aligned)
      const catColWidth = 50;
      const priceColWidth = 28;
      const valColWidth = contentWidth - catColWidth - priceColWidth;
      const valColX = margin + catColWidth;
      const priceColX = pageWidth - margin;

      // Collect all selections for the table
      const selections = [];

      // Add size (price = base price, not getOptionPrice which returns 0)
      for (const handle of sizeHandles) {
        if (this.state.selections[handle]?.[0]) {
          const sizeValue = this.state.selections[handle][0];
          const sizeGroup = this.data.optionGroups.find(g => g.handle === handle);
          const sizeOption = sizeGroup?.values.find(v => v.code === sizeValue);
          if (sizeOption) {
            selections.push({
              category: 'Size',
              value: sizeOption.label,
              price: this.state.basePrice // Size price = base price
            });
          }
          break;
        }
      }

      // Add all other selections
      this.steps.forEach((step) => {
        step.groups.forEach(groupHandle => {
          const value = this.state.selections[groupHandle];
          const group = this.data.optionGroups.find(g => g.handle === groupHandle);
          if (!group) return;

          if (Array.isArray(value) && value.length > 0) {
            const option = group.values.find(v => v.code === value[0]);
            if (option) {
              const price = this.getOptionPrice(option, groupHandle) || 0;
              selections.push({
                category: group.displayName.replace(/^Fort Knox\s+/i, ''),
                value: option.label,
                price: price
              });
            }
          } else if (typeof value === 'object' && Object.keys(value).length > 0) {
            const categoryName = group.displayName.replace(/^Fort Knox\s+/i, '');
            for (const [optionCode, quantity] of Object.entries(value)) {
              const option = group.values.find(v => v.code === optionCode);
              if (option) {
                const unitPrice = this.getOptionPrice(option, groupHandle) || 0;
                const totalPrice = unitPrice * quantity;
                const isAccessory = group.handle === 'accessory' || /accessor/i.test(categoryName);
                selections.push({
                  category: isAccessory ? 'Accessories' : categoryName,
                  value: `${option.label}${quantity > 1 ? ` (${quantity}x)` : ''}`,
                  price: totalPrice
                });
              }
            }
          }
        });
      });

      // Add rivet color override if set
      if (this.state.rivetColorOverride) {
        const colorOption = this.getValueByCode('color_single', this.state.rivetColorOverride);
        if (colorOption) {
          const formatColorName = (name) => name.split('_').map(word => word.charAt(0) + word.slice(1).toLowerCase()).join(' ');
          selections.push({
            category: 'Rivet Trim Color',
            value: colorOption.displayName || formatColorName(this.state.rivetColorOverride),
            price: 0
          });
        }
      }

      // Add crane hinge color override if set
      if (this.state.craneHingeColorOverride) {
        const colorOption = this.getValueByCode('color_single', this.state.craneHingeColorOverride);
        if (colorOption) {
          const formatColorName = (name) => name.split('_').map(word => word.charAt(0) + word.slice(1).toLowerCase()).join(' ');
          selections.push({
            category: 'Crane Hinge Color',
            value: colorOption.displayName || formatColorName(this.state.craneHingeColorOverride),
            price: 0
          });
        }
      }

      // Render table rows
      doc.setFontSize(9);
      let lastCategory = null;
      let rowIsEven = false;

      selections.forEach((item) => {
        // Page break check
        if (yPosition > pageHeight - 35) {
          doc.addPage();
          yPosition = margin + 10;
        }

        // Alternate row background for readability
        if (rowIsEven) {
          doc.setFillColor(246, 248, 250);
          doc.rect(margin, yPosition - 3.5, contentWidth, 7, 'F');
        }
        rowIsEven = !rowIsEven;

        // Category (bold, only print when it changes)
        if (item.category !== lastCategory) {
          doc.setTextColor(80, 99, 116);
          doc.setFont(undefined, 'bold');
          doc.text(item.category, margin, yPosition);
          lastCategory = item.category;
        }

        // Value
        doc.setFont(undefined, 'normal');
        doc.setTextColor(51, 65, 85);
        const valLines = doc.splitTextToSize(item.value, valColWidth - 2);
        doc.text(valLines, valColX, yPosition);

        // Price (right-aligned, only show if > 0)
        if (item.price && item.price > 0) {
          doc.setTextColor(51, 65, 85);
          doc.text(`$${this.formatPrice(item.price)}`, priceColX, yPosition, { align: 'right' });
        }

        yPosition += Math.max(valLines.length * 4, 4) + 3.5;
      });

      // ── PRICING SUMMARY ──────────────────────────────────────────
      if (yPosition > pageHeight - 50) {
        doc.addPage();
        yPosition = margin + 10;
      }

      yPosition += 4;
      doc.setDrawColor(80, 99, 116);
      doc.setLineWidth(0.5);
      doc.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 8;

      const priceLabelX = pageWidth - margin - 55;

      // Base Price line
      doc.setFontSize(10);
      doc.setTextColor(80, 99, 116);
      doc.setFont(undefined, 'normal');
      doc.text('Base Price:', priceLabelX, yPosition);
      doc.text(`$${this.formatPrice(this.state.basePrice)}`, priceColX, yPosition, { align: 'right' });
      yPosition += 6;

      // Upgrades line (only if there are modifiers)
      if (this.state.modifiersTotal > 0) {
        doc.text('Upgrades:', priceLabelX, yPosition);
        doc.text(`+$${this.formatPrice(this.state.modifiersTotal)}`, priceColX, yPosition, { align: 'right' });
        yPosition += 6;
      }

      // Total line (bold, larger)
      doc.setDrawColor(210, 218, 226);
      doc.setLineWidth(0.3);
      doc.line(priceLabelX, yPosition, pageWidth - margin, yPosition);
      yPosition += 6;

      doc.setFontSize(13);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(51, 65, 85);
      doc.text('Total:', priceLabelX, yPosition);
      doc.text(`$${this.formatPrice(this.state.totalPrice)}`, priceColX, yPosition, { align: 'right' });

      // ── SINGLE SIGNATURE LINE ────────────────────────────────────
      if (yPosition > pageHeight - 45) {
        doc.addPage();
        yPosition = margin + 10;
      }

      yPosition += 18;
      doc.setFontSize(10);
      doc.setTextColor(51, 65, 85);
      doc.setFont(undefined, 'normal');
      doc.text('Signature:', margin, yPosition);

      // Signature line
      const sigLineStart = margin + 22;
      const sigLineEnd = pageWidth - margin - 45;
      doc.setDrawColor(120, 120, 120);
      doc.setLineWidth(0.5);
      doc.line(sigLineStart, yPosition + 2, sigLineEnd, yPosition + 2);

      // Date label and line
      const dateLabelX = sigLineEnd + 6;
      doc.text('Date:', dateLabelX, yPosition);
      const dateLineStart = dateLabelX + 12;
      const dateLineEnd = pageWidth - margin;
      doc.line(dateLineStart, yPosition + 2, dateLineEnd, yPosition + 2);

      // ── FOOTER ───────────────────────────────────────────────────
      const footerY = pageHeight - 12;
      doc.setFontSize(8);
      doc.setTextColor(160, 170, 185);
      doc.text('Generated by Norcal Safe and Vault', pageWidth / 2, footerY, { align: 'center' });

      // Save or return the PDF blob
      const fileName = `Safe-Configuration-${Date.now()}.pdf`;
      if (returnBlob) {
        const blob = doc.output('blob');
        return { blob, fileName };
      } else {
        doc.save(fileName);
      }

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('There was an error generating the PDF. Please try again.');
    } finally {
      if (!returnBlob && button) {
        button.innerHTML = originalText;
        button.disabled = false;
        if (typeof lucide !== 'undefined') {
          lucide.createIcons();
        }
      }
    }
  }
  
  async loadPDFLibraries() {
    return new Promise((resolve, reject) => {
      // Load jsPDF
      const script1 = document.createElement('script');
      script1.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
      script1.onload = () => {
        // Load html2canvas
        const script2 = document.createElement('script');
        script2.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
        script2.onload = () => resolve();
        script2.onerror = () => reject(new Error('Failed to load html2canvas'));
        document.head.appendChild(script2);
      };
      script1.onerror = () => reject(new Error('Failed to load jsPDF'));
      document.head.appendChild(script1);
    });
  }
  
  async captureElementAsCanvas(element) {
    // html2canvas doesn't support CSS masks/blend modes properly
    // We need to manually render the safe layer by layer
    console.log('🎨 Starting manual canvas rendering...');
    
    // Create canvas
    const canvas = document.createElement('canvas');
    canvas.width = 800;
    canvas.height = 800;
    const ctx = canvas.getContext('2d');
    
    // Fill with white background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, 800, 800);
    
    // Get current size for lock scaling calculation
    const sizeGroupHandles = ['size', 'amsec_size', 'ls_size'];
    let selectedSize = null;
    for (const handle of sizeGroupHandles) {
      if (this.state.selections[handle]?.[0]) {
        selectedSize = this.state.selections[handle][0];
        break;
      }
    }
    
    // Calculate lock scale factor based on safe size (same logic as DOM rendering)
    let lockScale = 1.0;
    if (selectedSize) {
      const sizeValue = this.getValueByCode('size', selectedSize) || 
                        this.getValueByCode('amsec_size', selectedSize) || 
                        this.getValueByCode('ls_size', selectedSize);
      const sizeCode = sizeValue?.code;
      
      if (sizeCode) {
        // Get current model manifest
        const currentModel = this.getCurrentModel();
        if (currentModel) {
          const manifest = this.modelImageManifests[currentModel.id];
          if (manifest) {
            const referenceSize = manifest.referenceSize || '4026x20';
            const scaleFactor = this.calculateAccessoryScale(sizeCode, referenceSize);
            lockScale = scaleFactor * 1.2; // Same as DOM: base scale * 1.2 for locks
            console.log('🔐 Lock scale factor:', lockScale, 'for size:', sizeCode);
          }
        }
      }
    }
    
    // Get all wrapper divs (they control z-index stacking)
    const wrappers = Array.from(element.children).filter(child => 
      child.style.position === 'absolute' || child.querySelector('[class*="safe-layer"]')
    );
    
    // Sort by z-index to render in correct order
    wrappers.sort((a, b) => {
      const zIndexA = parseInt(a.style.zIndex || '0');
      const zIndexB = parseInt(b.style.zIndex || '0');
      return zIndexA - zIndexB;
    });
    
    console.log(`📚 Found ${wrappers.length} layer wrappers to render`);
    
    // Process each wrapper and its children
    for (const wrapper of wrappers) {
      const layers = wrapper.querySelectorAll('[class*="safe-layer"]');
      
      for (const layer of layers) {
        const className = layer.className;
        const style = window.getComputedStyle(layer);
        const bgImage = style.backgroundImage;
        const bgColor = style.backgroundColor;
        const maskImage = style.webkitMaskImage || style.maskImage;
        const transform = style.transform;
        
        // Check if this layer is mirrored (scaleX(-1) for RIGHT swing)
        const isMirrored = transform && transform.includes('matrix(-1');
        
        // Extract z-index for logging
        const zIndex = wrapper.style.zIndex || '0';
        
        // Handle crane hinge texture layers (have both texture background AND mask)
        if (className.includes('-texture') && bgImage && bgImage !== 'none' && maskImage && maskImage !== 'none') {
          const bgUrlMatch = bgImage.match(/url\(["']?([^"']*)["']?\)/);
          const maskUrlMatch = maskImage.match(/url\(["']?([^"']*)["']?\)/);
          
          if (bgUrlMatch && bgUrlMatch[1] && maskUrlMatch && maskUrlMatch[1]) {
            const textureUrl = bgUrlMatch[1];
            const maskUrl = maskUrlMatch[1];
            const textureName = textureUrl.substring(textureUrl.lastIndexOf('/') + 1);
            const maskName = maskUrl.substring(maskUrl.lastIndexOf('/') + 1);
            
            // Check if this is rimrock texture for crane hinges (less transparent)
            const isRimrock = textureName.toLowerCase().includes('rimrock');
            const opacity = isRimrock ? 0.9 : 0.85; // Rimrock 90%, others 85%
            
            console.log(`🎨 [z:${zIndex}] ${className}: texture=${textureName} mask=${maskName}${isMirrored ? ' [MIRRORED]' : ''} opacity=${opacity}`);
            
            try {
              await this.drawTextureMaskToCanvas(ctx, maskUrl, textureUrl, 800, 800, isMirrored, opacity);
            } catch (error) {
              console.error('Failed to apply texture mask:', maskUrl, textureUrl, error);
            }
          }
        }
        // Handle body/door texture layers (background image only, no mask)
        else if ((className.includes('body-texture') || className.includes('door-texture')) && bgImage && bgImage !== 'none') {
          const urlMatch = bgImage.match(/url\(["']?([^"']*)["']?\)/);
          if (urlMatch && urlMatch[1]) {
            const imageUrl = urlMatch[1];
            const imageName = imageUrl.substring(imageUrl.lastIndexOf('/') + 1);
            console.log(`🖼️ [z:${zIndex}] ${className}: ${imageName}${isMirrored ? ' [MIRRORED]' : ''}`);
            
            try {
              await this.drawImageToCanvas(ctx, imageUrl, 800, 800, 1.0, isMirrored);
            } catch (error) {
              console.error('Failed to load texture:', imageUrl, error);
            }
          }
        }
        // Handle regular layers with background images (base images, locks, hinges, etc.)
        else if (bgImage && bgImage !== 'none' && !className.includes('-texture')) {
          const urlMatch = bgImage.match(/url\(["']?([^"']*)["']?\)/);
          if (urlMatch && urlMatch[1]) {
            const imageUrl = urlMatch[1];
            const imageName = imageUrl.substring(imageUrl.lastIndexOf('/') + 1);
            console.log(`🖼️ [z:${zIndex}] ${className}: ${imageName}${isMirrored ? ' [MIRRORED]' : ''}`);
            
            try {
              // Check if this is a lock image - use calculated lock scale
              const isLockLayer = className.includes('lock') && !className.includes('lockgraphic');
              if (isLockLayer) {
                await this.drawImageToCanvas(ctx, imageUrl, 800, 800, lockScale, isMirrored);
              } else {
                await this.drawImageToCanvas(ctx, imageUrl, 800, 800, 1.0, isMirrored);
              }
            } catch (error) {
              console.error('Failed to load image:', imageUrl, error);
            }
          }
        }
        // Handle color mask layers (they use mask-image + backgroundColor)
        else if (maskImage && maskImage !== 'none' && bgColor && bgColor !== 'rgba(0, 0, 0, 0)') {
          const maskUrlMatch = maskImage.match(/url\(["']?([^"']*)["']?\)/);
          if (maskUrlMatch && maskUrlMatch[1]) {
            const maskUrl = maskUrlMatch[1];
            const maskName = maskUrl.substring(maskUrl.lastIndexOf('/') + 1);
            console.log(`🎨 [z:${zIndex}] ${className}: color=${bgColor} mask=${maskName}${isMirrored ? ' [MIRRORED]' : ''}`);
            
            try {
              await this.drawColorMaskToCanvas(ctx, maskUrl, bgColor, 800, 800, isMirrored);
            } catch (error) {
              console.error('Failed to apply color mask:', maskUrl, error);
            }
          }
        }
      }
    }
    
    console.log('✅ Canvas rendering complete');
    return canvas;
  }
  
  async drawTextureMaskToCanvas(ctx, maskUrl, textureUrl, width, height, shouldMirror = false, opacity = 0.85) {
    return new Promise((resolve, reject) => {
      const maskImg = new Image();
      const textureImg = new Image();
      let maskLoaded = false;
      let textureLoaded = false;
      
      const checkBothLoaded = () => {
        if (maskLoaded && textureLoaded) {
          // Calculate dimensions to fit canvas (contain)
          const imgAspect = maskImg.width / maskImg.height;
          const canvasAspect = width / height;
          
          let drawWidth, drawHeight, x, y;
          
          if (imgAspect > canvasAspect) {
            drawWidth = width;
            drawHeight = width / imgAspect;
            x = 0;
            y = (height - drawHeight) / 2;
          } else {
            drawHeight = height;
            drawWidth = height * imgAspect;
            x = (width - drawWidth) / 2;
            y = 0;
          }
          
          // Create temporary canvas for masking operation
          const tempCanvas = document.createElement('canvas');
          tempCanvas.width = drawWidth;
          tempCanvas.height = drawHeight;
          const tempCtx = tempCanvas.getContext('2d');
          
          // If mirrored, flip the context
          if (shouldMirror) {
            tempCtx.translate(drawWidth, 0);
            tempCtx.scale(-1, 1);
          }
          
          // Draw mask image (grayscale base)
          tempCtx.drawImage(maskImg, 0, 0, drawWidth, drawHeight);
          
          // Apply texture using source-atop (texture only appears where mask exists)
          tempCtx.globalCompositeOperation = 'source-atop';
          tempCtx.globalAlpha = opacity; // Apply opacity for texture
          tempCtx.drawImage(textureImg, 0, 0, drawWidth, drawHeight);
          
          // Draw the result to main canvas
          ctx.drawImage(tempCanvas, x, y);
          
          resolve();
        }
      };
      
      maskImg.crossOrigin = 'anonymous';
      textureImg.crossOrigin = 'anonymous';
      
      maskImg.onload = () => {
        maskLoaded = true;
        checkBothLoaded();
      };
      
      textureImg.onload = () => {
        textureLoaded = true;
        checkBothLoaded();
      };
      
      maskImg.onerror = () => reject(new Error(`Failed to load mask: ${maskUrl}`));
      textureImg.onerror = () => reject(new Error(`Failed to load texture: ${textureUrl}`));
      
      maskImg.src = maskUrl;
      textureImg.src = textureUrl;
    });
  }
  
  async drawColorMaskToCanvas(ctx, maskUrl, color, width, height, shouldMirror = false) {
    return new Promise((resolve, reject) => {
      const maskImg = new Image();
      maskImg.crossOrigin = 'anonymous';
      
      maskImg.onload = () => {
        // Calculate dimensions to fit canvas (contain)
        const imgAspect = maskImg.width / maskImg.height;
        const canvasAspect = width / height;
        
        let drawWidth, drawHeight, x, y;
        
        if (imgAspect > canvasAspect) {
          drawWidth = width;
          drawHeight = width / imgAspect;
          x = 0;
          y = (height - drawHeight) / 2;
        } else {
          drawHeight = height;
          drawWidth = height * imgAspect;
          x = (width - drawWidth) / 2;
          y = 0;
        }
        
        // Create temporary canvas for masking operation
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width = drawWidth;
        tempCanvas.height = drawHeight;
        const tempCtx = tempCanvas.getContext('2d');
        
        // If mirrored, flip the context
        if (shouldMirror) {
          tempCtx.translate(drawWidth, 0);
          tempCtx.scale(-1, 1);
        }
        
        // Draw mask image
        tempCtx.drawImage(maskImg, 0, 0, drawWidth, drawHeight);
        
        // Use source-in to apply color only where mask exists
        tempCtx.globalCompositeOperation = 'source-in';
        tempCtx.fillStyle = color;
        tempCtx.fillRect(0, 0, drawWidth, drawHeight);
        
        // Draw the result to main canvas with transparency to show detail
        ctx.save();
        ctx.globalAlpha = 0.7; // 70% opacity to let underlying detail show
        ctx.drawImage(tempCanvas, x, y);
        ctx.restore();
        
        resolve();
      };
      
      maskImg.onerror = () => reject(new Error(`Failed to load mask: ${maskUrl}`));
      maskImg.src = maskUrl;
    });
  }
  
  async drawImageWithColorToCanvas(ctx, imageUrl, color, width, height) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        // Calculate dimensions to fit canvas (contain)
        const imgAspect = img.width / img.height;
        const canvasAspect = width / height;
        
        let drawWidth, drawHeight, x, y;
        
        if (imgAspect > canvasAspect) {
          drawWidth = width;
          drawHeight = width / imgAspect;
          x = 0;
          y = (height - drawHeight) / 2;
        } else {
          drawHeight = height;
          drawWidth = height * imgAspect;
          x = (width - drawWidth) / 2;
          y = 0;
        }
        
        // Draw grayscale image first
        ctx.drawImage(img, x, y, drawWidth, drawHeight);
        
        // Apply color using multiply blend mode
        if (color && color !== 'rgba(0, 0, 0, 0)' && color !== 'transparent') {
          // Save current state
          ctx.save();
          
          // Set up for color overlay
          ctx.globalCompositeOperation = 'multiply';
          ctx.fillStyle = color;
          
          // Draw color rectangle over the image area
          ctx.fillRect(x, y, drawWidth, drawHeight);
          
          // Restore state
          ctx.restore();
        }
        
        resolve();
      };
      
      img.onerror = () => reject(new Error(`Failed to load image: ${imageUrl}`));
      img.src = imageUrl;
    });
  }

  async drawImageToCanvas(ctx, imageUrl, width, height, scaleFactor = 1.0, shouldMirror = false) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        // Draw image centered and contained within canvas
        const imgAspect = img.width / img.height;
        const canvasAspect = width / height;
        
        let drawWidth, drawHeight, x, y;
        
        if (imgAspect > canvasAspect) {
          // Image is wider than canvas
          drawWidth = width * scaleFactor;
          drawHeight = (width / imgAspect) * scaleFactor;
          x = (width - drawWidth) / 2;
          y = (height - drawHeight) / 2;
        } else {
          // Image is taller than canvas
          drawHeight = height * scaleFactor;
          drawWidth = (height * imgAspect) * scaleFactor;
          x = (width - drawWidth) / 2;
          y = (height - drawHeight) / 2;
        }
        
        // If mirrored, flip the context before drawing
        if (shouldMirror) {
          ctx.save();
          ctx.translate(width, 0);
          ctx.scale(-1, 1);
          // Adjust x position for mirrored drawing
          const mirroredX = width - x - drawWidth;
          ctx.drawImage(img, mirroredX, y, drawWidth, drawHeight);
          ctx.restore();
        } else {
          ctx.drawImage(img, x, y, drawWidth, drawHeight);
        }
        
        resolve();
      };
      
      img.onerror = () => reject(new Error(`Failed to load image: ${imageUrl}`));
      img.src = imageUrl;
    });
  }
  
  async renderSafeToCanvas() {
    // Get current selections (same as renderSafeImage)
    const selectedFinish = this.state.selections['exterior_finish']?.[0];
    const selectedSize = this.state.selections['size']?.[0];
    const selectedLockType = this.state.selections['lock_type']?.[0];
    const selectedLockColor = this.state.selections['lockgraphic_color']?.[0];
    const selectedGraphic = this.state.selections['graphic']?.[0];
    const selectedHingeType = this.state.selections['hinge_type']?.[0];
    const selectedHingeColor = this.state.selections['hinge_color']?.[0];
    const selectedDoorColor = this.state.selections['door_color']?.[0];
    const selectedBodyColor = this.state.selections['body_color']?.[0];
    const selectedSingleColor = this.state.selections['color_single']?.[0];
    const accessories = this.state.selections['accessory'];
    
    if (!selectedFinish || !selectedSize) {
      console.warn('⚠️ Cannot render canvas: Missing finish or size');
      return null;
    }
    
    // Get current model
    const currentModel = this.getCurrentModel();
    if (!currentModel) {
      console.warn('⚠️ Cannot render canvas: No current model');
      return null;
    }
    
    const manifest = this.modelImageManifests[currentModel.id];
    if (!manifest) {
      console.warn('⚠️ Cannot render canvas: No manifest');
      return null;
    }
    
    // Normalize finish
    const normalizedFinish = selectedFinish.toLowerCase().replace(/_/g, '-');
    const sizeValue = this.getValueByCode('size', selectedSize);
    const sizeCode = sizeValue?.code;
    
    if (!sizeCode) {
      console.warn('⚠️ Cannot render canvas: No size code');
      return null;
    }
    
    // Create canvas (800x800 px for high quality)
    const canvas = document.createElement('canvas');
    const canvasWidth = 800;
    const canvasHeight = 800;
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    const ctx = canvas.getContext('2d');
    
    // White background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);
    
    console.log('🎨 Rendering safe to canvas...', { sizeCode, normalizedFinish });
    
    // Helper function to load image
    const loadImage = (src) => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => {
          console.warn('Failed to load image:', src);
          resolve(null);
        };
        img.src = src;
      });
    };
    
    // Helper to draw image at full canvas size (for base layers that should fill canvas)
    const drawFullSize = (img, opacity = 1) => {
      if (!img) return;
      const prevAlpha = ctx.globalAlpha;
      ctx.globalAlpha = opacity;
      ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);
      ctx.globalAlpha = prevAlpha;
    };
    
    // Helper to draw image centered with aspect ratio maintained (for accessories)
    const drawCentered = (img, opacity = 1) => {
      if (!img) return;
      const prevAlpha = ctx.globalAlpha;
      ctx.globalAlpha = opacity;
      
      // Calculate scaling to fit within canvas while maintaining aspect ratio
      const scale = Math.min(canvasWidth / img.width, canvasHeight / img.height);
      const scaledWidth = img.width * scale;
      const scaledHeight = img.height * scale;
      const x = (canvasWidth - scaledWidth) / 2;
      const y = (canvasHeight - scaledHeight) / 2;
      
      ctx.drawImage(img, x, y, scaledWidth, scaledHeight);
      ctx.globalAlpha = prevAlpha;
    };
    
    // Helper to draw image with color mask (multiply blend) - only applies to visible pixels
    const drawColorMask = async (maskSrc, color, opacity = 0.7) => {
      const mask = await loadImage(maskSrc);
      if (!mask) return;
      
      ctx.save();
      
      // Draw the mask image at full size
      ctx.drawImage(mask, 0, 0, canvasWidth, canvasHeight);
      
      // Set composite mode to only draw where the mask exists
      ctx.globalCompositeOperation = 'source-atop';
      ctx.globalAlpha = opacity;
      
      // Fill with color - will only appear where mask was drawn
      ctx.fillStyle = color;
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);
      
      // Apply multiply blend to the colored area
      ctx.globalCompositeOperation = 'multiply';
      ctx.globalAlpha = 1;
      ctx.fillStyle = color;
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);
      
      ctx.restore();
    };
    
    try {
      // Layer 1: Base image. Supports either a direct URL per size or the existing size + finish map.
      const baseImageEntry = manifest.baseImages?.[sizeCode];
      
      // Try hinge-side keyed images first (for products where hinge side determines the image)
      let baseImageUrl = null;
      const hingeSwing = this.state.selections['hinge_swing']?.[0] || this.state.selections['ls_hinge_side']?.[0];
      if (manifest.baseImagesByHingeSide && hingeSwing) {
        const hingeKey = hingeSwing.toUpperCase();
        const hingeKeyLower = hingeSwing.toLowerCase();
        const normalizedHingeSuffix = hingeKeyLower
          .replace(/^ls_hinge_side_/, '')
          .replace(/^hinge_swing_/, '');
        const hingeCandidates = [
          hingeSwing,
          hingeKey,
          hingeKeyLower,
          `ls_hinge_side_${normalizedHingeSuffix}`,
          `hinge_swing_${normalizedHingeSuffix}`
        ];

        // Preferred structure: baseImagesByHingeSide[sizeCode][hingeCode]
        const sizeScopedHingeImages = manifest.baseImagesByHingeSide[sizeCode];
        if (sizeScopedHingeImages && typeof sizeScopedHingeImages === 'object') {
          baseImageUrl = hingeCandidates.find(k => sizeScopedHingeImages[k])
            ? sizeScopedHingeImages[hingeCandidates.find(k => sizeScopedHingeImages[k])]
            : null;
        }

        // Backward compatibility: flat map baseImagesByHingeSide[hingeCode]
        if (!baseImageUrl) {
          const flatKey = hingeCandidates.find(k => manifest.baseImagesByHingeSide[k]);
          baseImageUrl = flatKey ? manifest.baseImagesByHingeSide[flatKey] : null;
        }
        if (baseImageUrl) {
          console.log('✅ Canvas: Using hinge-side keyed image:', { hingeKey, baseImageUrl });
        }
      }
      
      // Fall back to traditional size/finish keyed images
      if (!baseImageUrl) {
        const imageEntry = typeof baseImageEntry === 'string'
          ? baseImageEntry
          : baseImageEntry?.[normalizedFinish] || baseImageEntry?.default || baseImageEntry?.base;
        baseImageUrl = imageEntry;
      }
      
      if (baseImageUrl) {
        const baseImg = await loadImage(baseImageUrl);
        drawFullSize(baseImg, 1);
        console.log('✅ Canvas: Base image drawn');
      }

      // Color-keyed pre-colored body art. This bypasses masks entirely.
      const bodyColorKey = selectedSingleColor || selectedBodyColor || selectedDoorColor;
      if (manifest.bodyImagesByColor && bodyColorKey) {
        const bodyColorEntry = manifest.bodyImagesByColor[bodyColorKey] || manifest.bodyImagesByColor[bodyColorKey.toUpperCase()] || manifest.bodyImagesByColor[bodyColorKey.toLowerCase()];
        const bodyColorUrl = typeof bodyColorEntry === 'string' ? bodyColorEntry : bodyColorEntry?.[sizeCode] || null;
        if (bodyColorUrl) {
          const bodyColorImg = await loadImage(bodyColorUrl);
          drawFullSize(bodyColorImg, 1);
          console.log('✅ Canvas: Body color image drawn', { bodyColorKey, sizeCode });
        }
      }
      
      // Layer 2-6: Color layers (depends on finish type)
      if (normalizedFinish === 'two-tone-fade' && !manifest.bodyImagesByColor) {
        // Body color
        if (selectedBodyColor) {
          const bodyColorValue = this.getValueByCode('body_color', selectedBodyColor);
          const bodyColor = bodyColorValue?.hexColor;
          const bodyColorCode = selectedBodyColor.toUpperCase();
          const bodyTextureUrl = manifest.textures?.bodyTextures?.[bodyColorCode]?.[sizeCode];
          
          if (bodyTextureUrl) {
            const bodyTexture = await loadImage(bodyTextureUrl);
            drawFullSize(bodyTexture, 1);
          } else {
            const bodyMaskUrl = manifest.masks?.bodyMasks?.[sizeCode];
            if (bodyMaskUrl && bodyColor) {
              await drawColorMask(bodyMaskUrl, bodyColor, 0.7);
            }
          }
          
          // Apply body color to door mask
          const doorMaskUrl = manifest.masks?.doorMasks?.[sizeCode];
          if (doorMaskUrl && bodyColor) {
            await drawColorMask(doorMaskUrl, bodyColor, 0.7);
          }
        }
        
        // Door color fade
        if (selectedDoorColor && selectedBodyColor) {
          const doorColorValue = this.getValueByCode('door_color', selectedDoorColor);
          const doorColor = doorColorValue?.hexColor;
          const doorColorCode = selectedDoorColor.toUpperCase();
          const doorTextureUrl = manifest.textures?.doorTextures?.[doorColorCode]?.[sizeCode];
          
          if (doorTextureUrl) {
            const doorTexture = await loadImage(doorTextureUrl);
            drawFullSize(doorTexture, 1);
          } else {
            const doorFadeMaskUrl = manifest.masks?.doorFadeMasks?.[sizeCode];
            if (doorFadeMaskUrl && doorColor) {
              await drawColorMask(doorFadeMaskUrl, doorColor, 0.9);
            }
          }
        }
        
        // Door border
        const doorBorderUrl = manifest.doorBorders?.[sizeCode];
        if (doorBorderUrl) {
          const doorBorder = await loadImage(doorBorderUrl);
          drawFullSize(doorBorder, 1);
        }
        
      } else if (normalizedFinish === 'two-tone' && !manifest.bodyImagesByColor) {
        // Body and door colors
        if (selectedBodyColor) {
          const bodyColorValue = this.getValueByCode('body_color', selectedBodyColor);
          const bodyColor = bodyColorValue?.hexColor;
          const bodyColorCode = selectedBodyColor.toUpperCase();
          const bodyTextureUrl = manifest.textures?.bodyTextures?.[bodyColorCode]?.[sizeCode];
          
          if (bodyTextureUrl) {
            const bodyTexture = await loadImage(bodyTextureUrl);
            drawFullSize(bodyTexture, 1);
          } else {
            const bodyMaskUrl = manifest.masks?.bodyMasks?.[sizeCode];
            if (bodyMaskUrl && bodyColor) {
              await drawColorMask(bodyMaskUrl, bodyColor, 0.7);
            }
          }
        }
        
        if (selectedDoorColor) {
          const doorColorValue = this.getValueByCode('door_color', selectedDoorColor);
          const doorColor = doorColorValue?.hexColor;
          const doorColorCode = selectedDoorColor.toUpperCase();
          const doorTextureUrl = manifest.textures?.doorTextures?.[doorColorCode]?.[sizeCode];
          
          if (doorTextureUrl) {
            const doorTexture = await loadImage(doorTextureUrl);
            drawFullSize(doorTexture, 1);
          } else {
            const doorMaskUrl = manifest.masks?.doorMasks?.[sizeCode];
            if (doorMaskUrl && doorColor) {
              await drawColorMask(doorMaskUrl, doorColor, 0.7);
            }
          }
        }
        
        const doorBorderUrl = manifest.doorBorders?.[sizeCode];
        if (doorBorderUrl) {
          const doorBorder = await loadImage(doorBorderUrl);
          drawFullSize(doorBorder, 1);
        }
        
      } else if ((normalizedFinish === 'gloss' || normalizedFinish === 'textured') && selectedSingleColor && !manifest.bodyImagesByColor) {
        const singleColorValue = this.getValueByCode('color_single', selectedSingleColor);
        const glossColor = singleColorValue?.hexColor;
        const singleColorCode = selectedSingleColor.toUpperCase();
        
        const bodyTextureUrl = manifest.textures?.bodyTextures?.[singleColorCode]?.[sizeCode];
        const doorTextureUrl = manifest.textures?.doorTextures?.[singleColorCode]?.[sizeCode];
        
        if (bodyTextureUrl) {
          const bodyTexture = await loadImage(bodyTextureUrl);
          drawFullSize(bodyTexture, 1);
        } else if (glossColor) {
          const bodyMaskUrl = manifest.masks?.bodyMasks?.[sizeCode];
          if (bodyMaskUrl) {
            await drawColorMask(bodyMaskUrl, glossColor, 0.7);
          }
        }
        
        if (doorTextureUrl) {
          const doorTexture = await loadImage(doorTextureUrl);
          drawFullSize(doorTexture, 1);
        } else if (glossColor) {
          const doorMaskUrl = manifest.masks?.doorMasks?.[sizeCode];
          if (doorMaskUrl) {
            await drawColorMask(doorMaskUrl, glossColor, 0.7);
          }
        }
        
        const doorBorderUrl = manifest.doorBorders?.[sizeCode];
        if (doorBorderUrl) {
          const doorBorder = await loadImage(doorBorderUrl);
          drawFullSize(doorBorder, 1);
        }
      } else if (normalizedFinish === 'distressed' && selectedSingleColor && !manifest.bodyImagesByColor) {
        // Distressed uses texture images
        const singleColorCode = selectedSingleColor.toUpperCase();
        const bodyTextureUrl = manifest.textures?.bodyTextures?.[singleColorCode]?.[sizeCode];
        const doorTextureUrl = manifest.textures?.doorTextures?.[singleColorCode]?.[sizeCode];
        
        if (bodyTextureUrl) {
          const bodyTexture = await loadImage(bodyTextureUrl);
          drawFullSize(bodyTexture, 1);
        }
        
        if (doorTextureUrl) {
          const doorTexture = await loadImage(doorTextureUrl);
          drawFullSize(doorTexture, 1);
        }
      }
      
      // Layer 7: Graphic
      if (selectedGraphic && selectedGraphic.toLowerCase() !== 'none' && selectedLockColor) {
        const hasRivetsSelected = accessories && (Array.isArray(accessories) ? accessories.includes('RIVET_TRIM') : accessories['RIVET_TRIM']);
        const hasRivetsIncluded = this.checkIfIncluded('RIVET_TRIM');
        const hasRivets = hasRivetsSelected || hasRivetsIncluded;
        
        let effectiveGraphic = selectedGraphic;
        if (hasRivets && selectedGraphic.toUpperCase() === 'TRADITIONAL') {
          effectiveGraphic = 'LOGO_ONLY';
        }
        
        let grayscaleGraphicUrl = null;
        if (hasRivets) {
          grayscaleGraphicUrl = manifest.graphics?.[effectiveGraphic]?.RIVETS?.[sizeCode] ||
                               manifest.graphics?.[effectiveGraphic]?.rivets?.[sizeCode];
        }
        
        if (!grayscaleGraphicUrl) {
          grayscaleGraphicUrl = manifest.graphics?.[effectiveGraphic]?.GRAYSCALE?.[sizeCode] || 
                               manifest.graphics?.[effectiveGraphic]?.grayscale?.[sizeCode];
        }
        
        const isLogoOnly = effectiveGraphic.toUpperCase() === 'LOGO_ONLY' || effectiveGraphic.toUpperCase() === 'LOGO-ONLY';
        if (!grayscaleGraphicUrl && isLogoOnly) {
          const logoGrayscale = manifest.graphics?.[effectiveGraphic]?.GRAYSCALE || 
                               manifest.graphics?.[effectiveGraphic]?.grayscale;
          if (typeof logoGrayscale === 'string') {
            grayscaleGraphicUrl = logoGrayscale;
          }
        }
        
        if (grayscaleGraphicUrl) {
          // Just draw the graphic without color mask for testing
          const graphicImg = await loadImage(grayscaleGraphicUrl);
          if (graphicImg) {
            drawFullSize(graphicImg, 1);
            console.log('✅ Canvas: Graphic drawn (no color mask for testing)');
          }
        }
      }
      
      // Layer 8: Lock
      if (selectedLockType && selectedLockColor) {
        const lockImageUrl = manifest.locks?.[selectedLockType]?.[selectedLockColor];
        if (lockImageUrl) {
          const lockImg = await loadImage(lockImageUrl);
          drawFullSize(lockImg, 1);
          console.log('✅ Canvas: Lock drawn');
        }
      }
      
      // Layer 9: Hinges
      const selectedHingeSwing = this.state.selections['hinge_swing']?.[0];
      if (selectedHingeType && selectedHingeSwing) {
        const hingeTypeUpper = selectedHingeType.toUpperCase();
        const swingDirectionUpper = selectedHingeSwing.toUpperCase();
        const shouldMirror = swingDirectionUpper === 'RIGHT';
        
        // Helper to draw with optional mirroring at full canvas size
        const drawWithMirror = (img, mirror = false) => {
          if (!img) return;
          ctx.save();
          if (mirror) {
            ctx.translate(canvasWidth, 0);
            ctx.scale(-1, 1);
          }
          ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);
          ctx.restore();
        };
        
        if (hingeTypeUpper === 'EXTERNAL') {
          // External hinges
          const hingeConfig = manifest.hinges?.images?.EXTERNAL;
          if (hingeConfig && hingeConfig[sizeCode]) {
            const hingeImageUrl = hingeConfig[sizeCode]?.grayscale;
            if (hingeImageUrl) {
              const hingeImg = await loadImage(hingeImageUrl);
              drawWithMirror(hingeImg, shouldMirror);
              console.log('✅ Canvas: External hinges drawn', shouldMirror ? '(mirrored)' : '');
            }
          }
        } else if (hingeTypeUpper === 'CRANE' || hingeTypeUpper === 'CRANE_BLANK') {
          // CRANE_BLANK: use LEFT images and mirror for RIGHT swing
          // CRANE: use actual LEFT/RIGHT images (no mirroring)
          const checkSwing = hingeTypeUpper === 'CRANE_BLANK' ? 'LEFT' : swingDirectionUpper;
          const hingeData = manifest.hinges?.images?.[hingeTypeUpper]?.GRAYSCALE?.[checkSwing]?.[sizeCode];
          
          // Check if CRANE should use CRANE_BLANK's split structure (same logic as DOM rendering)
          const blankHingeDataLeft = manifest.hinges?.images?.CRANE_BLANK?.GRAYSCALE?.LEFT?.[sizeCode];
          const shouldUseSplitFromBlank = hingeTypeUpper === 'CRANE' && blankHingeDataLeft && 
                                         typeof blankHingeDataLeft === 'object' && 
                                         (blankHingeDataLeft.BODY_COLOR || blankHingeDataLeft.CRANE_COLOR);
          
          if (shouldUseSplitFromBlank) {
            // CRANE using CRANE_BLANK's split structure
            console.log('🔗 Canvas: CRANE using CRANE_BLANK split structure');
            
            // First draw the base CRANE image (use actual LEFT or RIGHT image)
            const craneBaseImageUrl = manifest.hinges?.images?.CRANE?.GRAYSCALE?.[swingDirectionUpper]?.[sizeCode];
            if (typeof craneBaseImageUrl === 'string') {
              const craneBaseImg = await loadImage(craneBaseImageUrl);
              drawFullSize(craneBaseImg, 1); // Use actual LEFT/RIGHT image, no mirroring
              console.log('✅ Canvas: CRANE base image drawn (' + swingDirectionUpper + ')');
            }
            
            // Then overlay CRANE_BLANK's split structure (LEFT images, mirror for RIGHT)
            if (blankHingeDataLeft.BODY_COLOR) {
              const bodyImg = await loadImage(blankHingeDataLeft.BODY_COLOR);
              drawWithMirror(bodyImg, shouldMirror);
            }
            if (blankHingeDataLeft.CRANE_COLOR) {
              const craneImg = await loadImage(blankHingeDataLeft.CRANE_COLOR);
              drawWithMirror(craneImg, shouldMirror);
            }
            if (blankHingeDataLeft.CAPS) {
              const capsImg = await loadImage(blankHingeDataLeft.CAPS);
              drawWithMirror(capsImg, shouldMirror);
            }
            console.log('✅ Canvas: CRANE with CRANE_BLANK overlays drawn', shouldMirror ? '(overlays mirrored)' : '');
            
          } else if (hingeData && typeof hingeData === 'object' && hingeData.BASE) {
            // Split structure - CRANE_BLANK uses LEFT and mirrors, others use actual swing direction
            const shouldMirrorSplit = hingeTypeUpper === 'CRANE_BLANK' && shouldMirror;
            if (hingeData.BASE) {
              const baseImg = await loadImage(hingeData.BASE);
              drawWithMirror(baseImg, shouldMirrorSplit);
            }
            if (hingeData.BODY_COLOR) {
              const bodyImg = await loadImage(hingeData.BODY_COLOR);
              drawWithMirror(bodyImg, shouldMirrorSplit);
            }
            if (hingeData.CRANE_COLOR) {
              const craneImg = await loadImage(hingeData.CRANE_COLOR);
              drawWithMirror(craneImg, shouldMirrorSplit);
            }
            if (hingeData.CAPS) {
              const capsImg = await loadImage(hingeData.CAPS);
              drawWithMirror(capsImg, shouldMirrorSplit);
            }
            console.log('✅ Canvas: Crane hinges (split structure) drawn', shouldMirrorSplit ? '(mirrored)' : '');
            
          } else if (typeof hingeData === 'string') {
            // Single grayscale image - CRANE_BLANK mirrors, CRANE uses actual swing
            const hingeImg = await loadImage(hingeData);
            const shouldMirrorSingle = hingeTypeUpper === 'CRANE_BLANK' && shouldMirror;
            drawWithMirror(hingeImg, shouldMirrorSingle);
            console.log('✅ Canvas: Crane hinges drawn', shouldMirrorSingle ? '(mirrored)' : '');
            
          } else {
            // Legacy structure - try LEFT first, then fallback to color variant
            let hingeImageUrl = manifest.hinges?.images?.[hingeTypeUpper]?.LEFT?.[selectedLockColor?.toUpperCase()];
            if (!hingeImageUrl) {
              hingeImageUrl = manifest.hinges?.images?.[hingeTypeUpper]?.[swingDirectionUpper]?.[selectedLockColor?.toUpperCase()];
            }
            if (hingeImageUrl) {
              const hingeImg = await loadImage(hingeImageUrl);
              drawWithMirror(hingeImg, shouldMirror);
              console.log('✅ Canvas: Crane hinges (legacy) drawn', shouldMirror ? '(mirrored)' : '');
            }
          }
        }
      }
      
      // Layer 10: Hinge Caps (if selected)
      const hasHingeCapsSelected = accessories && (Array.isArray(accessories) ? accessories.includes('HINGE_CAPS') : accessories['HINGE_CAPS']);
      const hasHingeCapsIncluded = this.checkIfIncluded('HINGE_CAPS');
      if ((hasHingeCapsSelected || hasHingeCapsIncluded) && selectedHingeType === 'EXTERNAL' && selectedHingeSwing) {
        const hingeCapImageUrl = manifest.hingeCaps?.images?.EXTERNAL?.[sizeCode]?.grayscale;
        if (hingeCapImageUrl) {
          const shouldMirrorCaps = selectedHingeSwing.toUpperCase() === 'RIGHT';
          const hingeCapImg = await loadImage(hingeCapImageUrl);
          if (hingeCapImg) {
            ctx.save();
            if (shouldMirrorCaps) {
              ctx.translate(canvasWidth, 0);
              ctx.scale(-1, 1);
            }
            drawFullSize(hingeCapImg, 1);
            ctx.restore();
            console.log('✅ Canvas: Hinge caps drawn', shouldMirrorCaps ? '(mirrored)' : '');
          }
        }
      }
      
      // Layer 11: Accessories
      if (accessories && typeof accessories === 'object') {
        for (const [accessoryCode, quantity] of Object.entries(accessories)) {
          if (quantity > 0) {
            const accessoryImageUrl = manifest.accessories?.[accessoryCode];
            if (accessoryImageUrl) {
              const accessoryImg = await loadImage(accessoryImageUrl);
              drawCentered(accessoryImg, 1);
              console.log('✅ Canvas: Accessory drawn:', accessoryCode);
            }
          }
        }
      }
      
      console.log('✅ Canvas rendering complete');
      return canvas;
      
    } catch (error) {
      console.error('Error rendering safe to canvas:', error);
      return null;
    }
  }

  reopenQuiz() {
    document.getElementById('summaryModal').classList.remove('active');
    // Set editing flag to true so first step gets the pulsating ring
    this.isEditingFromSummary = true;
    setTimeout(() => {
      // Keep selections, just go back to step 1
      this.currentStep = 1;
      
      // Don't rebuild steps - they should already be correct from the completed configuration
      // Just make sure we have the right step count
      
      this.saveSelectionsToStorage();
      document.getElementById('quizModal').classList.add('active');
      this.updateStepUI();
      this.updateProgress();
    }, 300);
  }

  async captureAndUploadConfiguredImage() {
    try {
      console.log('📸 Capturing configured safe image...');
      console.log('Current selections:', this.state.selections);
      
      // Render the safe to canvas (reuse existing method)
      const canvas = await this.renderSafeToCanvas();
      
      if (!canvas) {
        console.error('⚠️ Failed to render safe to canvas - canvas is null');
        return null;
      }
      
      console.log('✓ Canvas rendered successfully:', canvas.width, 'x', canvas.height);
      
      // Convert canvas to blob
      const blob = await new Promise(resolve => {
        canvas.toBlob(resolve, 'image/png', 0.95);
      });
      
      if (!blob) {
        console.error('⚠️ Failed to convert canvas to blob');
        return null;
      }
      
      console.log('✓ Canvas converted to blob:', blob.size, 'bytes');
      console.log('📤 Uploading configured safe image to Shopify...');
      
      // Create FormData for file upload
      const formData = new FormData();
      const timestamp = Date.now();
      const filename = `configured-safe-${timestamp}.png`;
      formData.append('file', blob, filename);
      
      console.log('📤 Posting to /apps/meta-variant-builder-api/upload-image');
      
      // Upload to Shopify Files API via your backend
      const uploadResponse = await fetch('/apps/meta-variant-builder-api/upload-image', {
        method: 'POST',
        body: formData
      });
      
      console.log('📡 Upload response status:', uploadResponse.status, uploadResponse.statusText);
      
      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        console.error('⚠️ Image upload failed:', uploadResponse.status, uploadResponse.statusText);
        console.error('Error details:', errorText);
        return null;
      }
      
      const uploadResult = await uploadResponse.json();
      console.log('📦 Upload result:', uploadResult);
      
      if (uploadResult.success && uploadResult.url) {
        console.log('✅ Image uploaded successfully:', uploadResult.url);
        return uploadResult.url;
      } else {
        console.error('⚠️ No URL returned from upload. Result:', uploadResult);
        return null;
      }
      
    } catch (error) {
      console.error('❌ Error capturing/uploading image:', error);
      console.error('Error stack:', error.stack);
      return null;
    }
  }

  async confirmConfiguration() {
    const btn = document.getElementById('confirmBtn');
    const originalText = btn.innerHTML;
    
    btn.disabled = true;
    btn.innerHTML = `
      <i data-lucide="loader-2" class="icon-sm"></i>
      Capturing your safe...
    `;
    
    if (typeof lucide !== 'undefined') {
      lucide.createIcons();
    }

    try {
      // Capture and upload configured safe image FIRST
      const customImageUrl = await this.captureAndUploadConfiguredImage();
      
      btn.innerHTML = `
        <i data-lucide="loader-2" class="icon-sm"></i>
        Building properties...
      `;
      if (typeof lucide !== 'undefined') {
        lucide.createIcons();
      }

      // Build properties
    const properties = {};
    
    // Get current size dimensions for price lookup
    const sizeDimensions = this.getCurrentSizeDimensions();
    
    for (const [groupHandle, selectedCodes] of Object.entries(this.state.selections)) {
      const group = this.data.optionGroups.find(g => g.handle === groupHandle);
      if (!group) continue;

      selectedCodes.forEach(code => {
        const value = this.getValueByCode(groupHandle, code);
        if (!value) return;

        // Use getOptionPrice method which properly handles defaultPrice -> dependencies -> priceModifierValue fallback
        const actualPrice = this.getOptionPrice(value, groupHandle);

        // Clean up the display value for Shopify compatibility
        let displayValue = value.label;
        if (actualPrice && actualPrice !== 0) {
          const sign = actualPrice > 0 ? '+' : '';
          displayValue += ` (${sign}$${this.formatPrice(actualPrice)})`;
        }

        // Clean property name and value for Shopify
        const cleanGroupName = group.displayName.replace(/[^\w\s-]/g, '').trim();
        
        // Remove HTML entities and clean up the value
        let cleanValue = displayValue
          .replace(/&quot;/g, '"')
          .replace(/&#39;/g, "'")
          .replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/"/g, '')
          .replace(/'/g, '')
          .replace(/[^\w\s\-+().,]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
        
        // Skip empty values
        if (cleanValue && cleanValue.length > 0) {
          properties[cleanGroupName] = cleanValue;
        }
      });
    }

    this.calculatePrice();
    properties['_Estimated_Total'] = this.formatPrice(this.state.totalPrice);
    properties['_Configured'] = 'true';
    properties['_Base_Price'] = this.formatPrice(this.state.basePrice);
    properties['_Add_ons_Total'] = this.formatPrice(this.state.modifiersTotal);
    properties['_Price_Note'] = 'Estimated price - final price calculated at checkout';
    properties['_Custom_Pricing'] = 'true'; // Flag to tell backend to ignore automatic price modifiers
    properties['_Selected_Size'] = sizeDimensions || 'unknown'; // Include size for backend reference
    
    // Add custom configured image URL (prefix with _ to hide from cart display as text)
    if (customImageUrl) {
      properties['_configured_image'] = customImageUrl;
      console.log('✅ Added configured image to properties:', customImageUrl);
    }

    // For products without variants, we need to use the base product
    // and add all configuration as properties
    const data = {
      id: this.data.productId,
      quantity: 1,
      properties: properties
    };

    // Validate product ID
    if (!this.data.productId || this.data.productId === 0) {
      console.error('❌ Invalid product ID:', this.data.productId);
      alert('Error: Invalid product configuration. Please refresh the page and try again.');
      return;
    }

    try {
      // Format selections for backend API (same format as app block)
      const flatSelections = {};
      for (const [groupHandle, selectedCodes] of Object.entries(this.state.selections)) {
        if (!selectedCodes) continue;
        
        // Handle different selection formats
        if (typeof selectedCodes === 'object' && !Array.isArray(selectedCodes)) {
          // Multi-select with quantities
          flatSelections[groupHandle] = selectedCodes;
        } else if (Array.isArray(selectedCodes)) {
          // Single-select (array with one value)
          flatSelections[groupHandle] = selectedCodes[0];
        }
      }
      
      // Add color overrides for cart rendering and display
      if (this.state.rivetColorOverride) {
        flatSelections['rivet_color'] = this.state.rivetColorOverride;
        console.log('📦 Adding rivet_color to selections:', this.state.rivetColorOverride);
      }
      
      if (this.state.craneHingeColorOverride) {
        flatSelections['crane_hinge_color'] = this.state.craneHingeColorOverride;
        console.log('📦 Adding crane_hinge_color to selections:', this.state.craneHingeColorOverride);
      }

      btn.innerHTML = `
        <i data-lucide="loader-2" class="icon-sm"></i>
        Building your custom safe...
      `;
      if (typeof lucide !== 'undefined') {
        lucide.createIcons();
      }

      // Call backend API to create variant with correct pricing
      const response = await fetch('/apps/meta-variant-builder-api', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: `
            mutation CreateConfiguredVariant(
              $productId: GadgetID!
              $selectedOptions: JSON!
              $quantity: Float
            ) {
              createConfiguredVariant(
                productId: $productId
                selectedOptions: $selectedOptions
                quantity: $quantity
              ) {
                success
                errors {
                  message
                  code
                }
                result
              }
            }
          `,
          variables: {
            productId: this.data.productId,
            selectedOptions: JSON.stringify(flatSelections),
            quantity: 1
          }
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error:', errorText);
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      // Handle GraphQL errors
      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message || 'Failed to create variant');
      }

      const data = result.data?.createConfiguredVariant;

      if (!data?.success) {
        const errorMsg = data?.errors?.[0]?.message || 'Failed to create configured variant';
        throw new Error(errorMsg);
      }

      // Extract the result data (Gadget wraps the return value in 'result')
      const variantData = data.result;

      if (!variantData?.variantId) {
        throw new Error('No variant ID returned from backend');
      }

      // Convert GID format to numeric ID for cart API
      let variantId = variantData.variantId;
      if (variantId.startsWith('gid://shopify/ProductVariant/')) {
        variantId = variantId.replace('gid://shopify/ProductVariant/', '');
      }

      // Variant created successfully - now add to cart
      btn.innerHTML = `
        <i data-lucide="loader-2" class="icon-sm"></i>
        Adding to cart...
      `;
      if (typeof lucide !== 'undefined') {
        lucide.createIcons();
      }

      // Merge backend properties with frontend properties (includes _configured_image)
      const cartProperties = {
        ...(variantData.properties || {}),
        ...(customImageUrl ? { '_configured_image': customImageUrl } : {})
      };

      console.log('🛒 Cart properties being sent:', cartProperties);
      console.log('🖼️ Configured image URL:', customImageUrl);

      const cartPayload = {
        items: [{
          id: variantId,
          quantity: 1,
          properties: cartProperties
        }]
      };
      
      console.log('🛒 Full cart payload:', cartPayload);

      const cartResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cartPayload)
      });

      if (!cartResponse.ok) {
        const cartError = await cartResponse.text();
        console.error('❌ Cart Error:', cartError);
        throw new Error(`Failed to add to cart: ${cartError}`);
      }

      const cartData = await cartResponse.json();

      // Clear selections and reset state before redirect
      this.state.selections = {};
      localStorage.removeItem('configuratorSelections');
      
      // Dispatch event to reset configurator
      window.dispatchEvent(new CustomEvent('configurator:reset'));

      // Show success message
      btn.innerHTML = `
        <i data-lucide="check" class="icon-sm"></i>
        Added to Cart!
      `;
      if (typeof lucide !== 'undefined') {
        lucide.createIcons();
      }

      // Wait and verify before redirecting
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Redirect to cart page
      window.location.href = '/cart';

    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('Sorry, there was an error adding this item to your cart. Please try again.');
      
      // Reset button state on error
      btn.disabled = false;
      btn.innerHTML = originalText;
      if (typeof lucide !== 'undefined') {
        lucide.createIcons();
      }
    }
    } catch (error) {
      console.error('Error in confirmConfiguration:', error);
      alert('Sorry, there was an error processing your configuration. Please try again.');
      
      // Reset button state on error
      btn.disabled = false;
      btn.innerHTML = originalText;
      if (typeof lucide !== 'undefined') {
        lucide.createIcons();
      }
    }
  }

  closeAllModals() {
    // Save current step so it can be restored when reopening
    const quizModal = document.getElementById('quizModal');
    const summaryModal = document.getElementById('summaryModal');
    if (quizModal && quizModal.classList.contains('active')) {
      this.savedStep = this.currentStep;
    } else if (summaryModal && summaryModal.classList.contains('active')) {
      // Mark that user was on summary so they return there
      this.savedStep = this.steps.length + 1; // sentinel: beyond last step = summary
    }

    // Save current state before closing
    this.saveSelectionsToStorage();
    
    document.querySelectorAll('.modal').forEach(modal => {
      modal.classList.remove('active');
    });
    
    // Restore body scroll
    document.body.classList.remove('modal-open');
    document.body.style.overflow = '';
  }

  resetConfigurator() {
    // Clear all selections
    this.state.selections = {};
    this.state.basePrice = 0;
    this.state.totalPrice = 0;
    this.state.modifiersTotal = 0;
    this.state.selectedModel = null;
    this.state.currentConfiguration = null;
    
    // Clear localStorage
    localStorage.removeItem('configuratorSelections');
    
    // Reset current step
    this.currentStep = 1;
    this.visitedSteps = new Set([1]); // Reset to only step 1
    this.isEditingFromSummary = false;
    
    // Close all modals
    document.querySelectorAll('.modal').forEach(modal => {
      modal.classList.remove('active');
    });
    
    // Clear all selections in UI
    document.querySelectorAll('.size-card').forEach(card => {
      card.classList.remove('selected');
    });
    document.querySelectorAll('.option-card').forEach(card => {
      card.classList.remove('selected');
    });
    
    // Reset price display
    this.updatePriceDisplay();
    
    // Notify app block that configuration is cleared
    window.configuratorState = {
      selections: {},
      totalPrice: 0,
      basePrice: 0,
      modifiersTotal: 0
    };
    
    window.dispatchEvent(new CustomEvent('configurator:updated', {
      detail: {
        selections: {},
        totalPrice: 0,
        basePrice: 0,
        modifiersTotal: 0
      }
    }));
  }

  updateConfigBadge() {
    // Config badge has been removed from the UI
    // This function is kept as a no-op to prevent errors
    return;
  }

  formatPrice(price) {
    // Format price with commas for thousands
    const formatted = Math.abs(price).toFixed(2);
    const parts = formatted.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  }

  initializePricing() {
    // Get the actual product price from the page or set a default
    // For now, we'll use a base price that can be adjusted
    this.state.originalPrice = this.getProductBasePrice();
    this.state.basePrice = this.state.originalPrice;
    this.state.modifiersTotal = 0;
    this.state.totalPrice = this.state.basePrice + this.state.modifiersTotal;
    
    // Update the price display
    this.updatePriceDisplay();
  }

  getCurrentModel() {
    if (!this.state.currentConfiguration || !this.state.currentConfiguration.modelIds || this.state.currentConfiguration.modelIds.length === 0) {
      return null;
    }
    const modelId = this.state.currentConfiguration.modelIds[0];
    return this.models.find(m => m.id === modelId);
  }

  /**
   * Helper to render a single cabinet_size option card into the option grid.
   * Extracted to allow grouped rendering (Standard vs Watch Winders).
   */
  _renderCabinetSizeOption(value, group, step, optionGrid, hideOptionValues) {
    // Check showOptions filtering
    const isFilteredBySize = this.isFilteredByShowOptions(group, value);
    if (isFilteredBySize) return;

    // Check hideOptions
    if (this.state.currentConfiguration &&
        this.state.currentConfiguration.hideOptions &&
        this.state.currentConfiguration.hideOptions.includes(value.code)) {
      return;
    }

    // Check model hideOptionValues
    if (hideOptionValues.includes(value.code)) return;

    const isAvailable = this.isValueAvailableForStep(step, value);
    const isMultiSelect = group.selectType === 'multi';
    if (!isAvailable && !isMultiSelect) return;

    const hasColor = value.hexColor && value.hexColor.trim() !== '';
    const hasSizeImage = value.sizeImage && value.sizeImage.trim() !== '';
    const hasOptionImage = value.optionImage && value.optionImage.trim() !== '';
    const hasTooltip = value.tooltip && value.tooltip.trim() !== '';
    const isIncluded = this.checkIfIncluded(value.code);

    let priceDisplay = '';
    if (isIncluded) {
      priceDisplay = '<span class="option-price included">INCLUDED</span>';
    } else {
      const optionPrice = this.getOptionPrice(value, group.handle);
      if (optionPrice !== 0) {
        const sign = optionPrice > 0 ? '+' : '';
        priceDisplay = `<span class="option-price">${sign}$${this.formatPrice(optionPrice)}</span>`;
      }
    }

    const optionCard = document.createElement('button');
    optionCard.className = [
      'option-card',
      hasOptionImage ? 'has-image' : '',
      isIncluded ? 'option-included' : '',
      !isAvailable ? 'option-disabled' : ''
    ].filter(Boolean).join(' ');
    optionCard.dataset.value = value.code;
    optionCard.dataset.group = group.handle;

    if (!isAvailable) {
      optionCard.disabled = true;
      optionCard.style.cursor = 'not-allowed';
    }

    if (hasOptionImage) {
      optionCard.innerHTML = `
        ${hasTooltip ? `<div class="tooltip-icon" data-tooltip="${value.tooltip.replace(/"/g, '&quot;')}" tabindex="0"><i data-lucide="info"></i></div>` : ''}
        <div class="size-image-container">
          <img src="${value.optionImage}" alt="${value.label.replace(/"/g, '&quot;')}" class="size-image" />
          <div class="size-tooltip">
            <div class="size-tooltip-text">${value.label}</div>
            ${priceDisplay ? `<div class="size-tooltip-price">${priceDisplay}</div>` : ''}
          </div>
        </div>
        <span>${value.label}</span>
        ${priceDisplay}
      `;
    } else if (hasColor) {
      optionCard.innerHTML = `
        ${hasTooltip ? `<div class="tooltip-icon" data-tooltip="${value.tooltip.replace(/"/g, '&quot;')}" tabindex="0"><i data-lucide="info"></i></div>` : ''}
        <div class="color-preview" style="background-color: ${value.hexColor}"></div>
        <span>${value.label}</span>
        ${priceDisplay}
      `;
    } else {
      optionCard.innerHTML = `
        ${hasTooltip ? `<div class="tooltip-icon" data-tooltip="${value.tooltip.replace(/"/g, '&quot;')}" tabindex="0"><i data-lucide="info"></i></div>` : ''}
        <div class="option-icon"><i data-lucide="check" class="icon-lg"></i></div>
        <div class="option-content">
          <h3>${value.label}</h3>
          ${priceDisplay ? `<p>${priceDisplay}</p>` : ''}
        </div>
      `;
    }

    if (hasTooltip) {
      const tooltipIcon = optionCard.querySelector('.tooltip-icon');
      if (tooltipIcon) {
        tooltipIcon.addEventListener('click', (e) => e.stopPropagation());
      }
    }

    optionGrid.appendChild(optionCard);
  }

  calculateAccessoryScale(currentSizeCode, referenceSizeCode) {
    // Parse dimensions from size codes (e.g., "4026x20" -> height: 40)
    // We'll use the height dimension to calculate scale
    const parseHeight = (sizeCode) => {
      const match = sizeCode.match(/^(\d+)/);
      return match ? parseInt(match[1], 10) : null;
    };
    
    const currentHeight = parseHeight(currentSizeCode);
    const referenceHeight = parseHeight(referenceSizeCode);
    
    if (!currentHeight || !referenceHeight) {
      console.warn('⚠️ Could not parse size dimensions for scaling:', { currentSizeCode, referenceSizeCode });
      return 1; // No scaling if we can't parse
    }
    
    // Calculate inverse scale: as safe gets bigger, accessories get proportionally smaller
    // Reference size gets scale of 1.0
    const scaleFactor = referenceHeight / currentHeight;
    
    // Clamp scale between 0.5 and 1.5 to prevent extreme scaling
    return Math.max(0.5, Math.min(1.5, scaleFactor));
  }

  calculateGraphicScale(currentSizeCode, referenceSizeCode) {
    // Parse dimensions from size codes (e.g., "2214x18" -> height: 22)
    const parseHeight = (sizeCode) => {
      const match = sizeCode.match(/^(\d+)/);
      return match ? parseInt(match[1], 10) : null;
    };
    
    const currentHeight = parseHeight(currentSizeCode);
    const referenceHeight = parseHeight(referenceSizeCode);
    
    if (!currentHeight || !referenceHeight) {
      console.warn('⚠️ Could not parse size dimensions for graphic scaling:', { currentSizeCode, referenceSizeCode });
      return 1; // No scaling if we can't parse
    }
    
    // Calculate proportional scale: as safe gets bigger, graphics get proportionally bigger
    // This is the OPPOSITE of accessory scaling
    // Graphics are designed for the smallest safe (reference), so reference size gets scale of 1.0
    const scaleFactor = currentHeight / referenceHeight;
    
    // Clamp scale between 0.6 and 2.0 to prevent extreme scaling
    return Math.max(0.6, Math.min(2.0, scaleFactor));
  }

  initKeyFeatures() {
    try {
      const features = this.container.querySelector('.key-features');
      if (!features) return;

      // Only enable the simple mobile carousel behavior on small screens
      const mq = window.matchMedia('(max-width: 767px)');
      if (!mq.matches) return;

      const items = Array.from(features.querySelectorAll('.feature-item'));
      if (items.length === 0) return;

      // Ensure indicators container exists and is scoped inside this.container
      let indicators = this.container.querySelector('.carousel-indicators');
      if (!indicators) {
        indicators = document.createElement('div');
        indicators.className = 'carousel-indicators';
        features.insertAdjacentElement('afterend', indicators);
      } else {
        indicators.innerHTML = '';
      }

      const itemsPerPage = 2; // CSS shows two items per view on mobile
      const pageCount = Math.max(1, Math.ceil(items.length / itemsPerPage));

      const updateActiveDot = () => {
        const scrollLeft = features.scrollLeft || 0;
        const width = features.offsetWidth || 1;
        const page = Math.round(scrollLeft / width);
        Array.from(indicators.children).forEach((dot, idx) => {
          dot.classList.toggle('active', idx === page);
        });
      };

      for (let i = 0; i < pageCount; i++) {
        const dot = document.createElement('div');
        dot.className = 'carousel-dot';
        dot.dataset.page = i;
        dot.addEventListener('click', () => {
          features.scrollTo({ left: i * features.offsetWidth, behavior: 'smooth' });
        });
        indicators.appendChild(dot);
      }

      // Update active dot on scroll
      features.addEventListener('scroll', () => {
        updateActiveDot();
      }, { passive: true });

      // Initial activation
      updateActiveDot();
    } catch (e) {
      // Fail silently to avoid breaking configurator
      console.warn('initKeyFeatures error', e);
    }
  }

  getProductBasePrice() {
    // NEW: Get the base price from the Model Size Configuration (convert from cents to dollars)
    if (this.state.currentConfiguration && this.state.currentConfiguration.basePrice) {
      const priceInCents = parseFloat(this.state.currentConfiguration.basePrice) || 0;
      return priceInCents / 100;
    }
    
    // Fallback: Try to get the product price from the page
    const priceElement = document.querySelector('.price .money, .product-price .money, [data-price]');
    if (priceElement) {
      const priceText = priceElement.textContent || priceElement.getAttribute('data-price');
      const price = parseFloat(priceText.replace(/[^0-9.-]/g, ''));
      if (!isNaN(price)) {
        return price;
      }
    }
    
    // Fallback to a default base price
    return 0;
  }

  checkIfIncluded(optionCode) {
    // NEW: First check if included in Model Size Configuration
    if (this.state.currentConfiguration && 
        this.state.currentConfiguration.includedOptions && 
        this.state.currentConfiguration.includedOptions.includes(optionCode)) {
      return true;
    }
    
    // Check if this option code is included in any selected value's includesOptions array OR dependencies.includes array
    for (const [groupHandle, selectedCodes] of Object.entries(this.state.selections)) {
      const selected = Array.isArray(selectedCodes) ? selectedCodes : Object.keys(selectedCodes);
      
      for (const code of selected) {
        const value = this.getValueByCode(groupHandle, code);
        if (value) {
          // Check includesOptions field
          if (value.includesOptions && value.includesOptions.includes(optionCode)) {
            return true;
          }
          // Check dependencies.includes field (for options like DISTRESSED finish)
          if (value.dependencies && value.dependencies.includes && Array.isArray(value.dependencies.includes) && value.dependencies.includes.includes(optionCode)) {
            return true;
          }
        }
      }
    }
    return false;
  }

  getOptionPrice(option, groupHandle) {
    // Get the price for an option, handling new Default Price logic
    if (!option) return 0;
    
    // If this option is included, price is 0
    if (this.checkIfIncluded(option.code)) {
      return 0;
    }
    
    // NEW PRICING LOGIC:
    // 1. First check Default Price (new field replacing old Base Price for add-ons) - convert from cents to dollars
    if (option.defaultPrice !== undefined && option.defaultPrice !== null && option.defaultPrice !== '') {
      const priceInCents = parseFloat(option.defaultPrice) || 0;
      return priceInCents / 100;
    }
    
    // 2. If Default Price is blank, check dependencies for size-based price
    if (option.dependencies) {
      const selectedSize = this.state.selections['size']?.[0]
        || this.state.selections['ls_size']?.[0]
        || this.state.selections['amsec_size']?.[0];
      
      // Check dependencies.price_by_size first (new format)
      // NOTE: price_by_size values are already in DOLLARS (manually entered in JSON), not cents
      if (option.dependencies.price_by_size && selectedSize) {
        const currentModel = this.getCurrentModel();
        
        // First try model-specific pricing (new format)
        if (currentModel && option.dependencies.price_by_size[currentModel.id]) {
          const modelPrices = option.dependencies.price_by_size[currentModel.id];
          const sizePrice = modelPrices[selectedSize];
          if (sizePrice !== undefined && sizePrice !== null) {
            const price = parseFloat(sizePrice) || 0;
            return price; // Already in dollars, no conversion needed
          }
        }
        
        // Fall back to direct size pricing (old format)
        const sizePrice = option.dependencies.price_by_size[selectedSize];
        if (sizePrice !== undefined && sizePrice !== null) {
          const price = parseFloat(sizePrice) || 0;
          return price; // Already in dollars, no conversion needed
        }
      }
      
      // Check for direct size key in dependencies (alternative format)
      if (selectedSize && option.dependencies[selectedSize] !== undefined) {
        const price = parseFloat(option.dependencies[selectedSize]) || 0;
        return price;
      }
    }
    
    // 3. Fall back to priceModifierValue (legacy support)
    if (option.priceModifierValue !== undefined && option.priceModifierValue !== null) {
      const price = parseFloat(option.priceModifierValue) || 0;
      return price;
    }
    
    // 4. If nothing found, price is $0
    return 0;
  }

  getCurrentSizeDimensions() {
    // Get the current size selection and extract width x height x depth
    if (this.state.selections['size'] && this.state.selections['size'].length > 0) {
      const sizeCode = this.state.selections['size'][0];
      const sizeValue = this.getValueByCode('size', sizeCode);
      
      if (sizeValue && sizeValue.code) {
        // Size codes are like "4026x20" or "7251x27" or "7251x40XD"
        // Format: [width][height]x[depth] where width and height are 2 digits each
        
        // Return the exact code format used in price_by_size
        return sizeValue.code;
      }
    }
    return null;
  }

  calculatePrice() {
    
    // Get the base price from Model Size Configuration
    let basePrice = this.getProductBasePrice();
    
    let modifiersTotal = 0;
    
    // Check if custom crane hinge color should incur two-tone pricing
    if (this.state.craneHingeColorOverride) {
      const selectedFinish = this.state.selections['exterior_finish']?.[0];
      const normalizedFinish = selectedFinish?.toLowerCase().replace(/_/g, '-');
      let bodyColor = null;
      
      if (normalizedFinish === 'two-tone' || normalizedFinish === 'two-tone-fade') {
        bodyColor = this.state.selections['body_color']?.[0];
      } else {
        bodyColor = this.state.selections['color_single']?.[0];
      }
      
      // If crane hinge color differs from body color, add two-tone finish price
      if (bodyColor && this.state.craneHingeColorOverride !== bodyColor) {
        const finishGroup = this.data.optionGroups.find(g => g.handle === 'exterior_finish');
        const twoToneOption = finishGroup?.values.find(v => v.code === 'TWO_TONE');
        
        if (twoToneOption) {
          const twoTonePrice = this.getOptionPrice(twoToneOption, 'exterior_finish');
          modifiersTotal += twoTonePrice;
          console.log('💰 Adding two-tone price for custom crane hinge color:', twoTonePrice);
        }
      }
    }

    // Calculate modifiers from all selections (excluding size since it's in the base)
    const sizeGroupHandles = new Set(['size', 'ls_size', 'amsec_size']);
    for (const [groupHandle, selectedCodes] of Object.entries(this.state.selections)) {
      // Skip size groups since they're included in the base price
      if (sizeGroupHandles.has(groupHandle)) continue;
      
      // Check if this is a namespaced inline group key (e.g., "cabinet_inserts_for_cabinet_drawer_small")
      // Extract the actual group handle from the namespace
      let actualGroupHandle = groupHandle;
      if (groupHandle.includes('_for_')) {
        actualGroupHandle = groupHandle.split('_for_')[0];
      }
      
      if (Array.isArray(selectedCodes)) {
        // Single-select options
        selectedCodes.forEach(code => {
          const value = this.getValueByCode(actualGroupHandle, code);
          if (!value) return;

          // Use the new getOptionPrice method which handles all fallback logic
          const optionPrice = this.getOptionPrice(value, actualGroupHandle);
          
          if (optionPrice > 0) {
            modifiersTotal += optionPrice;
          } 
        });
      } else if (typeof selectedCodes === 'object') {
        // Multi-select options with quantities
        for (const [code, quantity] of Object.entries(selectedCodes)) {
          const value = this.getValueByCode(actualGroupHandle, code);
          if (!value) {
            console.warn(`  ⚠️ Value not found for ${actualGroupHandle}.${code}`);
            continue;
          }

          // Ensure quantity is a number
          const qty = parseInt(quantity) || 1;

          // Use the new getOptionPrice method which handles all fallback logic
          const optionPrice = this.getOptionPrice(value, actualGroupHandle);

          // Add price multiplied by quantity
          if (optionPrice > 0) {
            const itemTotal = optionPrice * qty;
            modifiersTotal += itemTotal;
          } 
        }
      }
    }

    this.state.basePrice = basePrice;
    this.state.modifiersTotal = modifiersTotal;
    this.state.totalPrice = basePrice + modifiersTotal;
    
    // Update the price display
    this.updatePriceDisplay();
  }

  updatePriceDisplay() {
    const totalPriceEl = document.getElementById('totalPriceDisplay');
    const summaryBasePriceEl = document.getElementById('summaryBasePrice');
    const summaryBasePriceLabelEl = document.getElementById('summaryBasePriceLabel');
    const summaryModifiersEl = document.getElementById('summaryModifiers');
    const summaryModifiersPriceEl = document.getElementById('summaryModifiersPrice');
    const summaryTotalPriceEl = document.getElementById('summaryTotalPrice');
    const footerPriceEl = document.getElementById('modalFooterPrice');
    const footerPriceValueEl = document.getElementById('footerPriceValue');

    if (totalPriceEl) {
      totalPriceEl.textContent = `$${this.formatPrice(this.state.totalPrice)}`;
    }

    if (summaryBasePriceEl) {
      summaryBasePriceEl.textContent = `$${this.formatPrice(this.state.basePrice)}`;
    }

    // Build dynamic base price label: "{Model} {Size} Base Price:"
    if (summaryBasePriceLabelEl) {
      let labelParts = [];
      const currentModel = this.getCurrentModel();
      if (currentModel) {
        labelParts.push(currentModel.name);
      }
      // Find selected size label
      const sizeHandles = ['size', 'amsec_size', 'ls_size'];
      for (const handle of sizeHandles) {
        if (this.state.selections[handle]?.[0]) {
          const sizeGroup = this.data.optionGroups.find(g => g.handle === handle);
          const sizeOption = sizeGroup?.values.find(v => v.code === this.state.selections[handle][0]);
          if (sizeOption) {
            labelParts.push(sizeOption.label);
          }
          break;
        }
      }
      labelParts.push('Base Price:');
      summaryBasePriceLabelEl.textContent = labelParts.join(' ');
    }
    
    if (this.state.modifiersTotal > 0) {
      if (summaryModifiersEl) {
        summaryModifiersEl.style.display = 'flex';
      }
      if (summaryModifiersPriceEl) {
        summaryModifiersPriceEl.textContent = `+$${this.formatPrice(this.state.modifiersTotal)}`;
      }
    } else {
      if (summaryModifiersEl) {
        summaryModifiersEl.style.display = 'none';
      }
    }
    
    if (summaryTotalPriceEl) {
      summaryTotalPriceEl.textContent = `$${this.formatPrice(this.state.totalPrice)}`;
    }
    
    // Update footer price in quiz modal
    if (footerPriceEl && footerPriceValueEl) {
      if (this.state.totalPrice > 0) {
        footerPriceEl.style.display = 'flex';
        footerPriceValueEl.textContent = `$${this.formatPrice(this.state.totalPrice)}`;
      } else {
        footerPriceEl.style.display = 'none';
      }
    }
  }

  isValueHiddenByDependencies(value) {
    // Check all current selections to see if any of them hide this value
    for (const [groupHandle, selectedCodes] of Object.entries(this.state.selections)) {
      // Handle both array format (single-select) and object format (multi-select)
      const codes = Array.isArray(selectedCodes) ? selectedCodes : Object.keys(selectedCodes);
      
      for (const selectedCode of codes) {
        // Get the selected option value
        const selectedValue = this.getValueByCode(groupHandle, selectedCode);
        
        if (!selectedValue) continue;
        
        // Check if this selected value has a "hides" dependency
        if (selectedValue.dependencies?.hides) {
          const hiddenCodes = selectedValue.dependencies.hides;
          
          // If this value's code is in the hides list, it should be hidden
          if (Array.isArray(hiddenCodes) && hiddenCodes.includes(value.code)) {
            return true;
          }
        }
      }
    }
    
    return false;
  }

  isValueAvailable(group, value) {
    
    // CHECK: Option value dependencies - hides property
    // Check if any currently selected option hides this option value
    if (this.isValueHiddenByDependencies(value)) {
      return false;
    }
    
    // CRANE HINGE GRAPHICS RESTRICTION
    // When crane or crane_blank hinge is selected, only allow 'no graphic'
    if (group.handle === 'graphic') {
      const selectedHingeType = this.state.selections['hinge_type']?.[0];
      const hingeTypeUpper = selectedHingeType?.toUpperCase();
      
      if (hingeTypeUpper === 'CRANE' || hingeTypeUpper === 'CRANE_BLANK') {
        // Only allow NONE option (the "No Graphic" option code)
        if (value.code !== 'NONE') {
          return false;
        }
      }
    }
    
    // PRIMARY CHECK: Model Size Configuration hideOptions
    // If an option is in hideOptions, it should be hidden
    if (this.state.currentConfiguration && 
        this.state.currentConfiguration.hideOptions && 
        this.state.currentConfiguration.hideOptions.length > 0) {
      
      if (this.state.currentConfiguration.hideOptions.includes(value.code)) {
        return false;
      }
    }
    
    // SECONDARY CHECK: Model Size Configuration availableOptions
    // Only filter if we have a configuration AND availableOptions is defined
    if (this.state.currentConfiguration && 
        this.state.currentConfiguration.availableOptions && 
        this.state.currentConfiguration.availableOptions.length > 0) {
      
      // Skip this check for size group since sizes are filtered at render time
      if (group.handle !== 'size') {
        // Get all option values in this group
        const allCodesInGroup = group.values.map(v => v.code);
        const matchingAvailableCodesInGroup = allCodesInGroup.filter(code =>
          this.state.currentConfiguration.availableOptions.includes(code)
        );
        
        // Check if ANY option from this group is in availableOptions
        const hasOptionsForThisGroup = matchingAvailableCodesInGroup.length > 0;

        // Liberty color groups are sometimes configured with a single default code in
        // availableOptions; do not collapse the UI to one swatch in that case.
        const isColorGroup = group.handle.includes('color') || group.type === 'color' || group.type === 'color_single';
        const shouldSkipSingleColorWhitelist = isColorGroup && matchingAvailableCodesInGroup.length <= 1;
        
        // Only filter if this group has some options in the availableOptions list
        // This way, groups not mentioned in availableOptions show all their options
        if (hasOptionsForThisGroup && !shouldSkipSingleColorWhitelist) {
          let isAvailable = this.state.currentConfiguration.availableOptions.includes(value.code);
          
          // Check for _WW twin: if this value ends with _WW and its twin (without _WW) is available, allow it
          if (!isAvailable && value.code.endsWith('_WW')) {
            const twinCode = value.code.slice(0, -3); // Remove _WW suffix
            isAvailable = this.state.currentConfiguration.availableOptions.includes(twinCode);
          }
          
          if (!isAvailable) {
            return false;
          }
        } 
      }
    }

    // SPECIAL CASE: watch_winders visibility based on selected cabinet_size
    // If a cabinet size is selected, only allow watch winder options that match
    // the cabinet size suffix mapping: SML_HALF -> SH, SML_FULL -> SF, LRG_HALF -> LH, LRG_FULL -> LF
    if (group.handle === 'watch_winders') {
      // Try to get the selected cabinet size code (handle array, object, or direct string storage formats)
      let selectedCabinetCode = undefined;
      const cabSel = this.state.selections['cabinet_size'];
      if (Array.isArray(cabSel)) selectedCabinetCode = cabSel[0];
      else if (cabSel && typeof cabSel === 'object') selectedCabinetCode = Object.keys(cabSel)[0];
      else if (typeof cabSel === 'string') selectedCabinetCode = cabSel;

      console.log('🎯 Watch winder filtering - cabinet selection:', selectedCabinetCode);
      console.log('🎯 Watch winder option being checked:', value.code);

      if (selectedCabinetCode) {
        const code = selectedCabinetCode.toUpperCase();
        let sizePrefix = null;
        if (code.includes('SML') || code.includes('SMALL')) sizePrefix = 'S';
        else if (code.includes('LRG') || code.includes('LARGE')) sizePrefix = 'L';

        let sizeType = null;
        if (code.includes('HALF')) sizeType = 'H';
        else if (code.includes('FULL')) sizeType = 'F';

        console.log('🔍 Parsed cabinet: prefix=', sizePrefix, 'type=', sizeType);

        if (sizePrefix && sizeType) {
          const expectedSuffix = `${sizePrefix}${sizeType}`; // e.g., SH, SF, LH, LF
          const valCode = (value.code || '').toUpperCase();
          // Robust suffix match: allow `_LH`, `-LH`, or `LH` at end of code (case-insensitive)
          const suffixPattern = new RegExp(`(?:_|-|\\b)${expectedSuffix}$`, 'i');
          const matches = suffixPattern.test(valCode);
          console.log(`🎯 Expected suffix: ${expectedSuffix}, Value: ${valCode}, Matches: ${matches}`);
          if (!matches) return false;
        }
      } else {
        console.log('⚠️ No cabinet size selected yet');
      }
    }

    // Check if this value would be available based on current dependencies
    if (!value.dependencies) {
      return true;
    }

    const deps = value.dependencies;

    // Check requires
    if (deps.requires) {
      for (const [reqGroup, reqCodes] of Object.entries(deps.requires)) {
        const reqGroupHandle = reqGroup.toLowerCase().replace(/[^\w]+/g, '_');
        const selected = this.state.selections[reqGroupHandle] || [];
        
        // Handle both array format (single-select) and object format (multi-select)
        const selectedCodes = Array.isArray(selected) ? selected : Object.keys(selected);
        const hasRequired = reqCodes.some(code => selectedCodes.includes(code));
        
        if (!hasRequired) {
          return false;
        }
      }
    }

    // Check requires_any — OR logic between condition sets, AND logic within each set
    // Example: [{ "ls_color": ["ls_gloss_blue"] }, { "ls_color": ["ls_mirror_black"], "ls_size": ["CP50-7242x27"] }]
    // Value is available if ANY condition set is fully satisfied
    if (deps.requires_any) {
      const anySatisfied = deps.requires_any.some(conditionSet => {
        return Object.entries(conditionSet).every(([reqGroup, reqCodes]) => {
          const reqGroupHandle = reqGroup.toLowerCase().replace(/[^\w]+/g, '_');
          const selected = this.state.selections[reqGroupHandle] || [];
          const selectedCodes = Array.isArray(selected) ? selected : Object.keys(selected);
          return reqCodes.some(code => selectedCodes.includes(code));
        });
      });
      if (!anySatisfied) {
        return false;
      }
    }

    // Check requires_not
    if (deps.requires_not) {
      for (const [reqGroup, reqCodes] of Object.entries(deps.requires_not)) {
        const reqGroupHandle = reqGroup.toLowerCase().replace(/[^\w]+/g, '_');
        const selected = this.state.selections[reqGroupHandle] || [];
        const hasExcluded = reqCodes.some(code => selected.includes(code));
        if (hasExcluded) {
          return false;
        }
      }
    }

    // Check size dependencies
    if (deps.requires_one_of_size || deps.requires_not_size) {
      const sizeSelection = this.state.selections['size']?.[0];
      const sizeValue = this.getValueByCode('size', sizeSelection);
      
      if (deps.requires_one_of_size && sizeValue) {
        const matches = deps.requires_one_of_size.some(size => sizeValue.label.includes(size));
        if (!matches) {
          return false;
        }
      }
      
      if (deps.requires_not_size && sizeValue) {
        const matches = deps.requires_not_size.some(size => sizeValue.label.includes(size));
        if (matches) {
          return false;
        }
      }
    }

    // SPECIAL CASE: watch_winders visibility based on selected cabinet_size
    // If a cabinet size is selected, only allow watch winder options that match
    // the cabinet size suffix mapping: SML_HALF -> SH, SML_FULL -> SF, LRG_HALF -> LH, LRG_FULL -> LF
    if (group.handle === 'watch_winders') {
      // Try to get the selected cabinet size code (handle array, object, or direct string storage formats)
      let selectedCabinetCode = undefined;
      const cabSel = this.state.selections['cabinet_size'];
      if (Array.isArray(cabSel)) selectedCabinetCode = cabSel[0];
      else if (cabSel && typeof cabSel === 'object') selectedCabinetCode = Object.keys(cabSel)[0];
      else if (typeof cabSel === 'string') selectedCabinetCode = cabSel;

      console.log('🎯 Watch winder filtering - cabinet selection:', selectedCabinetCode);
      console.log('🎯 Watch winder option being checked:', value.code);

      if (selectedCabinetCode) {
        const code = selectedCabinetCode.toUpperCase();
        let sizePrefix = null;
        if (code.includes('SML') || code.includes('SMALL')) sizePrefix = 'S';
        else if (code.includes('LRG') || code.includes('LARGE')) sizePrefix = 'L';

        let sizeType = null;
        if (code.includes('HALF')) sizeType = 'H';
        else if (code.includes('FULL')) sizeType = 'F';

        console.log('🔍 Parsed cabinet: prefix=', sizePrefix, 'type=', sizeType);

        if (sizePrefix && sizeType) {
          const expectedSuffix = `${sizePrefix}${sizeType}`; // e.g., SH, SF, LH, LF
          const valCode = (value.code || '').toUpperCase();
          // Robust suffix match: allow `_LH`, `-LH`, or `LH` at end of code (case-insensitive)
          const suffixPattern = new RegExp(`(?:_|-|\\b)${expectedSuffix}$`, 'i');
          const matches = suffixPattern.test(valCode);
          console.log(`🎯 Expected suffix: ${expectedSuffix}, Value: ${valCode}, Matches: ${matches}`);
          if (!matches) return false;
        }
      } else {
        console.log('⚠️ No cabinet size selected yet');
      }
    }

    return true;
  }

  getValueByCode(groupHandle, code) {
    const group = this.data.optionGroups.find(g => g.handle === groupHandle);
    return group?.values.find(v => v.code === code);
  }

  isValueAvailableForStep(step, value) {
    // For other steps, use the existing dependency logic
    const group = this.data.optionGroups.find(g => 
      g.values.some(v => v.code === value.code)
    );
    
    return this.isValueAvailable(group, value);
  }
  
  isFilteredByShowOptions(group, value) {
    // Check if this value is hidden by showOptions (size-based filtering)
    let hasShowOptionsForThisGroup = false;
    let isAllowedByShowOptions = false;
    
    for (const [groupHandle, selectedCodes] of Object.entries(this.state.selections)) {
      const selected = Array.isArray(selectedCodes) ? selectedCodes : Object.keys(selectedCodes);
      
      for (const selectedCode of selected) {
        const selectedValue = this.getValueByCode(groupHandle, selectedCode);
        
        if (selectedValue && selectedValue.showOptions && selectedValue.showOptions.length > 0) {
          const showOptionsInThisGroup = selectedValue.showOptions.filter(showCode => {
            const showOption = this.getValueByCode(group.handle, showCode);
            return showOption !== undefined;
          });
          
          if (showOptionsInThisGroup.length > 0) {
            hasShowOptionsForThisGroup = true;
            
            if (selectedValue.showOptions.includes(value.code)) {
              isAllowedByShowOptions = true;
              break;
            }
            
            // Check for _WW twin: if this value ends with _WW, check if its twin (without _WW) is in showOptions
            if (value.code.endsWith('_WW')) {
              const twinCode = value.code.slice(0, -3); // Remove _WW suffix
              if (selectedValue.showOptions.includes(twinCode)) {
                isAllowedByShowOptions = true;
                break;
              }
            }
          }
        }
      }
      if (isAllowedByShowOptions) break;
    }
    
    // Returns true if filtered OUT by showOptions
    return hasShowOptionsForThisGroup && !isAllowedByShowOptions;
  }
  
  organizeAccessoriesByCategory(accessories) {
    // Define category order
    const categoryOrder = ['bundle', 'door_organization', 'exterior_accessories', 'mirrors_lighting', 'other'];
    const categoryLabels = {
      'bundle': 'Bundles',
      'door_organization': 'Door Organization',
      'exterior_accessories': 'Exterior Accessories',
      'mirrors_lighting': 'Mirrors & Lighting',
      'other': 'Other'
    };
    
    const categorized = {};
    
    accessories.forEach(accessory => {
      // Check if accessory has a category in its dependencies
      let category = 'other'; // default category
      
      if (accessory.dependencies && accessory.dependencies.category) {
        category = accessory.dependencies.category;
      }
      
      if (!categorized[category]) {
        categorized[category] = [];
      }
      categorized[category].push(accessory);
    });
    
    // Return categories in the specified order
    const orderedCategories = [];
    categoryOrder.forEach(cat => {
      if (categorized[cat] && categorized[cat].length > 0) {
        orderedCategories.push({
          key: cat,
          label: categoryLabels[cat] || cat,
          items: categorized[cat]
        });
      }
    });
    
    return orderedCategories;
  }

  renderQuizSteps() {
    const quizSteps = document.getElementById('quiz-steps');
    if (!quizSteps) return;

    quizSteps.innerHTML = '';

    // Get current model for hiding options
    const currentModel = this.getCurrentModel();
    const hideOptionValues = currentModel ? currentModel.hideOptionValues || [] : [];

    // Render all steps (excluding size which is handled separately)
    this.steps.forEach((step, index) => {
      const stepEl = document.createElement('div');
      stepEl.className = 'quiz-step';
      stepEl.dataset.step = index + 1;
      
      // Get all option groups for this step
      const stepGroups = this.data.optionGroups.filter(group => 
        step.groups.includes(group.handle)
      );
      
      // Determine title and description for the step header
      let stepTitle = step.title;
      let stepDescription = step.description;
      
      // Special handling for exterior finish - use titleName if available
      if (step.key === 'exterior_finish' && stepGroups.length > 0 && stepGroups[0].titleName) {
        // Check if it's a color selection step (color_single or color_gloss)
        if (stepGroups[0].handle === 'color_single' || stepGroups[0].handle === 'color_gloss') {
          stepTitle = 'Choose a Color';
        } else {
          stepTitle = stepGroups[0].titleName;
        }
        stepDescription = 'Select one';
      } else if (step.key === 'securityfire_upgrade') {
        // For securityfire_upgrade, add instruction about 4 liner limit
        stepDescription = (step.description || 'Choose your fort knox security/fire upgrade') + ' (you can choose up to 4 liners (mix and match))';
      } else if (step.key === 'cabinet_size' || stepGroups.some(g => g.handle === 'cabinet_size')) {
        // For cabinet_size, add helpful description about watch winder options
        stepDescription = (step.description || 'Select a cabinet size') + ' (options with "Watch Winders" allow you to add watch winders to your cabinet)';
      } else if (step.key === 'bundle_accessories') {
        // For bundle_accessories, use the accessory group's titleName
        const accessoryGroup = stepGroups.find(g => g.handle === 'accessory');
        if (accessoryGroup && accessoryGroup.titleName) {
          stepTitle = accessoryGroup.titleName;
        }
      } else if (stepGroups.length > 0 && stepGroups[0].titleName) {
        // For all other steps, use titleName if available
        stepTitle = stepGroups[0].titleName;
      }
      
      const stepHeader = document.createElement('div');
      stepHeader.className = 'step-header';
      
      // Add "(Optional)" to title if step is optional
      const optionalLabel = step.isOptional ? ' <span class="optional-label">(Optional)</span>' : '';
      
      stepHeader.innerHTML = `
        <h2>${stepTitle}${optionalLabel}</h2>
        <p>${stepDescription}</p>
      `;
      
      let optionGrid;
      
      if (step.isTwoTone) {
        // Create tabbed interface for two-tone colors
        optionGrid = this.createTwoToneInterface(stepGroups);
      } else {
        // Create regular option grid
        optionGrid = document.createElement('div');
        optionGrid.className = 'option-grid';
        
        // Check if any group in this step has custom column amount
        const groupWithColumns = stepGroups.find(g => g.columnAmount);
        if (groupWithColumns && groupWithColumns.columnAmount) {
          try {
            const columnConfig = typeof groupWithColumns.columnAmount === 'string' 
              ? JSON.parse(groupWithColumns.columnAmount) 
              : groupWithColumns.columnAmount;
            
            if (columnConfig.column_amount) {
              optionGrid.setAttribute('data-custom-columns', columnConfig.column_amount);
            }
          } catch (e) {
            console.warn('Failed to parse column_amount JSON:', e);
          }
        }
        
        // Check if any group has image position setting
        const groupWithImagePosition = stepGroups.find(g => g.imagePosition);
        if (groupWithImagePosition && groupWithImagePosition.imagePosition) {
          optionGrid.setAttribute('data-image-position', groupWithImagePosition.imagePosition);
        }
        
        // Calculate total option count for all groups in this step (only available options)
        const totalOptionCount = stepGroups.reduce((total, group) => {
          const availableCount = group.values.filter(v => this.isValueAvailableForStep(step, v)).length;
          return total + availableCount;
        }, 0);
        optionGrid.setAttribute('data-option-count', totalOptionCount);
      }
      
      if (!step.isTwoTone) {
      stepGroups.forEach(group => {
        // Sort exterior finish options by price (lowest to highest)
        if (group.handle === 'exterior_finish') {
          group.values.sort((a, b) => {
            const priceA = this.getOptionPrice(a, group.handle);
            const priceB = this.getOptionPrice(b, group.handle);
            return priceA - priceB; // Lowest to highest
          });
        }
        
        // Special handling for accessories - organize by category
        if (group.handle === 'accessory') {
          const categories = this.organizeAccessoriesByCategory(group.values);
          
          categories.forEach(category => {
            // Add category header
            const categoryHeader = document.createElement('h3');
            categoryHeader.className = 'group-header';
            categoryHeader.textContent = category.label;
            categoryHeader.style.cssText = `
              grid-column: 1 / -1;
              margin: 1.5rem 0 0.75rem 0;
              font-size: 1.1rem;
              font-weight: 600;
              color: var(--text);
              border-bottom: 1px solid var(--border);
              padding-bottom: 0.5rem;
            `;
            optionGrid.appendChild(categoryHeader);
            
            // Render accessories in this category
            category.items.forEach(value => {
              // Check showOptions filtering first (size-based visibility)
              const isFilteredBySize = this.isFilteredByShowOptions(group, value);
              if (isFilteredBySize) {
                // Hidden by showOptions - don't render at all
                return;
              }
              
              // Check if hidden by hideOptions - skip rendering completely
              if (this.state.currentConfiguration && 
                  this.state.currentConfiguration.hideOptions && 
                  this.state.currentConfiguration.hideOptions.includes(value.code)) {
                // Hidden by hideOptions - don't render at all
                return;
              }
              
              // Check if hidden by model hideOptionValues
              if (hideOptionValues.includes(value.code)) {
                // Hidden by model - don't render at all
                return;
              }
              
              // For multi-select groups, show all options but disable those that don't meet requirements
              // For single-select groups, hide options that don't meet requirements
              const isAvailable = this.isValueAvailableForStep(step, value);
              const isMultiSelect = group.selectType === 'multi' || group.handle === 'cabinet';
              
              // Skip rendering only for single-select unavailable options
              if (!isAvailable && !isMultiSelect) {
                return;
              }
              
              const hasColor = value.hexColor && value.hexColor.trim() !== '';
              const hasSizeImage = value.sizeImage && value.sizeImage.trim() !== '';
              const hasOptionImage = value.optionImage && value.optionImage.trim() !== '';
              const hasTooltip = value.tooltip && value.tooltip.trim() !== '';
              
              // Check if this option is included in a bundle
              const isIncluded = this.checkIfIncluded(value.code);
              
              let priceDisplay = '';
              if (isIncluded) {
                priceDisplay = '<span class="option-price included">INCLUDED</span>';
              } else {
                // Use the getOptionPrice method which handles defaultPrice -> dependencies -> priceModifierValue fallback
                const optionPrice = this.getOptionPrice(value, group.handle);
                
                // Only display price if not zero
                if (optionPrice !== 0) {
                  const sign = optionPrice > 0 ? '+' : '';
                  priceDisplay = `<span class="option-price">${sign}$${this.formatPrice(optionPrice)}</span>`;
                }
              }

              const optionCard = document.createElement('button');
              optionCard.className = [
                'option-card',
                hasOptionImage ? 'has-image' : '',
                isIncluded ? 'option-included' : '',
                !isAvailable ? 'option-disabled' : ''
              ].filter(Boolean).join(' ');
              optionCard.dataset.value = value.code;
              optionCard.dataset.group = group.handle;
              
              // Disable button if not available
              if (!isAvailable) {
                optionCard.disabled = true;
                optionCard.style.cursor = 'not-allowed';
              }
              
          if (hasOptionImage) {
            optionCard.innerHTML = `
              ${hasTooltip ? `
                <div class="tooltip-icon" data-tooltip="${value.tooltip.replace(/"/g, '&quot;')}" tabindex="0">
                  <i data-lucide="info"></i>
                </div>
              ` : ''}
              <div class="size-image-container">
                <img src="${value.optionImage}" alt="${value.label.replace(/"/g, '&quot;')}" class="size-image" />
                <div class="size-tooltip">
                  <div class="size-tooltip-text">${value.label}</div>
                  ${priceDisplay ? `<div class="size-tooltip-price">${priceDisplay}</div>` : ''}
                </div>
              </div>
              <span>${value.label}</span>
              ${priceDisplay}
            `;
            
            // Add click handler to tooltip icon to prevent card selection
            if (hasTooltip) {
              const tooltipIcon = optionCard.querySelector('.tooltip-icon');
              if (tooltipIcon) {
                tooltipIcon.addEventListener('click', (e) => {
                  e.stopPropagation();
                });
              }
            }
          } else if (hasColor) {
            optionCard.innerHTML = `
              ${hasTooltip ? `
                <div class="tooltip-icon" data-tooltip="${value.tooltip.replace(/"/g, '&quot;')}" tabindex="0">
                  <i data-lucide="info"></i>
                </div>
              ` : ''}
              <div class="color-preview" style="background-color: ${value.hexColor}"></div>
              <span>${value.label}</span>
              ${priceDisplay}
            `;
            
            // Add click handler to tooltip icon to prevent card selection
            if (hasTooltip) {
              const tooltipIcon = optionCard.querySelector('.tooltip-icon');
              if (tooltipIcon) {
                tooltipIcon.addEventListener('click', (e) => {
                  e.stopPropagation();
                });
              }
            }
          } else if (hasSizeImage) {
            optionCard.innerHTML = `
              ${hasTooltip ? `
                <div class="tooltip-icon" data-tooltip="${value.tooltip.replace(/"/g, '&quot;')}" tabindex="0">
                  <i data-lucide="info"></i>
                </div>
              ` : ''}
              <div class="size-image-container">
                <img src="${value.sizeImage}" alt="${value.label.replace(/"/g, '&quot;')}" class="size-image" />
                <div class="size-tooltip">
                  <div class="size-tooltip-text">${value.label}</div>
                  ${priceDisplay ? `<div class="size-tooltip-price">${priceDisplay}</div>` : ''}
                </div>
              </div>
              <span>${value.label}</span>
              ${priceDisplay}
            `;
            
            // Add click handler to tooltip icon to prevent card selection
            if (hasTooltip) {
              const tooltipIcon = optionCard.querySelector('.tooltip-icon');
              if (tooltipIcon) {
                tooltipIcon.addEventListener('click', (e) => {
                  e.stopPropagation();
                });
              }
            }
          } else {
            optionCard.innerHTML = `
              ${hasTooltip ? `
                <div class="tooltip-icon" data-tooltip="${value.tooltip.replace(/"/g, '&quot;')}" tabindex="0">
                  <i data-lucide="info"></i>
                </div>
              ` : ''}
              <div class="option-icon">
                <i data-lucide="check" class="icon-lg"></i>
              </div>
              <div class="option-content">
                <h3>${value.label}</h3>
                ${priceDisplay ? `<p>${priceDisplay}</p>` : ''}
              </div>
            `;
            
            // Add click handler to tooltip icon to prevent card selection
            if (hasTooltip) {
              const tooltipIcon = optionCard.querySelector('.tooltip-icon');
              if (tooltipIcon) {
                tooltipIcon.addEventListener('click', (e) => {
                  e.stopPropagation();
                });
              }
            }
          }              // Auto-select and mark as selected if this option is included
              if (isIncluded) {
                optionCard.classList.add('selected');
                // Auto-add to selections if not already selected
                if (!this.state.selections[group.handle]) {
                  this.state.selections[group.handle] = {};
                }
                if (!this.state.selections[group.handle][value.code]) {
                  this.state.selections[group.handle][value.code] = 1;
                }
              }
              
              optionGrid.appendChild(optionCard);
            });
          });
          
          return; // Skip the regular rendering for accessories
        }
        
        // Add group header if multiple groups in step (but not accessories)
        if (stepGroups.length > 1) {
          const groupHeader = document.createElement('h3');
          groupHeader.className = 'group-header';
          groupHeader.textContent = group.displayName;
          groupHeader.style.cssText = `
            grid-column: 1 / -1;
            margin: 1rem 0 0.5rem 0;
            font-size: 1.1rem;
            font-weight: 600;
            color: var(--text);
            border-bottom: 1px solid var(--border);
            padding-bottom: 0.5rem;
          `;
          optionGrid.appendChild(groupHeader);
        }
        
        // Cabinet size grouping: split into Standard / Watch Winders sections
        if (group.handle === 'cabinet_size') {
          const standardOptions = [];
          const watchWinderOptions = [];
          group.values.forEach(value => {
            const labelLower = value.label.toLowerCase();
            if (labelLower.includes('watch winder')) {
              watchWinderOptions.push(value);
            } else {
              standardOptions.push(value);
            }
          });

          // Only show group headers if both sections have options
          const showGroupHeaders = standardOptions.length > 0 && watchWinderOptions.length > 0;

          if (showGroupHeaders && standardOptions.length > 0) {
            const header = document.createElement('h3');
            header.className = 'group-header cabinet-group-header';
            header.textContent = 'Standard Cabinets';
            optionGrid.appendChild(header);
          }

          // Render standard options using the shared helper
          standardOptions.forEach(value => this._renderCabinetSizeOption(value, group, step, optionGrid, hideOptionValues));

          if (showGroupHeaders && watchWinderOptions.length > 0) {
            const header = document.createElement('h3');
            header.className = 'group-header cabinet-group-header';
            header.textContent = 'Cabinets with Watch Winders';
            optionGrid.appendChild(header);
          }

          // Render watch winder options using the shared helper
          watchWinderOptions.forEach(value => this._renderCabinetSizeOption(value, group, step, optionGrid, hideOptionValues));

          // Skip the normal rendering loop for cabinet_size
          stepEl.appendChild(stepHeader);
          stepEl.appendChild(optionGrid);
          quizSteps.appendChild(stepEl);

          // Re-render lucide icons for this step
          if (typeof lucide !== 'undefined') lucide.createIcons();
          return; // continue to next group — this group is done
        }

        group.values.forEach(value => {
          // Debug logging for cabinet_size to see why options are filtered
          if (group.handle === 'cabinet_size') {
            console.log('🔍 Cabinet option:', value.code, value.label);
          }
          
          // Check showOptions filtering first (size-based visibility)
          const isFilteredBySize = this.isFilteredByShowOptions(group, value);
          if (isFilteredBySize) {
            if (group.handle === 'cabinet_size') {
              console.log('❌ Filtered by showOptions:', value.code);
            }
            // Hidden by showOptions - don't render at all (for any group type)
            return;
          }
          
          // Check if hidden by hideOptions - skip rendering completely
          if (this.state.currentConfiguration && 
              this.state.currentConfiguration.hideOptions && 
              this.state.currentConfiguration.hideOptions.includes(value.code)) {
            if (group.handle === 'cabinet_size') {
              console.log('❌ Hidden by hideOptions:', value.code);
            }
            // Hidden by hideOptions - don't render at all
            return;
          }
          
          // Check if hidden by model hideOptionValues
          if (hideOptionValues.includes(value.code)) {
            if (group.handle === 'cabinet_size') {
              console.log('❌ Hidden by model hideOptionValues:', value.code);
            }
            // Hidden by model - don't render at all
            return;
          }
          
          // For multi-select groups, show all options but disable those that don't meet requirements
          // For single-select groups, hide options that don't meet requirements
          const isAvailable = this.isValueAvailableForStep(step, value);
          const isMultiSelect = group.selectType === 'multi' || group.handle === 'cabinet';
          
          if (group.handle === 'cabinet_size') {
            console.log('✅ Available:', value.code, isAvailable);
          }
          
          // Skip rendering only for single-select unavailable options
          if (!isAvailable && !isMultiSelect) {
            if (group.handle === 'cabinet_size') {
              console.log('❌ Skipped (not available, not multi-select):', value.code);
            }
            return;
          }
          
          const hasColor = value.hexColor && value.hexColor.trim() !== '';
          const hasSizeImage = value.sizeImage && value.sizeImage.trim() !== '';
          const hasOptionImage = value.optionImage && value.optionImage.trim() !== '';
          const hasTooltip = value.tooltip && value.tooltip.trim() !== '';
          
          // Check if this option is included in a bundle
          const isIncluded = this.checkIfIncluded(value.code);
          
          let priceDisplay = '';
          if (isIncluded) {
            priceDisplay = '<span class="option-price included">INCLUDED</span>';
          } else {
            // Use the getOptionPrice method which handles defaultPrice -> dependencies -> priceModifierValue fallback
            const optionPrice = this.getOptionPrice(value, group.handle);
            
            // Only display price if not zero
            if (optionPrice !== 0) {
              const sign = optionPrice > 0 ? '+' : '';
              priceDisplay = `<span class="option-price">${sign}$${this.formatPrice(optionPrice)}</span>`;
            }
          }

          const optionCard = document.createElement('button');
          optionCard.className = [
            'option-card',
            hasOptionImage ? 'has-image' : '',
            isIncluded ? 'option-included' : '',
            !isAvailable ? 'option-disabled' : ''
          ].filter(Boolean).join(' ');
          optionCard.dataset.value = value.code;
          optionCard.dataset.group = group.handle;
          
          // Disable button if not available
          if (!isAvailable) {
            optionCard.disabled = true;
            optionCard.style.cursor = 'not-allowed';
          }
          
          if (hasOptionImage) {
            optionCard.innerHTML = `
              ${hasTooltip ? `
                <div class="tooltip-icon" data-tooltip="${value.tooltip.replace(/"/g, '&quot;')}" tabindex="0">
                  <i data-lucide="info"></i>
                </div>
              ` : ''}
              <div class="size-image-container">
                <img src="${value.optionImage}" alt="${value.label.replace(/"/g, '&quot;')}" class="size-image" />
                <div class="size-tooltip">
                  <div class="size-tooltip-text">${value.label}</div>
                  ${priceDisplay ? `<div class="size-tooltip-price">${priceDisplay}</div>` : ''}
                </div>
              </div>
              <span>${value.label}</span>
              ${priceDisplay}
            `;
            
            // Add click handler to tooltip icon to prevent card selection
            if (hasTooltip) {
              const tooltipIcon = optionCard.querySelector('.tooltip-icon');
              if (tooltipIcon) {
                tooltipIcon.addEventListener('click', (e) => {
                  e.stopPropagation();
                });
              }
            }
          } else if (hasColor) {
            optionCard.innerHTML = `
              ${hasTooltip ? `
                <div class="tooltip-icon" data-tooltip="${value.tooltip.replace(/"/g, '&quot;')}" tabindex="0">
                  <i data-lucide="info"></i>
                </div>
              ` : ''}
              <div class="color-preview" style="background-color: ${value.hexColor}"></div>
              <span>${value.label}</span>
              ${priceDisplay}
            `;
            
            // Add click handler to tooltip icon to prevent card selection
            if (hasTooltip) {
              const tooltipIcon = optionCard.querySelector('.tooltip-icon');
              if (tooltipIcon) {
                tooltipIcon.addEventListener('click', (e) => {
                  e.stopPropagation();
                });
              }
            }
          } else if (hasSizeImage) {
            optionCard.innerHTML = `
              ${hasTooltip ? `
                <div class="tooltip-icon" data-tooltip="${value.tooltip.replace(/"/g, '&quot;')}" tabindex="0">
                  <i data-lucide="info"></i>
                </div>
              ` : ''}
              <div class="size-image-container">
                <img src="${value.sizeImage}" alt="${value.label.replace(/"/g, '&quot;')}" class="size-image" />
                <div class="size-tooltip">
                  <div class="size-tooltip-text">${value.label}</div>
                  ${priceDisplay ? `<div class="size-tooltip-price">${priceDisplay}</div>` : ''}
                </div>
              </div>
              <span>${value.label}</span>
              ${priceDisplay}
            `;
            
            // Add click handler to tooltip icon to prevent card selection
            if (hasTooltip) {
              const tooltipIcon = optionCard.querySelector('.tooltip-icon');
              if (tooltipIcon) {
                tooltipIcon.addEventListener('click', (e) => {
                  e.stopPropagation();
                });
              }
            }
          } else {
            optionCard.innerHTML = `
              ${hasTooltip ? `
                <div class="tooltip-icon" data-tooltip="${value.tooltip.replace(/"/g, '&quot;')}" tabindex="0">
                  <i data-lucide="info"></i>
                </div>
              ` : ''}
              <div class="option-icon">
                <i data-lucide="check" class="icon-lg"></i>
              </div>
              <div class="option-content">
                <h3>${value.label}</h3>
                ${priceDisplay ? `<p>${priceDisplay}</p>` : ''}
              </div>
            `;
            
            // Add click handler to tooltip icon to prevent card selection
            if (hasTooltip) {
              const tooltipIcon = optionCard.querySelector('.tooltip-icon');
              if (tooltipIcon) {
                tooltipIcon.addEventListener('click', (e) => {
                  e.stopPropagation();
                });
              }
            }
          }
          
          // Always add quantity selector for options with maxCount > 1
          // Also add for STAINLESS_10GA_PKG and ARMAKNOX_AR500_3_16
          const isQuantityEnabledLiner = ['STAINLESS_10GA_PKG', 'ARMAKNOX_AR500_3_16'].includes(value.code);
          
          if ((value.maxCount && value.maxCount > 1) || isQuantityEnabledLiner) {
            // Create effective option value with maxCount for liners
            const effectiveValue = isQuantityEnabledLiner && (!value.maxCount || value.maxCount === 1)
              ? { ...value, maxCount: 4 }
              : value;
            
            const quantitySelector = this.createQuantitySelector(effectiveValue);
            const optionContent = optionCard.querySelector('.option-content');
            if (optionContent) {
              optionContent.appendChild(quantitySelector);
            } else {
              optionCard.appendChild(quantitySelector);
            }
          }
          
          // Auto-select and mark as selected if this option is included
          if (isIncluded) {
            optionCard.classList.add('selected');
            // Auto-add to selections if not already selected
            const isMultiSelect = group.selectType === 'multi' || group.handle === 'cabinet' || group.handle === 'securityfire_upgrade' || group.handle === 'bundle';
            if (!this.state.selections[group.handle] || 
                (isMultiSelect && !this.state.selections[group.handle][value.code]) ||
                (!isMultiSelect && !this.state.selections[group.handle].includes(value.code))) {
              if (isMultiSelect) {
                // Multi-select: initialize as object if needed
                if (!this.state.selections[group.handle]) {
                  this.state.selections[group.handle] = {};
                }
                this.state.selections[group.handle][value.code] = 1;
              } else {
                // Single-select: initialize as array if needed
                if (!this.state.selections[group.handle]) {
                  this.state.selections[group.handle] = [];
                }
                // Don't auto-add for single-select, just highlight
                // Users might want to choose different option in that group
              }
            }
          }
          
          optionGrid.appendChild(optionCard);
        });
      });
      }

      stepEl.appendChild(stepHeader);
      stepEl.appendChild(optionGrid);
      quizSteps.appendChild(stepEl);
    });

    // Re-initialize icons
    if (typeof lucide !== 'undefined') {
      lucide.createIcons();
    }
  }
}

// Initialize configurator when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const configuratorEl = document.querySelector('.product-configurator');
  if (configuratorEl) {
    // Store globally so quote-request.js can access it
    window.configuratorInstance = new ProductConfigurator(configuratorEl);
  }
});

// Initialize sticky cart for back to top button
function initStickyCart() {
  const stickyCart = document.getElementById("sticky-cart");
  const jumpToTopBtn = document.getElementById("jump-to-top");
  const productSection = document.querySelector(".product-configurator");

  if (!stickyCart || !jumpToTopBtn) {
    return;
  }

  // Check if user has scrolled past the product section for jump to top button
  const jumpToTopObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) {
          // User has scrolled past the product section, show jump to top
          jumpToTopBtn.style.display = "flex";
          stickyCart.classList.add("visible");
        } else {
          // User is still in the product section, hide jump to top
          jumpToTopBtn.style.display = "none";
          stickyCart.classList.remove("visible");
        }
      });
    },
    { 
      threshold: 0.1,
      rootMargin: "0px 0px 50px 0px"
    }
  );

  if (productSection && jumpToTopBtn) {
    jumpToTopObserver.observe(productSection);
  }

  // Add click handler for jump to top button
  if (jumpToTopBtn) {
    jumpToTopBtn.addEventListener("click", () => {
      window.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    });
  }
}

// Initialize sticky cart when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  initStickyCart();
});

// ========== TEST BUTTON - REMOVE WHEN DONE TESTING ==========
// Set to false to disable the test button
const ENABLE_TEST_BUTTON = false;

if (ENABLE_TEST_BUTTON) {
  document.addEventListener('DOMContentLoaded', () => {
    setTimeout(() => {
      const configurator = window.productConfiguratorInstance;
      
      if (configurator && configurator.steps) {
        // Mark all steps as visited
        configurator.steps.forEach((step, index) => {
          configurator.visitedSteps.add(index + 1);
        });
        
        // Override updateStepIndicator to always enable all steps in test mode
        const originalUpdateStepIndicator = configurator.updateStepIndicator.bind(configurator);
        configurator.updateStepIndicator = function() {
          originalUpdateStepIndicator();
          // After normal update, force all steps to be enabled
          const stepCircles = document.querySelectorAll('.step-circle');
          stepCircles.forEach((circle) => {
            circle.classList.remove('disabled');
            circle.classList.add('completed');
            circle.style.cursor = 'pointer';
            circle.style.pointerEvents = 'auto';
          });
        };
        
        // Also override createStepIndicator to enable all steps immediately
        const originalCreateStepIndicator = configurator.createStepIndicator.bind(configurator);
        configurator.createStepIndicator = function() {
          originalCreateStepIndicator();
          // After creating, enable all steps
          const stepCircles = document.querySelectorAll('.step-circle');
          stepCircles.forEach((circle) => {
            circle.classList.remove('disabled');
            circle.classList.add('completed');
            circle.style.cursor = 'pointer';
            circle.style.pointerEvents = 'auto';
          });
        };
      }
      
      // Create the test button with dropdown
      const testBtn = document.createElement('div');
      testBtn.className = 'test-jump-to-summary';
      testBtn.innerHTML = `
        <div class="test-main-btn" title="Size Presets (Test Mode)">⚡</div>
        <div class="test-dropdown">
          <div class="test-dropdown-header">Quick Presets</div>
          <div class="test-preset-item" data-size="4026x20">4026x20 - Traditional</div>
          <div class="test-preset-item" data-size="6026x20">6026x20 - Traditional</div>
          <div class="test-preset-item" data-size="6637x27">6637x27 - Traditional</div>
          <div class="test-preset-item" data-size="6031x27">6031x27 - Traditional</div>
          <div class="test-preset-item" data-size="7241x27">7241x27 - Traditional</div>
          <div class="test-preset-item" data-size="7251x27">7251x27 - Traditional</div>
          <div class="test-preset-item" data-size="7251x40XD">7251x40XD - Traditional</div>
          <div class="test-preset-item" data-size="7261x27">7261x27 - Traditional</div>
          <div class="test-preset-item" data-size="7261x40XD">7261x40XD - Traditional</div>
        </div>
      `;
      
      const mainBtn = testBtn.querySelector('.test-main-btn');
      const dropdown = testBtn.querySelector('.test-dropdown');
      
      // Toggle dropdown on main button click
      mainBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        dropdown.classList.toggle('show');
      });
      
      // Close dropdown when clicking outside
      document.addEventListener('click', (e) => {
        if (!testBtn.contains(e.target)) {
          dropdown.classList.remove('show');
        }
      });
      
      // Handle preset clicks
      testBtn.querySelectorAll('.test-preset-item').forEach(item => {
        item.addEventListener('click', (e) => {
          e.stopPropagation();
          const sizeCode = item.dataset.size;
          if (configurator) {
            // Hide dropdown
            dropdown.classList.remove('show');
            // Select the size
            configurator.selectSize(sizeCode);
            // Preset is auto-applied in selectSize via applyPresetForSize
            // Jump to summary after a brief delay to let preset apply
            setTimeout(() => {
              configurator.showSummary();
            }, 100);
          }
        });
      });
      
      document.body.appendChild(testBtn);
    }, 500);
  });
}
// ========== END TEST BUTTON ========== */

